import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/Card'
import { Badge } from 'components/ui/Badge'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { Textarea } from 'components/ui/Textarea'
import { ModernDatePicker } from 'components/ui/ModernDatePicker'
import { CustomSelect } from 'components/ui/CustomSelect'
import { Switch } from 'components/ui/switch'
import { DynamicList } from 'components/ui/DynamicList'
import { LanguageField, type LanguageItem } from 'components/ui/LanguageField'
import { SimpleRichTextEditor } from 'components/ui/SimpleRichTextEditor'
import { DraggablePieChart } from 'components/ui/DraggablePieChart'
import { Modal } from 'components/ui/Modal'
import { Dropdown, DropdownItem } from 'components/ui/Dropdown'
import { offersApi, candidatesApi, applicationsApi, teamsApi, matchingJobsApi, interviewsApi, hiringProcessApi, interviewAudioApi, type Offer, type Candidate, type Application, type Team, type MatchingJob, type MatchingResultCategory, type Interview, type HiringStep, type CommonQuestion, type InterviewQuestionSection } from 'lib/api'
import { isOfferTranslationComplete } from 'lib/offerTranslationUtils'
import { sanitizeRichHtml } from 'lib/sanitize'
import { buildRecommendedSkillsDataFromAiResponse } from 'lib/offerRecommendedSkillsUtils'
import { translateFrToEn } from 'lib/translationService'
import { AIMatchingModal } from 'features/offers/components/AIMatchingModal'
import { RecommendedSkillsModal, type RecommendedSkillsData } from 'features/offers/components/RecommendedSkillsModal'
import { CreateGroupFromCandidatesModal } from 'features/offers/components/CreateGroupFromCandidatesModal'
import { ManageCandidatesModal } from 'features/offers/components/ManageCandidatesModal'
import { AddCandidatesModal } from 'features/candidates/components/AddCandidatesModal'
import { InterviewCalendar, type InterviewEvent } from 'features/offers/components/InterviewCalendar'
import { ScheduleInterviewModal } from 'features/offers/components/ScheduleInterviewModal'
import { DepartmentSelect } from '../components/DepartmentSelect'
import {
  OfferDetailPageHeader,
  type OfferDetailPanel,
} from 'features/offers/components/OfferDetailPageHeader'
import { OfferRecruitmentProcessBar } from 'features/offers/components/OfferRecruitmentProcessBar'
import { OfferDetailFullPageOverlay } from 'features/offers/components/OfferDetailFullPageOverlay'
import { HiringStepModal } from 'features/hiring/components/HiringStepModal'
import { STEP_COLORS, DEFAULT_STEP_COLOR, DEFAULT_STEP_TYPE, getUserAddStepCategoryOptions, isDuplicateNewStep, isResolvedStepCustom, buildStepPositionOptions } from 'constants/hiringProcess'
import { useAuth } from 'contexts/AuthContext'
import { useToast } from 'contexts/ToastContext'
import { ModernKanban } from 'components/kanban/ModernKanban'
import { getStatusColor, formatDate, generateMatchScore, getImageUrl, getMediaUrl } from 'lib/utils'
import {
  ArrowLeft,
  Edit,
  Building2,
  DollarSign,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Mail,
  Phone,
  MapPin,
  Plus,
  Eye,
  Copy,
  Link,
  Video,
  CheckCircle2,
  Star,
  FileText,
  BarChart3,
  Users,
  Brain,
  X,
  Sparkles,
  Check,
  Search,
  Upload,
  Target,
  ChevronDown,
  ChevronLeft,
  ChevronsLeft,
  ChevronRight,
  ChevronsRight,
  ExternalLink,
  Loader2,
  Trash2,
  Save,
  Pencil,
  FileEdit,
  Lightbulb,
  Info,
  Lock,
  Unlock,
  RotateCcw,
  Scale,
  Settings2,
  AlertTriangle
} from 'lucide-react'

const editableSections = [
  'job_overview',
  'description',
  'requirements',
  'main_missions',
  'soft_skills',
  'languages',
  'certifications',
  'benefits',
  'key_details',
  'ai_matching'
] as const

type MentionSplitDraft = {
  mentionnees: string[]
  nonMentionnees: string[]
}

const MAX_MANUAL_INTERVIEW_QUESTIONS = 15

const toStringList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return []
  return value
    .map(item => (typeof item === 'string' ? item : String(item ?? '')).trim())
    .filter(Boolean)
}

const extractMentionSplit = (
  source: unknown,
  mentionedKey: string,
  nonMentionedKey: string
): MentionSplitDraft => {
  if (Array.isArray(source)) {
    return {
      mentionnees: toStringList(source),
      nonMentionnees: []
    }
  }

  if (source && typeof source === 'object') {
    const data = source as Record<string, unknown>
    return {
      mentionnees: toStringList(data[mentionedKey]),
      nonMentionnees: toStringList(data[nonMentionedKey])
    }
  }

  return {
    mentionnees: [],
    nonMentionnees: []
  }
}

type SectionDrafts = {
  job_overview: {
    job_title: string
    department: string
    contract_type: string
    experience_level: string
    education_level: string
    remote_allowed: boolean
  }
  description: {
    description: string
    description_text?: string
  }
  requirements: {
    requirements_mentionnees: string[]
    requirements_non_mentionnees: string[]
  }
  main_missions: {
    main_missions: string[]
  }
  soft_skills: {
    soft_skills_mentionnees: string[]
    soft_skills_non_mentionnees: string[]
  }
  languages: {
    languages: LanguageItem[]
  }
  certifications: {
    certifications: string[]
  }
  benefits: {
    benefits_mentionnees: string[]
    benefits_non_mentionnees: string[]
  }
  key_details: {
    start_date: string
    expiry_date: string
    status: Offer['status']
    email_language: 'fr' | 'en'
  }
  ai_matching: {
    ai_matching_weights: NonNullable<Offer['ai_matching_weights']>
  }
}

type EditableSection = keyof SectionDrafts
type AiMatchingKey = keyof NonNullable<Offer['ai_matching_weights']>

// Order for AI matching weights to match Job Offer Details order
const aiMatchingWeightsOrder: AiMatchingKey[] = [
  'hard_skills',
  'soft_skills',
  'certifications',
  'nice_to_have',
  'languages',
  'experience_years',
  'niveau_etude',
  'main_missions',
]

const DEFAULT_SECTION_WEIGHTS = {
  hard_skills: 2.0,
  soft_skills: 1.5,
  experience: 2.0,
  projets: 1.5,
  certifications: 1.0,
  formations: 1.0,
  nice_to_have: 0.5,
  langues: 0.5,
  questions_communes: 1.0,
}

// Sections a manually authored question can be attached to. Each one is scored
// with the weight configured in the offer's section weights.
// `questions_communes` is excluded: those questions are asked to every candidate
// via the dedicated "Questions Communes" flow, not manual creation.
const MANUAL_QUESTION_SECTIONS: InterviewQuestionSection[] = [
  'hard_skills',
  'soft_skills',
  'experience',
  'projets',
  'formations',
  'certifications',
  'langues',
  'nice_to_have',
]

const DEFAULT_MANUAL_QUESTION_SECTION: InterviewQuestionSection = 'hard_skills'

// Statuses offered by the validation interviews filter, in lifecycle order.
const VALIDATION_INTERVIEW_STATUS_FILTERS = [
  'pending_generation',
  'generated',
  'sent',
  'recording',
  'partial',
  'completed',
  'missed',
]

type MatchingBreakdownCategoryForOffer = MatchingResultCategory

type MatchingResultWithFilterStatus = {
  result: any
  matchScore: number
  passesFilters: boolean
  filterReasons: string[]
  isSelected: boolean
}

type SectionErrorMessage = {
  key: string
  defaultMessage: string
}

type OfferLocationState = {
  initialTab?: string
  from?: string
}

const defaultAiMatchingWeights: NonNullable<Offer['ai_matching_weights']> = {
  hard_skills: 2,
  soft_skills: 2,
  experience_years: 2,
  niveau_etude: 2,
  languages: 1,
  certifications: 1,
  main_missions: 1,
  nice_to_have: 0,
}

const buildDefaultDepartments = (t: ReturnType<typeof useTranslation>['t']) => [
  t('createOffer.departments.development'),
  t('createOffer.departments.design'),
  t('createOffer.departments.marketing'),
  t('createOffer.departments.sales'),
  t('createOffer.departments.hr'),
  t('createOffer.departments.finance'),
  t('createOffer.departments.support'),
  t('createOffer.departments.other')
]

const clampPercentage = (value: number | string | undefined) => {
  const numericValue = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(numericValue)) return 0
  return Math.max(0, Math.min(10, Math.round(numericValue)))
}

const clampHardSkillWeight = (value: number | string | undefined) => {
  const numericValue = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(numericValue)) return 1
  return Math.max(1, Math.min(5, Math.round(numericValue)))
}

const redistributeAiMatchingWeights = (
  weights: NonNullable<Offer['ai_matching_weights']>,
  changedKey: AiMatchingKey,
  rawNextValue: number
): NonNullable<Offer['ai_matching_weights']> => {
  const sanitizedWeights = { ...weights }
    ; (Object.keys(sanitizedWeights) as AiMatchingKey[]).forEach(key => {
      sanitizedWeights[key] = clampPercentage(sanitizedWeights[key]) as typeof sanitizedWeights[typeof key]
    })

  const nextValue = clampPercentage(rawNextValue)
  sanitizedWeights[changedKey] = nextValue as typeof sanitizedWeights[typeof changedKey]

  const otherKeys = (Object.keys(sanitizedWeights) as AiMatchingKey[]).filter(key => key !== changedKey)

  if (otherKeys.length === 0) {
    sanitizedWeights[changedKey] = 10 as typeof sanitizedWeights[typeof changedKey]
    return sanitizedWeights
  }

  const targetOtherTotal = Math.max(0, 10 - nextValue)

  if (targetOtherTotal === 0) {
    otherKeys.forEach(key => {
      sanitizedWeights[key] = 0 as typeof sanitizedWeights[typeof key]
    })
  } else {
    const currentOtherTotal = otherKeys.reduce(
      (sum, key) => sum + (sanitizedWeights[key] as number),
      0
    )

    if (currentOtherTotal === 0) {
      const baseShare = Math.floor(targetOtherTotal / otherKeys.length)
      let remainder = targetOtherTotal - baseShare * otherKeys.length
      otherKeys.forEach(key => {
        let value = baseShare
        if (remainder > 0) {
          value += 1
          remainder -= 1
        }
        sanitizedWeights[key] = value as typeof sanitizedWeights[typeof key]
      })
    } else {
      const scaleFactor = targetOtherTotal / currentOtherTotal
      let accumulated = 0
      otherKeys.forEach((key, index) => {
        let adjusted = Math.round((sanitizedWeights[key] as number) * scaleFactor)
        if (index === otherKeys.length - 1) {
          adjusted = Math.max(0, targetOtherTotal - accumulated)
        } else {
          accumulated += adjusted
        }
        sanitizedWeights[key] = adjusted as typeof sanitizedWeights[typeof key]
      })
    }
  }

  const finalTotal = (Object.values(sanitizedWeights) as number[]).reduce((sum, val) => sum + val, 0)
  let remainingDiff = 10 - finalTotal

  if (remainingDiff !== 0) {
    const adjustmentOrder: AiMatchingKey[] = remainingDiff > 0 ? [changedKey, ...otherKeys] : [...otherKeys, changedKey]
    adjustmentOrder.some(key => {
      if (remainingDiff === 0) return true
      const currentValue = sanitizedWeights[key] as number
      if (remainingDiff > 0) {
        const adjustable = Math.min(remainingDiff, 10 - currentValue)
        if (adjustable <= 0) return false
        sanitizedWeights[key] = (currentValue + adjustable) as typeof sanitizedWeights[typeof key]
        remainingDiff -= adjustable
      } else {
        const adjustable = Math.min(currentValue, Math.abs(remainingDiff))
        if (adjustable <= 0) return false
        sanitizedWeights[key] = (currentValue - adjustable) as typeof sanitizedWeights[typeof key]
        remainingDiff += adjustable
      }
      return remainingDiff === 0
    })
  }

  return sanitizedWeights
}

const updateAiMatchingWeight = (
  weights: NonNullable<Offer['ai_matching_weights']>,
  changedKey: AiMatchingKey,
  rawNextValue: number
): NonNullable<Offer['ai_matching_weights']> => {
  const sanitizedWeights = { ...weights }
  const nextValue = clampPercentage(rawNextValue)
  sanitizedWeights[changedKey] = nextValue as typeof sanitizedWeights[typeof changedKey]
  return sanitizedWeights
}

const formatDateForInput = (date?: string | null) => {
  if (!date) return ''
  const dateOnlyMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (dateOnlyMatch) return dateOnlyMatch[0]
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return ''
  return parsed.toISOString().split('T')[0]
}

const createDefaultSectionDrafts = (): SectionDrafts => ({
  job_overview: {
    job_title: '',
    department: '',
    contract_type: '',
    experience_level: '',
    education_level: '',
    remote_allowed: false
  },
  description: {
    description: ''
  },
  requirements: {
    requirements_mentionnees: [''],
    requirements_non_mentionnees: ['']
  },
  main_missions: {
    main_missions: ['']
  },
  soft_skills: {
    soft_skills_mentionnees: [''],
    soft_skills_non_mentionnees: ['']
  },
  languages: {
    languages: []
  },
  certifications: {
    certifications: ['']
  },
  benefits: {
    benefits_mentionnees: [''],
    benefits_non_mentionnees: ['']
  },
  key_details: {
    start_date: '',
    expiry_date: '',
    status: 'draft',
    email_language: 'fr'
  },
  ai_matching: {
    ai_matching_weights: { ...defaultAiMatchingWeights }
  }
})

const buildSectionDraftsFromOffer = (offer?: Offer | null): SectionDrafts => {
  const defaults = createDefaultSectionDrafts()
  if (!offer) return defaults

  const requirementsSplit = extractMentionSplit(
    (offer as any).requirements,
    'hard_skills_mentionnées',
    'hard_skills_non_mentionnées'
  )
  const softSkillsSplit = extractMentionSplit(
    (offer as any).soft_skills,
    'soft_skills_mentionnées',
    'soft_skills_non_mentionnées'
  )
  const benefitsSplit = extractMentionSplit(
    (offer as any).benefits,
    'nice_to_have_mentionnées',
    'nice_to_have_non_mentionnées'
  )

  return {
    job_overview: {
      ...defaults.job_overview,
      job_title: offer.job_title || '',
      department: offer.department || '',
      contract_type: offer.contract_type || '',
      experience_level: offer.experience_level || '',
      education_level: offer.education_level || '',
      remote_allowed: Boolean(offer.remote_allowed)
    },
    description: {
      description: offer.description_text || offer.description || '',
      description_text: offer.description_text || offer.description || ''
    },
    requirements: {
      requirements_mentionnees: requirementsSplit.mentionnees.length
        ? [...requirementsSplit.mentionnees]
        : defaults.requirements.requirements_mentionnees,
      requirements_non_mentionnees: requirementsSplit.nonMentionnees.length
        ? [...requirementsSplit.nonMentionnees]
        : defaults.requirements.requirements_non_mentionnees
    },
    main_missions: {
      main_missions: offer.main_missions?.length ? [...offer.main_missions] : defaults.main_missions.main_missions
    },
    soft_skills: {
      soft_skills_mentionnees: softSkillsSplit.mentionnees.length
        ? [...softSkillsSplit.mentionnees]
        : defaults.soft_skills.soft_skills_mentionnees,
      soft_skills_non_mentionnees: softSkillsSplit.nonMentionnees.length
        ? [...softSkillsSplit.nonMentionnees]
        : defaults.soft_skills.soft_skills_non_mentionnees
    },
    languages: {
      languages:
        offer.languages?.map(lang => ({
          language: lang.language || lang.name || '',
          level: lang.level || ''
        })) || defaults.languages.languages
    },
    certifications: {
      certifications: offer.certifications?.length ? [...offer.certifications] : defaults.certifications.certifications
    },
    benefits: {
      benefits_mentionnees: benefitsSplit.mentionnees.length
        ? [...benefitsSplit.mentionnees]
        : defaults.benefits.benefits_mentionnees,
      benefits_non_mentionnees: benefitsSplit.nonMentionnees.length
        ? [...benefitsSplit.nonMentionnees]
        : defaults.benefits.benefits_non_mentionnees
    },
    key_details: {
      start_date: formatDateForInput(offer.start_date),
      expiry_date: formatDateForInput(offer.expiry_date),
      status: offer.status,
      email_language: offer.email_language || 'fr'
    },
    ai_matching: {
      ai_matching_weights: {
        ...defaultAiMatchingWeights,
        ...(offer.ai_matching_weights || {})
      } as NonNullable<Offer['ai_matching_weights']>
    }
  }
}

const createEditingState = (): Record<EditableSection, boolean> =>
  editableSections.reduce((acc, key) => {
    acc[key] = false
    return acc
  }, {} as Record<EditableSection, boolean>)

const deepClone = <T,>(value: T): T => {
  if (value === undefined || value === null) {
    return value
  }
  return JSON.parse(JSON.stringify(value)) as T
}

const parseDraftDate = (value?: string) => {
  if (!value) return null
  const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch
    return new Date(Number(year), Number(month) - 1, Number(day))
  }
  const date = new Date(value)
  return isNaN(date.getTime()) ? null : date
}

const formatDraftDate = (date: Date | null) => {
  if (!date) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// Remove the CandidateWithStatus interface since we'll use Application directly

// Helper function to translate server error messages
function translateServerError(errorMessage: string, t: ReturnType<typeof useTranslation>['t']): string {
  const lowerMessage = errorMessage.toLowerCase()

  // Map common server error messages to translation keys
  if (lowerMessage.includes('team with this name already exists') ||
    lowerMessage.includes('group with this name already exists') ||
    lowerMessage.includes('name already exists')) {
    return t('teams.messages.teamNameAlreadyExists')
  }

  if (lowerMessage.includes('name') && lowerMessage.includes('required') ||
    lowerMessage.includes('this field is required') && lowerMessage.includes('name')) {
    return t('teams.messages.teamNameRequired')
  }

  if (lowerMessage.includes('name') && (lowerMessage.includes('invalid') || lowerMessage.includes('not valid'))) {
    return t('teams.messages.teamNameInvalid')
  }

  // If no translation found, return the original message
  return errorMessage
}

// Helper to label AI matching categories (reused from AIMatching)
const getMatchingCategoryLabel = (key: string, t: ReturnType<typeof useTranslation>['t']) => {
  const labels: Record<string, string> = {
    hard_skills: t('matchings.categories.technicalSkills', 'Technical skills'),
    experience_years: t('matchings.categories.professionalExperience', 'Years of Experience'),
    soft_skills: t('matchings.categories.softSkills', 'Soft skills'),
    main_missions: t('matchings.categories.mainMissions', 'Main missions'),
    certifications: t('matchings.categories.certifications', 'Certifications'),
    languages: t('matchings.categories.languages', 'Languages'),
    niveau_etude: t('matchings.categories.education', 'Education'),
    semantic_similarity: t('matchings.categories.semanticSimilarity', 'Semantic similarity'),
    nice_to_have: t('matchings.categories.niceToHave', 'Nice to have'),
  }
  return labels[key] || t(`offerDetail.sections.${key}`, key.replace(/_/g, ' '))
}

export function OfferDetail() {
  const { t, i18n } = useTranslation()
  const defaultDepartments = useMemo(() => buildDefaultDepartments(t), [t])
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = location.state as OfferLocationState | null
  const currentPath = `${location.pathname}${location.search}${location.hash}`
  const { hasPermission } = useAuth()

  // Helper function to navigate back to previous page or default to /offers
  const handleBack = () => {
    if (locationState?.from) {
      navigate(locationState.from)
    } else {
      navigate('/offers')
    }
  }
  const { addToast } = useToast()
  const [offer, setOffer] = useState<Offer | null>(null)

  // Clôturée / Archived: freeze recruitment actions
  const isOfferClosed = useMemo(() => {
    if (!offer) return false
    return offer.status === 'offer_closed' || offer.status === 'closed'
  }, [offer])

  // Activated / Clôturée / Archived: freeze offer content edits (status may still change via key_details)
  const isOfferModificationLocked = useMemo(() => {
    if (!offer) return false
    return offer.status === 'published' || isOfferClosed
  }, [offer, isOfferClosed])
  const [applicationsByColumn, setApplicationsByColumn] = useState<Record<string, Application[]>>({})
  const applications = useMemo(() => Object.values(applicationsByColumn).flat(), [applicationsByColumn])
  const [stagePagination, setStagePagination] = useState<Record<string, { page: number, hasMore: boolean, loadingMore: boolean }>>({})
  const [stageTotalCounts, setStageTotalCounts] = useState<Record<string, number>>({})
  const totalApplicationsCount = useMemo(() => {
    return Object.values(stageTotalCounts).reduce((acc, count) => acc + count, 0)
  }, [stageTotalCounts])
  const [loading, setLoading] = useState(true)
  const [improvingDescription, setImprovingDescription] = useState(false)
  const [applyingAiDescription, setApplyingAiDescription] = useState(false)
  const tabOptions = ['applications', 'details', 'analytics', 'validation_interviews', 'calendar'] as const

  type TabOption = (typeof tabOptions)[number]

  const contractTypes = useMemo(
    () => [
      { value: 'CDI', label: t('createOffer.contractTypes.cdi') },
      { value: 'CDD', label: t('createOffer.contractTypes.cdd') },
      { value: 'Stage', label: t('createOffer.contractTypes.stage') },
      { value: 'Freelance', label: t('createOffer.contractTypes.freelance') },
      { value: 'Consultant', label: t('createOffer.contractTypes.consultant') },
      { value: 'Temps partiel', label: t('createOffer.contractTypes.partTime') }
    ],
    [t]
  )

  const experienceLevels = useMemo(
    () => [
      { value: 'Junior (0-2 ans)', label: t('createOffer.experienceLevels.junior') },
      { value: 'Confirmé (3-5 ans)', label: t('createOffer.experienceLevels.confirmed') },
      { value: 'Senior (6-10 ans)', label: t('createOffer.experienceLevels.senior') },
      { value: 'Expert (10+ ans)', label: t('createOffer.experienceLevels.expert') }
    ],
    [t]
  )

  const educationLevels = useMemo(
    () => [
      { value: 'Bac', label: 'Bac' },
      { value: 'Bac+2', label: 'Bac +2' },
      { value: 'Bac+3', label: 'Bac +3' },
      { value: 'Bac+5', label: 'Bac +5' },
      { value: 'Bac+8', label: 'Bac +8' }
    ],
    []
  )

  // Determine available status options based on current status
  const getAvailableStatusOptions = useCallback((currentStatus: Offer['status']): Offer['status'][] => {
    if (currentStatus === 'closed') {
      // Archived offers cannot change status
      return ['closed']
    }
    if (currentStatus === 'offer_closed') {
      // Clôturée: archive only (cannot reactivate)
      return ['offer_closed', 'closed']
    }
    if (currentStatus === 'published') {
      // Activated: close applications or archive
      return ['published', 'offer_closed', 'closed']
    }
    // Draft: publish, close, or archive
    return ['draft', 'published', 'offer_closed', 'closed']
  }, [])

  const getInitialTab = useCallback((): TabOption => {
    const stateTab = locationState?.initialTab
    if (stateTab && tabOptions.includes(stateTab as TabOption)) {
      return stateTab as TabOption
    }
    const params = new URLSearchParams(location.search)
    const tab = params.get('tab')
    return tabOptions.includes(tab as TabOption) ? (tab as TabOption) : 'applications'
  }, [locationState, location.search])

  const [activeTab, setActiveTab] = useState<TabOption>(() => getInitialTab())
  const [openPanel, setOpenPanel] = useState<OfferDetailPanel | null>(() => {
    const tab = getInitialTab()
    if (tab === 'details') return 'details'
    if (tab === 'analytics') return 'matching'
    if (tab === 'calendar') return 'calendar'
    return null
  })
  const [loadedSections, setLoadedSections] = useState<Set<string>>(new Set())
  const [loadingFullOffer, setLoadingFullOffer] = useState(false)
  const [loadingApplications, setLoadingApplications] = useState(false)
  const [showAddCandidateModal, setShowAddCandidateModal] = useState(false)
  const [availableCandidates, setAvailableCandidates] = useState<Candidate[]>([])
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<number[]>([])
  const [teams, setTeams] = useState<Team[]>([])
  const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>([])
  const [selectionMode, setSelectionMode] = useState<'group' | 'candidates'>('group')
  const [teamSearch, setTeamSearch] = useState('')
  const [candidateSearch, setCandidateSearch] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [addingCandidates, setAddingCandidates] = useState(false)
  const [loadingAddCandidatesData, setLoadingAddCandidatesData] = useState(false)
  const [isSearchingCandidates, setIsSearchingCandidates] = useState(false)
  const [candidatePage, setCandidatePage] = useState(1)
  const [totalCandidates, setTotalCandidates] = useState(0)
  const [isLocallyFetchingAllIds, setIsLocallyFetchingAllIds] = useState(false)
  const [hasMoreCandidates, setHasMoreCandidates] = useState(true)
  const [loadingMoreCandidates, setLoadingMoreCandidates] = useState(false)
  const [searchTimeout, setSearchTimeout] = useState<any>(null)
  const [showAIMatching, setShowAIMatching] = useState(false)
  const [showManageCandidatesModal, setShowManageCandidatesModal] = useState(false)
  const [showStepModal, setShowStepModal] = useState(false)
  const [hiringProcessName, setHiringProcessName] = useState<string | null>(null)
  const [stepsRefreshTrigger, setStepsRefreshTrigger] = useState(0)
  const [defaultTemplateId, setDefaultTemplateId] = useState<number | null>(null)
  const [offerStepsForPosition, setOfferStepsForPosition] = useState<HiringStep[]>([])
  const [showCreateGroupConfirm, setShowCreateGroupConfirm] = useState(false)
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false)
  const [showUploadCandidatesModal, setShowUploadCandidatesModal] = useState(false)
  const [allMatchingJobs, setAllMatchingJobs] = useState<MatchingJob[]>([])
  const [combinedMatchingResults, setCombinedMatchingResults] = useState<any[]>([])
  const [matchingSummary, setMatchingSummary] = useState<{
    total_candidates: number
    high_matches: number
    max_score: number
    total_jobs: number
  } | null>(null)
  const [loadingMatchingDetails, setLoadingMatchingDetails] = useState(false)
  const [expandedMatchingCandidateIds, setExpandedMatchingCandidateIds] = useState<number[]>([])
  const [matchingPreselectionCandidateIds, setMatchingPreselectionCandidateIds] = useState<Set<number>>(new Set())

  // Interview calendar state
  const [interviews, setInterviews] = useState<InterviewEvent[]>([])
  const [loadingInterviews, setLoadingInterviews] = useState(false)
  const [showScheduleInterviewModal, setShowScheduleInterviewModal] = useState(false)
  const [selectedCandidateForInterview, setSelectedCandidateForInterview] = useState<{
    candidate: Candidate
    application?: Application
    existingInterview?: {
      id: number | string
      title: string
      start: Date
      end: Date
      candidateId: number
      candidateName: string
      applicationId?: number
      stepOrder?: number
      stepName?: string
      location?: string
      meetingLink?: string
      notes?: string
      duration: number
    }
  } | null>(null)
  const [hiringSteps, setHiringSteps] = useState<HiringStep[]>([])
  const [selectedStepOrderForInterview, setSelectedStepOrderForInterview] = useState<number | undefined>(undefined)
  const preselectionStepIds = useMemo(() => {
    return new Set(
      hiringSteps
        .filter(step => {
          const normalizedName = step.name.toLowerCase()
          return step.step_type === 'screening' || normalizedName.includes('preselection') || normalizedName.includes('présélection')
        })
        .map(step => step.id)
    )
  }, [hiringSteps])

  const preselectionCandidateIdsFromColumns = useMemo(() => {
    const ids = new Set<number>()
    applications.forEach(app => {
      if (app.status === 'screening' || (app.current_step != null && preselectionStepIds.has(app.current_step))) {
        ids.add(app.candidate)
      }
    })
    return ids
  }, [applications, preselectionStepIds])

  const preselectionCandidateIdsForMatching = useMemo(() => {
    return new Set([...matchingPreselectionCandidateIds, ...preselectionCandidateIdsFromColumns])
  }, [matchingPreselectionCandidateIds, preselectionCandidateIdsFromColumns])

  // Validation interviews state
  const [interviewAudios, setInterviewAudios] = useState<any[]>([])
  const [loadingInterviewsList, setLoadingInterviewsList] = useState(false)
  const [isGeneratingBatch, setIsGeneratingBatch] = useState(false)
  const [validationCandidates, setValidationCandidates] = useState<Application[]>([])
  const [loadingValidationCandidates, setLoadingValidationCandidates] = useState(false)
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | null>(null)
  const [videoModalTitle, setVideoModalTitle] = useState('')
  const [currentVideoTime, setCurrentVideoTime] = useState(0)
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false)
  const [selectedInterviewForResponses, setSelectedInterviewForResponses] = useState<any>(null)
  const [isViewingOnlyQuestions, setIsViewingOnlyQuestions] = useState(false)
  const [activeSegmentIndex, setActiveSegmentIndex] = useState<number>(0)
  const [interviewLanguage, setInterviewLanguage] = useState<'fr' | 'en' | 'custom'>('fr')
  const [manualInterviewLanguage, setManualInterviewLanguage] = useState<'fr' | 'en' | 'custom'>('fr')
  const isCreatingManualQuestionsRef = useRef(false)
  const [isSendingInvitation, setIsSendingInvitation] = useState<number | null>(null)
  const [isSendingBatchInvitations, setIsSendingBatchInvitations] = useState(false)
  const [isEvaluating, setIsEvaluating] = useState<number | null>(null)
  const [evaluationModalOpen, setEvaluationModalOpen] = useState(false)
  const [selectedEvaluation, setSelectedEvaluation] = useState<any>(null)
  const [isManualEvaluationMode, setIsManualEvaluationMode] = useState(false)
  const [isCreatingInterviewForEvaluation, setIsCreatingInterviewForEvaluation] = useState<number | null>(null)
  const [isBatchEvaluating, setIsBatchEvaluating] = useState(false)
  const [interviewQuestionsPerSection, setInterviewQuestionsPerSection] = useState<Record<string, number>>({
    experience: 3,
    formations: 1,
    projets: 2,
    langues: 0,
    certifications: 1,
    hard_skills: 3,
    soft_skills: 2,
    nice_to_have: 1
  })
  const getInterviewGlobalScore = useCallback((audio: any) => {
    if (!audio?.score) return -1
    try {
      const scoreData = typeof audio.score === 'string' ? JSON.parse(audio.score) : audio.score
      return scoreData.global_score ?? -1
    } catch {
      return -1
    }
  }, [])

  const getInterviewEvaluationSource = useCallback((audio: any): 'ai' | 'human' | null => {
    if (!audio?.score) return null
    try {
      const scoreData = typeof audio.score === 'string' ? JSON.parse(audio.score) : audio.score
      if (!scoreData || typeof scoreData !== 'object') return null
      if (scoreData.evaluation_source === 'ai' || scoreData.evaluation_source === 'human') {
        return scoreData.evaluation_source
      }
      // Legacy scores: human evaluations used this summary by default
      const summary = String(scoreData.summary || '').toLowerCase()
      if (summary.includes('humaine') || summary.includes('human evaluation')) {
        return 'human'
      }
      return 'ai'
    } catch {
      return null
    }
  }, [])

  const openEvaluationViewer = useCallback((audio: any, scoreData?: any) => {
    let parsed = scoreData
    if (!parsed && audio?.score) {
      try {
        parsed = typeof audio.score === 'string' ? JSON.parse(audio.score) : audio.score
      } catch {
        parsed = null
      }
    }
    if (!parsed) return
    setSelectedEvaluation(parsed)
    setEvaluationInterviewId(audio.id)
    setIsManualEvaluationMode(getInterviewEvaluationSource(audio) === 'human')
    setIsEditingEvaluation(false)
    setEvaluationModalOpen(true)
  }, [getInterviewEvaluationSource])

  const validationInterviewRows = useMemo(() => {
    const audioByApplicationId = new Map<number, (typeof interviewAudios)[number]>()
    for (const audio of interviewAudios) {
      if (audio.application != null) {
        audioByApplicationId.set(audio.application, audio)
      }
    }

    const seenApplicationIds = new Set<number>()
    const rows: Array<
      | { type: 'generated'; audio: (typeof interviewAudios)[number] }
      | { type: 'pending'; application: Application }
    > = []

    for (const app of validationCandidates) {
      seenApplicationIds.add(app.id)
      const audio = audioByApplicationId.get(app.id)
      if (audio) {
        rows.push({ type: 'generated', audio })
      } else {
        rows.push({ type: 'pending', application: app })
      }
    }

    for (const audio of interviewAudios) {
      if (audio.application != null && !seenApplicationIds.has(audio.application)) {
        rows.push({ type: 'generated', audio })
      }
    }

    const rowEmails = (row: (typeof rows)[number]) => {
      const raw = row.type === 'generated' ? row.audio.candidate_email : row.application.candidate_email
      return String(raw || '')
        .replace(/,/g, '|')
        .split('|')
        .map((part) => part.trim().toLowerCase())
        .filter((part) => part.includes('@'))
    }
    const rowMatchScore = (row: (typeof rows)[number]) => (
      row.type === 'generated' ? (row.audio.match_score ?? 0) : (row.application.match_score ?? 0)
    )

    // Several CVs can share one email. Keep the row of the highest matching score.
    const parent = rows.map((_, index) => index)
    const find = (index: number): number => {
      if (parent[index] !== index) parent[index] = find(parent[index])
      return parent[index]
    }
    const union = (left: number, right: number) => {
      const leftRoot = find(left)
      const rightRoot = find(right)
      if (leftRoot !== rightRoot) parent[rightRoot] = leftRoot
    }
    const emailOwner = new Map<string, number>()
    rows.forEach((row, index) => {
      for (const email of rowEmails(row)) {
        const owner = emailOwner.get(email)
        if (owner === undefined) emailOwner.set(email, index)
        else union(index, owner)
      }
    })
    const groupedRows: typeof rows = []
    const groupedIndexes = new Map<number, number>()
    rows.forEach((row, index) => {
      const root = find(index)
      const existingIndex = groupedIndexes.get(root)
      if (existingIndex === undefined) {
        groupedIndexes.set(root, groupedRows.length)
        groupedRows.push(row)
        return
      }
      const current = groupedRows[existingIndex]
      const scoreDiff = rowMatchScore(row) - rowMatchScore(current)
      if (scoreDiff > 0 || (scoreDiff === 0 && row.type === 'generated' && current.type !== 'generated')) {
        groupedRows[existingIndex] = row
      }
    })

    // Primary sort: interview score (desc). Rows without an evaluation fall to the
    // bottom and are then ordered by their CV matching score.
    groupedRows.sort((a, b) => {
      const interviewScoreA = a.type === 'generated' ? getInterviewGlobalScore(a.audio) : -1
      const interviewScoreB = b.type === 'generated' ? getInterviewGlobalScore(b.audio) : -1

      if (interviewScoreA !== interviewScoreB) {
        return interviewScoreB - interviewScoreA
      }

      const matchScoreA = a.type === 'generated'
        ? (a.audio.match_score ?? 0)
        : (a.application.match_score ?? 0)
      const matchScoreB = b.type === 'generated'
        ? (b.audio.match_score ?? 0)
        : (b.application.match_score ?? 0)
      return matchScoreB - matchScoreA
    })

    return groupedRows
  }, [validationCandidates, interviewAudios, getInterviewGlobalScore])

  const hasPendingValidationInterviews = useMemo(
    () => validationInterviewRows.some((row) => row.type === 'pending'),
    [validationInterviewRows]
  )

  // Once AI batch generation has produced questions, manual creation is locked.
  const hasAiGeneratedInterviewQuestions = useMemo(() => {
    if (offer?.manual_interview_questions_created) return false
    return interviewAudios.some((audio: any) => {
      const questions = audio?.questions
      if (!questions) return false
      if (Array.isArray(questions)) return questions.length > 0
      if (typeof questions === 'object') {
        if ('fr' in questions || 'en' in questions) return true
        return Object.values(questions).some((q: any) => Array.isArray(q) && q.length > 0)
      }
      return false
    })
  }, [interviewAudios, offer?.manual_interview_questions_created])

  const isValidationInterviewDeadlinePassed = useMemo(() => {
    const deadline = offer?.interview_deadline || offer?.deadline
    if (!deadline) return false
    const dateOnlyMatch = String(deadline).match(/^(\d{4})-(\d{2})-(\d{2})/)
    const deadlineDate = dateOnlyMatch
      ? new Date(`${dateOnlyMatch[0]}T00:00:00`)
      : new Date(deadline)
    if (Number.isNaN(deadlineDate.getTime())) return false
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today > deadlineDate
  }, [offer?.interview_deadline, offer?.deadline])

  const canBatchEvaluateInterviews = useMemo(() => {
    // Partial interviews hold at least one answer, so they are evaluable too
    if (interviewAudios.some((a) => a.status === 'completed' || a.status === 'partial')) return true
    // After deadline, unfinished interviews are finalized then evaluated server-side
    if (!isValidationInterviewDeadlinePassed) return false
    return interviewAudios.some((a) =>
      ['generated', 'sent', 'recording', 'in_processing'].includes(a.status)
    )
  }, [interviewAudios, isValidationInterviewDeadlinePassed])

  const [showBatchGenerateModal, setShowBatchGenerateModal] = useState(false)
  const [commonQuestions, setCommonQuestions] = useState<CommonQuestion[]>([])
  const normalizeCommonQuestions = (questions?: (string | CommonQuestion)[] | null): CommonQuestion[] =>
    (questions || []).map((q) =>
      typeof q === 'string' ? { question: q, duration: 120 } : q
    )
  const [isSavingCommonQuestions, setIsSavingCommonQuestions] = useState(false)
  const [showCommonQuestionsModal, setShowCommonQuestionsModal] = useState(false)
  const [isCreatingManualQuestions, setIsCreatingManualQuestions] = useState(false)
  const [showConfirmLanguageModal, setShowConfirmLanguageModal] = useState(false)
  useEffect(() => {
    isCreatingManualQuestionsRef.current = isCreatingManualQuestions
  }, [isCreatingManualQuestions])
  const commonQuestionsLimitReached = commonQuestions.length >= MAX_MANUAL_INTERVIEW_QUESTIONS
  const commonQuestionsLimitExceeded = commonQuestions.length > MAX_MANUAL_INTERVIEW_QUESTIONS
  const isManualQuestionsLocked = Boolean(offer?.manual_interview_questions_created)
  const canAddManualQuestions = !isManualQuestionsLocked && !commonQuestionsLimitReached
  const manualQuestionSectionOptions = useMemo(
    () => MANUAL_QUESTION_SECTIONS.map(section => ({
      value: section,
      label: t(`offerDetail.sections.${section}`, section)
    })),
    [t]
  )
  const effectiveSectionWeights = useMemo(
    () => ({ ...DEFAULT_SECTION_WEIGHTS, ...(offer?.section_weights || {}) }) as Record<string, number>,
    [offer?.section_weights]
  )
  const commonQuestionsBySection = useMemo(() => {
    const counts: Record<string, number> = {}
    commonQuestions.forEach(question => {
      const section = question.section || DEFAULT_MANUAL_QUESTION_SECTION
      counts[section] = (counts[section] || 0) + 1
    })
    return MANUAL_QUESTION_SECTIONS
      .filter(section => counts[section])
      .map(section => ({ section, count: counts[section], weight: effectiveSectionWeights[section] ?? 1 }))
  }, [commonQuestions, effectiveSectionWeights])
  const [isEditingQuestions, setIsEditingQuestions] = useState(false)
  const [editingQuestions, setEditingQuestions] = useState<any>(null)
  const [editingCommonQuestions, setEditingCommonQuestions] = useState<any[]>(null)
  const [isSavingQuestions, setIsSavingQuestions] = useState(false)
  const [showAddInterviewQuestionSectionModal, setShowAddInterviewQuestionSectionModal] = useState(false)
  const [addingInterviewQuestionDraft, setAddingInterviewQuestionDraft] = useState<{
    section: InterviewQuestionSection
    question: string
    questionEn: string
    duration: number
    description: string
  } | null>(null)
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const [interviewToReset, setInterviewToReset] = useState<any>(null)
  const [isResetting, setIsResetting] = useState(false)
  const [showDeleteInterviewConfirm, setShowDeleteInterviewConfirm] = useState(false)
  const [interviewToDelete, setInterviewToDelete] = useState<any>(null)
  const [isEditingEvaluation, setIsEditingEvaluation] = useState(false)
  const [editedEvaluation, setEditedEvaluation] = useState<any>(null)
  const [evaluationInterviewId, setEvaluationInterviewId] = useState<number | null>(null)
  const [isSavingEvaluation, setIsSavingEvaluation] = useState(false)
  const [isDeletingInterview, setIsDeletingInterview] = useState(false)
  const [showDeleteQuestionModal, setShowDeleteQuestionModal] = useState(false)
  const [deleteQuestionTarget, setDeleteQuestionTarget] = useState<{
    type: 'standard' | 'common'
    section?: string
    index: number
    lang?: 'fr' | 'en'
  } | null>(null)

  const [showSectionWeightsModal, setShowSectionWeightsModal] = useState(false)
  const [isSavingSectionWeights, setIsSavingSectionWeights] = useState(false)
  const [editingSectionWeights, setEditingSectionWeights] = useState<Record<string, number>>(DEFAULT_SECTION_WEIGHTS)
  const [validationInterviewDeadline, setValidationInterviewDeadline] = useState('')
  const interviewDeadlineSaveSeq = useRef(0)
  const [interviewStatusFilter, setInterviewStatusFilter] = useState('all')
  const [validationInterviewPage, setValidationInterviewPage] = useState(1)
  const VALIDATION_INTERVIEWS_PAGE_SIZE = 20

  // Candidates without an interview yet are displayed as `pending_generation`
  // in the status column, so they are filterable under that same value.
  const filteredValidationInterviewRows = useMemo(() => {
    if (interviewStatusFilter === 'all') return validationInterviewRows
    return validationInterviewRows.filter((row) => (
      row.type === 'pending'
        ? interviewStatusFilter === 'pending_generation'
        : row.audio.status === interviewStatusFilter
    ))
  }, [validationInterviewRows, interviewStatusFilter])

  const validationInterviewTotalPages = Math.max(
    1,
    Math.ceil(filteredValidationInterviewRows.length / VALIDATION_INTERVIEWS_PAGE_SIZE)
  )
  const validationInterviewPageSafe = Math.min(validationInterviewPage, validationInterviewTotalPages)
  const pagedValidationInterviewRows = useMemo(() => {
    const start = (validationInterviewPageSafe - 1) * VALIDATION_INTERVIEWS_PAGE_SIZE
    return filteredValidationInterviewRows.slice(start, start + VALIDATION_INTERVIEWS_PAGE_SIZE)
  }, [filteredValidationInterviewRows, validationInterviewPageSafe])

  const [autoGenerateInterviews, setAutoGenerateInterviews] = useState(false)
  const [autoEvaluateInterviews, setAutoEvaluateInterviews] = useState(false)
  const [autoSendInterviewEmails, setAutoSendInterviewEmails] = useState(false)
  const [showConfigureAlert, setShowConfigureAlert] = useState(false)
  const [isSavingAutoConfig, setIsSavingAutoConfig] = useState(false)
  const [isSavingRecommendedSkills, setIsSavingRecommendedSkills] = useState(false)

  const [minimumScore, setMinimumScore] = useState<number>(70)
  const [minimumInterviewScore, setMinimumInterviewScore] = useState<number>(70)

  // Translation states for Offer fields
  const [translatedJobTitle, setTranslatedJobTitle] = useState<string>('')
  const [translatedDescription, setTranslatedDescription] = useState<string>('')
  const [translatedHardSkills, setTranslatedHardSkills] = useState<string[]>([])
  const [translatedHardSkillsNonMentionnees, setTranslatedHardSkillsNonMentionnees] = useState<string[]>([])
  const [translatedSoftSkills, setTranslatedSoftSkills] = useState<string[]>([])
  const [translatedSoftSkillsNonMentionnees, setTranslatedSoftSkillsNonMentionnees] = useState<string[]>([])
  const [translatedCertifications, setTranslatedCertifications] = useState<string[]>([])
  const [translatedBenefits, setTranslatedBenefits] = useState<string[]>([])
  const [translatedBenefitsNonMentionnees, setTranslatedBenefitsNonMentionnees] = useState<string[]>([])
  const [translatedLanguages, setTranslatedLanguages] = useState<Array<{ language: string; level?: string }>>([])
  const [translatedMainMissions, setTranslatedMainMissions] = useState<string[]>([])

  const requirementsSplit = useMemo(
    () => extractMentionSplit((offer as any)?.requirements, 'hard_skills_mentionnées', 'hard_skills_non_mentionnées'),
    [offer]
  )
  const softSkillsSplit = useMemo(
    () => extractMentionSplit((offer as any)?.soft_skills, 'soft_skills_mentionnées', 'soft_skills_non_mentionnées'),
    [offer]
  )
  const benefitsSplit = useMemo(
    () => extractMentionSplit((offer as any)?.benefits, 'nice_to_have_mentionnées', 'nice_to_have_non_mentionnées'),
    [offer]
  )

  const hardSkillsMentionnees = requirementsSplit.mentionnees
  const hardSkillsNonMentionnees = requirementsSplit.nonMentionnees
  const softSkillsMentionnees = softSkillsSplit.mentionnees
  const softSkillsNonMentionnees = softSkillsSplit.nonMentionnees
  const benefitsMentionnees = benefitsSplit.mentionnees
  const benefitsNonMentionnees = benefitsSplit.nonMentionnees

  const displayedHardSkills = useMemo(() => {
    if (!offer) return [] as string[]
    const hardSkills = (i18n.language === 'en' && translatedHardSkills.length > 0)
      ? translatedHardSkills
      : hardSkillsMentionnees
    return hardSkills.filter(skill => skill && skill.trim().length > 0)
  }, [i18n.language, translatedHardSkills, hardSkillsMentionnees, offer])

  const [hardSkillWeights, setHardSkillWeights] = useState<Record<string, number>>({})
  const [hardSkillSortWeights, setHardSkillSortWeights] = useState<Record<string, number>>({})
  const hardSortInitialized = useRef(false)
  const hardSortTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Sort hard skills by weight in descending order
  const sortedHardSkills = useMemo(() => {
    return [...displayedHardSkills].sort((a, b) => {
      const weightA = hardSkillSortWeights[a] ?? 1
      const weightB = hardSkillSortWeights[b] ?? 1
      return weightB - weightA // Descending order
    })
  }, [displayedHardSkills, hardSkillSortWeights])

  const displayedSoftSkills = useMemo(() => {
    if (!offer) return [] as string[]
    const softSkills = (i18n.language === 'en' && translatedSoftSkills.length > 0)
      ? translatedSoftSkills
      : softSkillsMentionnees
    return softSkills.filter(skill => skill && skill.trim().length > 0)
  }, [i18n.language, translatedSoftSkills, softSkillsMentionnees, offer])

  const [softSkillWeights, setSoftSkillWeights] = useState<Record<string, number>>({})
  const [softSkillSortWeights, setSoftSkillSortWeights] = useState<Record<string, number>>({})
  const softSortInitialized = useRef(false)
  const softSortTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const displayedBenefits = useMemo(() => {
    if (!offer) return [] as string[]
    const benefits = (i18n.language === 'en' && translatedBenefits.length > 0)
      ? translatedBenefits
      : benefitsMentionnees
    return benefits.filter(benefit => benefit && benefit.trim().length > 0)
  }, [i18n.language, translatedBenefits, benefitsMentionnees, offer])

  const displayedHardSkillsNonMentionnees = useMemo(() => {
    if (!offer) return [] as string[]
    const hardSkills = (i18n.language === 'en' && translatedHardSkillsNonMentionnees.length > 0)
      ? translatedHardSkillsNonMentionnees
      : hardSkillsNonMentionnees
    return hardSkills.filter(skill => skill && skill.trim().length > 0)
  }, [i18n.language, translatedHardSkillsNonMentionnees, hardSkillsNonMentionnees, offer])

  const sortedHardSkillsNonMentionnees = useMemo(() => {
    return [...displayedHardSkillsNonMentionnees].sort((a, b) => {
      const weightA = hardSkillSortWeights[a] ?? 1
      const weightB = hardSkillSortWeights[b] ?? 1
      return weightB - weightA
    })
  }, [displayedHardSkillsNonMentionnees, hardSkillSortWeights])

  const displayedSoftSkillsNonMentionnees = useMemo(() => {
    if (!offer) return [] as string[]
    const softSkills = (i18n.language === 'en' && translatedSoftSkillsNonMentionnees.length > 0)
      ? translatedSoftSkillsNonMentionnees
      : softSkillsNonMentionnees
    return softSkills.filter(skill => skill && skill.trim().length > 0)
  }, [i18n.language, translatedSoftSkillsNonMentionnees, softSkillsNonMentionnees, offer])

  const displayedBenefitsNonMentionnees = useMemo(() => {
    if (!offer) return [] as string[]
    const benefits = (i18n.language === 'en' && translatedBenefitsNonMentionnees.length > 0)
      ? translatedBenefitsNonMentionnees
      : benefitsNonMentionnees
    return benefits.filter(benefit => benefit && benefit.trim().length > 0)
  }, [i18n.language, translatedBenefitsNonMentionnees, benefitsNonMentionnees, offer])

  // Lock states for each element type
  const [lockedItems, setLockedItems] = useState<Set<string>>(new Set())
  const [includeInternshipExperience, setIncludeInternshipExperience] = useState(true)

  // Load locked items from filter_matching
  const loadLockedItemsFromFilterMatching = useCallback((filterMatching: Offer['filter_matching'], offerData: Offer): Set<string> => {
    if (!filterMatching) return new Set()

    const lockedSet = new Set<string>()

    // Experience
    if (filterMatching.experience !== undefined && filterMatching.experience !== null) {
      lockedSet.add('experience_level')
    }

    // Education level
    if (filterMatching.education_level) {
      lockedSet.add('education_level')
    }

    // Hard skills
    if (filterMatching.hard_skills) {
      Object.keys(filterMatching.hard_skills).forEach(skillName => {
        lockedSet.add(`hardskill_${skillName}`)
      })
    }

    // Soft skills
    if (filterMatching.soft_skills) {
      Object.keys(filterMatching.soft_skills).forEach(skillName => {
        lockedSet.add(`softskill_${skillName}`)
      })
    }

    // Certifications
    if (filterMatching.certifications && offerData.certifications) {
      const certifications = offerData.certifications
      Object.keys(filterMatching.certifications).forEach(certName => {
        // Find the index of this certification in the array
        const index = certifications.findIndex((cert: string) => cert === certName)
        if (index !== -1) {
          lockedSet.add(`certification_${certName}_${index}`)
        }
      })
    }

    // Languages
    if (filterMatching.languages && offerData.languages) {
      const languages = offerData.languages
      Object.keys(filterMatching.languages).forEach(fullLangName => {
        // Extract language name from format "Français (C1)" -> "Français"
        const langName = fullLangName.split(' (')[0]

        // Find the index of this language in the array
        const index = languages.findIndex((lang: LanguageItem) => lang.language === langName)
        if (index !== -1) {
          lockedSet.add(`language_${langName}_${index}`)
        }
      })
    }

    return lockedSet
  }, [])

  // Build filter_matching object from locked items
  const buildFilterMatchingFromLockedItems = useCallback((
    lockedSet: Set<string>,
    includeStageExperience: boolean
  ): Offer['filter_matching'] => {
    if (!offer) return {}

    const formattedFilters: NonNullable<Offer['filter_matching']> = {}
    formattedFilters.stage_exclu = includeStageExperience ? 0 : 1

    // Parse locked items
    lockedSet.forEach(itemKey => {
      if (itemKey === 'experience_level') {
        // Experience: use the offer's experience_level value
        if (offer.experience_level !== undefined && offer.experience_level !== null && offer.experience_level !== '') {
          formattedFilters.experience = Number(offer.experience_level)
        }
      } else if (itemKey === 'education_level') {
        // Education level: level with similarity 100
        if (offer.education_level) {
          formattedFilters.education_level = {
            level: offer.education_level,
            similarity: 100
          }
        }
      } else if (itemKey.startsWith('hardskill_')) {
        // Hard skills: 65 for similarity
        const skillName = itemKey.replace('hardskill_', '')
        if (!formattedFilters.hard_skills) {
          formattedFilters.hard_skills = {}
        }
        formattedFilters.hard_skills[skillName] = 65
      } else if (itemKey.startsWith('softskill_')) {
        // Soft skills: 30 for similarity
        const skillName = itemKey.replace('softskill_', '')
        if (!formattedFilters.soft_skills) {
          formattedFilters.soft_skills = {}
        }
        formattedFilters.soft_skills[skillName] = 30
      } else if (itemKey.startsWith('certification_')) {
        // Certifications: 20 for similarity
        // Extract certification name from key (format: certification_${cert}_${index})
        const parts = itemKey.replace('certification_', '').split('_')
        // Remove last part (index) to get the certification name
        const certName = parts.slice(0, -1).join('_')
        if (!formattedFilters.certifications) {
          formattedFilters.certifications = {}
        }
        formattedFilters.certifications[certName] = 20
      } else if (itemKey.startsWith('language_')) {
        // Languages: 100 for similarity
        // Extract language name and level from the actual languages array
        const languages = offer.languages || []
        const parts = itemKey.replace('language_', '').split('_')
        // Remove last part (index) to get the language name
        const langName = parts.slice(0, -1).join('_')

        // Find the language object to get the level
        const langObj = languages.find((lang: LanguageItem) => lang.language === langName)
        if (langObj) {
          const fullLangName = `${langObj.language} (${langObj.level})`
          if (!formattedFilters.languages) {
            formattedFilters.languages = {}
          }
          formattedFilters.languages[fullLangName] = 100
        }
      }
    })

    return formattedFilters
  }, [offer])

  // Save filter_matching to backend
  const saveFilterMatching = useCallback(async (
    lockedSet: Set<string>,
    includeStageExperience: boolean = includeInternshipExperience
  ) => {
    if (!offer || !id) return

    try {
      const formattedFilters = buildFilterMatchingFromLockedItems(lockedSet, includeStageExperience)
      await offersApi.update(Number(id), { filter_matching: formattedFilters })
    } catch (error) {
      console.error('Failed to save filter_matching:', error)
    }
  }, [offer, id, buildFilterMatchingFromLockedItems, includeInternshipExperience])

  const toggleLock = (itemKey: string) => {
    setLockedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(itemKey)) {
        newSet.delete(itemKey)
      } else {
        newSet.add(itemKey)
      }

      // Save filter_matching to backend
      saveFilterMatching(newSet)

      return newSet
    })
  }

  const isLocked = (itemKey: string) => lockedItems.has(itemKey)

  const handleStageExperienceToggle = (checked: boolean) => {
    setIncludeInternshipExperience(Boolean(checked))
    saveFilterMatching(lockedItems, Boolean(checked))
  }

  const getPublishedLockButtonClass = (locked: boolean, variant: 'inline' | 'floating' = 'inline') => {
    const baseClass = 'cursor-not-allowed border border-slate-200'

    if (locked) {
      return variant === 'floating'
        ? 'cursor-not-allowed border border-blue-700 bg-blue-600 text-white shadow-sm'
        : 'cursor-not-allowed border border-blue-700 bg-blue-600 text-white'
    }

    return variant === 'floating'
      ? `${baseClass} bg-slate-50 text-slate-300 shadow-none`
      : `${baseClass} bg-slate-50 text-slate-300`
  }

  // Sort soft skills by weight in descending order
  const sortedSoftSkills = useMemo(() => {
    return [...displayedSoftSkills].sort((a, b) => {
      const weightA = softSkillSortWeights[a] ?? 1
      const weightB = softSkillSortWeights[b] ?? 1
      return weightB - weightA // Descending order
    })
  }, [displayedSoftSkills, softSkillSortWeights])

  const sortedSoftSkillsNonMentionnees = useMemo(() => {
    return [...displayedSoftSkillsNonMentionnees].sort((a, b) => {
      const weightA = softSkillSortWeights[a] ?? 1
      const weightB = softSkillSortWeights[b] ?? 1
      return weightB - weightA
    })
  }, [displayedSoftSkillsNonMentionnees, softSkillSortWeights])

  useEffect(() => {
    if (!hardSortInitialized.current) {
      setHardSkillSortWeights(hardSkillWeights)
      hardSortInitialized.current = true
      return
    }

    if (hardSortTimerRef.current) {
      clearTimeout(hardSortTimerRef.current)
    }

    hardSortTimerRef.current = setTimeout(() => {
      setHardSkillSortWeights(hardSkillWeights)
      hardSortTimerRef.current = null
    }, 5000)

    return () => {
      if (hardSortTimerRef.current) {
        clearTimeout(hardSortTimerRef.current)
        hardSortTimerRef.current = null
      }
    }
  }, [hardSkillWeights])

  useEffect(() => {
    if (!softSortInitialized.current) {
      setSoftSkillSortWeights(softSkillWeights)
      softSortInitialized.current = true
      return
    }

    if (softSortTimerRef.current) {
      clearTimeout(softSortTimerRef.current)
    }

    softSortTimerRef.current = setTimeout(() => {
      setSoftSkillSortWeights(softSkillWeights)
      softSortTimerRef.current = null
    }, 5000)

    return () => {
      if (softSortTimerRef.current) {
        clearTimeout(softSortTimerRef.current)
        softSortTimerRef.current = null
      }
    }
  }, [softSkillWeights])

  // Initialize locked items from filter_matching when offer loads
  useEffect(() => {
    if (!offer) return

    const loadedLockedItems = loadLockedItemsFromFilterMatching(offer.filter_matching, offer)
    setLockedItems(loadedLockedItems)
    setIncludeInternshipExperience(offer.filter_matching?.stage_exclu !== 1)
  }, [offer, offer?.id, offer?.filter_matching, loadLockedItemsFromFilterMatching])

  useEffect(() => {
    if (!offer?.skill_weights) {
      setHardSkillWeights(prev => {
        const next: Record<string, number> = {}
        displayedHardSkills.forEach(skill => {
          next[skill] = clampHardSkillWeight(prev[skill] ?? 1)
        })
        displayedHardSkillsNonMentionnees.forEach(skill => {
          next[skill] = clampHardSkillWeight(prev[skill] ?? 1)
        })
        return next
      })
    } else {
      const savedWeights = offer.skill_weights.hard_skills || {}
      setHardSkillWeights(prev => {
        const next: Record<string, number> = {}
        displayedHardSkills.forEach((skill, index) => {
          // Try to find weight by displayed name first
          let weight = savedWeights[skill]
          // If not found, try to find weight by French name from hard skills mentionnées
          if (weight === undefined && hardSkillsMentionnees[index]) {
            weight = savedWeights[hardSkillsMentionnees[index]]
          }
          next[skill] = clampHardSkillWeight(weight ?? prev[skill] ?? 1)
        })
        displayedHardSkillsNonMentionnees.forEach((skill, index) => {
          let weight = savedWeights[skill]
          if (weight === undefined && hardSkillsNonMentionnees[index]) {
            weight = savedWeights[hardSkillsNonMentionnees[index]]
          }
          next[skill] = clampHardSkillWeight(weight ?? prev[skill] ?? 1)
        })
        return next
      })
    }

  }, [displayedHardSkills, displayedHardSkillsNonMentionnees, hardSkillsMentionnees, hardSkillsNonMentionnees, offer?.skill_weights])

  useEffect(() => {
    if (!offer?.skill_weights) {
      setSoftSkillWeights(prev => {
        const next: Record<string, number> = {}
        displayedSoftSkills.forEach(skill => {
          next[skill] = clampHardSkillWeight(prev[skill] ?? 1)
        })
        displayedSoftSkillsNonMentionnees.forEach(skill => {
          next[skill] = clampHardSkillWeight(prev[skill] ?? 1)
        })
        return next
      })
    } else {
      const savedWeights = offer.skill_weights.soft_skills || {}
      setSoftSkillWeights(prev => {
        const next: Record<string, number> = {}
        displayedSoftSkills.forEach((skill, index) => {
          // Try to find weight by displayed name first
          let weight = savedWeights[skill]
          // If not found, try to find weight by French name from soft skills mentionnées
          if (weight === undefined && softSkillsMentionnees[index]) {
            weight = savedWeights[softSkillsMentionnees[index]]
          }
          next[skill] = clampHardSkillWeight(weight ?? prev[skill] ?? 1)
        })
        displayedSoftSkillsNonMentionnees.forEach((skill, index) => {
          let weight = savedWeights[skill]
          if (weight === undefined && softSkillsNonMentionnees[index]) {
            weight = savedWeights[softSkillsNonMentionnees[index]]
          }
          next[skill] = clampHardSkillWeight(weight ?? prev[skill] ?? 1)
        })
        return next
      })
    }

  }, [displayedSoftSkills, displayedSoftSkillsNonMentionnees, softSkillsMentionnees, softSkillsNonMentionnees, offer?.skill_weights])

  const getNextSkillWeight = useCallback((currentWeight: number) => {
    return currentWeight >= 5 ? 1 : currentWeight + 1
  }, [])

  const getSavedHardSkillKey = useCallback((skill: string) => {
    let skillKey = skill
    if (i18n.language === 'en' && translatedHardSkills.length > 0) {
      const englishIndex = translatedHardSkills.indexOf(skill)
      if (englishIndex >= 0 && hardSkillsMentionnees[englishIndex]) {
        skillKey = hardSkillsMentionnees[englishIndex]
      }
    }
    if (i18n.language === 'en' && translatedHardSkillsNonMentionnees.length > 0) {
      const englishRecommendedIndex = translatedHardSkillsNonMentionnees.indexOf(skill)
      if (englishRecommendedIndex >= 0 && hardSkillsNonMentionnees[englishRecommendedIndex]) {
        skillKey = hardSkillsNonMentionnees[englishRecommendedIndex]
      }
    }
    return skillKey
  }, [hardSkillsMentionnees, hardSkillsNonMentionnees, i18n.language, translatedHardSkills, translatedHardSkillsNonMentionnees])

  const getSavedSoftSkillKey = useCallback((skill: string) => {
    let skillKey = skill
    if (i18n.language === 'en' && translatedSoftSkills.length > 0) {
      const englishIndex = translatedSoftSkills.indexOf(skill)
      if (englishIndex >= 0 && softSkillsMentionnees[englishIndex]) {
        skillKey = softSkillsMentionnees[englishIndex]
      }
    }
    if (i18n.language === 'en' && translatedSoftSkillsNonMentionnees.length > 0) {
      const englishRecommendedIndex = translatedSoftSkillsNonMentionnees.indexOf(skill)
      if (englishRecommendedIndex >= 0 && softSkillsNonMentionnees[englishRecommendedIndex]) {
        skillKey = softSkillsNonMentionnees[englishRecommendedIndex]
      }
    }
    return skillKey
  }, [i18n.language, softSkillsMentionnees, softSkillsNonMentionnees, translatedSoftSkills, translatedSoftSkillsNonMentionnees])

  const buildFilteredSkillWeights = useCallback((weights: Record<string, number>, mapKey: (skill: string) => string) => {
    return Object.fromEntries(
      Object.entries(weights)
        .filter(([_, weight]) => weight > 1)
        .map(([skill, weight]) => [mapKey(skill), weight])
    )
  }, [])

  // Check if matching has been applied (candidates moved beyond 'applied' status) - blocks editing of sensitive fields
  const hasMatchingApplied = applications.some(app => app.status !== 'applied')

  // Define sensitive sections that affect matching scores
  const sensitiveSections: EditableSection[] = [
    'job_overview', // Contains experience_level and education_level
    'description',
    'requirements',
    'main_missions',
    'soft_skills',
    'certifications',
    'benefits',
    'languages',
    'ai_matching'
  ]

  const isSectionLocked = (section: EditableSection): boolean => {
    return hasMatchingApplied && sensitiveSections.includes(section)
  }

  const handleIncrementHardSkill = useCallback(async (skill: string) => {
    if (!offer) return
    if (isOfferModificationLocked) {
      addToast({
        type: 'warning',
        title: t('common.warning', 'Warning'),
        message: isOfferClosed
          ? t('offerDetail.cannotEditClosedOffer', 'Cannot modify a closed offer.')
          : t('offerDetail.cannotEditPublishedOffer', 'Cannot modify a published offer.'),
        duration: 5000
      })
      return
    }
    if (isSectionLocked('requirements')) {
      addToast({
        type: 'warning',
        title: t('common.warning', 'Warning'),
        message: t('offerDetail.cannotEditAfterMatching', 'This field cannot be modified because matching has been applied to this offer.'),
        duration: 5000
      })
      return
    }
    const nextWeight = getNextSkillWeight(hardSkillWeights[skill] ?? 1)
    const updatedHardWeights = { ...hardSkillWeights, [skill]: nextWeight }
    setHardSkillWeights(updatedHardWeights)

    try {
      const filteredHardSkills = buildFilteredSkillWeights(updatedHardWeights, getSavedHardSkillKey)
      const filteredSoftSkills = buildFilteredSkillWeights(softSkillWeights, getSavedSoftSkillKey)
      await offersApi.update(offer.id, {
        skill_weights: {
          hard_skills: filteredHardSkills,
          soft_skills: filteredSoftSkills
        }
      })
    } catch (error) {
      console.error('Error saving hard skill weight:', error)
    }
  }, [offer, hardSkillWeights, softSkillWeights, getNextSkillWeight, buildFilteredSkillWeights, getSavedHardSkillKey, getSavedSoftSkillKey, isSectionLocked, isOfferModificationLocked, isOfferClosed, addToast, t])

  const handleIncrementSoftSkill = useCallback(async (skill: string) => {
    if (!offer) return
    if (isOfferModificationLocked) {
      addToast({
        type: 'warning',
        title: t('common.warning', 'Warning'),
        message: isOfferClosed
          ? t('offerDetail.cannotEditClosedOffer', 'Cannot modify a closed offer.')
          : t('offerDetail.cannotEditPublishedOffer', 'Cannot modify a published offer.'),
        duration: 5000
      })
      return
    }
    if (isSectionLocked('soft_skills')) {
      addToast({
        type: 'warning',
        title: t('common.warning', 'Warning'),
        message: t('offerDetail.cannotEditAfterMatching', 'This field cannot be modified because matching has been applied to this offer.'),
        duration: 5000
      })
      return
    }
    const nextWeight = getNextSkillWeight(softSkillWeights[skill] ?? 1)
    const updatedSoftWeights = { ...softSkillWeights, [skill]: nextWeight }
    setSoftSkillWeights(updatedSoftWeights)

    try {
      const filteredSoftSkills = buildFilteredSkillWeights(updatedSoftWeights, getSavedSoftSkillKey)
      const filteredHardSkills = buildFilteredSkillWeights(hardSkillWeights, getSavedHardSkillKey)
      await offersApi.update(offer.id, {
        skill_weights: {
          hard_skills: filteredHardSkills,
          soft_skills: filteredSoftSkills
        }
      })
    } catch (error) {
      console.error('Error saving soft skill weight:', error)
    }
  }, [offer, softSkillWeights, hardSkillWeights, getNextSkillWeight, buildFilteredSkillWeights, getSavedSoftSkillKey, getSavedHardSkillKey, isSectionLocked, isOfferModificationLocked, isOfferClosed, addToast, t])

  // Translation stage tracking for sequential translation
  const [translationStage, setTranslationStage] = useState<'idle' | 'job_title' | 'description' | 'hard_skills' | 'soft_skills' | 'certifications' | 'benefits' | 'languages' | 'main_missions' | 'hardSkillsNonMentionnees' | 'softSkillsNonMentionnees' | 'benefitsNonMentionnees'>('idle')
  const translationPipelineCompleteRef = useRef(false)

  const finishTranslationPipeline = useCallback(() => {
    translationPipelineCompleteRef.current = true
    setTranslationStage('idle')
  }, [])

  // Validation candidates fetching
  const fetchValidationCandidates = useCallback(async (options?: { silent?: boolean }) => {
    if (!offer?.id) return
    try {
      if (!options?.silent) {
        setLoadingValidationCandidates(true)
      }
      const params: any = {
        validation_interviews: true,
        page: 1,
        page_size: 1000,
        ordering: '-match_score'
      }
      const response = await applicationsApi.getByOffer(offer.id, params)
      if (response?.data) {
        setValidationCandidates(response.data.applications || [])
      }
    } catch (error) {
      console.error('Error loading validation candidates:', error)
    } finally {
      if (!options?.silent) {
        setLoadingValidationCandidates(false)
      }
    }
  }, [offer?.id])

  useEffect(() => {
    if (offer?.id && (activeTab === 'validation_interviews' || openPanel === 'validation')) {
      fetchValidationCandidates()
    }
  }, [offer?.id, activeTab, openPanel, fetchValidationCandidates])

  // Update minimum score from offer when it loads

  useEffect(() => {
    if (offer) {
      if (offer.minimum_score !== undefined) {
        setMinimumScore(offer.minimum_score ?? 70)
      }
      if (offer.minimum_interview_score !== undefined) {
        setMinimumInterviewScore(offer.minimum_interview_score ?? 70)
      }
    }
  }, [offer])

  // Load ALL AI matching job details for this offer when Analytics tab is opened
  useEffect(() => {
    const loadMatchingDetails = async () => {
      if (!offer || openPanel !== 'matching') return
      try {
        setLoadingMatchingDetails(true)

        // Load only completed matching jobs for this specific offer using API filters
        const [response, preselectionResponse] = await Promise.all([
          matchingJobsApi.getAll(1, {
            offer: offer.id,
            status: 'completed'
          }),
          applicationsApi.getByOffer(offer.id, {
            status: 'screening',
            page_size: 1000,
            summary: true,
            ordering: '-match_score'
          })
        ])
        setMatchingPreselectionCandidateIds(
          new Set((preselectionResponse.data.applications || []).map(app => app.candidate))
        )
        const offerJobs = response.data.results || []

        // console.log('✅ Found', offerJobs.length, 'completed matching jobs for offer', offer.id)

        if (offerJobs.length === 0) {
          setAllMatchingJobs([])
          setCombinedMatchingResults([])
          setMatchingSummary(null)
          return
        }

        // Sort by most recent first
        const sorted = [...offerJobs].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )

        setAllMatchingJobs(sorted)

        // Combine all results from all matching jobs
        // Use a Map to track the most recent result for each candidate
        const candidateResultsMap = new Map<number, { result: any; jobDate: Date }>()

        sorted.forEach(job => {
          const jobDate = new Date(job.created_at)
          if (job.results && Array.isArray(job.results)) {
            job.results.forEach((result: any) => {
              const candidateId = result.candidate?.id
              if (candidateId) {
                const existing = candidateResultsMap.get(candidateId)
                // Keep the most recent result for each candidate
                if (!existing || jobDate > existing.jobDate) {
                  candidateResultsMap.set(candidateId, { result, jobDate })
                }
              }
            })
          }
        })

        // Convert map to array of results
        const combinedResults = Array.from(candidateResultsMap.values()).map(item => item.result)

        setCombinedMatchingResults(combinedResults)

        // Calculate summary from all jobs
        const totalCandidates = combinedResults.length
        const highMatches = combinedResults.filter((r: any) => {
          const score = r.category_score ?? r.match_score ?? 0
          return score >= minimumScore
        }).length
        const maxScore = combinedResults.reduce((max: number, r: any) => {
          const score = r.category_score ?? r.match_score ?? 0
          return Math.max(max, score)
        }, 0)

        setMatchingSummary({
          total_candidates: totalCandidates,
          high_matches: highMatches,
          max_score: maxScore,
          total_jobs: sorted.length
        })
      } catch (error) {
        console.error('Error loading matching details for offer:', error)
        setAllMatchingJobs([])
        setCombinedMatchingResults([])
        setMatchingSummary(null)
        setMatchingPreselectionCandidateIds(new Set())
      } finally {
        setLoadingMatchingDetails(false)
      }
    }

    loadMatchingDetails()
  }, [offer, openPanel, minimumScore])


  const sortedCombinedMatchingResults = useMemo(() => {
    const filterMatching = offer?.filter_matching
    const hasActiveFilters = Boolean(
      filterMatching &&
      (
        (filterMatching.experience !== undefined && filterMatching.experience !== null && filterMatching.experience > 0) ||
        (filterMatching.hard_skills && Object.keys(filterMatching.hard_skills).length > 0) ||
        (filterMatching.soft_skills && Object.keys(filterMatching.soft_skills).length > 0) ||
        (filterMatching.certifications && Object.keys(filterMatching.certifications).length > 0) ||
        (filterMatching.languages && Object.keys(filterMatching.languages).length > 0) ||
        filterMatching.education_level
      )
    )

    const getFilterReason = (
      filterType: 'experience' | 'hardSkills' | 'softSkills' | 'certifications' | 'languages' | 'education',
      detailedReason?: string
    ): string => {
      if (detailedReason) return detailedReason

      const standardReasons: Record<string, string> = {
        experience: t('aiMatching.filterReason.experience', 'Eliminated by years of experience'),
        hardSkills: t('aiMatching.filterReason.hardSkills', 'Eliminated by required skills'),
        softSkills: t('aiMatching.filterReason.softSkills', 'Eliminated by soft skills'),
        certifications: t('aiMatching.filterReason.certifications', 'Eliminated by certifications'),
        languages: t('aiMatching.filterReason.languages', 'Eliminated by languages'),
        education: t('aiMatching.filterReason.education', 'Eliminated by education level'),
      }

      return standardReasons[filterType] || t('aiMatching.filterReason.default', 'Eliminated by filter')
    }

    const getNormalizedSkillSimilarity = (
      detail: any,
      field: 'hard_skills' | 'soft_skills',
      skillNameKey: 'job_competence' | 'job_soft_skill'
    ) => {
      const rawSimilarity = Number(detail?.similarity ?? 0)
      const weight = Number(offer?.skill_weights?.[field]?.[detail?.[skillNameKey]] ?? 1)
      return Math.min(1, weight > 0 ? rawSimilarity / weight : rawSimilarity)
    }

    const resultsWithFilterStatus: MatchingResultWithFilterStatus[] = combinedMatchingResults.map((result: any) => {
      const matchScore = Number(result?.category_score ?? result?.match_score ?? 0)
      const candidate = result?.candidate ?? {}
      const breakdown = result?.score_breakdown as Record<string, MatchingBreakdownCategoryForOffer> | undefined
      const filterReasons: string[] = []
      let passesFilters = true
      const isInPreselection = typeof candidate?.id === 'number' && preselectionCandidateIdsForMatching.has(candidate.id)

      if (hasActiveFilters && filterMatching) {
        if (filterMatching.experience !== undefined && filterMatching.experience !== null && filterMatching.experience > 0) {
          const experienceYears = (breakdown as any)?.experience_years?.details?.cv_experience_pertinente
          let years: number | null = experienceYears ?? null

          if (years === null) {
            const experienceText = String(candidate?.experience ?? '')
            const match = experienceText.match(/(\d+(?:\.\d+)?)/)
            years = match ? Number(match[1]) : null
          }

          if (years === null || years < filterMatching.experience) {
            passesFilters = false
            filterReasons.push(getFilterReason('experience', `Expérience: ${years ?? 0}/${filterMatching.experience} ans requis`))
          }
        }

        if (filterMatching.hard_skills && Object.keys(filterMatching.hard_skills).length > 0) {
          const details = (breakdown as any)?.hard_skills?.details
          const missingSkills: string[] = []

          if (Array.isArray(details)) {
            const similarityMap = new Map<string, number>()
            details.forEach((detail: any) => {
              if (detail.job_competence && detail.similarity !== undefined) {
                similarityMap.set(detail.job_competence.toLowerCase(), getNormalizedSkillSimilarity(detail, 'hard_skills', 'job_competence'))
              }
            })

            Object.entries(filterMatching.hard_skills).forEach(([skillName, requiredSimilarity]) => {
              const similarity = similarityMap.get(skillName.toLowerCase())
              const threshold = Number(requiredSimilarity) / 100
              if (similarity === undefined || similarity < threshold) {
                missingSkills.push(`${skillName} (${Math.round((similarity ?? 0) * 100)}%/${requiredSimilarity}%)`)
              }
            })
          } else {
            missingSkills.push(...Object.keys(filterMatching.hard_skills))
          }

          if (missingSkills.length > 0) {
            passesFilters = false
            filterReasons.push(getFilterReason('hardSkills', `Hard skills: ${missingSkills.join(', ')}`))
          }
        }

        if (filterMatching.soft_skills && Object.keys(filterMatching.soft_skills).length > 0) {
          const details = (breakdown as any)?.soft_skills?.details
          const missingSkills: string[] = []

          if (Array.isArray(details)) {
            const similarityMap = new Map<string, number>()
            details.forEach((detail: any) => {
              if (detail.job_soft_skill && detail.similarity !== undefined) {
                similarityMap.set(detail.job_soft_skill.toLowerCase(), getNormalizedSkillSimilarity(detail, 'soft_skills', 'job_soft_skill'))
              }
            })

            Object.entries(filterMatching.soft_skills).forEach(([skillName, requiredSimilarity]) => {
              const similarity = similarityMap.get(skillName.toLowerCase())
              const threshold = Number(requiredSimilarity) / 100
              if (similarity === undefined || similarity < threshold) {
                missingSkills.push(`${skillName} (${Math.round((similarity ?? 0) * 100)}%/${requiredSimilarity}%)`)
              }
            })
          } else {
            missingSkills.push(...Object.keys(filterMatching.soft_skills))
          }

          if (missingSkills.length > 0) {
            passesFilters = false
            filterReasons.push(getFilterReason('softSkills', `Soft skills: ${missingSkills.join(', ')}`))
          }
        }

        if (filterMatching.certifications && Object.keys(filterMatching.certifications).length > 0) {
          const details = (breakdown as any)?.certifications?.details
          const missingCerts: string[] = []

          if (Array.isArray(details)) {
            const similarityMap = new Map<string, number>()
            details.forEach((detail: any) => {
              if (detail.job_certification && detail.similarity !== undefined) {
                similarityMap.set(detail.job_certification.toLowerCase(), Math.min(1, Number(detail.similarity)))
              }
            })

            Object.entries(filterMatching.certifications).forEach(([certName, requiredSimilarity]) => {
              const similarity = similarityMap.get(certName.toLowerCase())
              const threshold = Number(requiredSimilarity) / 100
              if (similarity === undefined || similarity < threshold) {
                missingCerts.push(`${certName} (${Math.round((similarity ?? 0) * 100)}%/${requiredSimilarity}%)`)
              }
            })
          } else {
            missingCerts.push(...Object.keys(filterMatching.certifications))
          }

          if (missingCerts.length > 0) {
            passesFilters = false
            filterReasons.push(getFilterReason('certifications', `Certifications: ${missingCerts.join(', ')}`))
          }
        }

        if (filterMatching.languages && Object.keys(filterMatching.languages).length > 0) {
          const details = (breakdown as any)?.languages?.details
          const missingLangs: string[] = []

          if (Array.isArray(details)) {
            const similarityMap = new Map<string, number>()
            details.forEach((detail: any) => {
              if (detail.job_langue && detail.job_niveau && detail.similarity_lang !== undefined) {
                const key = `${detail.job_langue} (${detail.job_niveau})`.toLowerCase()
                similarityMap.set(key, Number(detail.similarity_lang))
              }
            })

            Object.entries(filterMatching.languages).forEach(([langName, requiredSimilarity]) => {
              const similarity = similarityMap.get(langName.toLowerCase())
              const threshold = Number(requiredSimilarity) / 100
              if (similarity === undefined || similarity < threshold) {
                missingLangs.push(`${langName} (${Math.round((similarity ?? 0) * 100)}%/${requiredSimilarity}%)`)
              }
            })
          } else {
            missingLangs.push(...Object.keys(filterMatching.languages))
          }

          if (missingLangs.length > 0) {
            passesFilters = false
            filterReasons.push(getFilterReason('languages', `Langues: ${missingLangs.join(', ')}`))
          }
        }

        if (filterMatching.education_level) {
          const details = (breakdown as any)?.niveau_etude?.details
          if (details && details.similarity !== undefined) {
            const normalizedSimilarity = Math.min(1, Number(details.similarity))
            const threshold = Number(filterMatching.education_level.similarity) / 100
            if (normalizedSimilarity < threshold) {
              passesFilters = false
              filterReasons.push(getFilterReason('education', `Niveau d'étude: ${Math.round(normalizedSimilarity * 100)}%/${filterMatching.education_level.similarity}%`))
            }
          } else {
            passesFilters = false
            filterReasons.push(getFilterReason('education', `Niveau d'étude: non renseigné`))
          }
        }
      }

      return {
        result,
        matchScore,
        passesFilters,
        filterReasons,
        isSelected: isInPreselection || (passesFilters && matchScore >= minimumScore),
      }
    })

    return resultsWithFilterStatus.sort((a, b) => {
      if (a.isSelected !== b.isSelected) return a.isSelected ? -1 : 1
      if (a.passesFilters !== b.passesFilters) return a.passesFilters ? -1 : 1
      if (b.matchScore !== a.matchScore) return b.matchScore - a.matchScore

      const nameA = String(a.result?.candidate?.name ?? a.result?.candidate?.email ?? '')
      const nameB = String(b.result?.candidate?.name ?? b.result?.candidate?.email ?? '')
      return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' })
    })
  }, [combinedMatchingResults, minimumScore, offer?.filter_matching, offer?.skill_weights, preselectionCandidateIdsForMatching, t])

  const selectedMatchingResultCount = useMemo(() => {
    return sortedCombinedMatchingResults.filter(item => item.isSelected).length
  }, [sortedCombinedMatchingResults])

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    addToast({
      type: 'success',
      title: t('common.success', 'Success'),
      message: t('common.copiedToClipboard', 'Copié dans le presse-papier')
    })
  }

  // Load interview audios when validation_interviews or applications tab (Kanban) is opened
  useEffect(() => {
    if (offer?.id && (activeTab === 'validation_interviews' || activeTab === 'applications' || openPanel === 'validation')) {
      refreshInterviewAudios()
    }
  }, [offer?.id, activeTab, openPanel])

  useEffect(() => {
    if (offer?.interview_questions_per_section && Object.keys(offer.interview_questions_per_section).length > 0) {
      setInterviewQuestionsPerSection(offer.interview_questions_per_section)
    }
    if (offer && offer.auto_generate_interviews !== undefined) {
      setAutoGenerateInterviews(offer.auto_generate_interviews)
    }
    if (offer && offer.auto_send_interview_emails !== undefined) {
      setAutoSendInterviewEmails(offer.auto_send_interview_emails)
    }
    if (offer && offer.auto_evaluate_interviews !== undefined) {
      setAutoEvaluateInterviews(offer.auto_evaluate_interviews)
    }
    if (offer && offer.interview_language && !isCreatingManualQuestionsRef.current) {
      setInterviewLanguage(offer.interview_language as 'fr' | 'en' | 'custom')
    }
    setValidationInterviewDeadline(formatDateForInput(offer?.interview_deadline))
  }, [offer])


  const handleShowResponses = (audio: any) => {
    setSelectedInterviewForResponses(audio)
    setIsViewingOnlyQuestions(false)
    setIsResponseModalOpen(true)
  }


  const refreshInterviewAudios = async (options?: { silent?: boolean }) => {
    if (!offer) return
    const silent = options?.silent ?? interviewAudios.length > 0
    try {
      if (!silent) {
        setLoadingInterviewsList(true)
      }
      const response = await interviewAudioApi.getAll({ application__offer_id: offer.id, page_size: 1000 })
      const audios = response.data.results || []
      setInterviewAudios(audios)

      // Do not overwrite the recruiter's language choice from empty interviews
      // (placeholders default to 'fr') while creating manual questions.
      if (!isCreatingManualQuestionsRef.current) {
        const interviewWithQuestions = audios.find((audio: any) => {
          const questions = audio?.questions
          if (!questions) return false
          if (Array.isArray(questions)) return questions.length > 0
          if (typeof questions === 'object') {
            if ('fr' in questions || 'en' in questions) return true
            return Object.values(questions).some((q: any) => Array.isArray(q) && q.length > 0)
          }
          return false
        })
        if (interviewWithQuestions?.language) {
          setInterviewLanguage(interviewWithQuestions.language)
        }
      }

      if (openPanel === 'validation') {
        fetchValidationCandidates({ silent: validationCandidates.length > 0 })
      }
    } catch (error) {
      console.error('Error loading interview audios:', error)
    } finally {
      if (!silent) {
        setLoadingInterviewsList(false)
      }
    }
  }

  const requireSavedInterviewDeadline = () => {
    if (offer?.interview_deadline) return true
    addToast({
      type: 'warning',
      title: t('common.warning', 'Avertissement'),
      message: t(
        'offerDetail.interviewDeadlineRequiredBeforeGeneration',
        'La date limite des entretiens est obligatoire avant de générer ou de créer manuellement les questions. Enregistrez-la pour continuer.'
      ),
    })
    return false
  }

  const handleOpenBatchGenerateModal = () => {
    if (!guardOfferOpenForMutations(
      'offerDetail.cannotUseValidationInterviewsClosed',
      'Validation interview actions are disabled while this offer is closed.'
    )) return
    if (!requireSavedInterviewDeadline()) return
    setShowBatchGenerateModal(true)
  }

  const handleResetInterview = async () => {
    if (!guardOfferOpenForMutations(
      'offerDetail.cannotUseValidationInterviewsClosed',
      'Validation interview actions are disabled while this offer is closed.'
    )) return
    if (!interviewToReset) return
    setIsResetting(true)
    try {
      await interviewAudioApi.resetInterview(interviewToReset.id)
      addToast({
        type: 'success',
        title: t('common.success'),
        message: t('offerDetail.interviewResetSuccess', 'L\'entretien a été réinitialisé avec succès.')
      })
      refreshInterviewAudios()
      setShowResetConfirm(false)
      setInterviewToReset(null)
    } catch (error: any) {
      addToast({
        type: 'error',
        title: t('common.error'),
        message: error.response?.data?.error || t('offerDetail.interviewResetError', 'Échec de la réinitialisation de l\'entretien.')
      })
    } finally {
      setIsResetting(false)
    }
  }

  const handleDeleteInterview = async () => {
    if (!guardOfferOpenForMutations(
      'offerDetail.cannotUseValidationInterviewsClosed',
      'Validation interview actions are disabled while this offer is closed.'
    )) return
    if (!interviewToDelete) return

    const deletedId = interviewToDelete.id
    try {
      setIsDeletingInterview(true)
      await interviewAudioApi.delete(deletedId)

      setInterviewAudios((prev) => prev.filter((audio) => audio.id !== deletedId))
      setShowDeleteInterviewConfirm(false)
      setInterviewToDelete(null)
      addToast({
        type: 'success',
        title: t('common.success', 'Success'),
        message: t(
          'offerDetail.interviewDeletedSuccess',
          'Entretien supprimé. Le candidat est de nouveau en attente de génération.'
        ),
      })
      void refreshInterviewAudios({ silent: true })
      if (openPanel !== 'validation') {
        void fetchValidationCandidates({ silent: true })
      }
    } catch (error: any) {
      console.error('Error deleting interview:', error)
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message:
          error?.response?.data?.error ||
          t('offerDetail.interviewDeleteFailed', 'Échec de la suppression de l\'entretien'),
      })
    } finally {
      setIsDeletingInterview(false)
    }
  }

  const handleBatchGenerate = async () => {
    if (!guardOfferOpenForMutations(
      'offerDetail.cannotUseValidationInterviewsClosed',
      'Validation interview actions are disabled while this offer is closed.'
    )) return
    if (!offer) return
    if (!requireSavedInterviewDeadline()) return
    try {
      setIsGeneratingBatch(true)
      const hasExistingInterviews = interviewAudios.length > 0
      const totalFromSection = (Object.values(interviewQuestionsPerSection) as number[]).reduce(
        (a: number, b: number) => a + b,
        0
      )
      const numberOfQuestions = hasExistingInterviews
        ? (offer.total_interview_questions ?? totalFromSection)
        : totalFromSection
      const questionsPerSection = hasExistingInterviews
        ? (offer.interview_questions_per_section ?? interviewQuestionsPerSection)
        : interviewQuestionsPerSection

      await interviewAudioApi.batchGenerateQuestions(
        offer.id,
        interviewLanguage,
        numberOfQuestions,
        questionsPerSection
      )
      addToast({
        type: 'success',
        title: t('common.success', 'Success'),
        message: t('offerDetail.batchGenerationSuccess', 'Génération des entretiens lancée avec succès')
      })
      refreshInterviewAudios()
      setShowBatchGenerateModal(false)
    } catch (error: any) {
      console.error('Error in batch generation:', error)
      const apiError = error?.response?.data?.error
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: apiError || t('offerDetail.batchGenerationFailed', 'Échec de la génération des entretiens')
      })
    } finally {
      setIsGeneratingBatch(false)
    }
  }

  const handleSaveQuestionsAndEnableAuto = async () => {
    if (!offer) return
    if (!requireSavedInterviewDeadline()) return
    setIsSavingAutoConfig(true)
    try {
      const totalQuestions = (Object.values(interviewQuestionsPerSection) as number[]).reduce((a: number, b: number) => a + b, 0)
      await offersApi.update(offer.id, {
        auto_generate_interviews: true,
        interview_questions_per_section: interviewQuestionsPerSection,
        total_interview_questions: totalQuestions,
        interview_language: interviewLanguage
      })

      setOffer(prev => prev ? {
        ...prev,
        auto_generate_interviews: true,
        interview_questions_per_section: interviewQuestionsPerSection,
        total_interview_questions: totalQuestions
      } : null)

      setAutoGenerateInterviews(true)
      setShowConfigureAlert(false)

      addToast({
        type: 'success',
        title: t('common.success', 'Success'),
        message: t('offerDetail.sectionUpdated', 'Section mise à jour')
      })
    } catch (error) {
      console.error('Failed to save auto config:', error)
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('offerDetail.sectionUpdateFailed', 'Impossible d’enregistrer les modifications')
      })
    } finally {
      setIsSavingAutoConfig(false)
    }
  }

  const handleEnableAutoGenerateOnly = async () => {
    if (!offer) return
    if (!requireSavedInterviewDeadline()) return
    setIsSavingAutoConfig(true)
    try {
      await offersApi.update(offer.id, { auto_generate_interviews: true })
      setOffer(prev => prev ? { ...prev, auto_generate_interviews: true } : null)
      setAutoGenerateInterviews(true)
      setShowConfigureAlert(false)
      addToast({
        type: 'success',
        title: t('common.success', 'Success'),
        message: t('offerDetail.sectionUpdated', 'Section mise à jour')
      })
    } catch (error) {
      console.error('Failed to enable auto generation:', error)
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('offerDetail.sectionUpdateFailed', 'Impossible d’enregistrer les modifications')
      })
    } finally {
      setIsSavingAutoConfig(false)
    }
  }

  const handleLanguageChange = async (value: 'fr' | 'en' | 'custom') => {
    if (!offer) return
    const prevLanguage = interviewLanguage
    setInterviewLanguage(value)
    try {
      await offersApi.update(offer.id, { interview_language: value })
      setOffer(prev => prev ? { ...prev, interview_language: value } : null)
      addToast({
        type: 'success',
        title: t('common.success', 'Success'),
        message: t('offerDetail.languageUpdated', 'Langue mise à jour avec succès')
      })
    } catch (error) {
      console.error('Error updating interview language:', error)
      setInterviewLanguage(prevLanguage)
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('offerDetail.languageUpdateFailed', 'Échec de la mise à jour de la langue')
      })
    }
  }

  const handleAutoGenerateToggle = async (checked: boolean) => {
    if (!guardOfferOpenForMutations(
      'offerDetail.cannotUseValidationInterviewsClosed',
      'Validation interview actions are disabled while this offer is closed.'
    )) return
    if (!offer) return
    if (offer.manual_interview_questions_created) return

    if (checked && !requireSavedInterviewDeadline()) return

    if (checked) {
      const hasOfferQuestionsConfig = Boolean(
        offer.interview_questions_per_section &&
        Object.keys(offer.interview_questions_per_section).length > 0
      )
      const hasExistingInterviews = interviewAudios.length > 0

      // Require question configuration only when no interviews exist yet.
      // Allow toggling on even with zero candidates pending generation.
      if (!hasOfferQuestionsConfig && !hasExistingInterviews) {
        setShowConfigureAlert(true)
        return
      }
    }

    setAutoGenerateInterviews(checked)
    try {
      await offersApi.update(offer.id, { auto_generate_interviews: checked })
      setOffer(prev => prev ? { ...prev, auto_generate_interviews: checked } : null)
      addToast({
        type: 'success',
        title: t('common.success', 'Success'),
        message: t('offerDetail.sectionUpdated', 'Section mise à jour')
      })
    } catch (error) {
      console.error('Auto generate toggle error:', error)
      setAutoGenerateInterviews(!checked)
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('offerDetail.sectionUpdateFailed', 'Impossible d’enregistrer les modifications')
      })
    }
  }

  const handleAutoSendEmailsToggle = async (checked: boolean) => {
    if (!guardOfferOpenForMutations(
      'offerDetail.cannotUseValidationInterviewsClosed',
      'Validation interview actions are disabled while this offer is closed.'
    )) return
    if (!offer) return


    setAutoSendInterviewEmails(checked)
    try {
      await offersApi.update(offer.id, { auto_send_interview_emails: checked })
      setOffer(prev => prev ? { ...prev, auto_send_interview_emails: checked } : null)
      addToast({
        type: 'success',
        title: t('common.success', 'Success'),
        message: t('offerDetail.sectionUpdated', 'Section mise à jour')
      })
    } catch (error) {
      console.error('Auto send emails toggle error:', error)
      setAutoSendInterviewEmails(!checked)
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('offerDetail.sectionUpdateFailed', 'Impossible d’enregistrer les modifications')
      })
    }
  }

  const handleAutoEvaluateToggle = async (checked: boolean) => {
    if (!guardOfferOpenForMutations(
      'offerDetail.cannotUseValidationInterviewsClosed',
      'Validation interview actions are disabled while this offer is closed.'
    )) return
    if (!offer) return

    setAutoEvaluateInterviews(checked)
    try {
      await offersApi.update(offer.id, { auto_evaluate_interviews: checked })
      setOffer(prev => prev ? { ...prev, auto_evaluate_interviews: checked } : null)
      addToast({
        type: 'success',
        title: t('common.success', 'Success'),
        message: t('offerDetail.sectionUpdated', 'Section mise à jour')
      })
    } catch (error) {
      console.error('Auto evaluate toggle error:', error)
      setAutoEvaluateInterviews(!checked)
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('offerDetail.sectionUpdateFailed', 'Impossible d’enregistrer les modifications')
      })
    }
  }

  const handleStartEditingQuestions = () => {
    if (!selectedInterviewForResponses) return
    setEditingQuestions(deepClone(selectedInterviewForResponses.questions))
    setEditingCommonQuestions(deepClone(selectedInterviewForResponses.common_questions || []))
    setIsEditingQuestions(true)
  }

  const handleOpenAddInterviewQuestionModal = () => {
    if (!selectedInterviewForResponses) return
    setShowAddInterviewQuestionSectionModal(true)
  }

  const handleAddQuestion = (sectionName: InterviewQuestionSection = DEFAULT_MANUAL_QUESTION_SECTION) => {
    if (!selectedInterviewForResponses) return
    setShowAddInterviewQuestionSectionModal(false)

    // Already in bulk edit: append into the editing draft (user is already editing all).
    if (isEditingQuestions && editingQuestions) {
      const draft = deepClone(editingQuestions)
      const newQuestion = {
        question: '',
        duration: selectedInterviewForResponses.default_question_duration || 120,
      }

      if (selectedInterviewForResponses.language === 'custom' && (draft.fr || draft.en)) {
        for (const language of ['fr', 'en'] as const) {
          draft[language] = draft[language] || {}
          draft[language][sectionName] = [
            ...(draft[language][sectionName] || []),
            deepClone(newQuestion),
          ]
        }
      } else {
        draft[sectionName] = [
          ...(draft[sectionName] || []),
          newQuestion,
        ]
      }

      setEditingQuestions(draft)
      return
    }

    // View mode: only the new question is editable; others stay read-only.
    setAddingInterviewQuestionDraft({
      section: sectionName,
      question: '',
      questionEn: '',
      duration: selectedInterviewForResponses.default_question_duration || 120,
      description: '',
    })
  }

  const handleCancelAddingInterviewQuestion = () => {
    setAddingInterviewQuestionDraft(null)
  }

  const handleSaveAddingInterviewQuestion = async () => {
    if (!selectedInterviewForResponses || !addingInterviewQuestionDraft) return

    const questionText = addingInterviewQuestionDraft.question.trim()
    if (!questionText) {
      addToast({
        type: 'error',
        title: t('common.error', 'Erreur'),
        message: t('offerDetail.questionTextRequired', 'Le texte de la question est obligatoire')
      })
      return
    }

    const isCustomLanguage = selectedInterviewForResponses.language === 'custom'
    const questionEnText = addingInterviewQuestionDraft.questionEn.trim()
    if (isCustomLanguage && !questionEnText) {
      addToast({
        type: 'error',
        title: t('common.error', 'Erreur'),
        message: t('offerDetail.questionTextRequiredEn', 'Le texte de la question en anglais est obligatoire')
      })
      return
    }

    try {
      setIsSavingQuestions(true)
      const sectionName = addingInterviewQuestionDraft.section
      const updatedQuestions = deepClone(selectedInterviewForResponses.questions || {})
      const newQuestionFr = {
        question: questionText,
        duration: addingInterviewQuestionDraft.duration || 120,
        description: addingInterviewQuestionDraft.description || '',
      }

      if (isCustomLanguage) {
        updatedQuestions.fr = updatedQuestions.fr || {}
        updatedQuestions.en = updatedQuestions.en || {}
        updatedQuestions.fr[sectionName] = [
          ...(updatedQuestions.fr[sectionName] || []),
          newQuestionFr,
        ]
        updatedQuestions.en[sectionName] = [
          ...(updatedQuestions.en[sectionName] || []),
          {
            question: questionEnText,
            duration: addingInterviewQuestionDraft.duration || 120,
            description: addingInterviewQuestionDraft.description || '',
          },
        ]
      } else {
        updatedQuestions[sectionName] = [
          ...(updatedQuestions[sectionName] || []),
          newQuestionFr,
        ]
      }

      await interviewAudioApi.update(selectedInterviewForResponses.id, { questions: updatedQuestions })

      const updatedInterview = {
        ...selectedInterviewForResponses,
        questions: updatedQuestions,
      }
      setSelectedInterviewForResponses(updatedInterview)
      setInterviewAudios(prev =>
        prev.map(a => (a.id === updatedInterview.id ? updatedInterview : a))
      )
      setAddingInterviewQuestionDraft(null)
      addToast({
        type: 'success',
        title: t('common.success', 'Succès'),
        message: t('offerDetail.questionAddedSuccess', 'Question ajoutée avec succès')
      })
    } catch (error) {
      console.error('Error adding interview question:', error)
      addToast({
        type: 'error',
        title: t('common.error', 'Erreur'),
        message: t('offerDetail.questionAddFailed', "Échec de l'ajout de la question")
      })
    } finally {
      setIsSavingQuestions(false)
    }
  }

  const handleSendInvitation = async (interviewId: number) => {
    if (!guardOfferOpenForMutations(
      'offerDetail.cannotUseValidationInterviewsClosed',
      'Validation interview actions are disabled while this offer is closed.'
    )) return
    try {
      setIsSendingInvitation(interviewId)
      await interviewAudioApi.sendInvitation(interviewId)
      addToast({
        type: 'success',
        title: t('common.success', 'Succès'),
        message: t('offerDetail.invitationSentSuccess', 'Invitation envoyée avec succès')
      })
      refreshInterviewAudios()
    } catch (error) {
      console.error('Error sending invitation:', error)
      addToast({
        type: 'error',
        title: t('common.error', 'Erreur'),
        message: t('offerDetail.invitationSentError', "Échec de l'envoi de l'invitation")
      })
    } finally {
      setIsSendingInvitation(null)
    }
  }

  const handleBatchSendInvitations = async () => {
    if (!guardOfferOpenForMutations(
      'offerDetail.cannotUseValidationInterviewsClosed',
      'Validation interview actions are disabled while this offer is closed.'
    )) return
    if (!offer) return
    try {
      setIsSendingBatchInvitations(true)
      const response = await interviewAudioApi.batchSendInvitations(offer.id)
      addToast({
        type: 'success',
        title: t('common.success', 'Succès'),
        message: t('offerDetail.batchInvitationsSentSuccess', '{{count}} invitations ont été envoyées', { count: response.data.count })
      })
      refreshInterviewAudios()
    } catch (error) {
      console.error('Error sending batch invitations:', error)
      addToast({
        type: 'error',
        title: t('common.error', 'Erreur'),
        message: t('offerDetail.batchInvitationsSentError', "Échec de l'envoi des invitations")
      })
    } finally {
      setIsSendingBatchInvitations(false)
    }
  }

  const reloadKanbanRef = useRef<(() => void) | null>(null)

  const handleEvaluateInterview = async (interviewId: number) => {
    if (!guardOfferOpenForMutations(
      'offerDetail.cannotUseValidationInterviewsClosed',
      'Validation interview actions are disabled while this offer is closed.'
    )) return
    setIsEvaluating(interviewId)
    try {
      const response = await interviewAudioApi.evaluateInterview(interviewId)
      addToast({ type: 'success', message: t('offerDetail.evaluationSuccess', 'Évaluation terminée avec succès') })
      refreshInterviewAudios()
      fetchValidationCandidates()
      reloadKanbanRef.current?.()

      if (response.data.evaluation) {
        setSelectedEvaluation(response.data.evaluation)
        setEvaluationInterviewId(interviewId)
        setIsManualEvaluationMode(false)
        setIsEditingEvaluation(false)
        setEvaluationModalOpen(true)
      }
    } catch (error: any) {
      console.error('Error evaluating interview:', error)
      addToast({ type: 'error', message: error.message || t('offerDetail.evaluationError', "Erreur lors de l'évaluation") })
    } finally {
      setIsEvaluating(null)
    }
  }

  const handleSaveEvaluation = async () => {
    if (!evaluationInterviewId || !editedEvaluation) return;

    setIsSavingEvaluation(true);
    try {
      const evaluationPayload = {
        ...editedEvaluation,
        evaluation_source: isManualEvaluationMode
          ? 'human'
          : (editedEvaluation.evaluation_source || 'ai'),
      }
      const response = await interviewAudioApi.updateEvaluation(evaluationInterviewId, evaluationPayload);
      const recalculatedEvaluation = response.data.evaluation;

      setSelectedEvaluation(recalculatedEvaluation);
      setIsEditingEvaluation(false);

      // Update the local interview list to reflect changes with the recalculated data
      setInterviewAudios(prev => prev.map(audio => {
        if (audio.id === evaluationInterviewId) {
          return { ...audio, score: JSON.stringify(recalculatedEvaluation) };
        }
        return audio;
      }));

      fetchValidationCandidates()
      reloadKanbanRef.current?.()
      setIsManualEvaluationMode(false)
      addToast({ type: 'success', message: t('offerDetail.evaluationUpdated', 'Évaluation mise à jour avec succès') });
    } catch (error: any) {
      console.error('Error saving evaluation:', error);
      addToast({ type: 'error', message: error.message || t('offerDetail.evaluationUpdateError', "Erreur lors de la mise à jour de l'évaluation") });
    } finally {
      setIsSavingEvaluation(false);
    }
  };

  const buildInitialManualEvaluation = useCallback((audio: any) => {
    const MODE_A_SECTIONS = new Set(['hard_skills', 'soft_skills', 'langues', 'nice_to_have'])

    const sections: Record<string, any> = {}

    const questionsObj = audio?.questions || {}
    let sectionsData: Record<string, any> = {}
    if (questionsObj.fr && typeof questionsObj.fr === 'object') {
      sectionsData = questionsObj.fr
    } else if (questionsObj.en && typeof questionsObj.en === 'object') {
      sectionsData = questionsObj.en
    } else if (typeof questionsObj === 'object') {
      sectionsData = questionsObj
    }

    const addQuestionToSection = (sectionName: string, questionText: string, responseText?: string) => {
      const isModeA = MODE_A_SECTIONS.has(sectionName)
      if (!sections[sectionName]) {
        sections[sectionName] = {
          mode: isModeA ? 'A' : 'B',
          section_score: 0.0,
          evaluations: []
        }
      }

      const evalItem: any = {
        question: questionText,
        response: responseText || '',
        language_level: 'General',
        feedback: ''
      }

      if (isModeA) {
        evalItem.communication_score = 0
        evalItem.expertise_score = 0
        evalItem.credibility_score = 0
      } else {
        evalItem.clarity_score = 0
        evalItem.consistency_score = 0
        evalItem.detail_score = 0
      }

      sections[sectionName].evaluations.push(evalItem)
    }

    Object.entries(sectionsData).forEach(([secName, qList]) => {
      if (Array.isArray(qList)) {
        qList.forEach((qItem: any) => {
          const qText = typeof qItem === 'string' ? qItem : (qItem.question || String(qItem))
          const resp = audio?.interview_responses?.find((r: any) => r.question_text?.trim() === qText?.trim())
          addQuestionToSection(secName, qText, resp?.response)
        })
      }
    })

    const cqs = audio?.common_questions || audio?.questions?.questions_communes || []
    if (Array.isArray(cqs)) {
      cqs.forEach((cq: any) => {
        const qText = typeof cq === 'string' ? cq : (cq.question || String(cq))
        const exists = sections.questions_communes?.evaluations?.some((e: any) => e.question === qText)
        if (!exists) {
          const resp = audio?.interview_responses?.find((r: any) => r.question_text?.trim() === qText?.trim())
          addQuestionToSection('questions_communes', qText, resp?.response)
        }
      })
    }

    if (Array.isArray(audio?.interview_responses)) {
      audio.interview_responses.forEach((resp: any) => {
        const qText = resp.question_text
        if (qText) {
          let found = false
          Object.values(sections).forEach((sec: any) => {
            if (sec.evaluations?.some((e: any) => e.question === qText)) {
              found = true
            }
          })
          if (!found) {
            addQuestionToSection('nice_to_have', qText, resp.response)
          }
        }
      })
    }

    if (Object.keys(sections).length === 0) {
      sections['hard_skills'] = {
        mode: 'A',
        section_score: 0.0,
        evaluations: [
          {
            question: 'Évaluation des compétences techniques',
            response: '',
            communication_score: 0,
            expertise_score: 0,
            credibility_score: 0,
            language_level: 'General',
            feedback: ''
          }
        ]
      }
      sections['soft_skills'] = {
        mode: 'A',
        section_score: 0.0,
        evaluations: [
          {
            question: 'Évaluation des compétences relationnelles',
            response: '',
            communication_score: 0,
            expertise_score: 0,
            credibility_score: 0,
            language_level: 'General',
            feedback: ''
          }
        ]
      }
    }

    return {
      summary: "Évaluation humaine de l'entretien",
      overall_language_level: "B2",
      strengths: [""],
      weaknesses: [""],
      global_score: 0.0,
      final_recommendation: "Reservé",
      evaluation_source: "human",
      sections
    }
  }, [])

  const handleOpenManualEvaluation = async (row: any) => {
    if (!guardOfferOpenForMutations(
      'offerDetail.cannotUseValidationInterviewsClosed',
      'Validation interview actions are disabled while this offer is closed.'
    )) return
    if (row.type === 'generated' && row.audio) {
      let evalData: any
      if (row.audio.score) {
        try {
          evalData = typeof row.audio.score === 'string' ? JSON.parse(row.audio.score) : row.audio.score
        } catch (e) {
          evalData = buildInitialManualEvaluation(row.audio)
        }
      } else {
        evalData = buildInitialManualEvaluation(row.audio)
      }

      setSelectedEvaluation(evalData)
      setEditedEvaluation(JSON.parse(JSON.stringify(evalData)))
      setEvaluationInterviewId(row.audio.id)
      setIsEditingEvaluation(true)
      setIsManualEvaluationMode(true)
      setEvaluationModalOpen(true)
    } else if (row.type === 'pending' && row.application) {
      try {
        setIsCreatingInterviewForEvaluation(row.application.id)
        const res = await interviewAudioApi.create({ application: row.application.id })
        const newAudio = res.data
        const evalData = buildInitialManualEvaluation(newAudio)

        setSelectedEvaluation(evalData)
        setEditedEvaluation(JSON.parse(JSON.stringify(evalData)))
        setEvaluationInterviewId(newAudio.id)
        setIsEditingEvaluation(true)
        setIsManualEvaluationMode(true)
        setEvaluationModalOpen(true)
        await fetchValidationCandidates()
        refreshInterviewAudios()
      } catch (err: any) {
        console.error('Error creating interview for manual evaluation:', err)
        addToast({
          type: 'error',
          message: err.response?.data?.error || err.message || t('common.error', "Erreur lors de la préparation de l'entretien")
        })
      } finally {
        setIsCreatingInterviewForEvaluation(null)
      }
    }
  }

  const handleAddQuestionToSection = (sectionName: string) => {
    if (!editedEvaluation) return
    const isModeA = ['hard_skills', 'soft_skills', 'langues', 'nice_to_have'].includes(sectionName) ||
      editedEvaluation.sections?.[sectionName]?.mode === 'A'

    const newQuestionItem: any = {
      question: '',
      response: '',
      language_level: 'General',
      feedback: ''
    }
    if (isModeA) {
      newQuestionItem.communication_score = 0
      newQuestionItem.expertise_score = 0
      newQuestionItem.credibility_score = 0
    } else {
      newQuestionItem.clarity_score = 0
      newQuestionItem.consistency_score = 0
      newQuestionItem.detail_score = 0
    }

    const newSections = { ...(editedEvaluation.sections || {}) }
    const existingSection = newSections[sectionName] || {
      mode: isModeA ? 'A' : 'B',
      section_score: 0.0,
      evaluations: []
    }
    newSections[sectionName] = {
      ...existingSection,
      evaluations: [...(existingSection.evaluations || []), newQuestionItem]
    }
    setEditedEvaluation({ ...editedEvaluation, sections: newSections })
  }

  const handleDeleteQuestionFromSection = (sectionName: string, idx: number) => {
    if (!editedEvaluation?.sections?.[sectionName]) return
    const newSections = { ...editedEvaluation.sections }
    const newEvals = newSections[sectionName].evaluations.filter((_: any, i: number) => i !== idx)
    newSections[sectionName] = { ...newSections[sectionName], evaluations: newEvals }
    setEditedEvaluation({ ...editedEvaluation, sections: newSections })
  }

  const handleAddSectionToEvaluation = (secKey: string) => {
    if (!editedEvaluation) return
    const MODE_A_SET = new Set(['hard_skills', 'soft_skills', 'langues', 'nice_to_have'])
    const isModeA = MODE_A_SET.has(secKey)
    const newSections = { ...(editedEvaluation.sections || {}) }
    if (!newSections[secKey]) {
      newSections[secKey] = {
        mode: isModeA ? 'A' : 'B',
        section_score: 0.0,
        evaluations: [
          {
            question: '',
            response: '',
            language_level: 'General',
            feedback: '',
            ...(isModeA
              ? { communication_score: 0, expertise_score: 0, credibility_score: 0 }
              : { clarity_score: 0, consistency_score: 0, detail_score: 0 })
          }
        ]
      }
      setEditedEvaluation({ ...editedEvaluation, sections: newSections })
    }
  }

  const handleBatchEvaluate = async () => {
    if (!guardOfferOpenForMutations(
      'offerDetail.cannotUseValidationInterviewsClosed',
      'Validation interview actions are disabled while this offer is closed.'
    )) return
    if (!id) return
    setIsBatchEvaluating(true)
    try {
      const response = await interviewAudioApi.batchEvaluateInterviews(Number(id))
      addToast({ type: 'success', message: t('offerDetail.batchEvaluationSuccess', 'Évaluation par lot terminée : {{count}} entretiens traités', { count: response.data.count }) })
      refreshInterviewAudios()
      fetchValidationCandidates()
      reloadKanbanRef.current?.()
    } catch (error: any) {
      console.error('Error batch evaluating interviews:', error)
      addToast({ type: 'error', message: error.message || t('offerDetail.batchEvaluationError', "Erreur lors de l'évaluation par lot") })
    } finally {
      setIsBatchEvaluating(false)
    }
  }

  const handleCancelEditingQuestions = () => {
    setIsEditingQuestions(false)
    setEditingQuestions(null)
    setEditingCommonQuestions(null)
  }

  const handleUpdateQuestionText = (section: string, index: number, newText: string, lang?: 'fr' | 'en') => {
    setEditingQuestions((prev: any) => {
      const next = deepClone(prev)
      if (lang) {
        if (next[lang] && next[lang][section] && next[lang][section][index]) {
          if (typeof next[lang][section][index] === 'string') {
            next[lang][section][index] = newText
          } else {
            next[lang][section][index].question = newText
          }
        }
      } else {
        if (next[section] && next[section][index]) {
          if (typeof next[section][index] === 'string') {
            next[section][index] = newText
          } else {
            next[section][index].question = newText
          }
        }
      }
      return next
    })
  }

  const handleUpdateQuestionMetadata = (
    section: string,
    index: number,
    field: 'duration' | 'description',
    value: string,
    lang?: 'fr' | 'en'
  ) => {
    setEditingQuestions((prev: any) => {
      const next = deepClone(prev)
      const update = (target: any) => {
        if (typeof target === 'string') return { question: target, [field]: field === 'duration' ? (parseInt(value) || 0) : value }
        if (!target || typeof target !== 'object') return target
        const parsed = field === 'duration' ? (parseInt(value) || 0) : value
        target[field] = parsed
        if (field === 'duration' && 'duree_estimee_secondes' in target) {
          target.duree_estimee_secondes = parsed
        }
        return target
      }

      if (lang) {
        if (next[lang]?.[section]?.[index] !== undefined) {
          next[lang][section][index] = update(next[lang][section][index])
        }
        // Duration and AI description belong to the question, not its
        // translation, so keep both custom-language branches synchronized.
        const otherLang = lang === 'fr' ? 'en' : 'fr'
        if (next[otherLang]?.[section]?.[index] !== undefined) {
          next[otherLang][section][index] = update(next[otherLang][section][index])
        }
      } else if (next[section]?.[index] !== undefined) {
        next[section][index] = update(next[section][index])
      }
      return next
    })
  }

  const renderQuestionMetadata = (questionObj: any, className = '') => {
    if (!questionObj || typeof questionObj !== 'object') return null
    const duration = questionObj.duration ?? questionObj.duree_estimee_secondes
    const description = questionObj.description
    if (duration == null && !description) return null

    return (
      <div className={`mt-2 space-y-1.5 text-xs text-muted-foreground ${className}`}>
        {duration != null && (
          <div className="flex items-center gap-1.5 font-medium">
            <Clock className="h-3.5 w-3.5" />
            <span>{t('offerDetail.durationSeconds', 'Durée (secondes)')}: {duration}s</span>
          </div>
        )}
        {description && (
          <div className="rounded-md border border-primary/10 bg-primary/5 px-2.5 py-2">
            <span className="font-medium text-foreground/70">
              {t('offerDetail.questionDescription', 'Description pour l’évaluation IA')}:
            </span>{' '}
            <span>{description}</span>
          </div>
        )}
      </div>
    )
  }

  const [editingPath, setEditingPath] = useState<string | null>(null)
  const [individualEditingValue, setIndividualEditingValue] = useState<string>('')

  const handleStartIndividualEdit = (type: 'standard' | 'common', index: number, section?: string, lang?: 'fr' | 'en') => {
    if (!selectedInterviewForResponses) return

    let path = ''
    let value = ''

    if (type === 'common') {
      path = `common:${index}`
      const cq = selectedInterviewForResponses.common_questions?.[index]
      value = typeof cq === 'string' ? cq : cq?.question || ''
    } else {
      path = `${section}:${index}${lang ? `:${lang}` : ''}`
      if (lang) {
        const qObj = selectedInterviewForResponses.questions[lang]?.[section!]?.[index]
        value = typeof qObj === 'string' ? qObj : qObj?.question || ''
      } else {
        const qObj = selectedInterviewForResponses.questions[section!]?.[index]
        value = typeof qObj === 'string' ? qObj : qObj?.question || ''
      }
    }

    setEditingPath(path)
    setIndividualEditingValue(value)
  }

  const handleCancelIndividualEdit = () => {
    setEditingPath(null)
    setIndividualEditingValue(null)
  }

  const handleSaveIndividualEdit = async () => {
    if (!selectedInterviewForResponses || !editingPath) return

    try {
      setIsSavingQuestions(true)
      const [typeOrSection, indexStr, lang] = editingPath.split(':')
      const index = parseInt(indexStr)

      let updatedQuestions = deepClone(selectedInterviewForResponses.questions)
      let updatedCommonQuestions = deepClone(selectedInterviewForResponses.common_questions || [])

      if (typeOrSection === 'common') {
        if (typeof updatedCommonQuestions[index] === 'string') {
          updatedCommonQuestions[index] = individualEditingValue
        } else {
          updatedCommonQuestions[index] = { ...updatedCommonQuestions[index], question: individualEditingValue }
        }
      } else {
        if (lang) {
          const section = typeOrSection
          if (typeof updatedQuestions[lang][section][index] === 'string') {
            updatedQuestions[lang][section][index] = individualEditingValue
          } else {
            updatedQuestions[lang][section][index] = { ...updatedQuestions[lang][section][index], question: individualEditingValue }
          }
        } else {
          const section = typeOrSection
          if (typeof updatedQuestions[section][index] === 'string') {
            updatedQuestions[section][index] = individualEditingValue
          } else {
            updatedQuestions[section][index] = { ...updatedQuestions[section][index], question: individualEditingValue }
          }
        }
      }

      const updateData: any = { questions: updatedQuestions }
      // Legacy interviews expose offer-level common questions separately.
      // Manual interviews store them directly in `questions_communes`.
      if (typeOrSection === 'common') {
        updateData.common_questions = updatedCommonQuestions
      }
      await interviewAudioApi.update(selectedInterviewForResponses.id, updateData)

      // Update local state
      const updatedInterview = {
        ...selectedInterviewForResponses,
        questions: updatedQuestions,
        common_questions: updatedCommonQuestions
      }
      setSelectedInterviewForResponses(updatedInterview)
      setInterviewAudios(prev =>
        prev.map(a => (a.id === updatedInterview.id ? updatedInterview : a))
      )

      setEditingPath(null)
      setIndividualEditingValue(null)
      addToast({
        type: 'success',
        title: t('common.success', 'Success'),
        message: t('offerDetail.questionUpdatedSuccess', 'Question mise à jour avec succès')
      })
    } catch (error: any) {
      console.error('Error saving individual question:', error)
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('offerDetail.questionUpdateFailed', 'Échec de la mise à jour de la question')
      })
    } finally {
      setIsSavingQuestions(false)
    }
  }

  const handleUpdateCommonQuestionText = (index: number, newText: string) => {
    setEditingCommonQuestions((prev: any[]) => {
      if (!prev) return prev
      const next = [...prev]
      if (next[index]) {
        if (typeof next[index] === 'string') {
          next[index] = newText
        } else {
          next[index] = { ...next[index], question: newText }
        }
      }
      return next
    })
  }


  const handleRemoveQuestion = (section: string, index: number, lang?: 'fr' | 'en') => {
    if (!selectedInterviewForResponses) return

    if (isEditingQuestions) {
      // In edit mode, perform immediate removal from local draft state
      const remove = (qs: any) => {
        const next = deepClone(qs)
        if (lang) {
          if (next[lang] && next[lang][section]) {
            next[lang][section].splice(index, 1)
            if (next[lang][section].length === 0) {
              delete next[lang][section]
            }
          }
        } else {
          // If no lang provided, check standard structure
          if (next[section]) {
            next[section].splice(index, 1)
            if (next[section].length === 0) {
              delete next[section]
            }
          } else {
            // Handle custom/dual structure: remove from both if present
            if (next['fr'] && next['fr'][section]) {
              next['fr'][section].splice(index, 1)
              if (next['fr'][section].length === 0) delete next['fr'][section]
            }
            if (next['en'] && next['en'][section]) {
              next['en'][section].splice(index, 1)
              if (next['en'][section].length === 0) delete next['en'][section]
            }
          }
        }
        return next
      }
      setEditingQuestions(prev => remove(prev))
    } else {
      // In view mode, show confirmation modal
      setDeleteQuestionTarget({ type: 'standard', section, index, lang })
      setShowDeleteQuestionModal(true)
    }
  }

  const handleRemoveCommonQuestion = (index: number) => {
    if (!selectedInterviewForResponses) return

    if (isEditingQuestions) {
      // In edit mode, perform immediate removal from local draft state
      const remove = (cqs: any[]) => {
        const next = [...cqs]
        next.splice(index, 1)
        return next
      }
      setEditingCommonQuestions(prev => remove(prev || []))
    } else {
      // In view mode, show confirmation modal
      setDeleteQuestionTarget({ type: 'common', index })
      setShowDeleteQuestionModal(true)
    }
  }

  const confirmDelete = async () => {
    if (!selectedInterviewForResponses || !deleteQuestionTarget) return

    try {
      setIsSavingQuestions(true)
      const { type, section, index, lang } = deleteQuestionTarget

      if (type === 'standard') {
        const remove = (qs: any) => {
          const next = deepClone(qs)
          if (lang) {
            if (next[lang] && next[lang][section]) {
              next[lang][section].splice(index, 1)
              if (next[lang][section].length === 0) {
                delete next[lang][section]
              }
            }
          } else {
            // If no lang provided, check standard structure
            if (next[section]) {
              next[section].splice(index, 1)
              if (next[section].length === 0) {
                delete next[section]
              }
            } else {
              // Handle custom/dual structure: remove from both if present
              if (next['fr'] && next['fr'][section]) {
                next['fr'][section].splice(index, 1)
                if (next['fr'][section].length === 0) delete next['fr'][section]
              }
              if (next['en'] && next['en'][section]) {
                next['en'][section].splice(index, 1)
                if (next['en'][section].length === 0) delete next['en'][section]
              }
            }
          }
          return next
        }

        const updatedQuestions = remove(selectedInterviewForResponses.questions)
        await interviewAudioApi.update(selectedInterviewForResponses.id, { questions: updatedQuestions })

        // Update local state
        const updatedInterview = { ...selectedInterviewForResponses, questions: updatedQuestions }
        setSelectedInterviewForResponses(updatedInterview)
        setInterviewAudios(prev => prev.map(a => (a.id === updatedInterview.id ? updatedInterview : a)))
      } else {
        const remove = (cqs: any[]) => {
          const next = [...cqs]
          next.splice(index, 1)
          return next
        }

        const updatedCqs = remove(selectedInterviewForResponses.common_questions || [])
        await interviewAudioApi.update(selectedInterviewForResponses.id, { common_questions: updatedCqs })

        // Update local state
        const updatedInterview = { ...selectedInterviewForResponses, common_questions: updatedCqs }
        setSelectedInterviewForResponses(updatedInterview)
        setInterviewAudios(prev => prev.map(a => (a.id === updatedInterview.id ? updatedInterview : a)))
      }

      addToast({
        type: 'success',
        title: t('common.success', 'Success'),
        message: t('offerDetail.questionDeletedSuccess', 'Question supprimée avec succès')
      })
    } catch (error) {
      console.error('Error deleting question:', error)
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('offerDetail.questionDeleteFailed', 'Échec de la suppression de la question')
      })
    } finally {
      setIsSavingQuestions(false)
      setShowDeleteQuestionModal(false)
      setDeleteQuestionTarget(null)
    }
  }

  const handleSaveEditedQuestions = async () => {
    if (!selectedInterviewForResponses || !editingQuestions) return
    try {
      const containsEmptyQuestion = (value: any): boolean => {
        if (Array.isArray(value)) return value.some(containsEmptyQuestion)
        if (!value || typeof value !== 'object') return false
        if ('question' in value && !String(value.question || '').trim()) return true
        return Object.values(value).some(containsEmptyQuestion)
      }
      const hasEmptyQuestion = containsEmptyQuestion(editingQuestions)
      if (hasEmptyQuestion) {
        addToast({
          type: 'error',
          title: t('common.error', 'Erreur'),
          message: t('offerDetail.questionTextRequired', 'Le texte de la question est obligatoire')
        })
        return
      }
      setIsSavingQuestions(true)
      const updateData: any = {
        questions: editingQuestions
      }
      const hasEmbeddedCommonQuestions = Boolean(
        editingQuestions &&
        typeof editingQuestions === 'object' &&
        Array.isArray(editingQuestions.questions_communes)
      )
      if (editingCommonQuestions !== null && !hasEmbeddedCommonQuestions) {
        updateData.common_questions = editingCommonQuestions
      }

      await interviewAudioApi.update(selectedInterviewForResponses.id, updateData)

      // Update local state
      const updatedInterview = {
        ...selectedInterviewForResponses,
        questions: editingQuestions,
        common_questions: editingCommonQuestions || selectedInterviewForResponses.common_questions
      }
      setSelectedInterviewForResponses(updatedInterview)
      setInterviewAudios(prev =>
        prev.map(a => (a.id === updatedInterview.id ? updatedInterview : a))
      )

      setIsEditingQuestions(false)
      setEditingQuestions(null)
      setEditingCommonQuestions(null)
      addToast({
        type: 'success',
        title: t('common.success', 'Success'),
        message: t('offerDetail.questionsUpdatedSuccess', 'Questions mises à jour avec succès')
      })
    } catch (error: any) {
      console.error('Error updating questions:', error)
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('offerDetail.questionsUpdateFailed', 'Échec de la mise à jour des questions')
      })
    } finally {
      setIsSavingQuestions(false)
    }
  }

  const handleSaveCommonQuestions = async () => {
    if (!offer) return
    try {
      setIsSavingCommonQuestions(true)
      // Questions Communes always belong to the questions_communes section.
      const questionsForCommonSection = commonQuestions.map(question => ({
        ...question,
        section: 'questions_communes' as InterviewQuestionSection,
      }))
      const response = await offersApi.update(offer.id, {
        common_questions: questionsForCommonSection
      })
      if (response.data) {
        setOffer(response.data)
        setCommonQuestions(normalizeCommonQuestions(response.data.common_questions))
      }
      addToast({
        type: 'success',
        title: t('common.success', 'Success'),
        message: t('offerDetail.commonQuestionsSaved', 'Questions communes enregistrées avec succès')
      })
    } catch (error) {
      console.error('Error saving common questions:', error)
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('offerDetail.commonQuestionsSaveError', 'Erreur lors de l\'enregistrement des questions communes')
      })
    } finally {
      setIsSavingCommonQuestions(false)
    }
  }

  const getInterviewLanguageLabel = (lang: 'fr' | 'en' | 'custom') => {
    if (lang === 'en') return t('common.english', 'Anglais')
    if (lang === 'custom') return t('common.custom', 'Personnalisé (Choix candidat)')
    return t('common.french', 'Français')
  }

  const addManualInterviewQuestion = () => {
    if (commonQuestionsLimitReached) return
    setCommonQuestions(prev => [
      ...prev,
      {
        question: '',
        duration: 120,
        description: '',
        // Questions Communes stay in questions_communes; manual create picks an evaluation section.
        section: isCreatingManualQuestions ? DEFAULT_MANUAL_QUESTION_SECTION : 'questions_communes',
      },
    ])
  }

  const handleAddManualInterviewQuestionClick = () => {
    if (!canAddManualQuestions) return

    // First question in the create-manual flow: confirm language before locking it.
    if (isCreatingManualQuestions && commonQuestions.length === 0) {
      if (!manualInterviewLanguage) {
        addToast({
          type: 'error',
          title: t('common.error', 'Error'),
          message: t('offerDetail.interviewLanguageRequired', 'Veuillez sélectionner une langue pour l\'entretien.'),
        })
        return
      }
      setShowConfirmLanguageModal(true)
      return
    }

    addManualInterviewQuestion()
  }

  const handleConfirmLanguageAndAddQuestion = () => {
    setShowConfirmLanguageModal(false)
    addManualInterviewQuestion()
  }

  const handleCreateManualQuestions = async () => {
    if (!guardOfferOpenForMutations(
      'offerDetail.cannotUseValidationInterviewsClosed',
      'Validation interview actions are disabled while this offer is closed.'
    )) return
    if (!offer) return
    if (!requireSavedInterviewDeadline()) return
    if (offer.manual_interview_questions_created) {
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t(
          'offerDetail.manualQuestionsAlreadyCreated',
          'Les questions manuelles ont déjà été créées pour cette offre. L\'ajout n\'est plus possible.'
        ),
      })
      return
    }
    if (hasAiGeneratedInterviewQuestions) {
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t(
          'offerDetail.manualQuestionsDisabledAfterAiGeneration',
          'Des questions ont déjà été générées via la génération des entretiens. La création manuelle n\'est plus possible.'
        ),
      })
      return
    }
    if (!manualInterviewLanguage) {
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('offerDetail.interviewLanguageRequired', 'Veuillez sélectionner une langue pour l\'entretien.'),
      })
      return
    }
    if (commonQuestions.length > MAX_MANUAL_INTERVIEW_QUESTIONS) {
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('offerDetail.totalQuestionsExceeded', 'Le nombre total de questions ne peut pas dépasser 15.'),
      })
      return
    }
    try {
      setIsSavingCommonQuestions(true)
      const response = await interviewAudioApi.createManualQuestions(
        offer.id,
        commonQuestions,
        manualInterviewLanguage,
      )
      const savedLanguage = (response.data.language || manualInterviewLanguage) as 'fr' | 'en' | 'custom'
      setInterviewLanguage(savedLanguage)
      setManualInterviewLanguage(savedLanguage)
      setOffer(prev => prev ? {
        ...prev,
        common_questions: response.data.common_questions,
        manual_interview_questions_created: true,
        auto_generate_interviews: false,
        interview_language: savedLanguage,
      } : null)
      setCommonQuestions(response.data.common_questions)
      setAutoGenerateInterviews(false)
      setShowCommonQuestionsModal(false)
      setIsCreatingManualQuestions(false)
      addToast({
        type: 'success',
        title: t('common.success', 'Success'),
        message: t('offerDetail.manualQuestionsCreated', 'Questions manuelles créées et entretiens générés avec succès'),
      })
      refreshInterviewAudios()
    } catch (error: any) {
      console.error('Error creating manual questions:', error)
      const apiError = error?.response?.data?.error as string | undefined
      const alreadyCreated = typeof apiError === 'string' && apiError.toLowerCase().includes('already been created')
      if (alreadyCreated) {
        setOffer(prev => prev ? { ...prev, manual_interview_questions_created: true } : null)
      }
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: alreadyCreated
          ? t(
              'offerDetail.manualQuestionsAlreadyCreated',
              'Les questions manuelles ont déjà été créées pour cette offre. L\'ajout n\'est plus possible.'
            )
          : apiError || t('offerDetail.manualQuestionsCreateError', 'Impossible de créer les questions manuelles'),
      })
    } finally {
      setIsSavingCommonQuestions(false)
    }
  }

  const handleSaveSectionWeights = async () => {
    if (!offer || !id) return
    setIsSavingSectionWeights(true)
    try {
      const response = await offersApi.update(Number(id), {
        section_weights: editingSectionWeights
      })
      setOffer(response.data)
      // Recalculate and update the interview scores in the UI
      await refreshInterviewAudios()
      setShowSectionWeightsModal(false)
      addToast({
        type: 'success',
        title: t('common.success'),
        message: t('offerDetail.sectionWeightsSaved', 'Poids des sections enregistrés avec succès')
      })
    } catch (error: any) {
      console.error('Error saving section weights:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('offerDetail.sectionWeightsSaveError', 'Erreur lors de l\'enregistrement des poids')
      })
    } finally {
      setIsSavingSectionWeights(false)
    }
  }

  const handleSaveInterviewDeadline = async (nextDeadline: string) => {
    if (!guardOfferOpenForMutations(
      'offerDetail.cannotUseValidationInterviewsClosed',
      'Validation interview actions are disabled while this offer is closed.'
    )) return
    if (!offer) return
    if (nextDeadline === formatDateForInput(offer.interview_deadline)) return
    if (isValidationInterviewDeadlinePassed) {
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t(
          'offerDetail.interviewDeadlineExpiredLocked',
          'La date limite des entretiens est expirée et ne peut plus être modifiée.'
        )
      })
      return
    }
    const today = formatDraftDate(new Date())
    if (nextDeadline && nextDeadline < today) {
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t(
          'offerDetail.interviewDeadlineNotBeforeToday',
          'La date limite des entretiens ne peut pas être antérieure à aujourd\'hui.'
        )
      })
      return
    }
    const seq = ++interviewDeadlineSaveSeq.current
    try {
      const response = await offersApi.update(offer.id, {
        interview_deadline: nextDeadline || null
      })
      if (seq !== interviewDeadlineSaveSeq.current) return
      setOffer(response.data)
      void refreshInterviewAudios({ silent: true })
      addToast({
        type: 'success',
        title: t('common.success', 'Success'),
        message: t('offerDetail.interviewDeadlineSaved', 'Date limite des entretiens enregistrée')
      })
    } catch (error: any) {
      if (seq !== interviewDeadlineSaveSeq.current) return
      console.error('Error saving interview deadline:', error)
      setValidationInterviewDeadline(formatDateForInput(offer.interview_deadline))
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('offerDetail.interviewDeadlineSaveError', 'Erreur lors de l\'enregistrement de la date limite des entretiens')
      })
    }
  }

  // Function to update minimum score via API
  const updateMinimumScore = async (newScore: number): Promise<void> => {
    if (!offer) return

    const previousScore = minimumScore

    setMinimumScore(newScore)

    try {
      const payload: Partial<Offer> = {
        minimum_score: newScore
      }
      const response = await offersApi.update(offer.id, payload)
      setOffer(response.data)

      // Wait a bit for backend to update application statuses, then refresh
      await new Promise(resolve => setTimeout(resolve, 500))
      await refreshApplications()
    } catch (error: any) {
      console.error('Error updating matching config:', error)
      setMinimumScore(previousScore)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('offerDetail.minimumScoreUpdateFailed', 'Failed to update matching configuration')
      })
    }
  }

  const updateMinimumInterviewScore = async (newScore: number): Promise<void> => {
    if (!offer) return

    const previousScore = minimumInterviewScore

    if (newScore > previousScore) {
      addToast({
        type: 'error',
        title: t('kanban.scoreLimitError', 'Score Limit'),
        message: t('kanban.cannotIncreaseScore', {
          score: previousScore,
          defaultValue: `Minimum score cannot exceed ${previousScore}%`,
        }),
        duration: 4000,
      })
      return
    }

    setMinimumInterviewScore(newScore)

    try {
      const payload: Partial<Offer> = {
        minimum_interview_score: newScore
      }
      const response = await offersApi.update(offer.id, payload)
      setOffer((prev) => ({
        ...(prev ?? response.data),
        ...response.data,
        minimum_interview_score: response.data.minimum_interview_score ?? newScore,
      }))
      await new Promise(resolve => setTimeout(resolve, 500))
      await refreshApplications()
    } catch (error: any) {
      console.error('Error updating interview score threshold:', error)
      setMinimumInterviewScore(previousScore)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('offerDetail.minimumInterviewScoreUpdateFailed', 'Failed to update interview score threshold')
      })
    }
  }

  const baseSectionDrafts = useMemo(() => buildSectionDraftsFromOffer(offer), [offer])
  const [editingSections, setEditingSections] = useState<Record<EditableSection, boolean>>(() => createEditingState())
  const [sectionDrafts, setSectionDrafts] = useState<Partial<SectionDrafts>>({})
  const [savingSection, setSavingSection] = useState<EditableSection | null>(null)
  const [sectionErrors, setSectionErrors] = useState<Partial<Record<EditableSection, SectionErrorMessage | null>>>({})
  const [hoveredWeightKey, setHoveredWeightKey] = useState<string | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null)
  const [showStatusChangeConfirm, setShowStatusChangeConfirm] = useState(false)
  const [showZeroWeightsWarning, setShowZeroWeightsWarning] = useState(false)
  const [pendingStatusChange, setPendingStatusChange] = useState<{ newStatus: Offer['status']; section: EditableSection } | null>(null)
  const [showRecommendedSkillsModal, setShowRecommendedSkillsModal] = useState(false)
  const [recommendedSkillsData, setRecommendedSkillsData] = useState<RecommendedSkillsData>({})
  // Common questions only apply to AI-generated interviews, so they share the same gate.
  const canLaunchBatchGeneration =
    !isOfferClosed &&
    !isGeneratingBatch &&
    hasPendingValidationInterviews &&
    !offer?.manual_interview_questions_created

  const canCreateManualQuestions =
    !isOfferClosed && !isGeneratingBatch && !hasAiGeneratedInterviewQuestions

  const guardOfferOpenForMutations = useCallback((messageKey?: string, defaultMessage?: string) => {
    if (!isOfferClosed) return true
    addToast({
      type: 'warning',
      title: t('offerDetail.closedOffer', 'Offer closed'),
      message: t(
        messageKey || 'offerDetail.closedOfferActionsDisabled',
        defaultMessage || 'This offer is closed. Matching, candidate moves, and validation interviews are disabled.'
      ),
    })
    return false
  }, [addToast, isOfferClosed, t])

  const [aiEditOrder, setAiEditOrder] = useState<AiMatchingKey[] | null>(null)

  const canMassImport = hasPermission('upload_cvs')

  const menuItems: Array<{
    id: TabOption
    label: string
    icon: typeof Users
    description: string
  }> = [
      {
        id: 'applications',
        label: t('offerDetail.applications'),
        icon: Users,
        description: t('offerDetail.manageApplications')
      },
      {
        id: 'details',
        label: t('offerDetail.details'),
        icon: FileText,
        description: t('offerDetail.viewEditOffer')
      },
      {
        id: 'analytics',
        label: t('aiMatching.matchingDetails'),
        icon: BarChart3,
        description: t(
          'offerDetail.aiMatchingSummary',
          'Vue détaillée du matching IA '
        )
      },
      {
        id: 'validation_interviews',
        label: t('offerDetail.validationInterviews', 'Interviews de validation'),
        icon: Brain,
        description: t('offerDetail.validationInterviewsDesc', 'Générer et suivre les entretiens vidéo')
      },
      {
        id: 'calendar',
        label: t('offerDetail.calendarTab', 'Calendar'),
        icon: Calendar,
        description: t('offerDetail.calendarDescription', 'View and manage interview schedule')
      }
    ];


  useEffect(() => {
    const fetchData = async () => {
      if (!id || isNaN(parseInt(id))) {
        navigate('/offers')
        return
      }

      try {
        const offerId = parseInt(id)

        // Initial load: Only the offer summary
        const offerResponse = await offersApi.getById(offerId, { summary: true })

        if (offerResponse?.data) {
          const offerData = offerResponse.data
          setOffer(offerData)
          setLoadedSections(prev => new Set(prev).add('summary'))

          // If we had common_questions in summary (though we might not), set them
          if (offerData.common_questions) {
            setCommonQuestions(normalizeCommonQuestions(offerData.common_questions))
          }
        } else {
          setOffer(null)
          addToast({
            type: 'error',
            message: t('offerDetail.offerNotFound')
          })
          return
        }

        // Initialize applications to empty while loading
        setApplicationsByColumn({})
        // Start loading applications in background
        loadApplicationsData(true)
      } catch (error: any) {
        console.error('Error fetching offer data:', error)
        setOffer(null)
        // If offer not found (404), show specific message
        if (error?.response?.status === 404 || error?.status === 404) {
          addToast({
            type: 'error',
            message: t('offerDetail.offerNotFound')
          })
        } else {
          // For other errors, show error message
          addToast({
            type: 'error',
            message: t('offerDetail.errorLoadingOffer', 'Error loading offer. Please try again.')
          })
        }
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [id, navigate, addToast])

  // Function to load the full offer details (for Details and AI Matching tabs)
  const loadFullOfferDetails = async () => {
    if (!id || loadedSections.has('full_offer') || loadingFullOffer) return

    try {
      setLoadingFullOffer(true)
      const offerId = parseInt(id)
      const response = await offersApi.getById(offerId) // Full offer (no summary param)

      if (response?.data) {
        const fullOfferData = response.data

        // Handle weight normalization if needed
        if (fullOfferData.ai_matching_weights) {
          const weights = fullOfferData.ai_matching_weights
          const maxWeight = Math.max(...Object.values(weights).filter(v => typeof v === 'number') as number[])

          if (maxWeight > 10) {
            const normalizedWeights = Object.entries(weights).reduce((acc, [key, value]) => {
              acc[key as string] = typeof value === 'number' ? Math.round(value / 10) : value
              return acc
            }, {} as Record<string, any>)

            try {
              await offersApi.update(fullOfferData.id, {
                ...fullOfferData,
                ai_matching_weights: normalizedWeights
              })
              fullOfferData.ai_matching_weights = normalizedWeights
            } catch (err) {
              console.error('Error auto-normalizing weights:', err)
            }
          }
        }

        setOffer(fullOfferData)
        setCommonQuestions(normalizeCommonQuestions(fullOfferData.common_questions))
        setLoadedSections(prev => new Set(prev).add('full_offer'))
      }
    } catch (error) {
      console.error('Error loading full offer details:', error)
    } finally {
      setLoadingFullOffer(false)
    }
  }

  // Function to load applications for a specific stage or all stages
  const loadApplicationsData = useCallback(async (force: boolean = false, columnId?: string, stepId?: number, status?: string, requestedPage?: number) => {
    if (!id || (loadingApplications && !force && !columnId)) return
    const offerId = parseInt(id)

    // Helper to load a single column/stage
    const loadStage = async (pColumnId: string, pStepId?: number, pStatus?: string, pPage: number = 1) => {
      try {
        if (columnId === pColumnId) setLoadingApplications(true)
        else setStagePagination(prev => ({
          ...prev,
          [pColumnId]: { ...prev[pColumnId] || { page: 1, hasMore: false }, loadingMore: true }
        }))

        const params: any = {
          summary: true,
          page: pPage,
          page_size: 20,
          ordering: 'match_score'
        }
        if (pStepId) params.current_step = pStepId
        if (pStatus) params.status = pStatus

        const response = await applicationsApi.getByOffer(offerId, params)

        if (response?.data) {
          const newApps = response.data.applications
          const hasMore = response.data.has_more
          const totalCount = response.data.total_count ?? response.data.count

          setApplicationsByColumn(prev => ({
            ...prev,
            [pColumnId]: newApps
          }))

          setStagePagination(prev => ({
            ...prev,
            [pColumnId]: { page: pPage, hasMore, loadingMore: false }
          }))

          if (totalCount !== undefined) {
            setStageTotalCounts(prev => ({
              ...prev,
              [pColumnId]: totalCount
            }))
          }
        }
      } catch (error) {
        console.error(`Error loading applications for stage ${pColumnId}:`, error)
      } finally {
        if (columnId === pColumnId) setLoadingApplications(false)
        else setStagePagination(prev => ({
          ...prev,
          [pColumnId]: { ...prev[pColumnId], loadingMore: false }
        }))
      }
    }

    try {
      if (!columnId) setLoadingApplications(true)

      // Load steps first if not loaded
      let steps = hiringSteps
      if (!loadedSections.has('applications') || force || steps.length === 0) {
        const stepsResponse = await hiringProcessApi.getStepsForOffer(offerId)
        steps = Array.isArray(stepsResponse.data) ? stepsResponse.data : (stepsResponse.data?.results ?? [])
        if (steps.length > 0) {
          setOfferStepsForPosition(steps)
          setHiringSteps(steps)
        }
        setLoadedSections(prev => new Set(prev).add('applications'))
      }

      if (columnId) {
        // Load for a specific column
        const targetPage = requestedPage || (stagePagination[columnId]?.page || 0) + 1
        await loadStage(columnId, stepId, status, targetPage)
      } else {
        // Initial load for all active steps
        const activeSteps = steps.filter(s => s.is_active)
        await Promise.all(activeSteps.map(step => {
          const cId = `step-${step.id}`
          const cStatus = (function (stepType: string): string {
            const reverseMapping: Record<string, string> = {
              'initial': 'applied',
              'scanning': 'scanning',
              'screening': 'screening',
              'interview': 'interview',
              'hr_interview': 'hr_interview',
              'assessment': 'interview',
              'reference_check': 'interview',
              'offer': 'offer',
              'hired': 'hired',
              'rejected': 'rejected',
              'onboarding': 'hired',
              'withdrawn': 'rejected',
              'custom': 'interview',
            }
            return reverseMapping[stepType] || stepType
          })(step.step_type)
          return loadStage(cId, step.id, cStatus, 1)
        }))
      }
    } catch (error) {
      console.error('Error loading applications data:', error)
    } finally {
      if (!columnId) setLoadingApplications(false)
    }
  }, [id, loadedSections, loadingApplications, hiringSteps, stagePagination, applicationsByColumn])

  reloadKanbanRef.current = () => {
    loadApplicationsData(true)
  }

  const loadMoreForColumn = useCallback((columnId: string, stepId?: number, status?: string) => {
    loadApplicationsData(false, columnId, stepId, status)
  }, [loadApplicationsData])

  const handlePageChange = useCallback((columnId: string, page: number, stepId?: number, status?: string) => {
    loadApplicationsData(false, columnId, stepId, status, page)
  }, [loadApplicationsData])


  useEffect(() => {
    if (!offer) return
    loadApplicationsData()
  }, [offer?.id, id])

  useEffect(() => {
    if (!offer) return
    if (openPanel === 'details' || openPanel === 'matching') {
      loadFullOfferDetails()
    }
  }, [openPanel, offer?.id, id])

  // Remove automatic successive loading
  // It is now handled by the infinite scroll in each column via loadMoreForColumn


  useEffect(() => {
    setActiveTab(getInitialTab())
  }, [getInitialTab])

  // Load saved translations from database if they exist
  useEffect(() => {
    if (!offer || i18n.language !== 'en') {
      return
    }

    const translation_en = (offer as any).translation_en
    if (isOfferTranslationComplete(translation_en, offer)) {
      // Load complete translations from database
      console.log('Loading saved translations from database for offer')
      setTranslatedJobTitle(translation_en.job_title || '')
      setTranslatedDescription(translation_en.description || '')
      setTranslatedHardSkills(translation_en.hard_skills || [])
      setTranslatedHardSkillsNonMentionnees(translation_en.hard_skills_non_mentionnées || [])
      setTranslatedSoftSkills(translation_en.soft_skills || [])
      setTranslatedSoftSkillsNonMentionnees(translation_en.soft_skills_non_mentionnées || [])
      setTranslatedCertifications(translation_en.certifications || [])
      setTranslatedBenefits(translation_en.benefits || [])
      setTranslatedBenefitsNonMentionnees(translation_en.benefits_non_mentionnées || [])
      setTranslatedLanguages(translation_en.languages || [])
      setTranslatedMainMissions(translation_en.main_missions || [])
      translationPipelineCompleteRef.current = false
      // Skip translation process, go directly to idle
      setTranslationStage('idle')
    } else if ((offer.job_title || '').trim()) {
      // Stale or missing translations — re-translate from current offer data
      translationPipelineCompleteRef.current = false
      setTranslationStage('job_title')
    }
    // If full offer fields are needed for other translations, wait for full_offer load
  }, [offer?.id, offer?.job_title, i18n.language, loadedSections])

  // Save all translations to database when the full translation pipeline completes
  useEffect(() => {
    if (translationStage !== 'idle' || i18n.language !== 'en' || !translationPipelineCompleteRef.current) {
      return
    }

    if (
      !offer ||
      !loadedSections.has('full_offer') ||
      (!translatedJobTitle && !translatedDescription && translatedHardSkills.length === 0 && translatedSoftSkills.length === 0 &&
        translatedCertifications.length === 0 && translatedBenefits.length === 0 &&
        translatedLanguages.length === 0 && translatedMainMissions.length === 0)
    ) {
      return
    }

    const saveTranslations = async () => {
      try {
        const translationData = {
          translation_en: {
            job_title: translatedJobTitle,
            source_job_title: offer.job_title,
            description: translatedDescription,
            source_description: offer.description_text || offer.description || '',
            hard_skills: translatedHardSkills,
            hard_skills_non_mentionnées: translatedHardSkillsNonMentionnees,
            soft_skills: translatedSoftSkills,
            soft_skills_non_mentionnées: translatedSoftSkillsNonMentionnees,
            certifications: translatedCertifications,
            benefits: translatedBenefits,
            benefits_non_mentionnées: translatedBenefitsNonMentionnees,
            languages: translatedLanguages,
            main_missions: translatedMainMissions,
            timestamp: new Date().toISOString()
          }
        }

        await offersApi.update(offer.id, translationData)
        translationPipelineCompleteRef.current = false
        console.log('Translations saved to database successfully for offer')
      } catch (error) {
        console.error('Error saving translations to database for offer:', error)
      }
    }

    // Debounce save to avoid too frequent updates
    const timer = setTimeout(saveTranslations, 1000)
    return () => clearTimeout(timer)
  }, [translationStage, translatedJobTitle, translatedDescription, translatedHardSkills, translatedHardSkillsNonMentionnees,
    translatedSoftSkills, translatedSoftSkillsNonMentionnees, translatedCertifications, translatedBenefits,
    translatedBenefitsNonMentionnees, translatedLanguages, translatedMainMissions, offer, i18n.language, loadedSections])

  // Sequential translation: Start with job_title
  useEffect(() => {
    if (!offer || i18n.language !== 'en' || translationStage !== 'job_title') {
      if (!offer || i18n.language !== 'en') {
        translationPipelineCompleteRef.current = false
        setTranslationStage('idle')
        setTranslatedJobTitle(offer?.job_title || '')
        setTranslatedDescription(offer?.description_text || offer?.description || '')
        setTranslatedHardSkills([])
        setTranslatedHardSkillsNonMentionnees([])
        setTranslatedSoftSkills([])
        setTranslatedSoftSkillsNonMentionnees([])
        setTranslatedCertifications([])
        setTranslatedBenefits([])
        setTranslatedBenefitsNonMentionnees([])
        setTranslatedLanguages([])
        setTranslatedMainMissions([])
      }
      return
    }

    const translateJobTitle = async () => {
      try {
        if (offer.job_title) {
          setTranslatedJobTitle(await translateFrToEn(offer.job_title))
        } else {
          setTranslatedJobTitle('')
        }

        // After job_title is done, move to description
        setTranslationStage('description')
      } catch (error) {
        console.error('Error translating job_title:', error)
        setTranslatedJobTitle(offer.job_title || '')
        setTranslationStage('description')
      }
    }

    translateJobTitle()
  }, [offer, translationStage, i18n.language])

  // Step 2: Translate description after job_title is done
  useEffect(() => {
    if (!offer || i18n.language !== 'en' || translationStage !== 'description') {
      return
    }

    const description = offer.description_text || offer.description || ''
    if (!description || description.trim().length === 0) {
      setTranslatedDescription('')
      setTranslationStage('hard_skills')
      return
    }

    const translateDescription = async () => {
      try {
        setTranslatedDescription(await translateFrToEn(description))
        setTranslationStage('hard_skills')
      } catch (error) {
        console.error('Error translating description:', error)
        setTranslatedDescription(description)
        setTranslationStage('hard_skills')
      }
    }

    translateDescription()
  }, [offer, translationStage, i18n.language])

  // Step 3: Translate hard_skills after description is done
  useEffect(() => {
    if (!offer || i18n.language !== 'en' || translationStage !== 'hard_skills') {
      return
    }

    const hardSkills = hardSkillsMentionnees || []
    if (!Array.isArray(hardSkills) || hardSkills.length === 0) {
      setTranslatedHardSkills([])
      setTranslationStage('soft_skills')
      return
    }

    const translateHardSkillsProgressively = async () => {
      const translated: string[] = []
      for (const skill of hardSkills) {
        try {
          const translatedSkill = await translateFrToEn(skill)
          translated.push(translatedSkill)
          setTranslatedHardSkills([...translated])
        } catch (error) {
          console.error('Error translating hard skill:', error)
          translated.push(skill)
          setTranslatedHardSkills([...translated])
        }
      }
      setTranslationStage('soft_skills')
    }

    translateHardSkillsProgressively()
  }, [hardSkillsMentionnees, offer, translationStage, i18n.language])

  // Step 4: Translate soft_skills after hard_skills are done
  useEffect(() => {
    if (!offer || i18n.language !== 'en' || translationStage !== 'soft_skills') {
      return
    }

    const softSkills = softSkillsMentionnees || []
    if (!Array.isArray(softSkills) || softSkills.length === 0) {
      setTranslatedSoftSkills([])
      setTranslationStage('certifications')
      return
    }

    const translateSoftSkillsProgressively = async () => {
      const translated: string[] = []
      for (const skill of softSkills) {
        try {
          const translatedSkill = await translateFrToEn(skill)
          translated.push(translatedSkill)
          setTranslatedSoftSkills([...translated])
        } catch (error) {
          console.error('Error translating soft skill:', error)
          translated.push(skill)
          setTranslatedSoftSkills([...translated])
        }
      }
      setTranslationStage('certifications')
    }

    translateSoftSkillsProgressively()
  }, [offer, softSkillsMentionnees, translationStage, i18n.language])

  // Step 5: Translate certifications after soft_skills are done
  useEffect(() => {
    if (!offer || i18n.language !== 'en' || translationStage !== 'certifications') {
      return
    }

    const certifications = offer.certifications || []
    if (!Array.isArray(certifications) || certifications.length === 0) {
      setTranslatedCertifications([])
      setTranslationStage('benefits')
      return
    }

    const translateCertificationsProgressively = async () => {
      const translated: string[] = []
      for (const cert of certifications) {
        try {
          const translatedCert = await translateFrToEn(cert)
          translated.push(translatedCert)
          setTranslatedCertifications([...translated])
        } catch (error) {
          console.error('Error translating certification:', error)
          translated.push(cert)
          setTranslatedCertifications([...translated])
        }
      }
      setTranslationStage('benefits')
    }

    translateCertificationsProgressively()
  }, [offer, translationStage, i18n.language])

  // Step 6: Translate benefits after certifications are done
  useEffect(() => {
    if (!offer || i18n.language !== 'en' || translationStage !== 'benefits') {
      return
    }

    const benefits = benefitsMentionnees || []
    if (!Array.isArray(benefits) || benefits.length === 0) {
      setTranslatedBenefits([])
      setTranslationStage('languages')
      return
    }

    const translateBenefitsProgressively = async () => {
      const translated: string[] = []
      for (const item of benefits) {
        try {
          const translatedItem = await translateFrToEn(item)
          translated.push(translatedItem)
          setTranslatedBenefits([...translated])
        } catch (error) {
          console.error('Error translating benefit item:', error)
          translated.push(item)
          setTranslatedBenefits([...translated])
        }
      }
      setTranslationStage('languages')
    }

    translateBenefitsProgressively()
  }, [benefitsMentionnees, offer, translationStage, i18n.language])

  // Step 7: Translate languages after benefits are done
  useEffect(() => {
    if (!offer || i18n.language !== 'en' || translationStage !== 'languages') {
      return
    }

    const languages = offer.languages || []
    if (!Array.isArray(languages) || languages.length === 0) {
      setTranslatedLanguages([])
      setTranslationStage('main_missions')
      return
    }

    const translateLanguagesProgressively = async () => {
      const translated: Array<{ language: string; level?: string }> = []
      for (const lang of languages) {
        try {
          // Handle both string and object formats
          const language = typeof lang === 'string' ? lang : (lang.language || lang.name || '')
          const level = typeof lang === 'object' ? (lang.level || '') : ''
          const translatedLanguage = language ? await translateFrToEn(language) : ''
          translated.push({ language: translatedLanguage, level })
          setTranslatedLanguages([...translated])
        } catch (error) {
          console.error('Error translating language:', error)
          const language = typeof lang === 'string' ? lang : (lang.language || lang.name || '')
          const level = typeof lang === 'object' ? (lang.level || '') : ''
          translated.push({ language, level })
          setTranslatedLanguages([...translated])
        }
      }
      setTranslationStage('main_missions')
    }

    translateLanguagesProgressively()
  }, [offer, translationStage, i18n.language])

  // Step 8: Translate main_missions after languages are done
  useEffect(() => {
    if (!offer || i18n.language !== 'en' || translationStage !== 'main_missions') {
      return
    }

    const mainMissions = offer.main_missions || []
    if (!Array.isArray(mainMissions) || mainMissions.length === 0) {
      setTranslatedMainMissions([])
      setTranslationStage('hardSkillsNonMentionnees')
      return
    }

    const translateMissionsProgressively = async () => {
      const translated: string[] = []
      for (const mission of mainMissions) {
        try {
          const translatedMission = await translateFrToEn(mission)
          translated.push(translatedMission)
          setTranslatedMainMissions([...translated])
        } catch (error) {
          console.error('Error translating mission:', error)
          translated.push(mission)
          setTranslatedMainMissions([...translated])
        }
      }
      setTranslationStage('hardSkillsNonMentionnees')
    }

    translateMissionsProgressively()
  }, [offer, translationStage, i18n.language])

  // Step 9: Translate hard skills non_mentionnees (AI recommended) after main_missions
  useEffect(() => {
    if (!offer || i18n.language !== 'en' || translationStage !== 'hardSkillsNonMentionnees') {
      return
    }

    const hardSkillsNonMentionnees = (offer as any).requirements?.hard_skills_non_mentionnées || []
    if (!Array.isArray(hardSkillsNonMentionnees) || hardSkillsNonMentionnees.length === 0) {
      setTranslatedHardSkillsNonMentionnees([])
      setTranslationStage('softSkillsNonMentionnees')
      return
    }

    const translateSkillsProgressively = async () => {
      const translated: string[] = []
      for (const skill of hardSkillsNonMentionnees) {
        try {
          const translatedSkill = await translateFrToEn(skill)
          translated.push(translatedSkill)
          setTranslatedHardSkillsNonMentionnees([...translated])
        } catch (error) {
          console.error('Error translating hard skill:', error)
          translated.push(skill)
          setTranslatedHardSkillsNonMentionnees([...translated])
        }
      }
      setTranslationStage('softSkillsNonMentionnees')
    }

    translateSkillsProgressively()
  }, [offer, translationStage, i18n.language])

  // Step 10: Translate soft skills non_mentionnees (AI recommended) after hard skills
  useEffect(() => {
    if (!offer || i18n.language !== 'en' || translationStage !== 'softSkillsNonMentionnees') {
      return
    }

    const softSkillsNonMentionnees = (offer as any).soft_skills?.soft_skills_non_mentionnées || []
    if (!Array.isArray(softSkillsNonMentionnees) || softSkillsNonMentionnees.length === 0) {
      setTranslatedSoftSkillsNonMentionnees([])
      setTranslationStage('benefitsNonMentionnees')
      return
    }

    const translateSkillsProgressively = async () => {
      const translated: string[] = []
      for (const skill of softSkillsNonMentionnees) {
        try {
          const translatedSkill = await translateFrToEn(skill)
          translated.push(translatedSkill)
          setTranslatedSoftSkillsNonMentionnees([...translated])
        } catch (error) {
          console.error('Error translating soft skill:', error)
          translated.push(skill)
          setTranslatedSoftSkillsNonMentionnees([...translated])
        }
      }
      setTranslationStage('benefitsNonMentionnees')
    }

    translateSkillsProgressively()
  }, [offer, translationStage, i18n.language])

  // Step 11: Translate benefits non_mentionnees (AI recommended) after soft skills
  useEffect(() => {
    if (!offer || i18n.language !== 'en' || translationStage !== 'benefitsNonMentionnees') {
      return
    }

    const benefitsNonMentionnees = (offer as any).benefits?.nice_to_have_non_mentionnées || []
    if (!Array.isArray(benefitsNonMentionnees) || benefitsNonMentionnees.length === 0) {
      setTranslatedBenefitsNonMentionnees([])
      finishTranslationPipeline()
      return
    }

    const translateBenefitsProgressively = async () => {
      const translated: string[] = []
      for (const benefit of benefitsNonMentionnees) {
        try {
          const translatedBenefit = await translateFrToEn(benefit)
          translated.push(translatedBenefit)
          setTranslatedBenefitsNonMentionnees([...translated])
        } catch (error) {
          console.error('Error translating benefit:', error)
          translated.push(benefit)
          setTranslatedBenefitsNonMentionnees([...translated])
        }
      }
      finishTranslationPipeline()
    }

    translateBenefitsProgressively()
  }, [offer, translationStage, i18n.language, finishTranslationPipeline])

  // Load teams when component mounts or when applications change

  useEffect(() => {
    const loadTeams = async () => {
      try {
        // Load all pages — getAll() only returns PAGE_SIZE (20)
        const allTeams = await teamsApi.getAllPages()
        // Only keep active (non-archived) groups when linking to an offer
        const activeTeams = (allTeams || []).filter(team => !team.is_archived)

        // Fetch full team details with candidates for each active team
        const teamsWithCandidates = await Promise.all(
          activeTeams.map(async (team) => {
            try {
              const teamDetails = await teamsApi.getById(team.id)
              return {
                ...team,
                candidates: teamDetails.data.candidates || []
              }
            } catch (error) {
              console.error(`Error fetching team ${team.id} details:`, error)
              return {
                ...team,
                candidates: []
              }
            }
          })
        )

        setTeams(teamsWithCandidates)
      } catch (error) {
        console.error('Error fetching teams:', error)
      }
    }
    loadTeams()
  }, [id]) // Load teams when offer ID changes

  const fetchAvailableCandidates = async (page = 1, search = '', isSearch = false) => {
    try {
      if (page === 1) {
        if (isSearch) setIsSearchingCandidates(true);
        else setLoadingAddCandidatesData(true);
      } else {
        setLoadingMoreCandidates(true);
      }

      const response = await candidatesApi.getAll({
        page,
        search,
        exclude_offer_id: id,
        summary: 'true'
      });

      // Special case for page 1: load teams only on initial load, not on every search
      if (page === 1 && !isSearch) {
        try {
          // Load all pages — getAll() only returns PAGE_SIZE (20)
          const allTeams = await teamsApi.getAllPages();
          const activeTeams = (allTeams || []).filter((team: any) => !team.is_archived);
          setTeams(activeTeams);
        } catch (err) {
          console.error('Error fetching teams:', err);
        }
      }

      setAvailableCandidates(response.data.results || []);
      setTotalCandidates(response.data.count || 0);
      setCandidatePage(page);
      setHasMoreCandidates(!!response.data.next);
    } catch (error) {
      console.error('Error fetching candidates:', error);
    } finally {
      setLoadingAddCandidatesData(false);
      setLoadingMoreCandidates(false);
      setIsSearchingCandidates(false);
    }
  };

  const goToCandidatePage = (page: number) => {
    if (page >= 1 && !loadingMoreCandidates) {
      fetchAvailableCandidates(page, candidateSearch, true);
    }
  };

  const handleSearchCandidates = (query: string) => {
    setCandidateSearch(query);

    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for debouncing
    const timeout = setTimeout(() => {
      fetchAvailableCandidates(1, query, true);
    }, 500);

    setSearchTimeout(timeout);
  };

  const handleSelectAllGlobally = async () => {
    if (isLocallyFetchingAllIds) return;

    try {
      setIsLocallyFetchingAllIds(true);
      const response = await candidatesApi.getAllIds({
        search: candidateSearch,
        exclude_offer_id: id
      });

      const allIds = response.data;
      setSelectedCandidateIds(allIds);
    } catch (error) {
      console.error('Error fetching all candidate IDs:', error);
    } finally {
      setIsLocallyFetchingAllIds(false);
    }
  };

  const handleAddCandidate = async () => {
    if (!id) return

    // Check if multiple candidates are selected in candidate mode
    if (selectionMode === 'candidates' && selectedCandidateIds.length > 1) {
      setShowCreateGroupConfirm(true)
      return
    }

    // Proceed with adding candidates
    await addCandidatesToOffer()
  }

  const linkTeamsToOffer = useCallback(async (teamIds: number[]) => {
    if (!id || teamIds.length === 0) return
    const offerId = parseInt(id, 10)

    await Promise.all(
      teamIds.map(async (teamId) => {
        const targetTeam = teams.find(team => team.id === teamId)
        if (!targetTeam) return

        const existingOfferIds = targetTeam.offers?.map(offer => offer.id) || []
        if (existingOfferIds.includes(offerId)) return

        try {
          const response = await teamsApi.update(teamId, {
            offer_ids: [...existingOfferIds, offerId]
          })
          setTeams(prev =>
            prev.map(team => (team.id === teamId ? response.data : team))
          )
        } catch (error) {
          console.error(`Error linking offer ${offerId} to team ${teamId}:`, error)
        }
      })
    )
  }, [id, teams])

  const addCandidatesToOffer = async () => {
    if (!id) return

    setAddingCandidates(true)

    let candidateIdsToAdd: number[] = []
    let totalGroupCandidatesDiscovered = 0
    // Map candidate IDs to their source groups
    const candidateToGroupsMap: Map<number, string[]> = new Map()

    try {
      if (selectionMode === 'group') {
        if (selectedTeamIds.length === 0) {
          setAddingCandidates(false)
          return
        }

        // Get all candidates from selected teams
        const selectedTeams = teams.filter(team => selectedTeamIds.includes(team.id))
        for (const team of selectedTeams) {
          let teamCandidates: Candidate[] = []

          // Fetch team details to get candidates if not already loaded
          if (!team.candidates || team.candidates.length === 0) {
            try {
              const teamDetails = await teamsApi.getById(team.id)
              if (teamDetails.data.candidates) {
                teamCandidates = teamDetails.data.candidates
              }
            } catch (error) {
              console.error(`Error fetching team ${team.id} details:`, error)
              continue
            }
          } else {
            teamCandidates = team.candidates
          }

          // Add candidates from this team and track which group they're from
          teamCandidates.forEach(candidate => {
            const candidateId = candidate.id
            totalGroupCandidatesDiscovered++
            if (!applications.some(app => app.candidate === candidateId)) {
              candidateIdsToAdd.push(candidateId)
              // Track which groups this candidate belongs to
              const existingGroups = candidateToGroupsMap.get(candidateId) || []
              candidateToGroupsMap.set(candidateId, [...existingGroups, team.name])
            }
          })
        }
      } else {
        if (selectedCandidateIds.length === 0) {
          setAddingCandidates(false)
          return
        }
        candidateIdsToAdd = selectedCandidateIds
      }

      // Remove duplicates
      candidateIdsToAdd = [...new Set(candidateIdsToAdd)]

      if (candidateIdsToAdd.length === 0) {
        const noGroupCandidates = selectionMode === 'group' && totalGroupCandidatesDiscovered === 0
        setAddingCandidates(false)
        addToast({
          type: 'info',
          title: t('offerDetail.noCandidatesToAdd', 'No candidates to add'),
          message: noGroupCandidates
            ? t(
              'offerDetail.selectedGroupsHaveNoCandidates',
              'Selected groups do not include any candidates to add.'
            )
            : t(
              'offerDetail.allCandidatesAlreadyAdded',
              'All selected candidates have already been added to this offer.'
            )
        })
        return
      }

      // Create applications for all selected candidates using bulk creation for performance
      const notes = selectionMode === 'group'
        ? t('offerDetail.addedFromGroups', 'Added from groups')
        : undefined;

      await applicationsApi.bulkCreate({
        candidate_ids: candidateIdsToAdd,
        offer: parseInt(id),
        notes: notes || undefined
      });

      // Refresh the applications list from the API to get the latest data
      await refreshApplications()

      if (selectionMode === 'group') {
        await linkTeamsToOffer(selectedTeamIds)
      }

      // Show success message
      addToast({
        type: 'success',
        title: t('offerDetail.candidatesAdded', 'Candidates added'),
        message: t('offerDetail.candidatesAddedMessage', {
          count: candidateIdsToAdd.length,
          defaultValue: `${candidateIdsToAdd.length} candidate${candidateIdsToAdd.length > 1 ? 's' : ''} added successfully`
        })
      })

      setShowAddCandidateModal(false)
      setSelectedCandidateIds([])
      setSelectedTeamIds([])
      setTeamSearch('')
      setCandidateSearch('')

      // Refresh available candidates
      await fetchAvailableCandidates()
    } catch (error) {
      console.error('Error adding candidates:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('offerDetail.addCandidatesFailed', 'Failed to add candidates. Please try again.')
      })
    } finally {
      setAddingCandidates(false)
    }
  }

  const handleCreateGroupFromCandidates = async (groupData: { name: string; description?: string; color: string }) => {
    if (!id || selectedCandidateIds.length === 0) return

    try {
      setAddingCandidates(true)
      const offerId = parseInt(id, 10)

      // Create the group
      const teamResponse = await teamsApi.create({
        name: groupData.name,
        description: groupData.description || '',
        color: groupData.color,
        offer_ids: [offerId]
      })

      const newTeam = teamResponse.data

      // Add selected candidates to the group
      await teamsApi.update(newTeam.id, { candidate_ids: selectedCandidateIds })

      // Add candidates to the offer
      await Promise.all(
        selectedCandidateIds.map(async (candidateId) => {
          return applicationsApi.create({
            candidate: candidateId,
            offer: parseInt(id),
            status: 'applied',
            notes: `Added from groups: ${groupData.name}`
          })
        })
      )

      // Refresh teams and available candidates first to get updated team data
      await fetchAvailableCandidates()

      // Refresh the applications list to get the latest data with updated notes
      await refreshApplications()

      // Show success message
      addToast({
        type: 'success',
        title: t('offerDetail.groupCreatedAndCandidatesAdded', 'Group created and candidates added'),
        message: t('offerDetail.groupCreatedAndCandidatesAddedMessage', {
          groupName: groupData.name,
          count: selectedCandidateIds.length,
          defaultValue: `Group "${groupData.name}" created and ${selectedCandidateIds.length} candidate${selectedCandidateIds.length > 1 ? 's' : ''} added successfully`
        })
      })

      setShowCreateGroupModal(false)
      setShowCreateGroupConfirm(false)
      setShowAddCandidateModal(false)
      setSelectedCandidateIds([])
      setSelectedTeamIds([])
      setTeamSearch('')
      setCandidateSearch('')
    } catch (error: any) {
      console.error('Error creating group and adding candidates:', error)
      // Extract and translate error message from various response formats
      let errorMessage = t('offerDetail.createGroupFailed', 'Failed to create group. Please try again.')
      const data = error?.response?.data

      if (data) {
        if (typeof data === 'string') {
          errorMessage = translateServerError(data, t)
        } else if (data.error) {
          errorMessage = translateServerError(data.error, t)
        } else if (data.message) {
          errorMessage = translateServerError(data.message, t)
        } else if (data.details) {
          errorMessage = translateServerError(data.details, t)
        } else if (typeof data === 'object') {
          // Handle DRF validation errors
          const parts: string[] = []
          Object.entries(data).forEach(([field, value]) => {
            if (Array.isArray(value)) {
              const translatedErrors = value.map((v: string) => translateServerError(v, t))
              const fieldLabel = field === 'name' ? t('teams.teamName', 'Group name') : field
              parts.push(`${fieldLabel}: ${translatedErrors.join(', ')}`)
            } else if (typeof value === 'string') {
              const fieldLabel = field === 'name' ? t('teams.teamName', 'Group name') : field
              parts.push(`${fieldLabel}: ${translateServerError(value, t)}`)
            }
          })
          if (parts.length) {
            errorMessage = parts.join(' | ')
          }
        }
      }

      addToast({
        type: 'error',
        title: t('common.error'),
        message: errorMessage
      })
    } finally {
      setAddingCandidates(false)
    }
  }

  const handleSkipGroupCreation = () => {
    setShowCreateGroupConfirm(false)
    addCandidatesToOffer()
  }

  const handleEditOffer = () => {
    if (!offer) return
    navigate(`/offers/${offer.id}/edit`)
  }

  const getSectionDraft = useCallback(
    (section: EditableSection): SectionDrafts[EditableSection] => {
      return deepClone(baseSectionDrafts[section])
    },
    [baseSectionDrafts]
  )

  const startEditingSection = (section: EditableSection) => {
    // Double-check if section is locked before allowing edit mode
    if (isSectionLocked(section)) {
      addToast({
        type: 'warning',
        title: t('common.warning'),
        message: t('offerDetail.cannotEditAfterMatching', 'This field cannot be modified because matching has been applied to this offer.'),
        duration: 5000
      })
      return
    }

    const isArchived = offer?.status === 'closed'
    const canEditKeyDetailsOnly = offer?.status === 'published' || offer?.status === 'offer_closed'
    if (isArchived || (isOfferModificationLocked && !(canEditKeyDetailsOnly && section === 'key_details'))) {
      addToast({
        type: 'warning',
        title: t('common.warning'),
        message: isOfferClosed
          ? t('offerDetail.cannotEditClosed', 'This field cannot be modified because the offer is closed.')
          : t('offerDetail.cannotEditPublished', 'This field cannot be modified because the offer is activated.'),
        duration: 5000
      })
      return
    }

    // Close all other editing sections before opening the new one
    const currentlyEditingSections = Object.keys(editingSections).filter(
      key => editingSections[key as EditableSection] && key !== section
    ) as EditableSection[]

    // Cancel all other editing sections
    currentlyEditingSections.forEach(otherSection => {
      cancelEditingSection(otherSection)
    })

    const draft = getSectionDraft(section) as SectionDrafts[typeof section]
    setSectionDrafts(prev => ({
      ...prev,
      [section]: draft
    }))
    setEditingSections(prev => ({ ...prev, [section]: true }))
    setSectionErrors(prev => ({ ...prev, [section]: null }))
    if (section === 'ai_matching') {
      // Use predefined order instead of sorting by weight
      const weights =
        (draft as SectionDrafts['ai_matching']).ai_matching_weights ?? defaultAiMatchingWeights
      const availableKeys = Object.keys(weights) as AiMatchingKey[]
      const orderedKeys = [
        ...aiMatchingWeightsOrder.filter(key => availableKeys.includes(key)),
        ...availableKeys.filter(key => !aiMatchingWeightsOrder.includes(key))
      ]
      setAiEditOrder(orderedKeys)
    }
  }

  const cancelEditingSection = (section: EditableSection) => {
    setEditingSections(prev => ({ ...prev, [section]: false }))
    setSectionDrafts(prev => {
      const updated = { ...prev }
      delete updated[section]
      return updated
    })
    setSectionErrors(prev => ({ ...prev, [section]: null }))
    if (section === 'ai_matching') {
      setAiEditOrder(null)
    }
  }

  const sanitizeStringArray = (items?: string[]) =>
    (items || []).map(item => item.trim()).filter(item => item.length > 0)

  const buildSectionPayload = (section: EditableSection, draft: SectionDrafts[EditableSection]) => {
    switch (section) {
      case 'job_overview': {
        const data = draft as SectionDrafts['job_overview']
        const experienceLevelValue = data.experience_level === '' || data.experience_level == null
          ? null
          : String(data.experience_level)
        const educationLevelValue = data.education_level?.trim?.() || null

        const payload: any = {
          job_title: data.job_title.trim(),
          department: data.department?.trim?.() || null,
          contract_type: data.contract_type?.trim?.() || null,
          experience_level: experienceLevelValue,
          education_level: educationLevelValue,
          remote_allowed: data.remote_allowed
        }

        // If education_level or experience_level become empty, set their weights to 0
        if (offer?.ai_matching_weights) {
          const updatedWeights: any = { ...offer.ai_matching_weights }
          let weightsChanged = false

          if (!educationLevelValue || educationLevelValue.trim() === '') {
            updatedWeights.niveau_etude = 0
            weightsChanged = true
          }

          if (!experienceLevelValue || experienceLevelValue.trim() === '') {
            updatedWeights.experience_years = 0
            weightsChanged = true
          }

          if (weightsChanged) {
            payload.ai_matching_weights = updatedWeights
          }
        }

        return payload
      }
      case 'description': {
        const data = draft as SectionDrafts['description']
        return { description_text: data.description_text || data.description }
      }
      case 'requirements': {
        const data = draft as SectionDrafts['requirements']
        const cleanedMentionnees = sanitizeStringArray(data.requirements_mentionnees)
        const cleanedNonMentionnees = sanitizeStringArray(data.requirements_non_mentionnees)

        const payload: any = {
          requirements: {
            hard_skills_mentionnées: cleanedMentionnees,
            hard_skills_non_mentionnées: cleanedNonMentionnees
          }
        }

        // If hard_skills_mentionnees become empty, set hard_skills weight to 0
        if (cleanedMentionnees.length === 0 && offer?.ai_matching_weights) {
          payload.ai_matching_weights = {
            ...offer.ai_matching_weights,
            hard_skills: 0
          }
        }

        return payload
      }
      case 'main_missions': {
        const data = draft as SectionDrafts['main_missions']
        const cleanedMainMissions = sanitizeStringArray(data.main_missions)
        const payload: any = { main_missions: cleanedMainMissions }

        // If main_missions become empty, set main_missions weight to 0
        if (cleanedMainMissions.length === 0 && offer?.ai_matching_weights) {
          payload.ai_matching_weights = {
            ...offer.ai_matching_weights,
            main_missions: 0
          }
        }

        return payload
      }
      case 'soft_skills': {
        const data = draft as SectionDrafts['soft_skills']
        const cleanedMentionnees = sanitizeStringArray(data.soft_skills_mentionnees)
        const cleanedNonMentionnees = sanitizeStringArray(data.soft_skills_non_mentionnees)

        const payload: any = {
          soft_skills: {
            soft_skills_mentionnées: cleanedMentionnees,
            soft_skills_non_mentionnées: cleanedNonMentionnees
          }
        }

        // If soft_skills_mentionnees become empty, set soft_skills weight to 0
        if (cleanedMentionnees.length === 0 && offer?.ai_matching_weights) {
          payload.ai_matching_weights = {
            ...offer.ai_matching_weights,
            soft_skills: 0
          }
        }

        return payload
      }
      case 'languages': {
        const data = draft as SectionDrafts['languages']
        const cleanedLanguages = (data.languages || [])
          .filter(lang => lang?.language?.trim() && lang?.level?.trim())
          .map(lang => ({
            language: (lang?.language || '').trim(),
            level: (lang?.level || '').trim()
          }))

        const payload: any = { languages: cleanedLanguages }

        // If languages become empty, set languages weight to 0
        if (cleanedLanguages.length === 0 && offer?.ai_matching_weights) {
          payload.ai_matching_weights = {
            ...offer.ai_matching_weights,
            languages: 0
          }
        }

        return payload
      }
      case 'certifications': {
        const data = draft as SectionDrafts['certifications']
        const cleanedCertifications = sanitizeStringArray(data.certifications)
        const payload: any = { certifications: cleanedCertifications }

        // If certifications become empty, set certifications weight to 0
        if (cleanedCertifications.length === 0 && offer?.ai_matching_weights) {
          payload.ai_matching_weights = {
            ...offer.ai_matching_weights,
            certifications: 0
          }
        }

        return payload
      }
      case 'benefits': {
        const data = draft as SectionDrafts['benefits']
        const cleanedMentionnees = sanitizeStringArray(data.benefits_mentionnees)
        const cleanedNonMentionnees = sanitizeStringArray(data.benefits_non_mentionnees)

        const payload: any = {
          benefits: {
            nice_to_have_mentionnées: cleanedMentionnees,
            nice_to_have_non_mentionnées: cleanedNonMentionnees
          }
        }

        // If benefits_mentionnees become empty, set nice_to_have weight to 0
        if (cleanedMentionnees.length === 0 && offer?.ai_matching_weights) {
          payload.ai_matching_weights = {
            ...offer.ai_matching_weights,
            nice_to_have: 0
          }
        }

        return payload
      }
      case 'key_details': {
        const data = draft as SectionDrafts['key_details']
        return {
          start_date: data.start_date || null,
          expiry_date: data.expiry_date || null,
          status: data.status,
          email_language: data.email_language || 'fr'
        }
      }
      case 'ai_matching': {
        const data = draft as SectionDrafts['ai_matching']
        const normalized = Object.entries(data.ai_matching_weights || {}).reduce(
          (acc, [key, value]) => {
            acc[key as keyof NonNullable<Offer['ai_matching_weights']>] = Number(value) || 0
            return acc
          },
          { ...defaultAiMatchingWeights }
        ) as NonNullable<Offer['ai_matching_weights']>

        // Set weights to 0 for empty fields
        if (!offer) return { ai_matching_weights: normalized }

        // Get current values from offer or section drafts (if being edited)
        const currentBenefits = sectionDrafts.benefits?.benefits_mentionnees || benefitsMentionnees || []
        const currentEducationLevel = sectionDrafts.job_overview?.education_level || offer.education_level || ''
        const currentLanguages = sectionDrafts.languages?.languages || offer.languages || []
        const currentRequirements = sectionDrafts.requirements?.requirements_mentionnees || hardSkillsMentionnees || []
        const currentCertifications = sectionDrafts.certifications?.certifications || offer.certifications || []
        const currentMainMissions = sectionDrafts.main_missions?.main_missions || offer.main_missions || []
        const currentSoftSkills = sectionDrafts.soft_skills?.soft_skills_mentionnees || softSkillsMentionnees || []
        const currentExperienceLevel = sectionDrafts.job_overview?.experience_level ?? offer.experience_level ?? ''

        // Check each field and set its weight to 0 if the field is empty
        if (!currentBenefits.length || !currentBenefits.some(b => b?.trim())) {
          normalized.nice_to_have = 0
        }
        if (!currentEducationLevel || !currentEducationLevel.toString().trim()) {
          normalized.niveau_etude = 0
        }
        if (!currentLanguages.length || !currentLanguages.some(l => l?.language?.trim() && l?.level?.trim())) {
          normalized.languages = 0
        }
        if (!currentRequirements.length || !currentRequirements.some(r => r?.trim())) {
          normalized.hard_skills = 0
        }
        if (!currentCertifications.length || !currentCertifications.some(c => c?.trim())) {
          normalized.certifications = 0
        }
        if (!currentMainMissions.length || !currentMainMissions.some(m => m?.trim())) {
          normalized.main_missions = 0
        }
        if (!currentSoftSkills.length || !currentSoftSkills.some(s => s?.trim())) {
          normalized.soft_skills = 0
        }
        if (!currentExperienceLevel || currentExperienceLevel.toString().trim() === '') {
          normalized.experience_years = 0
        }

        return { ai_matching_weights: normalized }
      }
      default:
        return {}
    }
  }

  const validateSectionDraft = (
    section: EditableSection,
    draft: SectionDrafts[EditableSection]
  ): SectionErrorMessage | null => {
    if (section === 'job_overview') {
      const data = draft as SectionDrafts['job_overview']
      if (!data.job_title.trim()) {
        return {
          key: 'offerDetail.validation.jobTitleRequired',
          defaultMessage: 'Job title is required.'
        }
      }
    }

    if (section === 'key_details') {
      const data = draft as SectionDrafts['key_details']
      const startDate = data.start_date ? new Date(data.start_date) : null
      const expiryDate = data.expiry_date ? new Date(data.expiry_date) : null

      if (startDate && expiryDate && startDate > expiryDate) {
        return {
          key: 'offerDetail.validation.expiryAfterStart',
          defaultMessage: 'Expiry date must be on or after the start date.'
        }
      }
    }

    return null
  }

  // Helper function to check if a field related to AI matching is empty
  const isFieldEmpty = useCallback((key: AiMatchingKey): boolean => {
    if (!offer) return true

    switch (key) {
      case 'hard_skills':
        return !hardSkillsMentionnees || hardSkillsMentionnees.length === 0 || !hardSkillsMentionnees.some(r => r?.trim())
      case 'soft_skills':
        return !softSkillsMentionnees || softSkillsMentionnees.length === 0 || !softSkillsMentionnees.some(s => s?.trim())
      case 'certifications':
        return !offer.certifications || offer.certifications.length === 0 || !offer.certifications.some(c => c?.trim())
      case 'nice_to_have':
        return !benefitsMentionnees || benefitsMentionnees.length === 0 || !benefitsMentionnees.some(b => b?.trim())
      case 'languages':
        return !offer.languages || offer.languages.length === 0 || !offer.languages.some(l => l?.language?.trim() && l?.level?.trim())
      case 'experience_years':
        return !offer.experience_level || offer.experience_level.toString().trim() === '' || offer.experience_level.toString().trim() === '0'
      case 'niveau_etude':
        return !offer.education_level || !offer.education_level.trim()
      case 'main_missions':
        return !offer.main_missions || offer.main_missions.length === 0 || !offer.main_missions.some(m => m?.trim())
      default:
        return false
    }
  }, [benefitsMentionnees, hardSkillsMentionnees, offer, softSkillsMentionnees])

  const handleSaveSection = async (section: EditableSection) => {
    if (!offer) return

    // Check if section is locked before attempting to save
    if (isSectionLocked(section)) {
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('offerDetail.cannotEditAfterMatching', 'This field cannot be modified because matching has been applied to this offer.'),
        duration: 5000
      })
      cancelEditingSection(section)
      return
    }

    const isArchived = offer.status === 'closed'
    const canEditKeyDetailsOnly = offer.status === 'published' || offer.status === 'offer_closed'
    if (isArchived || (isOfferModificationLocked && !(canEditKeyDetailsOnly && section === 'key_details'))) {
      addToast({
        type: 'error',
        title: t('common.error'),
        message: isOfferClosed
          ? t('offerDetail.cannotEditClosed', 'This field cannot be modified because the offer is closed.')
          : t('offerDetail.cannotEditPublished', 'This field cannot be modified because the offer is activated.'),
        duration: 5000
      })
      cancelEditingSection(section)
      return
    }

    const draft = (sectionDrafts[section] as SectionDrafts[typeof section]) ?? (getSectionDraft(section) as SectionDrafts[typeof section])
    const errorMessageDescriptor = validateSectionDraft(section, draft)
    if (errorMessageDescriptor) {
      // For AI matching, show flash warning instead of inline error
      if (section === 'ai_matching') {
        addToast({
          type: 'warning',
          title: t('common.warning', 'Warning'),
          message: t(errorMessageDescriptor.key, errorMessageDescriptor.defaultMessage),
          duration: 5000
        })
      } else {
        setSectionErrors(prev => ({ ...prev, [section]: errorMessageDescriptor }))
      }
      return
    }

    // Check if this is a status change (in key_details section)
    if (section === 'key_details') {
      const keyDetailsDraft = draft as SectionDrafts['key_details']
      const currentStatus = offer.status
      const newStatus = keyDetailsDraft.status

      if (currentStatus === 'offer_closed' && newStatus === 'published') {
        addToast({
          type: 'error',
          title: t('common.error'),
          message: t(
            'offerDetail.cannotReactivateClosed',
            'A closed offer cannot be activated again. You can only archive it.'
          ),
          duration: 5000
        })
        return
      }

      // Confirm activate / clôturer / archive transitions
      if (
        currentStatus !== newStatus &&
        (newStatus === 'published' || newStatus === 'offer_closed' || newStatus === 'closed')
      ) {
        if (newStatus === 'published') {
          const weights = offer.ai_matching_weights || {}
          const sumWeights = Object.values(weights).reduce((sum: number, val: any) => sum + (Number(val) || 0), 0)
          if (sumWeights === 0) {
            setShowZeroWeightsWarning(true)
            return
          }
        }
        setPendingStatusChange({ newStatus, section })
        setShowStatusChangeConfirm(true)
        return
      }
    }

    // Proceed with saving if no confirmation needed
    performSaveSection(section)
  }

  const performSaveSection = async (section: EditableSection) => {
    if (!offer) return

    const draft = (sectionDrafts[section] as SectionDrafts[typeof section]) ?? (getSectionDraft(section) as SectionDrafts[typeof section])

    const payload = buildSectionPayload(section, draft)
    // Clôturée: only status may be updated (archive)
    const effectivePayload =
      section === 'key_details' && offer.status === 'offer_closed'
        ? { status: (payload as { status: Offer['status'] }).status }
        : payload
    const isPublishing = section === 'key_details' && (effectivePayload as any).status === 'published'
    setSavingSection(section)
    try {
      const response = await offersApi.update(offer.id, effectivePayload)
      setOffer(response.data)

      if (section === 'job_overview') {
        setTranslatedJobTitle('')
        if (i18n.language === 'en') {
          translationPipelineCompleteRef.current = false
          setTranslationStage('job_title')
        }
      }

      // After activation: refetch fresh offer data (LLM runs synchronously in backend,
      // the updated ai_response is already in the PATCH response)
      if (isPublishing) {
        let freshAiResponse = response.data?.ai_response
        let freshOffer = response.data
        // Fallback: if ai_response not in PATCH response, do a GET to fetch fresh data
        if (!freshAiResponse) {
          try {
            const freshResponse = await offersApi.getById(offer.id)
            setOffer(freshResponse.data)
            freshOffer = freshResponse.data
            freshAiResponse = freshResponse.data?.ai_response
          } catch {
            // ignore fetch errors for the modal – offer state is already set
          }
        }
        const recommendedSkills = buildRecommendedSkillsDataFromAiResponse(freshAiResponse)
        if (recommendedSkills) {
          setRecommendedSkillsData(recommendedSkills)
          setShowRecommendedSkillsModal(true)
        }
      }
      addToast({
        type: 'success',
        title: t('offerDetail.sectionUpdated', 'Section updated'),
        message: t('offerDetail.sectionUpdatedDescription', 'Your changes have been saved.')
      })
      setSectionErrors(prev => ({ ...prev, [section]: null }))
      cancelEditingSection(section)
    } catch (error: any) {
      console.error('Error updating section:', error)
      console.error('Error response:', error?.response)
      console.error('Error response data:', error?.response?.data)

      // Extract specific error messages from backend response
      let errorMessage = t('offerDetail.sectionUpdateFailed', 'Unable to save changes. Please try again.')

      if (error?.response?.data) {
        const errorData = error.response.data

        // Check for field-specific errors (e.g., requirements, minimum_score, etc.)
        // Include all fields that might be sent in the payload
        const allPossibleFields = [
          'requirements', 'main_missions', 'soft_skills', 'certifications', 'languages',
          'education_level', 'experience_level', 'ai_matching_weights', 'minimum_score',
          'job_title', 'department', 'contract_type', 'remote_allowed', 'description_text', 'status'
        ]

        // First, try to find any field-specific error
        for (const field of allPossibleFields) {
          if (errorData[field]) {
            const fieldError = Array.isArray(errorData[field]) ? errorData[field][0] : errorData[field]
            if (typeof fieldError === 'string' && fieldError.trim()) {
              errorMessage = fieldError
              break
            }
          }
        }

        // If no field-specific error found, check for non-field errors
        if (errorMessage === t('offerDetail.sectionUpdateFailed', 'Unable to save changes. Please try again.')) {
          // Check if errorData is an object with multiple field errors - combine them
          if (typeof errorData === 'object' && !Array.isArray(errorData)) {
            const errorMessages: string[] = []
            Object.keys(errorData).forEach(key => {
              const value = errorData[key]
              if (Array.isArray(value) && value.length > 0) {
                errorMessages.push(value[0])
              } else if (typeof value === 'string' && value.trim()) {
                errorMessages.push(value)
              }
            })
            if (errorMessages.length > 0) {
              errorMessage = errorMessages.join('. ')
            }
          }

          // Fallback to general error messages
          if (errorMessage === t('offerDetail.sectionUpdateFailed', 'Unable to save changes. Please try again.')) {
            if (errorData.detail) {
              errorMessage = errorData.detail
            } else if (errorData.error) {
              errorMessage = errorData.error
            } else if (typeof errorData === 'string') {
              errorMessage = errorData
            } else if (errorData.message) {
              errorMessage = errorData.message
            }
          }
        }
      } else if (error?.message) {
        // If no response data, use the error message
        errorMessage = error.message
      }

      const failureMessageDescriptor: SectionErrorMessage = {
        key: 'offerDetail.sectionUpdateFailed',
        defaultMessage: errorMessage
      }
      setSectionErrors(prev => ({ ...prev, [section]: failureMessageDescriptor }))
      addToast({
        type: 'error',
        title: t('common.error'),
        message: errorMessage
      })
    } finally {
      setSavingSection(null)
    }
  }

  const handleAcceptRecommendedSkills = async (
    selected: {
      hard_skills_non_mentionnées: string[]
      soft_skills_non_mentionnées: string[]
      nice_to_have_non_mentionnées: string[]
    },
    weights?: {
      hard_skills: number
      soft_skills: number
      nice_to_have: number
    },
    skillWeights?: Record<string, number>,
    lockedSkills?: string[]
  ) => {
    if (!offer) return
    setIsSavingRecommendedSkills(true)
    try {
      const payload: any = {
        requirements: {
          hard_skills_mentionnées: offer.requirements?.hard_skills_mentionnées ?? [],
          hard_skills_non_mentionnées: selected.hard_skills_non_mentionnées
        },
        soft_skills: {
          soft_skills_mentionnées: offer.soft_skills?.soft_skills_mentionnées ?? [],
          soft_skills_non_mentionnées: selected.soft_skills_non_mentionnées
        },
        benefits: {
          nice_to_have_mentionnées: offer.benefits?.nice_to_have_mentionnées ?? [],
          nice_to_have_non_mentionnées: selected.nice_to_have_non_mentionnées
        }
      }
      if (weights) {
        payload.ai_matching_weights = weights
      }
      if (skillWeights) {
        payload.skill_weights = skillWeights
      }

      if (lockedSkills) {
        const newLockedSet = new Set(lockedSkills)
        setLockedItems(newLockedSet)
        payload.filter_matching = buildFilterMatchingFromLockedItems(newLockedSet, includeInternshipExperience)
      }

      // Appel sur l'endpoint dédié → sauvegarde directe en BDD, sans LLM
      const response = await offersApi.updateAiSkills(offer.id, payload)
      setOffer(response.data)

      setShowRecommendedSkillsModal(false)
      addToast({
        type: 'success',
        title: t('common.success'),
        message: t('recommendedSkills.successMessage', 'Les compétences sélectionnées ont été ajoutées avec succès à l’offre.')
      })
    } catch (err) {
      console.error('Error saving recommended skills:', err)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('recommendedSkills.errorMessage', 'Impossible d’enregistrer les compétences.')
      })
    } finally {
      setIsSavingRecommendedSkills(false)
    }
  }

  const openSectionEditor = (section: EditableSection) => {
    if (!hasPermission('edit_offers')) return
    if (isSectionLocked(section)) {
      addToast({
        type: 'warning',
        title: t('common.warning'),
        message: t('offerDetail.cannotEditAfterMatching', 'This field cannot be modified because matching has been applied to this offer.'),
        duration: 5000
      })
      return
    }
    const isArchived = offer?.status === 'closed'
    const canEditKeyDetailsOnly = offer?.status === 'published' || offer?.status === 'offer_closed'
    if (isArchived || (isOfferModificationLocked && !(canEditKeyDetailsOnly && section === 'key_details'))) {
      addToast({
        type: 'warning',
        title: t('common.warning'),
        message: isOfferClosed
          ? t('offerDetail.cannotEditClosed', 'This field cannot be modified because the offer is closed.')
          : t('offerDetail.cannotEditPublished', 'This field cannot be modified because the offer is activated.'),
        duration: 5000
      })
      return
    }
    startEditingSection(section)
  }

  const renderSectionEditButton = (sectionKey: EditableSection) => {
    if (!offer || !hasPermission('edit_offers')) return null
    const isEditing = editingSections[sectionKey]
    const isLocked = isSectionLocked(sectionKey)
    const isPublished = offer?.status === 'published'
    const isArchived = offer?.status === 'closed'
    const canEditKeyDetailsOnly = isPublished || offer?.status === 'offer_closed'
    const sectionEditDisabled =
      isLocked ||
      isArchived ||
      (isOfferModificationLocked && !(canEditKeyDetailsOnly && sectionKey === 'key_details'))

    if (sectionEditDisabled && !isEditing) {
      const disabledMessage = isOfferClosed
        ? t('offerDetail.cannotEditClosed', 'This field cannot be modified because the offer is closed.')
        : isPublished
          ? t('offerDetail.cannotEditPublished', 'This field cannot be modified because the offer is activated.')
          : t('offerDetail.cannotEditAfterMatching', 'This field cannot be modified because matching has been applied to this offer.')
      const disabledTitle = isOfferClosed
        ? t('offerDetail.closedOfferCannotEdit', 'Cannot edit: offer is closed')
        : isPublished
          ? t('offerDetail.publishedOfferCannotEdit', 'Cannot edit: offer is activated')
          : t('offerDetail.lockedAfterMatching', 'Cannot edit: matching has been applied')
      return (
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground cursor-not-allowed opacity-50 max-[1149px]:h-8 max-[1149px]:w-8"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            // Show orange warning flash alert
            addToast({
              type: 'warning',
              title: t('common.warning', 'Warning'),
              message: disabledMessage,
              duration: 5000
            })
          }}
          title={disabledTitle}
          aria-label={disabledTitle}
          aria-disabled="true"
        >
          <Edit className="h-4 w-4" />
        </Button>
      )
    }

    return (
      <Button
        variant="ghost"
        size="icon"
        className="text-muted-foreground hover:text-primary max-[1149px]:h-8 max-[1149px]:w-8"
        onClick={() => (isEditing ? cancelEditingSection(sectionKey) : openSectionEditor(sectionKey))}
        title={isEditing ? t('common.cancel') : t('offerDetail.editSection', 'Edit section')}
        aria-label={isEditing ? t('common.cancel') : t('offerDetail.editSection', 'Edit section')}
      >
        {isEditing ? <X className="h-4 w-4" /> : <Edit className="h-4 w-4" />}
      </Button>
    )
  }

  const renderSectionError = (sectionKey: EditableSection) => {
    if (!sectionErrors[sectionKey]) return null

    // AI matching errors are shown as flash warnings, not inline
    if (sectionKey === 'ai_matching') return null

    return (
      <div className="mt-2 mb-2 p-3 rounded-md bg-destructive/10 border border-destructive/20">
        <p className="text-sm flex items-center gap-2 text-destructive">
          <XCircle className="h-4 w-4 flex-shrink-0" />
          {t(sectionErrors[sectionKey]!.key, sectionErrors[sectionKey]!.defaultMessage)}
        </p>
      </div>
    )
  }

  const renderSectionEditorActions = (sectionKey: EditableSection) => {
    return (
      <div className="flex flex-col gap-2 pt-4 border-t border-border">
        {/* AI matching errors are shown as flash warnings, not inline */}
        {sectionErrors[sectionKey] && sectionKey !== 'ai_matching' && (
          <p className="text-sm text-destructive">
            {t(sectionErrors[sectionKey]!.key, sectionErrors[sectionKey]!.defaultMessage)}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => cancelEditingSection(sectionKey)}
            className="max-[1149px]:h-9 max-[1149px]:text-sm"
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => handleSaveSection(sectionKey)}
            loading={savingSection === sectionKey}
            className="max-[1149px]:h-9 max-[1149px]:text-sm"
          >
            {t('common.save')}
          </Button>
        </div>
      </div>
    )
  }

  const updateSectionDraft = <T extends EditableSection>(
    sectionKey: T,
    updater: (draft: SectionDrafts[T]) => SectionDrafts[T]
  ) => {
    setSectionDrafts(prev => {
      const currentDraft = (prev[sectionKey] as SectionDrafts[T]) ?? (getSectionDraft(sectionKey) as SectionDrafts[T])
      return {
        ...prev,
        [sectionKey]: updater(currentDraft)
      }
    })
  }

  const getActiveDraft = <T extends EditableSection>(sectionKey: T): SectionDrafts[T] => {
    return (sectionDrafts[sectionKey] as SectionDrafts[T]) ?? (getSectionDraft(sectionKey) as SectionDrafts[T])
  }

  // Validate section on blur for sensitive fields
  const validateSectionOnBlur = (section: EditableSection) => {
    if (!sensitiveSections.includes(section)) return

    const draft = (sectionDrafts[section] as SectionDrafts[typeof section]) ?? (getSectionDraft(section) as SectionDrafts[typeof section])
    const errorMessageDescriptor = validateSectionDraft(section, draft)
    if (errorMessageDescriptor) {
      setSectionErrors(prev => ({ ...prev, [section]: errorMessageDescriptor }))
    } else {
      // Clear error if validation passes
      setSectionErrors(prev => ({ ...prev, [section]: null }))
    }
  }

  // Validate section on change for real-time feedback
  const validateSectionOnChange = (section: EditableSection) => {
    if (!sensitiveSections.includes(section)) return

    const draft = (sectionDrafts[section] as SectionDrafts[typeof section]) ?? (getSectionDraft(section) as SectionDrafts[typeof section])
    const errorMessageDescriptor = validateSectionDraft(section, draft)
    if (errorMessageDescriptor) {
      setSectionErrors(prev => ({ ...prev, [section]: errorMessageDescriptor }))
    } else {
      // Clear error if validation passes
      setSectionErrors(prev => ({ ...prev, [section]: null }))
    }
  }

  // Get field-specific error message for a section
  const getFieldError = (section: EditableSection, fieldName: string): string | null => {
    if (!sectionErrors[section]) return null
    const error = sectionErrors[section]
    if (!error) return null

    // For job_overview, check if it's the job_title field
    if (section === 'job_overview' && fieldName === 'job_title' && error.key === 'offerDetail.validation.jobTitleRequired') {
      return t(error.key, error.defaultMessage)
    }

    // For other sections, return the error if it's relevant
    return t(error.key, error.defaultMessage)
  }

  const handleAiMatchingWeightChange = (field: AiMatchingKey, value: number) => {
    updateSectionDraft('ai_matching', draft => {
      const currentWeights = {
        ...(draft.ai_matching_weights ?? defaultAiMatchingWeights)
      }

      // Update the weight without auto-redistributing
      const updatedWeights = updateAiMatchingWeight(currentWeights, field, value)

      // Always update the draft (don't block changes)
      return {
        ...draft,
        ai_matching_weights: updatedWeights
      }
    })
  }

  const handleOpenAddCandidateModal = async () => {
    if (isOfferClosed) {
      addToast({
        type: 'warning',
        title: t('offerDetail.closedOffer', 'Offer closed'),
        message: t('offerDetail.cannotAddCandidatesClosed', 'You cannot add candidates to a closed offer.')
      })
      return
    }
    setSelectionMode('group') // Set group as default (first position)
    setSelectedCandidateIds([])
    setSelectedTeamIds([])
    setTeamSearch('')
    setCandidateSearch('')
    setAddingCandidates(false) // Reset loading state
    setLoadingAddCandidatesData(true)
    setShowAddCandidateModal(true)
    try {
      await fetchAvailableCandidates()
    } finally {
      setLoadingAddCandidatesData(false)
    }
  }

  const handleOpenAddStepModal = () => {
    if (!offer?.id) return

    const cachedSteps = offerStepsForPosition.length > 0 ? offerStepsForPosition : hiringSteps
    if (cachedSteps.length > 0) {
      setOfferStepsForPosition(cachedSteps)
    }
    const knownTemplateId =
      offer.hiring_template ?? defaultTemplateId ?? cachedSteps[0]?.template ?? null
    if (knownTemplateId != null && defaultTemplateId == null) {
      setDefaultTemplateId(knownTemplateId)
    }
    setShowStepModal(true)

    void (async () => {
      try {
        const [templateRes, stepsRes] = await Promise.all([
          knownTemplateId != null
            ? Promise.resolve({ data: { id: knownTemplateId } })
            : hiringProcessApi.getDefaultTemplate(),
          hiringProcessApi.getStepsForOffer(offer.id)
        ])
        if (templateRes.data?.id) setDefaultTemplateId(templateRes.data.id)
        const data = stepsRes.data as { results?: HiringStep[] } | HiringStep[]
        const steps = Array.isArray(data) ? data : (data?.results ?? [])
        setOfferStepsForPosition(steps)
        setHiringSteps(steps.sort((a, b) => a.order - b.order))
      } catch (e: any) {
        if (cachedSteps.length === 0) {
          setShowStepModal(false)
          addToast({
            type: 'error',
            title: t('common.error'),
            message: e?.response?.data?.detail || t('hiringProcess.messages.templateLoadError', { error: String(e?.message || e) })
          })
        }
      }
    })()
  }

  const handleAddStepSuccess = async (stepData: HiringStep, positionOrder?: number) => {
    const templateId =
      offerStepsForPosition[0]?.template ??
      offer?.hiring_template ??
      defaultTemplateId
    if (!offer?.id || templateId == null) return

    const isCustom = isResolvedStepCustom(stepData, t)
    if (isDuplicateNewStep(offerStepsForPosition, stepData.step_type || DEFAULT_STEP_TYPE, stepData.name, isCustom)) {
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t(
          'hiringProcess.errors.duplicateStepType',
          'A step with the same name already exists in this hiring process.'
        ),
      })
      return
    }

    setShowStepModal(false)

    try {
      // Create an offer-specific step so the template (standard hiring process) is not modified.
      const payload: Parameters<typeof hiringProcessApi.createStep>[0] = {
        template: templateId,
        offer: offer.id,
        name: stepData.name.trim(),
        description: stepData.description?.trim() || '',
        step_type: stepData.step_type || DEFAULT_STEP_TYPE,
        ...(positionOrder != null ? { order: positionOrder } : {}),
        color: stepData.color || DEFAULT_STEP_COLOR,
        is_required: stepData.is_required !== undefined ? stepData.is_required : false,
        estimated_duration: stepData.estimated_duration || 1,
        is_active: stepData.is_active !== undefined ? stepData.is_active : true
      }
      const response = await hiringProcessApi.createStep(payload)
      if (response.data?.template != null) {
        setDefaultTemplateId(response.data.template)
      }
      setStepsRefreshTrigger(s => s + 1)
      addToast({ type: 'success', title: t('common.success'), message: t('hiringProcess.messages.stepCreated') })
    } catch (error: any) {
      setShowStepModal(true)
      const errMsg = error?.response?.data?.detail || error?.response?.data?.non_field_errors?.[0] || error?.message
      addToast({ type: 'error', title: t('common.error'), message: t('hiringProcess.messages.stepCreateError', { error: errMsg }) })
    }
  }

  const handleDeleteOfferStep = async (stepId: number) => {
    try {
      await hiringProcessApi.deleteStep(stepId)
      setStepsRefreshTrigger(s => s + 1)
      addToast({ type: 'success', title: t('common.success'), message: t('hiringProcess.messages.stepDeleted') })
    } catch (error: any) {
      const errMsg = error?.response?.data?.detail || error?.response?.data?.non_field_errors?.[0] || error?.message
      addToast({ type: 'error', title: t('common.error'), message: t('hiringProcess.messages.stepDeleteError', { error: errMsg }) })
    }
  }

  const handleDeleteOffer = async () => {
    if (!offer) return

    try {
      await offersApi.delete(offer.id)
      setShowDeleteConfirm(false)
      navigate('/offers')
    } catch (error) {
      console.error('Error deleting offer:', error)
    }
  }


  const refreshApplications = async () => {
    if (!id) return
    try {
      // Small delay to ensure backend changes have propagated through DB/Cache
      await new Promise(resolve => setTimeout(resolve, 800))

      // Trigger the first page load WITH force: true to bypass loadedSections guard
      await loadApplicationsData(true)

      // Also refresh the offer to get updated data (including filter_matching and common_questions)
      // This ensures Kanban has the latest filters after Apply Results
      const offerResponse = await offersApi.getById(parseInt(id))
      if (offerResponse?.data) {
        setOffer(offerResponse.data)
        setCommonQuestions(normalizeCommonQuestions(offerResponse.data.common_questions))
      }
    } catch (error) {
      console.error('Error refreshing applications:', error)
    }
  }

  const stepTypeToApplicationStatus = (stepType: string): string => {
    const reverseMapping: Record<string, string> = {
      'initial': 'applied',
      'scanning': 'scanning',
      'screening': 'screening',
      'interview': 'interview',
      'hr_interview': 'hr_interview',
      'assessment': 'interview',
      'reference_check': 'interview',
      'offer': 'offer',
      'hired': 'hired',
      'rejected': 'rejected',
      'onboarding': 'hired',
      'withdrawn': 'rejected',
      'custom': 'interview',
    }
    return reverseMapping[stepType] || stepType
  }

  const updateApplicationStatus = async (applicationId: number, newStatus: string, stepId?: number | null) => {
    if (!guardOfferOpenForMutations(
      'offerDetail.cannotMoveCandidatesClosed',
      'You cannot move candidates between steps while this offer is closed.'
    )) {
      return
    }
    try {
      const application = applications.find(app => app.id === applicationId)
      if (!application) {
        console.error('Application not found:', applicationId)
        return
      }

      const response = await applicationsApi.updateStatus(applicationId, newStatus, stepId)
      if (response.data.success) {
        const updated = response.data.application

        setApplicationsByColumn(prev => {
          const newState = { ...prev }
          // Remove from all existing columns
          Object.keys(newState).forEach(colId => {
            newState[colId] = (newState[colId] || []).filter(app => app.id !== applicationId)
          })

          // Determine target column ID
          let targetStepId = updated?.current_step || stepId
          if (!targetStepId) {
            const statusToStepTypeMap: Record<string, string> = {
              'applied': 'initial',
              'scanning': 'scanning',
              'screening': 'screening',
              'interview': 'interview',
              'hr_interview': 'hr_interview',
              'offer': 'offer',
              'hired': 'hired',
              'rejected': 'rejected',
            }
            const stepType = statusToStepTypeMap[newStatus] || newStatus
            const matchingStep = hiringSteps.find(s => s.step_type === stepType)
            targetStepId = matchingStep?.id
          }

          const newColumnId = targetStepId ? `step-${targetStepId}` : `status-${newStatus}`
          const updatedApp = {
            ...application,
            status: newStatus as Application['status'],
            current_step: targetStepId ?? application.current_step
          }

          // Add to the new column and maintain match_score ordering
          newState[newColumnId] = [...(newState[newColumnId] || []), updatedApp].sort((a, b) => (b.match_score || 0) - (a.match_score || 0))

          return newState
        })

        // Local state update is sufficient to update the UI instantly


        // Refresh offer data if candidate moved beyond 'applied'
        // This ensures the UI correctly reflects the current state
        if (newStatus !== 'applied' && offer) {
          try {
            const offerResponse = await offersApi.getById(offer.id)
            setOffer(offerResponse.data)
          } catch (refreshError) {
            console.error('Error refreshing offer after status change:', refreshError)
            // Don't fail the status update if refresh fails
          }
        }

        // Automatic step emails are triggered server-side when current_step changes (see step_sync).
        if (
          openPanel === 'validation'
          || ['screening', 'interview', 'hr_interview', 'rh_interview'].includes(newStatus)
        ) {
          fetchValidationCandidates()
        }
      }
    } catch (error) {
      console.error('Error updating application status:', error)
    }
  }

  // Load interviews for the calendar
  const loadInterviews = async () => {
    if (!id) return
    try {
      setLoadingInterviews(true)
      const response = await interviewsApi.getByOffer(parseInt(id))

      // Transform API response to InterviewEvent format
      const transformedInterviews: InterviewEvent[] = response.data.map((interview: Interview) => ({
        id: interview.id,
        title: interview.title,
        start: new Date(interview.scheduled_at),
        end: new Date(interview.end_time),
        candidateId: interview.candidate_id,
        candidateName: interview.candidate_name,
        applicationId: interview.application_id || undefined,
        stepOrder: interview.step_order || undefined,
        stepName: interview.step_name || undefined,
        location: interview.location || undefined,
        meetingLink: interview.meeting_link || undefined,
        notes: interview.notes || undefined,
        interviewerName: (interview as any).interviewer_name || undefined,
      }))

      setInterviews(transformedInterviews)
    } catch (error) {
      console.error('Error loading interviews:', error)
      setInterviews([])
    } finally {
      setLoadingInterviews(false)
    }
  }

  // Load hiring process steps
  useEffect(() => {
    const loadSteps = async () => {
      try {
        if (!offer) return
        // Backend getStepsForOffer already resolves the correct template (offer.hiring_template or default)
        const response = await hiringProcessApi.getStepsForOffer(offer.id)
        const data = response.data as { results?: HiringStep[] } | HiringStep[]
        const steps = Array.isArray(data) ? data : (data?.results ?? [])
        const sorted = steps.sort((a, b) => a.order - b.order)
        setHiringSteps(sorted)
        setOfferStepsForPosition(sorted)
        const templateId = offer.hiring_template ?? sorted[0]?.template ?? null
        if (templateId != null) {
          setDefaultTemplateId(templateId)
        }
      } catch (error) {
        console.error('Error loading hiring steps:', error)
        setHiringSteps([])
      }
    }
    loadSteps()
  }, [offer?.id, i18n.language])

  // Load interviews when calendar tab is active or when offer changes
  useEffect(() => {
    if (offer && id) {
      // Load interviews for both calendar tab and kanban (to show indicators)
      loadInterviews()
    }
  }, [offer?.id, id])

  // Handle interview scheduling from candidate card
  const handleScheduleInterview = async (candidate: any, applicationId?: number) => {
    try {
      // Fetch full candidate data from API
      let fullCandidate: Candidate
      try {
        const candidateResponse = await candidatesApi.getById(candidate.id)
        fullCandidate = candidateResponse.data
      } catch (error) {
        console.error('Error fetching candidate:', error)
        // Fallback: create a minimal candidate object from kanban data
        fullCandidate = {
          id: candidate.id,
          name: candidate.name || 'Unknown',
          email: candidate.email || '',
          role: candidate.role || '',
          source: 'internal' as const,
        } as Candidate
      }

      // Find application if applicationId is provided
      const application = applicationId ? applications.find(app => app.id === applicationId) : undefined

      // Find the step order from the application status FIRST
      let defaultStepOrder: number | undefined = undefined
      if (application && hiringSteps.length > 0) {
        // Map application status to step
        const statusMapping: Record<string, string> = {
          'initial': 'applied',
          'scanning': 'scanning',
          'screening': 'screening',
          'interview': 'interview',
          'hr_interview': 'hr_interview',
          'offer': 'offer',
          'hired': 'hired',
          'rejected': 'rejected',
        }

        const step = hiringSteps.find(s => {
          const mappedStatus = statusMapping[s.step_type]
          return mappedStatus === application.status
        })

        console.log('Finding step for application:', {
          applicationStatus: application.status,
          hiringSteps: hiringSteps.map(s => ({ type: s.step_type, order: s.order, name: s.name })),
          foundStep: step,
          defaultStepOrder: step?.order
        })

        defaultStepOrder = step?.order
      } else {
        console.log('Cannot find step order:', {
          hasApplication: !!application,
          hiringStepsLength: hiringSteps.length
        })
      }

      // Find existing interview ONLY for the CURRENT step (not all interviews)
      // This ensures we can have multiple interviews for different steps
      const existingInterview = interviews.find(interview => {
        const matchesCandidate = interview.candidateId === candidate.id
        const matchesApplication = applicationId ? interview.applicationId === applicationId : true
        const matchesStep = defaultStepOrder !== undefined
          ? (interview.stepOrder || interview.extendedProps?.stepOrder) === defaultStepOrder
          : true // If no step order, match any (for backward compatibility)
        return matchesCandidate && matchesApplication && matchesStep
      })

      // Calculate duration from existing interview if available
      let existingInterviewWithDuration: {
        id: number | string
        title: string
        start: Date
        end: Date
        candidateId: number
        candidateName: string
        applicationId?: number
        stepOrder?: number
        stepName?: string
        location?: string
        meetingLink?: string
        notes?: string
        interviewerName?: string
        duration: number
      } | undefined = undefined
      if (existingInterview) {
        const start = new Date(existingInterview.start)
        const end = existingInterview.end ? new Date(existingInterview.end) : new Date(existingInterview.start)
        const durationMinutes = Math.round((end.getTime() - start.getTime()) / 60000)
        existingInterviewWithDuration = {
          id: existingInterview.id,
          title: existingInterview.title,
          start: start,
          end: end,
          candidateId: existingInterview.candidateId || candidate.id,
          candidateName: existingInterview.candidateName || candidate.name,
          applicationId: existingInterview.applicationId,
          stepOrder: (existingInterview as any).stepOrder,
          stepName: (existingInterview as any).stepName,
          location: existingInterview.location,
          meetingLink: existingInterview.meetingLink,
          notes: existingInterview.notes,
          interviewerName: (existingInterview as any).interviewerName,
          duration: durationMinutes > 0 ? durationMinutes : 30
        }
      }

      setSelectedCandidateForInterview({
        candidate: fullCandidate,
        application,
        existingInterview: existingInterviewWithDuration
      })
      setSelectedStepOrderForInterview(defaultStepOrder)
      setShowScheduleInterviewModal(true)
    } catch (error) {
      console.error('Error preparing interview schedule:', error)
      addToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('offerDetail.scheduleInterview.errorPreparing', 'Error preparing interview schedule. Please try again.'),
      })
    }
  }

  // Handle successful interview creation
  const handleInterviewScheduled = (interview: InterviewEvent) => {
    // Add interview to the list - it will appear in calendar and kanban
    setInterviews(prev => [...prev, interview])
    // Reload interviews from API to ensure we have the latest data
    if (id) {
      loadInterviews().catch(err => console.error('Error reloading interviews:', err))
    }
    setShowScheduleInterviewModal(false)
    setSelectedCandidateForInterview(null)
    setSelectedStepOrderForInterview(undefined)
  }

  // Helper functions to get translated values when in English
  const getJobTitle = () => {
    const sourceTitle = offer?.job_title || ''
    if (i18n.language !== 'en') {
      return sourceTitle
    }
    const translation_en = (offer as any)?.translation_en
    if (isOfferTranslationComplete(translation_en, offer) && translatedJobTitle) {
      return translatedJobTitle
    }
    return sourceTitle
  }

  const getDescription = () => {
    return i18n.language === 'en' && translatedDescription ? translatedDescription : (offer?.description_text || offer?.description || offer?.display_description || '')
  }

  const getSoftSkills = () => {
    return i18n.language === 'en' && translatedSoftSkills.length > 0 ? translatedSoftSkills : (offer?.soft_skills || [])
  }

  const getCertifications = () => {
    return i18n.language === 'en' && translatedCertifications.length > 0 ? translatedCertifications : (offer?.certifications || [])
  }

  const getBenefits = () => {
    const benefits = offer?.benefits || []
    return i18n.language === 'en' && translatedBenefits.length > 0 ? translatedBenefits : benefits
  }

  const getLanguages = () => {
    return i18n.language === 'en' && translatedLanguages.length > 0 ? translatedLanguages : (offer?.languages || [])
  }

  const getMainMissions = () => {
    return i18n.language === 'en' && translatedMainMissions.length > 0 ? translatedMainMissions : (offer?.main_missions || [])
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!offer) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-foreground">{t('offerDetail.offerNotFound')}</h2>
        <Button onClick={handleBack} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('offerDetail.back')}
        </Button>
      </div>
    )
  }

  return (
    <div className="w-full max-w-full min-w-0 space-y-6 max-[1149px]:px-4 max-[1149px]:py-4">
      <OfferDetailPageHeader
        title={getJobTitle()}
        applicationsCount={totalApplicationsCount}
        activePanel={openPanel}
        onBack={handleBack}
        onOpenPanel={(panel) => setOpenPanel((prev) => (prev === panel ? null : panel))}
      />

      <div className="flex w-full min-w-0 flex-col gap-3">
        <OfferRecruitmentProcessBar
          hiringProcessName={hiringProcessName}
          validationActive={openPanel === 'validation'}
          onValidationInterviewsClick={() => {
            setActiveTab('validation_interviews')
            setOpenPanel((prev) => (prev === 'validation' ? null : 'validation'))
          }}
        />
        <ModernKanban
            hideHiringProcessLabel
            onHiringProcessNameChange={setHiringProcessName}
            onAddHiringStep={
              (hasPermission('manage_hiring_steps') || hasPermission('create_hiring_steps')) && !isOfferClosed
                ? handleOpenAddStepModal
                : undefined
            }
            offerId={offer?.id}
            stepsRefreshTrigger={stepsRefreshTrigger}
            onDeleteOfferStep={hasPermission('manage_hiring_steps') ? handleDeleteOfferStep : undefined}
            onManageCandidates={hasPermission('manage_applications') ? () => setShowManageCandidatesModal(true) : undefined}
            onLaunchAIMatching={
              hasPermission('use_ai_matching') && !isOfferClosed
                ? () => setShowAIMatching(true)
                : undefined
            }
            interactionsDisabled={isOfferClosed}
            minimumScore={minimumScore}
            onMinimumScoreChange={isOfferClosed ? undefined : updateMinimumScore}
            minimumInterviewScore={minimumInterviewScore}
            onMinimumInterviewScoreChange={isOfferClosed ? undefined : updateMinimumInterviewScore}
            onStatusChange={updateApplicationStatus}
            isLoadingMore={Object.fromEntries(
              Object.entries(stagePagination).map(([k, v]) => [k, (v as any).loadingMore])
            )}
            onPageChange={handlePageChange}
            currentPage={Object.fromEntries(
              Object.entries(stagePagination).map(([k, v]) => [k, (v as any).page || 1])
            )}
            hasMore={Object.fromEntries(
              Object.entries(stagePagination).map(([k, v]) => [k, (v as any).hasMore])
            )}
            stageTotalCounts={stageTotalCounts}
            hasMatchingRun={offer?.has_matching_run}
            filterMatching={offer?.filter_matching}
            skillWeights={offer?.skill_weights}
            onScheduleInterview={handleScheduleInterview}
            interviews={interviews.map(int => ({
              candidateId: int.candidateId,
              applicationId: int.applicationId,
              start: int.start,
              end: int.end,
              location: int.location,
              meetingLink: int.meetingLink,
              stepOrder: int.stepOrder,
              stepName: int.stepName,
              title: int.title,
            }))}
            candidates={applications.map(app => {
              // Get group tags from teams that include this candidate
              let groupTags: Array<{ name: string; color: string }> = []

              // First, try to get team names and colors from the teams state
              const candidateTeams = teams.filter(team =>
                team.candidates?.some(c => c.id === app.candidate)
              )
              if (candidateTeams.length > 0) {
                groupTags = candidateTeams.map(team => ({
                  name: team.name,
                  color: team.color || '#007bff' // Default color if not set
                }))
              }

              // Also parse group tags from notes if they exist
              if (app.notes) {
                if (app.notes.includes('Added from groups:')) {
                  const groupsPart = app.notes.split('Added from groups:')[1]?.trim()
                  if (groupsPart) {
                    const noteGroups = groupsPart.split(',').map(g => g.trim()).filter(g => g.length > 0)
                    noteGroups.forEach(groupName => {
                      if (!groupTags.some(tag => tag.name === groupName)) {
                        const team = teams.find(t => t.name === groupName)
                        groupTags.push({
                          name: groupName,
                          color: team?.color || '#007bff'
                        })
                      }
                    })
                  }
                } else if (app.notes.includes('Added from newly created group:')) {
                  const groupName = app.notes.split('Added from newly created group:')[1]?.trim()
                  if (groupName && !groupTags.some(tag => tag.name === groupName)) {
                    const team = teams.find(t => t.name === groupName)
                    groupTags.push({
                      name: groupName,
                      color: team?.color || '#007bff'
                    })
                  }
                }
              }

              const teamCandidateWithAvatar = candidateTeams
                .flatMap(team => team.candidates || [])
                .find(c => c.id === app.candidate)

              const avatar =
                (app as any).candidate_profile_picture ||
                (teamCandidateWithAvatar && (teamCandidateWithAvatar as any).profile_picture) ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(app.candidate_name || app.candidate_email || 'Candidate')}&background=random&color=fff&size=128`

              const appInterview = interviewAudios.find(a => a.application === app.id)

              return {
                id: app.candidate,
                applicationId: app.id,
                name: app.candidate_name,
                email: app.candidate_email,
                role: (app as any).candidate_role?.split('/')[0].trim() || 'Candidate',
                experience: app.candidate_experience || 'Not specified',
                skills: app.candidate_skills || [],
                education: app.candidate_education || null,
                previousEmployer: undefined,
                location: app.candidate_location || 'Remote',
                appliedDate: app.applied_date,
                status: app.status,
                matchScore: app.match_score ?? 0,
                aiDecision: app.ai_decision ?? null,
                commentsCount: 0,
                attachmentsCount: 0,
                actionsCount: 0,
                lastActivity: undefined,
                seniorityLevel: undefined,
                groupTags: groupTags.length > 0 ? groupTags : undefined,
                avatar,
                interviewStatus: appInterview?.status,
                interviewScore: (() => {
                  if (appInterview?.score) {
                    try {
                      return JSON.parse(appInterview.score).global_score
                    } catch (e) {
                      return undefined
                    }
                  }
                  return undefined
                })(),
                interviewScoreData: (() => {
                  if (appInterview?.score) {
                    try {
                      return JSON.parse(appInterview.score)
                    } catch (e) {
                      return undefined
                    }
                  }
                  return undefined
                })(),
                current_step_id: app.current_step ?? undefined,
              }
            })}
            onCandidateClick={(candidateId) => {
              window.open(`/candidates/${candidateId}`, '_blank', 'noopener,noreferrer')
            }}
            onAddCandidates={!isOfferClosed ? handleOpenAddCandidateModal : undefined}
          />
      </div>

      {openPanel === 'validation' && (
        <OfferDetailFullPageOverlay
          title={t('offerDetail.validationInterviews', 'Interviews de validation')}
          onClose={() => setOpenPanel(null)}
        >
        <Card>
          <CardHeader className="space-y-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-8">
              <div className="min-w-0 flex-1 space-y-1.5">
                <CardTitle className="text-xl leading-tight">
                  {t('offerDetail.validationInterviews', 'Interviews de validation')}
                </CardTitle>
                <CardDescription className="max-w-2xl leading-relaxed">
                  {t('offerDetail.validationInterviewsIntro', 'Gérez les entretiens vidéo pour les candidats en phase de présélection ou dans une étape de validation avant l\'entretien IA.')}
                </CardDescription>
              </div>
              <div className="w-full shrink-0 sm:w-72">
                <ModernDatePicker
                  label={t('offerDetail.interviewDeadline', 'Date limite des entretiens')}
                  required
                  selected={parseDraftDate(validationInterviewDeadline)}
                  onChange={(date) => {
                    const nextDeadline = formatDraftDate(date)
                    setValidationInterviewDeadline(nextDeadline)
                    void handleSaveInterviewDeadline(nextDeadline)
                  }}
                  placeholder={t('offerDetail.interviewDeadlinePlaceholder', 'Sélectionner la date limite')}
                  minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                  disabled={isOfferClosed || isValidationInterviewDeadlinePassed}
                />
              </div>
            </div>

            {isOfferClosed && (
              <p className="text-sm text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md px-3 py-2">
                {t(
                  'offerDetail.cannotUseValidationInterviewsClosed',
                  'Validation interview actions are disabled while this offer is closed.'
                )}
              </p>
            )}
            {!offer?.interview_deadline && !isValidationInterviewDeadlinePassed ? (
              <p className="text-xs leading-relaxed text-amber-700 dark:text-amber-300">
                {t(
                  'offerDetail.interviewDeadlineRequiredHint',
                  'Obligatoire avant de générer ou de créer manuellement les questions. La date est enregistrée dès que vous la choisissez.'
                )}
              </p>
            ) : null}
            {isValidationInterviewDeadlinePassed ? (
              <p className="text-xs leading-relaxed text-muted-foreground">
                {t(
                  'offerDetail.interviewDeadlineExpiredLocked',
                  'La date limite des entretiens est expirée et ne peut plus être modifiée.'
                )}
              </p>
            ) : null}

            <div className="flex flex-wrap items-center justify-end gap-3 pt-4 border-t border-border/40">
              <div className="mr-auto flex items-center gap-2">
                <label htmlFor="interview-status-filter" className="text-xs font-medium text-muted-foreground">
                  {t('common.status', 'Statut')}
                </label>
                <select
                  id="interview-status-filter"
                  value={interviewStatusFilter}
                  onChange={(e) => {
                    setInterviewStatusFilter(e.target.value)
                    setValidationInterviewPage(1)
                  }}
                  className="flex h-9 rounded-md border border-input bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">{t('offerDetail.allStatuses', 'Tous les statuts')}</option>
                  {VALIDATION_INTERVIEW_STATUS_FILTERS.map((status) => (
                    <option key={status} value={status}>
                      {t(`interviewStatus.${status}`, status)}
                    </option>
                  ))}
                </select>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open('/interview_audio/login', '_blank')}
                className="flex items-center gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                {t('offerDetail.loginPage', 'Page de connexion')}
              </Button>

              <Dropdown
                trigger={
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2 border-primary/20 hover:bg-primary/5"
                  >
                    <Sparkles className="h-4 w-4 text-primary" />
                    {t('offerDetail.automation', 'Automatisation')}
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </Button>
                }
              >
                <div className="p-4 min-w-[240px] space-y-4">
                  <div className="flex items-center justify-between gap-4">
                    <label htmlFor="auto-generate-interviews-dropdown" className="text-sm font-medium cursor-pointer">
                      {t('offerDetail.autoGenerate', 'Génération automatique')}
                    </label>
                    <Switch
                      id="auto-generate-interviews-dropdown"
                      checked={autoGenerateInterviews}
                      onCheckedChange={handleAutoGenerateToggle}
                      disabled={isOfferClosed || Boolean(offer?.manual_interview_questions_created)}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <label htmlFor="auto-send-emails-dropdown" className="text-sm font-medium cursor-pointer">
                      {t('offerDetail.autoSend', 'Envoi automatique d\'emails')}
                    </label>
                    <Switch
                      id="auto-send-emails-dropdown"
                      checked={autoSendInterviewEmails}
                      onCheckedChange={handleAutoSendEmailsToggle}
                      disabled={isOfferClosed}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <label htmlFor="auto-evaluate-interviews-dropdown" className="text-sm font-medium cursor-pointer">
                      {t('offerDetail.autoEvaluate', 'Évaluation automatique')}
                    </label>
                    <Switch
                      id="auto-evaluate-interviews-dropdown"
                      checked={autoEvaluateInterviews}
                      onCheckedChange={handleAutoEvaluateToggle}
                      disabled={isOfferClosed}
                    />
                  </div>
                </div>
              </Dropdown>

              <Dropdown
                trigger={
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2 border-primary/20 hover:bg-primary/5"
                  >
                    <Settings2 className="h-4 w-4" />
                    {t('common.actions', 'Actions')}
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </Button>
                }
              >
                <DropdownItem
                  onClick={handleOpenBatchGenerateModal}
                  className={!canLaunchBatchGeneration ? 'opacity-50 pointer-events-none' : ''}
                >
                  <div className="flex items-center gap-2">
                    {isGeneratingBatch ? (
                      <Clock className="h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4 text-primary" />
                    )}
                    <span>{t('offerDetail.launchBatchGeneration', 'Lancer la génération des entretiens')}</span>
                  </div>
                </DropdownItem>

                <DropdownItem
                  onClick={async () => {
                    if (!canCreateManualQuestions) return
                    if (!guardOfferOpenForMutations(
                      'offerDetail.cannotUseValidationInterviewsClosed',
                      'Validation interview actions are disabled while this offer is closed.'
                    )) return
                    if (!requireSavedInterviewDeadline()) return
                    if (!offer) return
                    try {
                      const { data } = await offersApi.getById(offer.id)
                      setOffer(data)
                      if (Array.isArray(data.common_questions)) {
                        setCommonQuestions(data.common_questions)
                      }
                      setManualInterviewLanguage(
                        (data.interview_language as 'fr' | 'en' | 'custom') || 'fr'
                      )
                      setInterviewLanguage(
                        (data.interview_language as 'fr' | 'en' | 'custom') || 'fr'
                      )
                      setIsCreatingManualQuestions(true)
                      setShowCommonQuestionsModal(true)
                      if (data.manual_interview_questions_created) {
                        addToast({
                          type: 'info',
                          title: t('common.info', 'Info'),
                          message: t(
                            'offerDetail.manualQuestionsAlreadyCreated',
                            'Les questions manuelles ont déjà été créées pour cette offre. L\'ajout n\'est plus possible.'
                          ),
                        })
                      }
                    } catch (error) {
                      console.error('Error opening manual questions modal:', error)
                      setManualInterviewLanguage(
                        (offer.interview_language as 'fr' | 'en' | 'custom') || 'fr'
                      )
                      setIsCreatingManualQuestions(true)
                      setShowCommonQuestionsModal(true)
                    }
                  }}
                  className={!canCreateManualQuestions ? 'opacity-50 pointer-events-none' : ''}
                >
                  <div
                    className="flex items-center gap-2"
                    title={
                      hasAiGeneratedInterviewQuestions
                        ? t(
                            'offerDetail.manualQuestionsDisabledAfterAiGeneration',
                            'Des questions ont déjà été générées via la génération des entretiens. La création manuelle n\'est plus possible.'
                          )
                        : undefined
                    }
                  >
                    <Plus className="h-4 w-4 text-emerald-500" />
                    <span>{t('offerDetail.createManualQuestions', 'Créer manuellement les questions')}</span>
                  </div>
                </DropdownItem>

                <DropdownItem
                  onClick={() => {
                    if (!canLaunchBatchGeneration) return
                    if (!guardOfferOpenForMutations(
                      'offerDetail.cannotUseValidationInterviewsClosed',
                      'Validation interview actions are disabled while this offer is closed.'
                    )) return
                    setIsCreatingManualQuestions(false)
                    // Questions Communes always use the questions_communes section.
                    const source = (commonQuestions.length > 0
                      ? commonQuestions
                      : (offer?.common_questions || [])) as Array<string | CommonQuestion>
                    setCommonQuestions(
                      source.map(question => {
                        if (typeof question === 'string') {
                          return {
                            question,
                            duration: 120,
                            description: '',
                            section: 'questions_communes' as InterviewQuestionSection,
                          }
                        }
                        return {
                          ...question,
                          section: 'questions_communes' as InterviewQuestionSection,
                        }
                      })
                    )
                    setShowCommonQuestionsModal(true)
                  }}
                  className={!canLaunchBatchGeneration ? 'opacity-50 pointer-events-none' : ''}
                >
                  <div className="flex items-center gap-2">
                    <Plus className="h-4 w-4 text-blue-500" />
                    <span>{t('offerDetail.manageCommonQuestions', 'Questions Communes')}</span>
                  </div>
                </DropdownItem>

                <DropdownItem
                  onClick={handleBatchSendInvitations}
                  className={isOfferClosed || isGeneratingBatch || isSendingBatchInvitations || !interviewAudios.some(a => a.status === 'generated' || a.status === 'failed') ? 'opacity-50 pointer-events-none' : ''}
                >
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-amber-500" />
                    <span>{t('offerDetail.sendAllInvitations', 'Envoyer toutes les invitations')}</span>
                  </div>
                </DropdownItem>

                <DropdownItem
                  onClick={handleBatchEvaluate}
                  className={isOfferClosed || isBatchEvaluating || loadingInterviewsList || !canBatchEvaluateInterviews ? 'opacity-50 pointer-events-none' : ''}
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-emerald-600" />
                    <span>{t('offerDetail.verifyAll', 'Évaluer tout')}</span>
                  </div>
                </DropdownItem>

                <DropdownItem
                  onClick={() => {
                    const currentWeights = (offer?.section_weights && Object.keys(offer.section_weights).length > 0)
                      ? { ...DEFAULT_SECTION_WEIGHTS, ...offer.section_weights }
                      : DEFAULT_SECTION_WEIGHTS;
                    setEditingSectionWeights(currentWeights)
                    setShowSectionWeightsModal(true)
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Scale className="h-4 w-4 text-slate-500" />
                    <span>{t('offerDetail.modifyWeights', 'Modifier les poids')}</span>
                  </div>
                </DropdownItem>
              </Dropdown>

            </div>
          </CardHeader>
          <CardContent>
            <div className="max-h-[min(640px,calc(100vh-16rem))] overflow-auto rounded-md border shadow-sm">
              <table className="w-full min-w-[1100px] border-separate border-spacing-0 text-sm">
                <thead className="sticky top-0 z-10">
                  <tr>
                    <th className="sticky top-0 whitespace-nowrap border-b bg-muted px-4 py-3 text-left font-medium">{t('common.candidate', 'Candidat')}</th>
                    <th className="sticky top-0 whitespace-nowrap border-b bg-muted px-4 py-3 text-left font-medium">{t('offerDetail.cvMatchingScore', 'Score matching du CV')}</th>
                    <th className="sticky top-0 whitespace-nowrap border-b bg-muted px-4 py-3 text-left font-medium">{t('offerDetail.interviewScore', 'Score entretien')}</th>
                    <th className="sticky top-0 whitespace-nowrap border-b bg-muted px-4 py-3 text-left font-medium">{t('offerDetail.accessCode', 'Code d\'accès')}</th>
                    <th className="sticky top-0 whitespace-nowrap border-b bg-muted px-4 py-3 text-left font-medium">{t('common.status', 'État')}</th>
                    <th className="sticky top-0 whitespace-nowrap border-b bg-muted px-4 py-3 text-left font-medium">{t('offerDetail.questions', 'Questions')}</th>
                    <th className="sticky top-0 whitespace-nowrap border-b bg-muted px-4 py-3 text-left font-medium">{t('offerDetail.interviewResponses', 'Réponses')}</th>
                    <th className="sticky top-0 whitespace-nowrap border-b bg-muted px-4 py-3 text-left font-medium">{t('common.video', 'Vidéo')}</th>
                    <th className="sticky top-0 whitespace-nowrap border-b bg-muted px-4 py-3 text-left font-medium">{t('common.language', 'Langue')}</th>
                    <th className="sticky top-0 whitespace-nowrap border-b bg-muted px-4 py-3 text-left font-medium">{t('common.date', 'Date')}</th>
                    <th className="sticky top-0 whitespace-nowrap border-b bg-muted px-4 py-3 text-left font-medium">{t('offerDetail.evaluation', 'Évaluation')}</th>
                    <th className="sticky top-0 border-b bg-muted px-4 py-3 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {(loadingInterviewsList || loadingValidationCandidates) && validationInterviewRows.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="px-4 py-8 text-center text-muted-foreground">
                        {t('common.loading', 'Chargement...')}
                      </td>
                    </tr>
                  ) : filteredValidationInterviewRows.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="px-4 py-8 text-center text-muted-foreground">
                        {interviewStatusFilter === 'all'
                          ? t('offerDetail.noValidationCandidates', 'Aucun candidat pour les entretiens de validation.')
                          : t('offerDetail.noValidationCandidatesForStatus', 'Aucun entretien avec ce statut.')}
                      </td>
                    </tr>
                  ) : (
                    pagedValidationInterviewRows.map((row) => {
                      if (row.type === 'pending') {
                        const app = row.application
                        return (
                          <tr key={`pending-${app.id}`} className="border-b transition-colors hover:bg-muted/30 bg-muted/10">
                            <td className="px-4 py-3">
                              <div className="font-medium text-foreground">{app.candidate_name}</div>
                              <div className="text-xs text-muted-foreground">{app.candidate_email}</div>
                            </td>
                            <td className="px-4 py-3">
                              <Badge
                                variant="outline"
                                className={`text-xs px-2 py-1 ${(app.match_score ?? 0) >= (minimumScore ?? 70)
                                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                  }`}
                              >
                                {Math.floor(app.match_score ?? 0)}%
                              </Badge>
                            </td>
                            <td className="px-4 py-3"><span className="text-muted-foreground">-</span></td>
                            <td className="px-4 py-3"><span className="text-muted-foreground">-</span></td>
                            <td className="px-4 py-3">
                              <Badge variant="outline" className="text-muted-foreground border-dashed">
                                {t('interviewStatus.pending_generation', 'En attente de génération')}
                              </Badge>
                            </td>
                            <td className="px-4 py-3"><span className="text-muted-foreground">-</span></td>
                            <td className="px-4 py-3"><span className="text-muted-foreground">-</span></td>
                            <td className="px-4 py-3"><span className="text-muted-foreground">-</span></td>
                            <td className="px-4 py-3"><span className="text-muted-foreground">-</span></td>
                            <td className="px-4 py-3"><span className="text-muted-foreground">-</span></td>
                            <td className="px-4 py-3"><span className="text-muted-foreground">-</span></td>
                            <td className="px-4 py-3 text-right" />
                          </tr>
                        )
                      }

                      const audio = row.audio
                      return (
                        <tr key={audio.id} className="border-b transition-colors hover:bg-muted/30">
                          <td className="px-4 py-3">
                            <div className="font-medium text-foreground">{audio.candidate_name}</div>
                            <div className="text-xs text-muted-foreground">{audio.candidate_email}</div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              variant="outline"
                              className={`text-xs px-2 py-1 ${(audio.match_score ?? 0) >= (minimumScore ?? 70)
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                }`}
                            >
                              {Math.floor(audio.match_score ?? 0)}%
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            {audio?.score ? (() => {
                              try {
                                const scoreData = JSON.parse(audio.score);
                                const globalScore = scoreData.global_score;
                                return (
                                  <div
                                    onClick={() => openEvaluationViewer(audio, scoreData)}
                                    className="cursor-pointer inline-block"
                                  >
                                    <Badge
                                      variant="outline"
                                      className={`text-xs px-2 py-1 ${globalScore >= 70
                                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200'
                                        : globalScore >= 40
                                          ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200'
                                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200'
                                        }`}
                                    >
                                      {globalScore}%
                                      <Brain className="ml-1 h-3 w-3" />
                                    </Badge>
                                  </div>
                                );
                              } catch (e) {
                                return <span className="text-muted-foreground">-</span>;
                              }
                            })() : <span className="text-muted-foreground">-</span>}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs">
                            {audio?.access_code ? (
                              <div className="flex items-center gap-2">
                                <span>{audio.access_code}</span>
                                <button
                                  onClick={() => copyToClipboard(audio.access_code)}
                                  className="text-muted-foreground hover:text-primary transition-colors"
                                  title={t('common.copy', 'Copier')}
                                >
                                  <Copy className="h-3 w-3" />
                                </button>
                              </div>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              variant={
                                audio.status === 'completed' ? 'default' :
                                  audio.status === 'failed' || audio.status === 'missed' ? 'destructive' :
                                    'outline'
                              }
                              className={`capitalize ${audio.status === 'partial'
                                ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 border-orange-200'
                                : ''}`}
                            >
                              {String(t(`interviewStatus.${audio.status}`, audio.status))}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            {(() => {
                              const generatedQuestionsCount = audio?.questions
                                ? (audio.language === 'custom' && audio.questions.fr
                                  ? Object.values(audio.questions.fr).flat().length
                                  : Object.values(audio.questions).flat().length)
                                : 0
                              const commonQuestionsCount = Array.isArray(audio?.common_questions)
                                ? audio.common_questions.length
                                : 0
                              const totalQuestionsCount = generatedQuestionsCount + commonQuestionsCount

                              return totalQuestionsCount > 0 ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedInterviewForResponses(audio)
                                    setIsViewingOnlyQuestions(true)
                                    setIsResponseModalOpen(true)
                                  }}
                                  className="flex items-center gap-2"
                                >
                                  <FileText className="h-4 w-4" />
                                  {totalQuestionsCount} {t('offerDetail.questions', 'Questions')}
                                </Button>
                              ) : '-'
                            })()}
                          </td>
                          <td className="px-4 py-3">
                            {audio?.interview_responses && audio.interview_responses.length > 0 ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleShowResponses(audio)}
                                className="flex items-center gap-2"
                              >
                                <FileText className="h-4 w-4" />
                                {t('offerDetail.viewResponses', 'Réponses')}
                              </Button>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3">
                            {audio?.video_file || (audio?.interview_responses && audio.interview_responses.some((r: any) => r.video_segment)) ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedInterviewForResponses(audio)
                                  const firstSegment = audio.interview_responses?.find((r: any) => r.video_segment)
                                  if (firstSegment) {
                                    setSelectedVideoUrl(firstSegment.video_segment)
                                    setActiveSegmentIndex(audio.interview_responses.indexOf(firstSegment))
                                  } else {
                                    setSelectedVideoUrl(audio.video_file)
                                    setActiveSegmentIndex(0)
                                  }
                                  setVideoModalTitle(`${t('offerDetail.interviewWith', 'Entretien de')} ${audio.candidate_name}`)
                                  setIsVideoModalOpen(true)
                                }}
                                className="flex items-center gap-2"
                              >
                                <Video className="h-4 w-4" />
                                {t('common.video', 'Vidéo')}
                              </Button>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className="uppercase text-[10px]">
                              {audio.language || 'fr'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {formatDate(audio.created_at)}
                          </td>
                          <td className="px-4 py-3">
                            {audio?.score ? (() => {
                              try {
                                const scoreData = typeof audio.score === 'string' ? JSON.parse(audio.score) : audio.score
                                const evaluationSource = getInterviewEvaluationSource(audio)
                                return (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openEvaluationViewer(audio, scoreData)}
                                    className="flex items-center gap-2 text-primary hover:text-primary hover:bg-primary/10 border-primary/30"
                                  >
                                    {evaluationSource === 'human' ? (
                                      <FileEdit className="h-4 w-4" />
                                    ) : (
                                      <Brain className="h-4 w-4" />
                                    )}
                                    {t('offerDetail.viewEvaluation', 'Voir')}
                                  </Button>
                                )
                              } catch (e) {
                                return <span className="text-muted-foreground">-</span>
                              }
                            })() : <span className="text-muted-foreground">-</span>}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex flex-col items-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSendInvitation(audio.id)}
                                disabled={isOfferClosed || audio.status !== 'generated' || isSendingInvitation === audio.id}
                                loading={isSendingInvitation === audio.id}
                                className="text-primary hover:text-primary hover:bg-primary/10 flex items-center gap-2"
                              >
                                <Mail className="h-4 w-4" />
                                {t('offerDetail.sendInvitation', 'Inviter')}
                              </Button>
                              {(() => {
                                const evaluationSource = getInterviewEvaluationSource(audio)
                                const canStartEvaluation = (audio.status === 'completed' || audio.status === 'partial') && !evaluationSource
                                const isEvaluated = !!evaluationSource
                                return (
                                  <>
                                    {canStartEvaluation && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleEvaluateInterview(audio.id)}
                                        disabled={isOfferClosed || isEvaluating === audio.id}
                                        loading={isEvaluating === audio.id}
                                        className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 flex items-center gap-2"
                                      >
                                        <Sparkles className="h-4 w-4" />
                                        {t('offerDetail.evaluateWithAI', 'Évaluation IA')}
                                      </Button>
                                    )}
                                    {canStartEvaluation && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleOpenManualEvaluation(row)}
                                        disabled={isOfferClosed}
                                        className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 flex items-center gap-2"
                                      >
                                        <FileEdit className="h-4 w-4" />
                                        {t('offerDetail.evaluateManually', 'Évaluation humaine')}
                                      </Button>
                                    )}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      disabled={isOfferClosed || isEvaluated}
                                      onClick={() => {
                                        if (isOfferClosed || isEvaluated) return
                                        setInterviewToDelete(audio)
                                        setShowDeleteInterviewConfirm(true)
                                      }}
                                      title={
                                        isOfferClosed
                                          ? t(
                                              'offerDetail.cannotUseValidationInterviewsClosed',
                                              'Validation interview actions are disabled while this offer is closed.'
                                            )
                                          : isEvaluated
                                          ? t(
                                              'offerDetail.cannotDeleteEvaluatedInterview',
                                              'Impossible de supprimer l\'entretien d\'un candidat déjà évalué'
                                            )
                                          : t('offerDetail.deleteInterview', 'Supprimer l\'entretien')
                                      }
                                      className={`h-8 w-8 p-0 transition-colors ${
                                        isOfferClosed || isEvaluated
                                          ? 'text-muted-foreground/40 cursor-not-allowed opacity-50'
                                          : 'text-muted-foreground hover:text-destructive'
                                      }`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </>
                                )
                              })()}
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={isOfferClosed}
                                onClick={() => {
                                  if (isOfferClosed) return
                                  setInterviewToReset(audio)
                                  setShowResetConfirm(true)
                                }}
                                title={
                                  isOfferClosed
                                    ? t(
                                        'offerDetail.cannotUseValidationInterviewsClosed',
                                        'Validation interview actions are disabled while this offer is closed.'
                                      )
                                    : t('offerDetail.resetInterview', 'Réinitialiser l\'entretien')
                                }
                                className={`h-8 w-8 p-0 transition-colors ${
                                  isOfferClosed
                                    ? 'text-muted-foreground/40 cursor-not-allowed opacity-50'
                                    : 'text-muted-foreground hover:text-destructive'
                                }`}
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
            {filteredValidationInterviewRows.length > VALIDATION_INTERVIEWS_PAGE_SIZE && (
              <div className="flex flex-col items-center gap-2 border-t border-border pt-4">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    disabled={validationInterviewPageSafe === 1}
                    onClick={() => setValidationInterviewPage(1)}
                    title={t('common.firstPage', 'Première page')}
                  >
                    <ChevronsLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    disabled={validationInterviewPageSafe === 1}
                    onClick={() => setValidationInterviewPage(validationInterviewPageSafe - 1)}
                    title={t('common.previousPage', 'Page précédente')}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="px-2 text-sm font-medium">
                    {t('common.page', 'Page')} {validationInterviewPageSafe} / {validationInterviewTotalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    disabled={validationInterviewPageSafe >= validationInterviewTotalPages}
                    onClick={() => setValidationInterviewPage(validationInterviewPageSafe + 1)}
                    title={t('common.nextPage', 'Page suivante')}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    disabled={validationInterviewPageSafe >= validationInterviewTotalPages}
                    onClick={() => setValidationInterviewPage(validationInterviewTotalPages)}
                    title={t('common.lastPage', 'Dernière page')}
                  >
                    <ChevronsRight className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('offerDetail.validationInterviewsPagination', {
                    from: (validationInterviewPageSafe - 1) * VALIDATION_INTERVIEWS_PAGE_SIZE + 1,
                    to: Math.min(
                      validationInterviewPageSafe * VALIDATION_INTERVIEWS_PAGE_SIZE,
                      filteredValidationInterviewRows.length
                    ),
                    total: filteredValidationInterviewRows.length,
                    defaultValue: `Affichage de ${(validationInterviewPageSafe - 1) * VALIDATION_INTERVIEWS_PAGE_SIZE + 1} à ${Math.min(validationInterviewPageSafe * VALIDATION_INTERVIEWS_PAGE_SIZE, filteredValidationInterviewRows.length)} sur ${filteredValidationInterviewRows.length} entretiens`,
                  })}
                </p>
              </div>
            )}
          </CardContent>
        </Card >
        </OfferDetailFullPageOverlay>
      )}

      {openPanel === 'details' && (
        <OfferDetailFullPageOverlay
          title={t('offerDetail.details')}
          onClose={() => setOpenPanel(null)}
        >
          <div className="grid gap-6 lg:grid-cols-3 max-[1149px]:grid-cols-1">
            <div className="lg:col-span-2 space-y-6 max-[1149px]:space-y-4">
              {/* Job Overview */}
              <Card>
                <CardHeader className="max-[1149px]:p-4">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="flex items-center space-x-2 max-[1149px]:text-base">
                      <Building2 className="h-5 w-5 max-[1149px]:h-4 max-[1149px]:w-4" />
                      <span>{t('createOffer.jobOverview', 'Job Overview')}</span>
                    </CardTitle>
                    {renderSectionEditButton('job_overview')}
                  </div>
                </CardHeader>
                {renderSectionError('job_overview')}
                <CardContent className="space-y-4 max-[1149px]:p-4 max-[1149px]:space-y-3">
                  {editingSections.job_overview ? (() => {
                    const jobOverviewDraft = getActiveDraft('job_overview')
                    return (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-[1149px]:gap-3">
                          <div>
                            <label className="text-sm font-medium text-muted-foreground max-[1149px]:text-xs">{t('createOffer.jobTitle')}</label>
                            <Input
                              value={jobOverviewDraft.job_title}
                              onChange={(e) => {
                                updateSectionDraft('job_overview', draft => ({
                                  ...draft,
                                  job_title: e.target.value
                                }))
                                // Validate on change for real-time feedback
                                setTimeout(() => validateSectionOnChange('job_overview'), 100)
                              }}
                              onBlur={() => validateSectionOnBlur('job_overview')}
                              className={getFieldError('job_overview', 'job_title') ? 'mt-1 border-destructive' : 'mt-1'}
                            />
                            {getFieldError('job_overview', 'job_title') && (
                              <p className="mt-1 text-sm text-destructive">{getFieldError('job_overview', 'job_title')}</p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground max-[1149px]:text-xs">{t('createOffer.department')}</label>
                            <div className="mt-1">
                              <DepartmentSelect
                                value={jobOverviewDraft.department || ''}
                                onChange={(newDepartment) =>
                                  updateSectionDraft('job_overview', draft => ({
                                    ...draft,
                                    department: newDepartment
                                  }))
                                }
                                placeholder={t('createOffer.form.departmentPlaceholder')}
                                defaultDepartments={defaultDepartments}
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground max-[1149px]:text-xs">{t('createOffer.contractType')}</label>
                            <div className="mt-1">
                              <CustomSelect
                                value={jobOverviewDraft.contract_type}
                                onChange={(value) =>
                                  updateSectionDraft('job_overview', draft => ({
                                    ...draft,
                                    contract_type: value
                                  }))
                                }
                                options={contractTypes}
                                placeholder={t('createOffer.form.contractTypePlaceholder')}
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground max-[1149px]:text-xs">{t('createOffer.experienceLevel')}</label>
                            <div className="mt-1">
                              <Input
                                type="number"
                                value={jobOverviewDraft.experience_level ?? ''}
                                onChange={(e) => {
                                  updateSectionDraft('job_overview', draft => ({
                                    ...draft,
                                    // keep as string (or null) to match Offer typing
                                    experience_level: e.target.value === '' ? null : e.target.value
                                  }))
                                  setTimeout(() => validateSectionOnChange('job_overview'), 100)
                                }}
                                onBlur={() => validateSectionOnBlur('job_overview')}
                                min={0}
                                step={1}
                                placeholder={t('createOffer.form.experienceLevelPlaceholder')}
                              />
                            </div>
                            <div className="mt-2 flex items-center justify-between rounded-lg border border-border px-3 py-2">
                              <div>
                                <p className="text-sm font-medium text-muted-foreground max-[1149px]:text-xs">
                                  {t('offerDetail.includeInternshipExperience', 'Internship counted')}
                                </p>
                              </div>
                              <Switch
                                checked={includeInternshipExperience}
                                onCheckedChange={handleStageExperienceToggle}
                                disabled={isOfferModificationLocked}
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground max-[1149px]:text-xs">{t('createOffer.educationLevel')}</label>
                            <div className="mt-1">
                              <CustomSelect
                                value={jobOverviewDraft.education_level}
                                onChange={(value) =>
                                  updateSectionDraft('job_overview', draft => ({
                                    ...draft,
                                    education_level: value
                                  }))
                                }
                                onBlur={() => validateSectionOnBlur('job_overview')}
                                options={educationLevels}
                                placeholder={t('createOffer.form.educationLevelPlaceholder')}
                              />
                            </div>
                          </div>
                          <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                            <div>
                              <p className="text-sm font-medium text-muted-foreground max-[1149px]:text-xs">{t('createOffer.remoteAllowed')}</p>
                              <p className="text-xs text-muted-foreground">{t('offerDetail.remoteToggleHelper', 'Toggle to allow remote work')}</p>
                            </div>
                            <Switch
                              checked={jobOverviewDraft.remote_allowed}
                              onCheckedChange={(checked) =>
                                updateSectionDraft('job_overview', draft => ({
                                  ...draft,
                                  remote_allowed: Boolean(checked)
                                }))
                              }
                            />
                          </div>
                        </div>
                        {renderSectionEditorActions('job_overview')}
                      </div>
                    )
                  })() : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-[1149px]:gap-3">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground max-[1149px]:text-xs">{t('createOffer.jobTitle')}</label>
                        <p className="text-lg font-semibold max-[1149px]:text-base">{getJobTitle()}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground max-[1149px]:text-xs">{t('createOffer.department')}</label>
                        <p className="text-lg max-[1149px]:text-base">{offer.department || t('common.none', 'Not specified')}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground max-[1149px]:text-xs">{t('createOffer.contractType')}</label>
                        <p className="text-lg max-[1149px]:text-base">{offer.contract_type || t('common.none', 'Not specified')}</p>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <label className="text-sm font-medium text-muted-foreground max-[1149px]:text-xs">{t('createOffer.experienceLevel')}</label>
                          <div className="inline-flex items-center gap-1.5">
                            <span className="text-xs text-muted-foreground max-[1149px]:text-[11px]">
                              {t('offerDetail.includeInternshipExperience', 'Exp. stage')}
                            </span>
                            <Switch
                              checked={includeInternshipExperience}
                              onCheckedChange={handleStageExperienceToggle}
                              disabled={isOfferModificationLocked}
                            />
                          </div>
                        </div>
                        <div className="mt-1 flex items-center gap-2">
                          <p className="text-lg max-[1149px]:text-base">
                            {offer.experience_level !== undefined && offer.experience_level !== null && offer.experience_level !== ''
                              ? String(offer.experience_level)
                              : t('common.none', 'Not specified')}
                          </p>
                          <button
                            onClick={() => toggleLock('experience_level')}
                            disabled={isOfferModificationLocked}
                            className={`transition-all rounded-full p-1 ${isOfferModificationLocked
                              ? getPublishedLockButtonClass(isLocked('experience_level'))
                              : isLocked('experience_level')
                                ? 'bg-blue-500 text-white'
                                : 'text-gray-400 hover:text-gray-600'
                              }`}
                          >
                            {isLocked('experience_level') ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground max-[1149px]:text-xs">{t('createOffer.educationLevel')}</label>
                        <div className="flex items-center gap-2">
                          <p className="text-lg max-[1149px]:text-base">{offer.education_level || t('common.none', 'Not specified')}</p>
                          <button
                            onClick={() => toggleLock('education_level')}
                            disabled={isOfferModificationLocked}
                            className={`transition-all rounded-full p-1 ${isOfferModificationLocked
                              ? getPublishedLockButtonClass(isLocked('education_level'))
                              : isLocked('education_level')
                                ? 'bg-blue-500 text-white'
                                : 'text-gray-400 hover:text-gray-600'
                              }`}
                          >
                            {isLocked('education_level') ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground max-[1149px]:text-xs">{t('createOffer.remoteAllowed')}</label>
                        <Badge variant={offer.remote_allowed ? "default" : "secondary"} className="max-[1149px]:text-xs">
                          {offer.remote_allowed ? t('common.yes') : t('common.no')}
                        </Badge>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Description */}
              <Card>
                <CardHeader className="max-[1149px]:p-4 max-[640px]:p-3">
                  <div className="flex items-center justify-between gap-2 max-[640px]:flex-col max-[640px]:items-stretch max-[640px]:gap-3">
                    <CardTitle className="flex items-center space-x-2 max-[1149px]:text-base max-[640px]:text-sm">
                      <FileText className="h-5 w-5 max-[1149px]:h-4 max-[1149px]:w-4 max-[640px]:h-3.5 max-[640px]:w-3.5" />
                      <span>{t('createOffer.description')}</span>
                    </CardTitle>
                    <div className="flex items-center gap-2 max-[640px]:w-full max-[640px]:justify-between">
                      {renderSectionEditButton('description')}
                    </div>
                  </div>
                </CardHeader>
                {renderSectionError('description')}
                <CardContent className="max-[1149px]:p-4 max-[640px]:p-3">
                  {editingSections.description ? (() => {
                    const descriptionDraft = getActiveDraft('description')
                    return (
                      <div className="space-y-4 max-[640px]:space-y-3">
                        <SimpleRichTextEditor
                          value={descriptionDraft.description_text || descriptionDraft.description}
                          onChange={(value) =>
                            updateSectionDraft('description', draft => ({
                              ...draft,
                              description: value,
                              description_text: value
                            }))
                          }
                          height={220}
                        />
                        {renderSectionEditorActions('description')}
                      </div>
                    )
                  })() : (
                    <div className="space-y-4 max-[640px]:space-y-3">
                      {/* AI Description Preview if available and not used */}
                      {offer?.ai_description && !offer.ai_description_used && (
                        <div className="border border-primary/20 rounded-lg p-4 bg-primary/5 space-y-3 max-[640px]:p-3 max-[640px]:space-y-2">
                          <div className="flex items-center justify-between max-[640px]:flex-col max-[640px]:items-stretch max-[640px]:gap-2">
                            <div className="flex items-center gap-2 max-[640px]:flex-wrap">
                              <Sparkles className="h-4 w-4 text-primary max-[640px]:h-3.5 max-[640px]:w-3.5 flex-shrink-0" />
                              <span className="text-sm font-medium text-primary max-[640px]:text-xs">
                                {t('offerDetail.aiImprovedVersion', 'AI Improved Version')}
                              </span>
                              {offer.ai_generated_at && (
                                <span className="text-xs text-muted-foreground max-[640px]:text-[10px] max-[640px]:hidden sm:inline">
                                  {t('offerDetail.generatedAt', 'Generated')} {new Date(offer.ai_generated_at).toLocaleString()}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 max-[640px]:w-full max-[640px]:justify-stretch">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                  if (!offer) return
                                  try {
                                    // Clear AI description by updating the offer
                                    const response = await offersApi.update(offer.id, {
                                      ai_description: null,
                                      ai_description_used: false,
                                      ai_generated_at: null,
                                      ai_generated_by: null
                                    })
                                    setOffer(response.data)
                                    addToast({
                                      type: 'success',
                                      title: t('offerDetail.descriptionCancelled', 'Version cancelled'),
                                      message: t('offerDetail.descriptionCancelledMessage', 'AI improved version has been cancelled.')
                                    })
                                  } catch (error: any) {
                                    console.error('Error cancelling AI description:', error)
                                    addToast({
                                      type: 'error',
                                      title: t('common.error'),
                                      message: error.response?.data?.error || t('offerDetail.descriptionCancelFailed', 'Failed to cancel AI description')
                                    })
                                  }
                                }}
                                className="max-[1149px]:h-8 max-[1149px]:text-xs max-[640px]:h-7 max-[640px]:text-[11px] max-[640px]:px-2 max-[640px]:flex-1"
                              >
                                <X className="h-4 w-4 mr-1 max-[1149px]:h-3 max-[1149px]:w-3 max-[640px]:h-2.5 max-[640px]:w-2.5 max-[640px]:mr-0" />
                                <span className="max-[640px]:hidden sm:inline">{t('common.cancel')}</span>
                              </Button>
                              <Button
                                variant="default"
                                size="sm"
                                onClick={async () => {
                                  if (!offer) return
                                  setApplyingAiDescription(true)
                                  try {
                                    const response = await offersApi.applyAiDescription(offer.id)
                                    setOffer(response.data.offer)
                                    addToast({
                                      type: 'success',
                                      title: t('offerDetail.descriptionApplied', 'Description applied'),
                                      message: t('offerDetail.descriptionAppliedMessage', 'AI description has been applied successfully.')
                                    })
                                  } catch (error: any) {
                                    console.error('Error applying AI description:', error)
                                    addToast({
                                      type: 'error',
                                      title: t('common.error'),
                                      message: error.response?.data?.error || t('offerDetail.descriptionApplyFailed', 'Failed to apply AI description')
                                    })
                                  } finally {
                                    setApplyingAiDescription(false)
                                  }
                                }}
                                loading={applyingAiDescription}
                                className="max-[1149px]:h-8 max-[1149px]:text-xs max-[640px]:h-7 max-[640px]:text-[11px] max-[640px]:px-2 max-[640px]:flex-1"
                              >
                                <Check className="h-4 w-4 mr-1 max-[1149px]:h-3 max-[1149px]:w-3 max-[640px]:h-2.5 max-[640px]:w-2.5 max-[640px]:mr-0" />
                                <span className="max-[640px]:hidden sm:inline">{t('offerDetail.apply', 'Apply')}</span>
                              </Button>
                            </div>
                          </div>
                          <div
                            className="prose prose-sm max-w-none max-[1149px]:prose-xs max-[1149px]:text-sm max-[640px]:prose-xs max-[640px]:text-xs [&_p]:max-[640px]:text-xs [&_ul]:max-[640px]:text-xs [&_ol]:max-[640px]:text-xs [&_li]:max-[640px]:text-xs [&_h1]:max-[640px]:text-sm [&_h2]:max-[640px]:text-sm [&_h3]:max-[640px]:text-xs [&_strong]:max-[640px]:text-xs"
                            dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(offer.ai_description) }}
                          />
                        </div>
                      )}
                      {/* Main Description */}
                      <div
                        className="prose prose-sm max-w-none max-[1149px]:prose-xs max-[1149px]:text-sm max-[640px]:prose-xs max-[640px]:text-xs [&_p]:max-[640px]:text-xs [&_ul]:max-[640px]:text-xs [&_ol]:max-[640px]:text-xs [&_li]:max-[640px]:text-xs [&_h1]:max-[640px]:text-sm [&_h2]:max-[640px]:text-sm [&_h3]:max-[640px]:text-xs [&_strong]:max-[640px]:text-xs"
                        dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(getDescription() || t('offerDetail.noDescription', 'No description available')) }}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Hard Skills */}
              <Card>
                <CardHeader className="max-[1149px]:p-4">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="flex items-center space-x-2 max-[1149px]:text-base">
                      <CheckCircle className="h-5 w-5 max-[1149px]:h-4 max-[1149px]:w-4" />
                      <span>{t('offerDetail.hardSkills', 'Hard Skills')}</span>
                    </CardTitle>
                    {renderSectionEditButton('requirements')}
                  </div>
                </CardHeader>
                {renderSectionError('requirements')}
                <CardContent className="max-[1149px]:p-4">
                  {editingSections.requirements ? (() => {
                    const requirementsDraft = getActiveDraft('requirements')
                    return (
                      <div className="space-y-6">
                        {/* Mentioned Hard Skills */}
                        <div className="space-y-2">
                          <div className="text-sm font-medium text-foreground">
                            {t('offerDetail.hardSkillsMentioned', 'Compétences techniques')}
                          </div>
                          <DynamicList
                            items={requirementsDraft.requirements_mentionnees}
                            onChange={(items) =>
                              updateSectionDraft('requirements', draft => ({
                                ...draft,
                                requirements_mentionnees: items
                              }))
                            }
                            onBlur={() => validateSectionOnBlur('requirements')}
                            placeholder={t('offerDetail.requirementPlaceholder', 'Add a requirement')}
                            minItems={0}
                            maxItems={50}
                          />
                        </div>

                        {/* AI Recommended Hard Skills */}
                        {requirementsDraft.requirements_non_mentionnees?.length > 0 && (
                          <div className="space-y-2">
                            <div className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                              <Lightbulb className="h-4 w-4 text-amber-500" />
                              {t('offerDetail.hardSkillsRecommended', 'Recommandé par l\'IA')}
                            </div>
                            <DynamicList
                              items={requirementsDraft.requirements_non_mentionnees}
                              onChange={(items) =>
                                updateSectionDraft('requirements', draft => ({
                                  ...draft,
                                  requirements_non_mentionnees: items
                                }))
                              }
                              onBlur={() => validateSectionOnBlur('requirements')}
                              placeholder={t('offerDetail.aiRecommendedPlaceholder', 'AI recommended skill')}
                              minItems={0}
                              maxItems={50}
                              allowAdd={false}
                              allowEdit={false}
                            />
                          </div>
                        )}

                        {renderSectionEditorActions('requirements')}
                      </div>
                    )
                  })() : (
                    <>
                      {displayedHardSkills.length > 0 ? (
                        <div className="flex flex-wrap gap-3 max-[1149px]:gap-2">
                          {sortedHardSkills.map((req, index) => {
                            const weightValue = hardSkillWeights[req] ?? 1
                            const isSectionLockedVal = isSectionLocked('requirements')
                            const isDisabled = isSectionLockedVal || isOfferModificationLocked
                            const itemKey = `hardskill_${req}`

                            // Determine badge color based on weight
                            const getBadgeColor = () => {
                              if (weightValue >= 5) return 'bg-yellow-500 hover:bg-yellow-600 text-white border-yellow-600'
                              if (weightValue > 1) return 'bg-gray-500 hover:bg-gray-600 text-white border-gray-600'
                              return '' // Original color for weight = 1
                            }

                            return (
                              <div key={`${req}-${index}`} className="flex flex-col gap-1">
                                <button
                                  type="button"
                                  onClick={() => handleIncrementHardSkill(req)}
                                  className={`inline-flex items-center gap-2 ${isDisabled ? 'cursor-not-allowed opacity-50' : ''}`}
                                  disabled={isDisabled}
                                >
                                  <div className="relative inline-flex items-center">
                                    <Badge
                                      variant="secondary"
                                      className={`text-sm max-[1149px]:text-xs max-[1149px]:px-1.5 max-[1149px]:py-0.5 ${getBadgeColor()} pr-10 max-[1149px]:pr-11 max-[640px]:pr-12`}
                                    >
                                      {req}
                                    </Badge>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        toggleLock(itemKey)
                                      }}
                                      disabled={isOfferModificationLocked}
                                      className={`absolute right-0 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center transition-all shadow-sm ${isOfferModificationLocked
                                        ? getPublishedLockButtonClass(isLocked(itemKey), 'floating')
                                        : isLocked(itemKey)
                                          ? 'bg-blue-600 text-white'
                                          : 'bg-gray-400 text-white hover:bg-gray-500'
                                        }`}
                                    >
                                      {isLocked(itemKey) ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                                    </button>
                                  </div>
                                  <span className="text-xs text-muted-foreground">{weightValue}/5</span>
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-8 max-[1149px]:py-6">
                          <CheckCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4 max-[1149px]:h-8 max-[1149px]:w-8 max-[1149px]:mb-3" />
                          <p className="text-sm text-muted-foreground max-[1149px]:text-xs">{t('offerDetail.noRequirements', 'No requirements specified')}</p>
                          <p className="text-xs text-muted-foreground mt-1 max-[1149px]:text-[10px]">{t('offerDetail.addRequirements', 'Add job requirements to help candidates understand what\'s needed')}</p>
                        </div>
                      )}

                      {/* AI-Recommended Requirements (non-edit mode) */}
                      {displayedHardSkillsNonMentionnees.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-amber-200">
                          <div className="flex items-center gap-2 mb-3">
                            <Lightbulb className="h-4 w-4 text-amber-500" />
                            <span className="text-sm font-medium text-muted-foreground">
                              {t('offerDetail.aiRecommended', 'Recommandé par l\'IA')}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-3 max-[1149px]:gap-2">
                            {sortedHardSkillsNonMentionnees.map((req, index) => {
                              const weightValue = hardSkillWeights[req] ?? 1
                              const isSectionLockedVal = isSectionLocked('requirements')
                              const isDisabled = isSectionLockedVal || isOfferModificationLocked
                              const itemKey = `hardskill_${req}`

                              const getBadgeColor = () => {
                                if (weightValue >= 5) return 'bg-yellow-500 hover:bg-yellow-600 text-white'
                                if (weightValue > 1) return 'bg-gray-500 hover:bg-gray-600 text-white'
                                return ''
                              }

                              return (
                                <div key={`ai-req-${req}-${index}`} className="flex flex-col gap-1">
                                  <button
                                    type="button"
                                    onClick={() => handleIncrementHardSkill(req)}
                                    className={`inline-flex items-center gap-2 ${isDisabled ? 'cursor-not-allowed opacity-50' : ''}`}
                                    disabled={isDisabled}
                                  >
                                    <div className="relative inline-flex items-center">
                                      <Badge
                                        variant="secondary"
                                        className={`text-sm max-[1149px]:text-xs max-[1149px]:px-1.5 max-[1149px]:py-0.5 ${getBadgeColor()} border-amber-200 pr-10 max-[1149px]:pr-11 max-[640px]:pr-12`}
                                      >
                                        {req}
                                      </Badge>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          toggleLock(itemKey)
                                        }}
                                        disabled={isOfferModificationLocked}
                                        className={`absolute right-0 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center transition-all shadow-sm ${isOfferModificationLocked
                                          ? getPublishedLockButtonClass(isLocked(itemKey), 'floating')
                                          : isLocked(itemKey)
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-400 text-white hover:bg-gray-500'
                                          }`}
                                      >
                                        {isLocked(itemKey) ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                                      </button>
                                    </div>
                                    <span className="text-xs text-muted-foreground">{weightValue}/5</span>
                                  </button>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Soft Skills */}
              <Card>
                <CardHeader className="max-[1149px]:p-4">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="flex items-center space-x-2 max-[1149px]:text-base">
                      <User className="h-5 w-5 max-[1149px]:h-4 max-[1149px]:w-4" />
                      <span>{t('createOffer.softSkills', 'Soft Skills')}</span>
                    </CardTitle>
                    {renderSectionEditButton('soft_skills')}
                  </div>
                </CardHeader>
                {renderSectionError('soft_skills')}
                <CardContent className="max-[1149px]:p-4">
                  {editingSections.soft_skills ? (() => {
                    const softSkillsDraft = getActiveDraft('soft_skills')
                    return (
                      <div className="space-y-6">
                        {/* Mentioned Soft Skills */}
                        <div className="space-y-2">
                          <div className="text-sm font-medium text-foreground">
                            {t('offerDetail.softSkillsMentioned', 'Soft skills')}
                          </div>
                          <DynamicList
                            items={softSkillsDraft.soft_skills_mentionnees}
                            onChange={(items) =>
                              updateSectionDraft('soft_skills', draft => ({
                                ...draft,
                                soft_skills_mentionnees: items
                              }))
                            }
                            onBlur={() => validateSectionOnBlur('soft_skills')}
                            placeholder={t('offerDetail.softSkillPlaceholder', 'Add a soft skill')}
                            minItems={0}
                            maxItems={50}
                          />
                        </div>

                        {/* AI Recommended Soft Skills */}
                        {softSkillsDraft.soft_skills_non_mentionnees?.length > 0 && (
                          <div className="space-y-2">
                            <div className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                              <Lightbulb className="h-4 w-4 text-amber-500" />
                              {t('offerDetail.softSkillsRecommended', 'Recommandé par l\'IA')}
                            </div>
                            <DynamicList
                              items={softSkillsDraft.soft_skills_non_mentionnees}
                              onChange={(items) =>
                                updateSectionDraft('soft_skills', draft => ({
                                  ...draft,
                                  soft_skills_non_mentionnees: items
                                }))
                              }
                              onBlur={() => validateSectionOnBlur('soft_skills')}
                              placeholder={t('offerDetail.aiRecommendedPlaceholder', 'AI recommended skill')}
                              minItems={0}
                              maxItems={50}
                              allowAdd={false}
                              allowEdit={false}
                            />
                          </div>
                        )}

                        {renderSectionEditorActions('soft_skills')}
                      </div>
                    )
                  })() : (
                    <>
                      {displayedSoftSkills.length > 0 ? (
                        <div className="flex flex-wrap gap-3 max-[1149px]:gap-2">
                          {sortedSoftSkills.map((skill, index) => {
                            const weightValue = softSkillWeights[skill] ?? 1
                            const isSectionLockedVal = isSectionLocked('soft_skills')
                            const isDisabled = isSectionLockedVal || isOfferModificationLocked
                            const itemKey = `softskill_${skill}`

                            // Determine badge color based on weight
                            const getBadgeColor = () => {
                              if (weightValue >= 5) return 'bg-yellow-500 hover:bg-yellow-600 text-white border-yellow-600'
                              if (weightValue > 1) return 'bg-gray-500 hover:bg-gray-600 text-white border-gray-600'
                              return '' // Original color for weight = 1
                            }

                            return (
                              <div key={`${skill}-${index}`} className="flex flex-col gap-1">
                                <button
                                  type="button"
                                  onClick={() => handleIncrementSoftSkill(skill)}
                                  className={`inline-flex items-center gap-2 ${isDisabled ? 'cursor-not-allowed opacity-50' : ''}`}
                                  disabled={isDisabled}
                                >
                                  <div className="relative inline-flex items-center">
                                    <Badge
                                      variant="secondary"
                                      className={`text-sm max-[1149px]:text-xs max-[1149px]:px-1.5 max-[1149px]:py-0.5 ${getBadgeColor()} pr-10 max-[1149px]:pr-11 max-[640px]:pr-12`}
                                    >
                                      {skill}
                                    </Badge>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        toggleLock(itemKey)
                                      }}
                                      disabled={isOfferModificationLocked}
                                      className={`absolute right-0 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center transition-all shadow-sm ${isOfferModificationLocked
                                        ? getPublishedLockButtonClass(isLocked(itemKey), 'floating')
                                        : isLocked(itemKey)
                                          ? 'bg-blue-600 text-white'
                                          : 'bg-gray-400 text-white hover:bg-gray-500'
                                        }`}
                                    >
                                      {isLocked(itemKey) ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                                    </button>
                                  </div>
                                  <span className="text-xs text-muted-foreground">{weightValue}/5</span>
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-8 max-[1149px]:py-6">
                          <User className="h-12 w-12 mx-auto text-muted-foreground mb-4 max-[1149px]:h-8 max-[1149px]:w-8 max-[1149px]:mb-3" />
                          <p className="text-sm text-muted-foreground max-[1149px]:text-xs">{t('offerDetail.noSoftSkills', 'No soft skills specified')}</p>
                          <p className="text-xs text-muted-foreground mt-1 max-[1149px]:text-[10px]">{t('offerDetail.addSoftSkills', 'Add interpersonal and communication skills required for this role')}</p>
                        </div>
                      )}

                      {/* AI-Recommended Soft Skills (non-edit mode) */}
                      {displayedSoftSkillsNonMentionnees.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-amber-200">
                          <div className="flex items-center gap-2 mb-3">
                            <Lightbulb className="h-4 w-4 text-amber-500" />
                            <span className="text-sm font-medium text-muted-foreground">
                              {t('offerDetail.aiRecommended', 'Recommandé par l\'IA')}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-3 max-[1149px]:gap-2">
                            {sortedSoftSkillsNonMentionnees.map((skill, index) => {
                              const weightValue = softSkillWeights[skill] ?? 1
                              const isSectionLockedVal = isSectionLocked('soft_skills')
                              const isDisabled = isSectionLockedVal || isOfferModificationLocked
                              const itemKey = `softskill_${skill}`

                              const getBadgeColor = () => {
                                if (weightValue >= 5) return 'bg-yellow-500 hover:bg-yellow-600 text-white'
                                if (weightValue > 1) return 'bg-gray-500 hover:bg-gray-600 text-white'
                                return ''
                              }

                              return (
                                <div key={`ai-skill-${skill}-${index}`} className="flex flex-col gap-1">
                                  <button
                                    type="button"
                                    onClick={() => handleIncrementSoftSkill(skill)}
                                    className={`inline-flex items-center gap-2 ${isDisabled ? 'cursor-not-allowed opacity-50' : ''}`}
                                    disabled={isDisabled}
                                  >
                                    <div className="relative inline-flex items-center">
                                      <Badge
                                        variant="secondary"
                                        className={`text-sm max-[1149px]:text-xs max-[1149px]:px-1.5 max-[1149px]:py-0.5 ${getBadgeColor()} border-amber-200 pr-10 max-[1149px]:pr-11 max-[640px]:pr-12`}
                                      >
                                        {skill}
                                      </Badge>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          toggleLock(itemKey)
                                        }}
                                        disabled={isOfferModificationLocked}
                                        className={`absolute right-0 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center transition-all shadow-sm ${isOfferModificationLocked
                                          ? getPublishedLockButtonClass(isLocked(itemKey), 'floating')
                                          : isLocked(itemKey)
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-400 text-white hover:bg-gray-500'
                                          }`}
                                      >
                                        {isLocked(itemKey) ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                                      </button>
                                    </div>
                                    <span className="text-xs text-muted-foreground">{weightValue}/5</span>
                                  </button>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Certifications */}
              <Card>
                <CardHeader className="max-[1149px]:p-4">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="flex items-center space-x-2 max-[1149px]:text-base">
                      <CheckCircle className="h-5 w-5 max-[1149px]:h-4 max-[1149px]:w-4" />
                      <span>{t('createOffer.certifications')}</span>
                    </CardTitle>
                    {renderSectionEditButton('certifications')}
                  </div>
                </CardHeader>
                {renderSectionError('certifications')}
                <CardContent className="max-[1149px]:p-4">
                  {editingSections.certifications ? (() => {
                    const certificationsDraft = getActiveDraft('certifications')
                    return (
                      <div className="space-y-4">
                        <DynamicList
                          items={certificationsDraft.certifications}
                          onChange={(items) =>
                            updateSectionDraft('certifications', draft => ({
                              ...draft,
                              certifications: items
                            }))
                          }
                          onBlur={() => validateSectionOnBlur('certifications')}
                          placeholder={t('offerDetail.certificationPlaceholder', 'Add a certification')}
                          minItems={0}
                        />
                        {renderSectionEditorActions('certifications')}
                      </div>
                    )
                  })() : (
                    <>
                      {getCertifications().length > 0 ? (
                        <div className="flex flex-wrap gap-2 max-[1149px]:gap-1.5">
                          {getCertifications().map((cert, index) => {
                            const itemKey = `certification_${cert}_${index}`
                            return (
                              <div key={index} className="relative inline-flex items-center">
                                <Badge
                                  variant="secondary"
                                  className="max-[1149px]:text-xs max-[1149px]:px-2 max-[1149px]:py-0.5 pr-10 max-[1149px]:pr-11 max-[640px]:pr-12"
                                >
                                  {cert}
                                </Badge>
                                <button
                                  onClick={() => toggleLock(itemKey)}
                                  disabled={isOfferModificationLocked}
                                  className={`absolute right-0 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center transition-all shadow-sm ${isOfferModificationLocked
                                    ? getPublishedLockButtonClass(isLocked(itemKey), 'floating')
                                    : isLocked(itemKey)
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-gray-400 text-white hover:bg-gray-500'
                                    }`}
                                >
                                  {isLocked(itemKey) ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-8 max-[1149px]:py-6">
                          <CheckCircle className="h-12 w-12 mx-auto text-gray-300 mb-4 max-[1149px]:h-8 max-[1149px]:w-8 max-[1149px]:mb-3" />
                          <p className="text-sm text-muted-foreground max-[1149px]:text-xs">{t('offerDetail.noCertifications', 'No certifications specified')}</p>
                          <p className="text-xs text-gray-400 mt-1 max-[1149px]:text-[10px]">{t('offerDetail.addCertifications', 'List any required professional certifications or licenses')}</p>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Nice to have */}
              <Card>
                <CardHeader className="max-[1149px]:p-4">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="flex items-center space-x-2 max-[1149px]:text-base">
                      <Sparkles className="h-5 w-5 max-[1149px]:h-4 max-[1149px]:w-4" />
                      <span>{t('offerDetail.niceToHave', 'Nice to have')}</span>
                    </CardTitle>
                    {renderSectionEditButton('benefits')}
                  </div>
                </CardHeader>
                {renderSectionError('benefits')}
                <CardContent className="max-[1149px]:p-4">
                  {editingSections.benefits ? (() => {
                    const benefitsDraft = getActiveDraft('benefits')
                    return (
                      <div className="space-y-6">
                        {/* Mentioned Benefits */}
                        <div className="space-y-2">
                          <div className="text-sm font-medium text-foreground">
                            {t('offerDetail.benefitsMentioned', 'Nice to have')}
                          </div>
                          <DynamicList
                            items={benefitsDraft.benefits_mentionnees}
                            onChange={(items) =>
                              updateSectionDraft('benefits', draft => ({
                                ...draft,
                                benefits_mentionnees: items
                              }))
                            }
                            placeholder={t('offerDetail.benefitPlaceholder', 'Add a benefit or perk')}
                            minItems={0}
                          />
                        </div>

                        {/* AI Recommended Benefits */}
                        {benefitsDraft.benefits_non_mentionnees?.length > 0 && (
                          <div className="space-y-2">
                            <div className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                              <Lightbulb className="h-4 w-4 text-amber-500" />
                              {t('offerDetail.benefitsRecommended', 'Recommandé par l\'IA')}
                            </div>
                            <DynamicList
                              items={benefitsDraft.benefits_non_mentionnees}
                              onChange={(items) =>
                                updateSectionDraft('benefits', draft => ({
                                  ...draft,
                                  benefits_non_mentionnees: items
                                }))
                              }
                              placeholder={t('offerDetail.aiRecommendedPlaceholder', 'AI recommended benefit')}
                              minItems={0}
                              allowAdd={false}
                              allowEdit={false}
                            />
                          </div>
                        )}

                        {renderSectionEditorActions('benefits')}
                      </div>
                    )
                  })() : (
                    <>
                      {displayedBenefits.length > 0 ? (
                        <div className="flex flex-wrap gap-2 max-[1149px]:gap-1.5">
                          {displayedBenefits.map((benefit, index) => (
                            <Badge key={index} variant="default" className="max-[1149px]:text-xs max-[1149px]:px-2 max-[1149px]:py-0.5">
                              {benefit}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 max-[1149px]:py-6">
                          <Sparkles className="h-12 w-12 mx-auto text-gray-300 mb-4 max-[1149px]:h-8 max-[1149px]:w-8 max-[1149px]:mb-3" />
                          <p className="text-sm text-muted-foreground max-[1149px]:text-xs">{t('offerDetail.noNiceToHave', 'No nice to have items specified')}</p>
                          <p className="text-xs text-gray-400 mt-1 max-[1149px]:text-[10px]">{t('offerDetail.addNiceToHave', 'Add nice to have skills or qualifications')}</p>
                        </div>
                      )}

                      {/* AI-Recommended Benefits (non-edit mode) */}
                      {displayedBenefitsNonMentionnees.length > 0 && (
                        <div className="mt-6 pt-6 border-t">
                          <div className="flex items-center gap-2 mb-3">
                            <Lightbulb className="h-4 w-4 text-amber-500" />
                            <span className="text-sm font-medium text-muted-foreground">
                              {t('offerDetail.aiRecommended', 'Recommandé par l\'IA')}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2 max-[1149px]:gap-1.5">
                            {displayedBenefitsNonMentionnees.map((benefit, index) => (
                              <Badge
                                key={`ai-benefit-${index}`}
                                variant="outline"
                                className="max-[1149px]:text-xs max-[1149px]:px-2 max-[1149px]:py-0.5 bg-amber-50 border-amber-200 text-amber-900"
                              >
                                {benefit}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Languages */}
              <Card>
                <CardHeader className="max-[1149px]:p-4">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="flex items-center space-x-2 max-[1149px]:text-base">
                      <MapPin className="h-5 w-5 max-[1149px]:h-4 max-[1149px]:w-4" />
                      <span>{t('createOffer.languages')}</span>
                    </CardTitle>
                    {renderSectionEditButton('languages')}
                  </div>
                </CardHeader>
                {renderSectionError('languages')}
                <CardContent className="max-[1149px]:p-4">
                  {editingSections.languages ? (() => {
                    const languagesDraft = getActiveDraft('languages')
                    return (
                      <div className="space-y-4">
                        <LanguageField
                          items={languagesDraft.languages}
                          onItemsChange={(items) =>
                            updateSectionDraft('languages', draft => ({
                              ...draft,
                              languages: items
                            }))
                          }
                          onBlur={() => validateSectionOnBlur('languages')}
                          placeholder={t('offerDetail.languagePlaceholder', 'Select language and proficiency')}
                          description={t('offerDetail.languageHelper', 'Add required languages with expected levels.')}
                        />
                        {renderSectionEditorActions('languages')}
                      </div>
                    )
                  })() : (
                    <>
                      {getLanguages().length > 0 ? (
                        <div className="space-y-2 max-[1149px]:space-y-1.5">
                          {getLanguages().map((lang, index) => {
                            const itemKey = `language_${lang.language}_${index}`
                            return (
                              <div key={index} className="flex items-center justify-between p-2 bg-muted rounded-lg max-[1149px]:p-1.5">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium max-[1149px]:text-sm">{lang.language}</span>
                                  <button
                                    onClick={() => toggleLock(itemKey)}
                                    disabled={isOfferModificationLocked}
                                    className={`transition-all rounded-full p-0.5 ${isOfferModificationLocked
                                      ? getPublishedLockButtonClass(isLocked(itemKey))
                                      : isLocked(itemKey)
                                        ? 'bg-blue-500 text-white'
                                        : 'text-gray-400 hover:text-gray-600'
                                      }`}
                                  >
                                    {isLocked(itemKey) ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
                                  </button>
                                </div>
                                <Badge variant="secondary" className="max-[1149px]:text-xs">{lang.level}</Badge>
                              </div>
                            )
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-8 max-[1149px]:py-6">
                          <MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-4 max-[1149px]:h-8 max-[1149px]:w-8 max-[1149px]:mb-3" />
                          <p className="text-sm text-muted-foreground max-[1149px]:text-xs">{t('offerDetail.noLanguages', 'No languages specified')}</p>
                          <p className="text-xs text-muted-foreground mt-1 max-[1149px]:text-[10px]">{t('offerDetail.addLanguages', 'Specify language requirements and proficiency levels')}</p>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Main Missions */}
              <Card>
                <CardHeader className="max-[1149px]:p-4">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="flex items-center space-x-2 max-[1149px]:text-base">
                      <Star className="h-5 w-5 max-[1149px]:h-4 max-[1149px]:w-4" />
                      <span>{t('createOffer.sections.mainMissions')}</span>
                    </CardTitle>
                    {renderSectionEditButton('main_missions')}
                  </div>
                </CardHeader>
                {renderSectionError('main_missions')}
                <CardContent className="max-[1149px]:p-4">
                  {editingSections.main_missions ? (() => {
                    const missionsDraft = getActiveDraft('main_missions')
                    return (
                      <div className="space-y-4">
                        <DynamicList
                          items={missionsDraft.main_missions}
                          onChange={(items) =>
                            updateSectionDraft('main_missions', draft => ({
                              ...draft,
                              main_missions: items
                            }))
                          }
                          onBlur={() => validateSectionOnBlur('main_missions')}
                          placeholder={t('offerDetail.missionPlaceholder', 'Add a mission')}
                          minItems={0}
                          maxItems={50}
                        />
                        {renderSectionEditorActions('main_missions')}
                      </div>
                    )
                  })() : (
                    <>
                      {getMainMissions().length > 0 ? (
                        <ul className="space-y-2 max-[1149px]:space-y-1.5">
                          {getMainMissions().map((mission, index) => (
                            <li key={index} className="flex items-start space-x-2">
                              <span className="text-primary mt-1 max-[1149px]:mt-0.5">•</span>
                              <span className="text-sm max-[1149px]:text-xs">{mission}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="text-center py-8 max-[1149px]:py-6">
                          <Star className="h-12 w-12 mx-auto text-muted-foreground mb-4 max-[1149px]:h-8 max-[1149px]:w-8 max-[1149px]:mb-3" />
                          <p className="text-sm text-muted-foreground max-[1149px]:text-xs">{t('offerDetail.noMainMissions', 'No main missions specified')}</p>
                          <p className="text-xs text-muted-foreground mt-1 max-[1149px]:text-[10px]">{t('offerDetail.addMainMissions', 'Define the key responsibilities and tasks for this role')}</p>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6 max-[1149px]:space-y-4">
              {/* Key Details */}
              <Card>
                <CardHeader className="max-[1149px]:p-4">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="max-[1149px]:text-base">{t('offerDetail.keyDetails', 'Key Details')}</CardTitle>
                    {renderSectionEditButton('key_details')}
                  </div>
                </CardHeader>
                {renderSectionError('key_details')}
                <CardContent className="space-y-4 max-[1149px]:p-4 max-[1149px]:space-y-3">
                  {editingSections.key_details ? (() => {
                    const keyDetailsDraft = getActiveDraft('key_details')
                    return (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="text-sm font-medium text-muted-foreground max-[1149px]:text-xs">{t('offerDetail.startDate')}</label>
                            <div className="mt-1">
                              <ModernDatePicker
                                selected={parseDraftDate(keyDetailsDraft.start_date)}
                                onChange={(date) => {
                                  if (offer?.status === 'offer_closed') return
                                  updateSectionDraft('key_details', draft => ({
                                    ...draft,
                                    start_date: formatDraftDate(date)
                                  }))
                                  setTimeout(() => validateSectionOnChange('key_details'), 100)
                                }}
                                onBlur={() => validateSectionOnBlur('key_details')}
                                placeholder={t('offerDetail.startDate')}
                                disabled={offer?.status === 'offer_closed'}
                              />
                            </div>
                            {sectionErrors.key_details && (
                              <p className="mt-1 text-sm text-destructive">
                                {t(sectionErrors.key_details.key, sectionErrors.key_details.defaultMessage)}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground max-[1149px]:text-xs">{t('offerDetail.status')}</label>
                            <select
                              value={keyDetailsDraft.status}
                              onChange={(e) =>
                                updateSectionDraft('key_details', draft => ({
                                  ...draft,
                                  status: e.target.value as Offer['status']
                                }))
                              }
                              disabled={offer?.status === 'closed'}
                              className="mt-1 w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {getAvailableStatusOptions(offer?.status || 'draft').map(option => (
                                <option key={option} value={option}>
                                  {t(`offers.status.${option}`, option)}
                                </option>
                              ))}
                            </select>
                            {offer?.status === 'closed' && (
                              <p className="mt-1 text-xs text-muted-foreground">
                                {t('offerDetail.statusLockedMessage', 'Les offres archivées ne peuvent pas changer de statut')}
                              </p>
                            )}
                            {offer?.status === 'offer_closed' && (
                              <p className="mt-1 text-xs text-muted-foreground">
                                {t('offerDetail.closedOfferEditStatusOnly', 'Offer is closed. It cannot be activated again. You can only archive it.')}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-muted-foreground max-[1149px]:text-xs">{t('offerDetail.emailLanguage', 'Email Language')}</label>
                            <select
                              value={keyDetailsDraft.email_language}
                              onChange={(e) =>
                                updateSectionDraft('key_details', draft => ({
                                  ...draft,
                                  email_language: e.target.value as 'fr' | 'en'
                                }))
                              }
                              disabled={offer?.status === 'offer_closed'}
                              className="mt-1 w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <option value="fr">{t('offerDetail.emailLanguageFrench', 'French')}</option>
                              <option value="en">{t('offerDetail.emailLanguageEnglish', 'English')}</option>
                            </select>
                          </div>
                        </div>
                        {renderSectionEditorActions('key_details')}
                      </div>
                    )
                  })() : (
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3 max-[1149px]:space-x-2">
                        <Calendar className="h-5 w-5 text-muted-foreground max-[1149px]:h-4 max-[1149px]:w-4 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium max-[1149px]:text-xs">{t('offerDetail.startDate')}</p>
                          <p className="text-lg max-[1149px]:text-sm truncate">{offer.start_date ? formatDate(offer.start_date) : t('common.none', 'Not specified')}</p>
                        </div>
                      </div>



                      <div className="flex items-center space-x-3 max-[1149px]:space-x-2">
                        <CheckCircle className="h-5 w-5 text-muted-foreground max-[1149px]:h-4 max-[1149px]:w-4 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium max-[1149px]:text-xs">{t('offerDetail.status')}</p>
                          <Badge variant="outline" className={`${getStatusColor(offer.status)} max-[1149px]:text-xs`}>
                            {t(`offers.status.${offer.status}`)}
                          </Badge>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3 max-[1149px]:space-x-2">
                        <Mail className="h-5 w-5 text-muted-foreground max-[1149px]:h-4 max-[1149px]:w-4 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium max-[1149px]:text-xs">{t('offerDetail.emailLanguage', 'Email Language')}</p>
                          <p className="text-lg max-[1149px]:text-sm truncate">
                            {offer.email_language === 'en'
                              ? t('offerDetail.emailLanguageEnglish', 'English')
                              : t('offerDetail.emailLanguageFrench', 'French')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* AI Matching Weights */}
              <Card>
                <CardHeader className="max-[1149px]:p-4">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="flex items-center space-x-2 max-[1149px]:text-base max-[1149px]:flex-wrap max-[1149px]:gap-1">
                      <BarChart3 className="h-5 w-5 max-[1149px]:h-4 max-[1149px]:w-4" />
                      <span>{t('createOffer.form.aiMatchingWeights', 'AI Matching Weights')}</span>
                      <Badge variant="secondary" className="ml-2 max-[1149px]:ml-0 max-[1149px]:text-xs whitespace-nowrap">
                        {t('offerDetail.aiGenerated', 'AI Generated')}
                      </Badge>
                    </CardTitle>
                    {renderSectionEditButton('ai_matching')}
                  </div>
                  <p className="text-sm text-muted-foreground max-[1149px]:text-xs max-[1149px]:mt-1">
                    {t('offerDetail.matchingWeightsDescription', 'Intelligent matching criteria based on job nature')}
                  </p>
                </CardHeader>
                {renderSectionError('ai_matching')}
                <CardContent className="max-[1149px]:p-4">
                  {editingSections.ai_matching ? (() => {
                    const aiDraft = getActiveDraft('ai_matching')
                    const weights = aiDraft.ai_matching_weights || defaultAiMatchingWeights
                    const availableKeys = Object.keys(weights) as AiMatchingKey[]
                    // Use predefined order, then add any missing keys
                    const orderedKeys: AiMatchingKey[] = [
                      ...aiMatchingWeightsOrder.filter(key => availableKeys.includes(key)),
                      ...availableKeys.filter(key => !aiMatchingWeightsOrder.includes(key))
                    ]
                    return (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 gap-4">
                          {orderedKeys.map((key) => {
                            const numericValue = clampPercentage(weights[key])
                            const fieldIsEmpty = isFieldEmpty(key)
                            const colorMap: Record<string, string> = {
                              hard_skills: '#3b82f6',
                              soft_skills: '#10b981',
                              experience_years: '#f59e0b',
                              niveau_etude: '#ef4444',
                              languages: '#06b6d4',
                              certifications: '#f97316',
                              main_missions: '#84cc16',
                              nice_to_have: '#a78bfa'
                            }
                            const getWeightColor = (key: string) => {
                              return colorMap[key] || '#9ca3af'
                            }
                            const weightCategoryOptions = [
                              { value: 1, label: t('offerDetail.veryLowPriority', 'Very Low Priority') },
                              { value: 3, label: t('offerDetail.lowPriority', 'Low Priority') },
                              { value: 5, label: t('offerDetail.mediumPriority', 'Medium Priority') },
                              { value: 7, label: t('offerDetail.highPriority', 'High Priority') },
                              { value: 9, label: t('offerDetail.veryHighPriority', 'Very High Priority') }
                            ]
                            const getCategoryValue = (weight: number) => {
                              if (weight >= 8) return 9
                              if (weight >= 6) return 7
                              if (weight >= 4) return 5
                              if (weight >= 2) return 3
                              return 1
                            }
                            const categoryValue = String(getCategoryValue(numericValue))
                            return (
                              <div key={key} className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <div className="flex flex-col">
                                    <label className="text-sm font-medium capitalize text-muted-foreground max-[1149px]:text-xs">
                                      {t(`createOffer.form.aiMatching.${key}`, key.replace('_', ' '))}
                                    </label>
                                    {fieldIsEmpty && (
                                      <span className="text-xs text-red-600 mt-0.5">
                                        {t('offerDetail.fieldEmpty', 'Le champ est vide')}
                                      </span>
                                    )}
                                  </div>
                                  <div className="relative">
                                    <Input
                                      type="number"
                                      min={0}
                                      max={10}
                                      value={numericValue}
                                      onChange={(e) => {
                                        const newValue = Number(e.target.value) || 0
                                        handleAiMatchingWeightChange(key, newValue)
                                        setTimeout(() => validateSectionOnChange('ai_matching'), 100)
                                      }}
                                      onBlur={() => validateSectionOnBlur('ai_matching')}
                                      className={`w-20 h-8 text-right text-sm ${fieldIsEmpty ? 'border-red-300 bg-gray-50 cursor-not-allowed' : ''}`}
                                      disabled={fieldIsEmpty}
                                    />
                                  </div>
                                </div>
                                <input
                                  type="range"
                                  min={0}
                                  max={10}
                                  value={numericValue}
                                  onChange={(e) => {
                                    handleAiMatchingWeightChange(key, Number(e.target.value))
                                    setTimeout(() => validateSectionOnChange('ai_matching'), 100)
                                  }}
                                  onBlur={() => validateSectionOnBlur('ai_matching')}
                                  className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                                  disabled={fieldIsEmpty}
                                  style={{
                                    accentColor: getWeightColor(key),
                                    background: `linear-gradient(to right, ${getWeightColor(key)} 0%, ${getWeightColor(key)} ${(numericValue / 10) * 100}%, #e5e7eb ${(numericValue / 10) * 100}%, #e5e7eb 100%)`
                                  }}
                                />
                                <div className="flex flex-wrap gap-1">
                                  {weightCategoryOptions.map(option => (
                                    <Button
                                      key={option.value}
                                      type="button"
                                      variant={categoryValue === String(option.value) ? 'default' : 'outline'}
                                      className="h-7 px-2 text-xs max-[1149px]:h-6 max-[1149px]:px-1.5 max-[1149px]:text-[10px]"
                                      style={
                                        categoryValue === String(option.value)
                                          ? {
                                            backgroundColor: getWeightColor(key),
                                            borderColor: getWeightColor(key),
                                            color: '#ffffff'
                                          }
                                          : undefined
                                      }
                                      onClick={() => {
                                        handleAiMatchingWeightChange(key, option.value)
                                        setTimeout(() => validateSectionOnChange('ai_matching'), 100)
                                      }}
                                      disabled={fieldIsEmpty}
                                    >
                                      {option.label}
                                    </Button>
                                  ))}
                                </div>
                              </div>
                            )
                          })}
                        </div>

                        {(() => {
                          const colorMap: Record<string, string> = {
                            hard_skills: '#3b82f6',
                            soft_skills: '#10b981',
                            experience_years: '#f59e0b',
                            niveau_etude: '#ef4444',
                            languages: '#06b6d4',
                            certifications: '#f97316',
                            main_missions: '#84cc16',
                            nice_to_have: '#a78bfa'
                          }

                          const chartData = orderedKeys.map(key => ({
                            name: t(`createOffer.form.aiMatching.${key}`),
                            value: clampPercentage(weights[key]),
                            field: key,
                            color: colorMap[key] || '#9ca3af'
                          }))

                          const rawTotal = chartData.reduce((sum, d) => sum + (Number.isFinite(d.value) ? d.value : 0), 0)
                          if (rawTotal <= 0) return null

                          const normalizedChartData = chartData.map(item => ({
                            ...item,
                            value: rawTotal > 0 ? (item.value / rawTotal) * 100 : 0
                          }))

                          return (
                            <div className="flex items-center justify-center">
                              <DraggablePieChart
                                data={normalizedChartData}
                                onValueChange={(field, value) => {
                                  const rawValue = rawTotal > 0 ? (value / 100) * rawTotal : value
                                  handleAiMatchingWeightChange(field as any, Math.round(rawValue))
                                }}
                              />
                            </div>
                          )
                        })()}

                        {renderSectionEditorActions('ai_matching')}
                      </div>
                    )
                  })() : (
                    <div className="space-y-4 max-[1149px]:space-y-3">
                      {offer.ai_matching_weights && (() => {
                        // Order entries according to predefined order
                        const entries = Object.entries(offer.ai_matching_weights)
                        const orderedEntries = [
                          ...aiMatchingWeightsOrder
                            .filter(key => offer.ai_matching_weights![key] !== undefined)
                            .map(key => [key, offer.ai_matching_weights![key]] as [string, number]),
                          ...entries.filter(([key]) => !aiMatchingWeightsOrder.includes(key as AiMatchingKey))
                        ]

                          // Show entries that have a positive weight OR whose related field is non-empty
                          const entriesToShow = orderedEntries.filter(([key, value]) => (value as number) > 0 || !isFieldEmpty(key as AiMatchingKey))

                          if (entriesToShow.length === 0) {
                          return (
                            <div className="text-center py-8 max-[1149px]:py-6">
                              <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4 max-[1149px]:h-8 max-[1149px]:w-8 max-[1149px]:mb-3" />
                              <p className="text-sm text-muted-foreground max-[1149px]:text-xs">{t('offerDetail.noAiWeights', 'No AI matching weights configured')}</p>
                            </div>
                          )
                        }

                        const colorMap: Record<string, string> = {
                          hard_skills: '#3b82f6',
                          soft_skills: '#10b981',
                          experience_years: '#f59e0b',
                          niveau_etude: '#ef4444',
                          languages: '#06b6d4',
                          certifications: '#f97316',
                          main_missions: '#84cc16',
                          nice_to_have: '#a78bfa'
                        }

                        const chartData = entriesToShow.map(([key, value]) => ({
                          name: t(`createOffer.form.aiMatching.${key}`, String(key).replace('_', ' ')),
                          value: clampPercentage(value as number),
                          field: key,
                          color: colorMap[String(key)] || '#9ca3af'
                        }))

                        const rawTotal = chartData.reduce((sum, d) => sum + (Number.isFinite(d.value) ? d.value : 0), 0)
                        const normalizedChartData = chartData.map(item => ({
                          ...item,
                          value: rawTotal > 0 ? (item.value / rawTotal) * 100 : 0
                        }))

                        return (
                          <>
                            {rawTotal > 0 && (
                              <div className="flex items-center justify-center">
                                <div className="pointer-events-none">
                                  <DraggablePieChart data={normalizedChartData} onValueChange={() => { }} showHandles={false} />
                                </div>
                              </div>
                            )}
                            <div className="space-y-2 max-[1149px]:space-y-1.5">
                              {entriesToShow.map(([key, value]) => {
                                const weight = value as number
                                const getWeightColor = (key: string) => {
                                  return colorMap[key] || '#9ca3af'
                                }

                                const getWeightLabel = (weight: number) => {
                                  if (weight >= 8) return t('offerDetail.veryHighPriority', 'Very High Priority')
                                  if (weight >= 6) return t('offerDetail.highPriority', 'High Priority')
                                  if (weight >= 4) return t('offerDetail.mediumPriority', 'Medium Priority')
                                  if (weight >= 2) return t('offerDetail.lowPriority', 'Low Priority')
                                  return t('offerDetail.veryLowPriority', 'Very Low Priority')
                                }

                                return (
                                  <div key={key} className="space-y-2 max-[1149px]:space-y-1.5">
                                    <div className="flex justify-between items-center max-[1149px]:flex-col max-[1149px]:items-stretch max-[1149px]:gap-1">
                                      <span className="text-sm font-medium capitalize max-[1149px]:text-xs">
                                        {t(`createOffer.form.aiMatching.${key}`, String(key).replace('_', ' '))}
                                      </span>
                                      <div className="flex items-center space-x-2 max-[1149px]:justify-between">
                                        <Badge
                                          variant={weight >= 8 ? "default" : weight >= 6 ? "secondary" : "outline"}
                                          className="text-xs max-[1149px]:text-[10px]"
                                        >
                                          {getWeightLabel(weight)}
                                        </Badge>
                                        <div className="relative inline-block">
                                          <span
                                            className="font-bold text-lg max-[1149px]:text-base cursor-help"
                                            onMouseEnter={(e) => {
                                              const rect = e.currentTarget.getBoundingClientRect()
                                              setHoveredWeightKey(String(key))
                                              setTooltipPosition({
                                                x: rect.left + rect.width / 2,
                                                y: rect.top - 10
                                              })
                                            }}
                                            onMouseLeave={() => {
                                              setHoveredWeightKey(null)
                                              setTooltipPosition(null)
                                            }}
                                          >
                                            {weight}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-3 max-[1149px]:h-2">
                                      {weight > 0 ? (
                                        <div
                                          className="h-3 rounded-full transition-all duration-500 flex items-center justify-end pr-2 max-[1149px]:h-2 max-[1149px]:pr-1"
                                          style={{ width: `${Math.min((weight / 10) * 100, 100)}%`, backgroundColor: getWeightColor(String(key)) }}
                                        >
                                          {weight > 4 && (
                                            <span className="text-xs font-medium text-white max-[1149px]:text-[10px]">
                                              {weight}
                                            </span>
                                          )}
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          </>
                        )
                      })()}

                      {/* Tooltip for evaluation names */}
                      {hoveredWeightKey && tooltipPosition && (
                        <div
                          className="fixed px-3 py-1.5 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-md shadow-2xl whitespace-nowrap z-[10000] pointer-events-none"
                          style={{
                            left: `${tooltipPosition.x}px`,
                            top: `${tooltipPosition.y}px`,
                            transform: 'translate(-50%, -100%)',
                            marginTop: '-4px'
                          }}
                        >
                          {t(`createOffer.form.aiMatching.${hoveredWeightKey}`, hoveredWeightKey.replace('_', ' '))}
                          <div
                            className="absolute top-full left-1/2"
                            style={{ transform: 'translateX(-50%)' }}
                          >
                            <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                          </div>
                        </div>
                      )}

                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Actions - Hidden for now */}
              {false && (
                <Card>
                  <CardHeader className="max-[1149px]:p-4">
                    <CardTitle className="max-[1149px]:text-base">{t('offerDetail.actions')}</CardTitle>
                  </CardHeader>
                  <CardContent className="max-[1149px]:p-4">
                    <div className="space-y-2 max-[1149px]:space-y-1.5">
                      <Button variant="outline" className="w-full max-[1149px]:h-9 max-[1149px]:text-sm">
                        <Mail className="h-4 w-4 mr-2 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                        {t('offerDetail.sendOfferLetter', 'Send Offer Letter')}
                      </Button>
                      <Button variant="outline" className="w-full max-[1149px]:h-9 max-[1149px]:text-sm">
                        <Calendar className="h-4 w-4 mr-2 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                        {t('offerDetail.scheduleFollowUp', 'Schedule Follow-up')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </OfferDetailFullPageOverlay>
      )}

      {openPanel === 'matching' && (
        <OfferDetailFullPageOverlay
          title={t('aiMatching.matchingDetails', 'Matching details')}
          onClose={() => setOpenPanel(null)}
        >
          <div className="space-y-6 max-[1149px]:space-y-4">
            <Card>

              <CardContent>
                {loadingMatchingDetails ? (
                  <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-3" />
                    <p className="text-sm">{t('common.loading', 'Chargement des détails du matching IA...')}</p>
                  </div>
                ) : !matchingSummary || combinedMatchingResults.length === 0 ? (
                  <div className="text-center py-10">
                    <Target className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm font-medium text-foreground">
                      {t(
                        'offerDetail.noMatchingDetails',
                        'Aucun détail de Matching IA disponible pour cette offre pour le moment.'
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t(
                        'offerDetail.noMatchingDetailsHint',
                        'Lancez un Matching IA depuis cette offre ou depuis la page Matching IA pour voir les détails ici.'
                      )}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Résumé de tous les matchings pour cette offre */}
                    <div className="p-4 rounded-lg border bg-muted border-border max-[1149px]:p-3">
                      <h3 className="text-sm font-semibold mb-3 text-foreground max-[1149px]:mb-2">
                        {t('matchings.matchingSummary', 'Résumé du Matching IA')}
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-[1149px]:grid-cols-2 max-[1149px]:gap-3">
                        <div>
                          <p className="text-xs text-muted-foreground max-[1149px]:text-[11px]">
                            {t('matchings.labels.candidatesAnalyzed', 'Candidats analysés')}
                          </p>
                          <p className="text-xl font-bold text-foreground max-[1149px]:text-lg">
                            {matchingSummary.total_candidates}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground max-[1149px]:text-[11px]">
                            {t('matchings.labels.excellentMatches', 'Excellentes correspondances')}
                          </p>
                          <p className="text-xl font-bold text-green-600 dark:text-green-400 max-[1149px]:text-lg">
                            {selectedMatchingResultCount}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground max-[1149px]:text-[11px]">
                            {t('matchings.labels.maxScore', 'Score maximum')}
                          </p>
                          <p className="text-xl font-bold text-foreground max-[1149px]:text-lg">
                            {matchingSummary.max_score}%
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground max-[1149px]:text-[11px]">
                            {t('matchings.labels.totalMatchings', 'Matchings effectués')}
                          </p>
                          <p className="text-xl font-bold text-foreground max-[1149px]:text-lg">
                            {matchingSummary.total_jobs}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Détails par candidat (comme dans la modale AIMatching) */}
                    <div>
                      <h3 className="text-sm font-semibold mb-4 text-foreground max-[1149px]:mb-3">
                        {t('matchings.labels.detailedResults', 'Résultats détaillés par candidat')}
                      </h3>
                      <div className="grid gap-4 max-[1149px]:gap-3">
                        {sortedCombinedMatchingResults.map(({ result, matchScore, passesFilters, filterReasons, isSelected }, index) => {
                          const breakdown = (result as any).score_breakdown as
                            | Record<string, MatchingBreakdownCategoryForOffer>
                            | undefined
                          const candidate = (result as any).candidate
                          const displayedScore = !isSelected && !passesFilters ? 0 : matchScore
                          const isExpanded =
                            typeof candidate.id === 'number' &&
                            expandedMatchingCandidateIds.includes(candidate.id as number)

                          // Try to get profile picture from applications if not in candidate object
                          const candidateApp = applications.find(app => app.candidate === candidate.id)
                          const profilePicture =
                            candidate.profile_picture ||
                            (candidateApp as any)?.candidate_profile_picture ||
                            null

                          // Use same avatar system as Candidates page (ui-avatars.com)
                          const avatarUrl = profilePicture ||
                            `https://ui-avatars.com/api/?name=${encodeURIComponent(candidate.name || candidate.email || 'Candidate')}&background=random&color=fff&size=128`

                          return (
                            <div
                              key={index}
                              className={`group relative overflow-hidden rounded-lg border transition-all duration-300 ${breakdown
                                ? 'cursor-pointer hover:shadow-lg hover:border-primary/50'
                                : ''
                                } ${isExpanded
                                  ? 'bg-muted/50 border-primary/30 shadow-md'
                                  : !passesFilters
                                    ? 'bg-red-50/70 border-red-200 dark:bg-red-900/10 dark:border-red-800'
                                    : isSelected
                                    ? 'bg-emerald-50/50 border-emerald-200 dark:bg-emerald-900/10 dark:border-emerald-800'
                                    : !passesFilters
                                    ? 'bg-red-50/70 border-red-200 dark:bg-red-900/10 dark:border-red-800'
                                    : 'bg-background border-border'
                                }`}
                            >
                              {/* Header cliquable pour expand/collapse */}
                              <div
                                onClick={() => {
                                  if (!breakdown || typeof candidate.id !== 'number') return
                                  setExpandedMatchingCandidateIds(prev =>
                                    prev.includes(candidate.id as number)
                                      ? prev.filter(id => id !== (candidate.id as number))
                                      : [...prev, candidate.id as number]
                                  )
                                }}
                                className={`p-4 transition-all duration-300 max-[1149px]:p-3 ${breakdown ? 'cursor-pointer' : ''
                                  }`}
                              >
                                <div className="flex items-start justify-between max-[1149px]:flex-col max-[1149px]:gap-3">
                                  <div className="flex items-center space-x-3 flex-1 min-w-0 max-[1149px]:w-full">
                                    {/* Avatar avec même système que la page Candidates */}
                                    <img
                                      src={getImageUrl(avatarUrl) || avatarUrl}
                                      alt={candidate.name}
                                      className="w-12 h-12 rounded-full object-cover border-2 border-border flex-shrink-0 max-[1149px]:w-10 max-[1149px]:h-10 ring-2 ring-offset-2 ring-offset-background ring-primary/20 transition-all duration-300 group-hover:ring-primary/40"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          navigate(`/candidates/${candidate.id}`, { state: { from: currentPath } })
                                        }}
                                        className="font-semibold truncate max-[1149px]:text-sm text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary text-foreground"
                                      >
                                        {candidate.name}
                                      </button>
                                      <p className="text-sm truncate max-[1149px]:text-xs text-muted-foreground">
                                        {candidate.email}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3 max-[1149px]:w-full max-[1149px]:justify-between">
                                    <div className="text-right max-[1149px]:text-left">
                                      <div
                                        className={`px-3 py-1 rounded-full text-base font-bold transition-all shadow-sm flex items-center gap-1 ${!passesFilters
                                          ? 'bg-red-100 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800'
                                          : isSelected
                                          ? 'bg-emerald-100 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300'
                                          : !passesFilters
                                          ? 'bg-gray-100 text-gray-600 border border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'
                                          : matchScore >= 60
                                            ? 'bg-amber-100 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300'
                                            : 'bg-orange-100 text-orange-700 border border-orange-200 dark:bg-orange-900/30 dark:text-orange-300'
                                          }`}
                                      >
                                        <Target className="w-3.5 h-3.5" />
                                        <span>{displayedScore}%</span>
                                        {!isSelected && !passesFilters && (
                                          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                                            ({matchScore}%)
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-xs max-[1149px]:text-[10px] text-muted-foreground">
                                        {t('matchings.labels.matchScore', 'Score de matching')}
                                      </div>
                                    </div>
                                    {/* Icône de collapse créative */}
                                    {breakdown && (
                                      <div className="flex-shrink-0">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${isExpanded
                                          ? 'bg-primary/10 text-primary rotate-180'
                                          : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary'
                                          }`}>
                                          <ChevronDown className="h-4 w-4 transition-transform duration-300" />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* {!passesFilters && filterReasons.length > 0 && (
                                <div className="px-4 pb-3 pt-0 max-[1149px]:px-3">
                                  <div className="flex flex-wrap gap-2">
                                    {filterReasons.map((reason, reasonIndex) => (
                                      <Badge
                                        key={`${candidate?.id ?? index}-filter-reason-${reasonIndex}`}
                                        variant="outline"
                                        className="bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800"
                                      >
                                        <AlertTriangle className="mr-1 h-3 w-3" />
                                        {reason}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )} */}

                              {/* Détails (render uniquement si ouvert pour éviter les freezes) */}
                              {breakdown && isExpanded && (() => {
                                // Calculate the sum of all weighted scores to normalize to 100%
                                const breakdownEntries = Object.entries(breakdown)
                                  .filter(([key]) => key !== 'semantic_similarity')

                                const totalWeightedScore = breakdownEntries.reduce((sum, [_, entry]) => {
                                  const categoryEntry = entry as MatchingBreakdownCategoryForOffer
                                  return sum + (categoryEntry.weighted_score ?? categoryEntry.score ?? 0)
                                }, 0)

                                // Calculate the sum of all weights
                                const totalWeights = breakdownEntries.reduce((sum, [_, entry]) => {
                                  const categoryEntry = entry as MatchingBreakdownCategoryForOffer
                                  return sum + (categoryEntry.weight ?? 0)
                                }, 0)

                                return (
                                  <div className="px-4 pb-4 pt-0 space-y-3 max-[1149px]:px-3 max-[1149px]:space-y-2">
                                    {breakdownEntries.map(([key, entry]) => {
                                      const categoryEntry = entry as MatchingBreakdownCategoryForOffer
                                      const rawWeightedScore = categoryEntry.weighted_score ?? categoryEntry.score ?? 0
                                      // Normalize weighted score to percentage of total (sum = 100%)
                                      const normalizedWeightedScore = totalWeightedScore > 0
                                        ? (rawWeightedScore / totalWeightedScore) * 100
                                        : 0
                                      // Calculate weight percentage
                                      const weightPercentage = totalWeights > 0
                                        ? ((categoryEntry.weight ?? 0) / totalWeights) * 100
                                        : 0

                                      return (
                                        <div
                                          key={key}
                                          className="rounded-lg border border-border bg-muted/30"
                                        >
                                          <div className="grid grid-cols-1 divide-y divide-border md:grid-cols-4 md:divide-y-0 md:divide-x">
                                            <div className="flex items-center justify-center px-3 py-3 text-center max-[1149px]:px-2 max-[1149px]:py-2">
                                              <p className="text-sm font-semibold text-foreground max-[1149px]:text-xs">
                                                {getMatchingCategoryLabel(key, t)}
                                              </p>
                                            </div>
                                            <div className="grid grid-rows-[auto,1fr] px-3 py-3 max-[1149px]:px-2 max-[1149px]:py-2">
                                              <p className="text-xs text-muted-foreground max-[1149px]:text-[10px]">
                                                {t('matchings.labels.rawScore', 'Score brut')}
                                              </p>
                                              <div className="flex items-center justify-center">
                                                <p className="text-xl font-bold text-foreground max-[1149px]:text-lg">
                                                  {(categoryEntry.score ?? 0).toFixed(2)}%
                                                </p>
                                              </div>
                                            </div>
                                            {/* <div className="grid grid-rows-[auto,1fr] px-3 py-3 max-[1149px]:px-2 max-[1149px]:py-2">
                                              <p className="text-xs text-muted-foreground max-[1149px]:text-[10px]">
                                                {t('matchings.labels.weight', 'Poids')}
                                              </p>
                                              <div className="flex items-center justify-center">
                                                <p className="text-xl font-bold text-green-600 dark:text-green-400 max-[1149px]:text-lg">
                                                  {categoryEntry.weight ?? 0}
                                                </p>
                                              </div>
                                            </div> */}
                                            <div className="grid grid-rows-[auto,1fr] px-3 py-3 max-[1149px]:px-2 max-[1149px]:py-2">
                                              <p className="text-xs text-muted-foreground max-[1149px]:text-[10px]">
                                                {t('matchings.labels.weightPercentage', 'Poids')}
                                              </p>
                                              <div className="flex items-center justify-center gap-1">
                                                <p className="text-xl font-bold text-blue-600 dark:text-blue-400 max-[1149px]:text-lg">
                                                  {weightPercentage.toFixed(2)}%
                                                </p>
                                                <span
                                                  className="text-[10px] text-muted-foreground/70 font-medium"
                                                  title={t('matchings.labels.originalWeight', 'Poids original')}
                                                >
                                                  ({categoryEntry.weight ?? 0})
                                                </span>
                                              </div>
                                            </div>
                                            <div className="flex items-center justify-center px-3 py-3 max-[1149px]:px-2 max-[1149px]:py-2">
                                              <p className="text-xl font-bold text-foreground max-[1149px]:text-lg">
                                                {(((categoryEntry.score ?? 0) * weightPercentage) / 100).toFixed(2)}%
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      )
                                    })}
                                  </div>
                                )
                              })()}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </OfferDetailFullPageOverlay>
      )}

      {openPanel === 'calendar' && (
        <OfferDetailFullPageOverlay
          title={t('offerDetail.calendarTab', 'Calendar')}
          onClose={() => setOpenPanel(null)}
        >
            <InterviewCalendar
              hideHeader
              events={interviews}
              loading={loadingInterviews}
              candidates={applications.map(app => ({
                id: app.candidate,
                name: app.candidate_name || `Candidate ${app.candidate}`
              }))}
              steps={hiringSteps.map(step => ({
                id: step.id,
                name: step.name,
                order: step.order,
                color: step.color,
                step_type: step.step_type
              }))}
              groups={teams.map(team => ({
                id: team.id,
                name: team.name,
                color: team.color
              }))}
              candidateGroups={(() => {
                const map = new Map<number, string[]>()
                applications.forEach(app => {
                  const candidateTeams = teams.filter(team =>
                    team.candidates?.some(c => c.id === app.candidate)
                  )
                  if (candidateTeams.length > 0) {
                    map.set(app.candidate, candidateTeams.map(t => t.name))
                  }
                })
                return map
              })()}
              onSlotSelect={(arg) => {
                // Optional: Allow creating interview from calendar slot selection
                console.log('Slot selected:', arg.start, arg.end)
              }}
              onEventClick={(arg) => {
                // Interview details are now shown in a modal by the InterviewCalendar component
                // This handler is kept for any additional logic if needed
              }}
              onEventDrop={(arg) => {
                // Handle rescheduling interview
                const event = arg.event
                const interview = interviews.find(i => String(i.id) === event.id)
                if (interview && event.start) {
                  const updatedInterview = {
                    ...interview,
                    start: event.start,
                    end: event.end || event.start,
                  }
                  setInterviews(prev => prev.map(i => i.id === interview.id ? updatedInterview : i))
                  addToast({
                    type: 'success',
                    title: t('offerDetail.calendar.interviewRescheduled', 'Interview Rescheduled'),
                    message: t('offerDetail.calendar.interviewRescheduledMessage', 'Interview has been rescheduled.'),
                  })
                }
              }}
            />
        </OfferDetailFullPageOverlay>
      )}


      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title={t('offers.deleteOfferTitle', 'Delete Offer')}
      >
        {loadingAddCandidatesData ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            <p className="mt-4 text-sm">
              {t('offerDetail.loadingAddCandidates', 'Loading groups and candidates...')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('offers.deleteOfferConfirm', { title: offer?.job_title })}
            </p>
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteOffer}
              >
                {t('offers.deleteOfferCta', 'Delete Offer')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Add Candidate Modal */}
      <Modal
        isOpen={showAddCandidateModal}
        onClose={() => {
          if (addingCandidates) return // Prevent closing while adding
          setShowAddCandidateModal(false)
          setSelectedCandidateIds([])
          setSelectedTeamIds([])
          setTeamSearch('')
          setCandidateSearch('')
          setLoadingAddCandidatesData(false)
        }}
        title={t('offerDetail.addCandidatesToOffer', 'Add Candidates to Offer')}
      >
        {loadingAddCandidatesData ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            <p className="mt-4 text-sm">
              {t('offerDetail.loadingAddCandidates', 'Loading groups and candidates...')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {canMassImport && (
              <div className="flex flex-col gap-2 rounded-lg border border-border bg-muted/40 p-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    {t('offerDetail.needMoreCandidates', 'Need more candidates?')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('offerDetail.uploadCandidatesHelper', 'Upload new candidates without leaving this offer.')}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setShowUploadCandidatesModal(true)}
                  disabled={addingCandidates}
                  className="w-full sm:w-auto"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {t('offerDetail.uploadCandidate', 'Upload Candidate')}
                </Button>
              </div>
            )}
            {/* Segmented Control - Group first, then Candidates */}
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-muted-foreground">
                {selectionMode === 'group'
                  ? t('offerDetail.selectGroupsToAdd', 'Select one or more groups to add all their candidates to this offer')
                  : t('offerDetail.selectCandidatesToAdd', 'Select one or more candidates to add to this offer')}
              </p>
              <div className="flex space-x-1 bg-muted rounded-lg p-0.5">
                <Button
                  variant={selectionMode === 'group' ? 'default' : 'ghost'}
                  onClick={() => !addingCandidates && setSelectionMode('group')}
                  size="sm"
                  className="px-2 py-1 h-7 text-xs"
                  title={t('aiMatching.groups', 'Groups')}
                  disabled={addingCandidates}
                >
                  <Users className="h-3 w-3 mr-1" />
                  {t('aiMatching.groups', 'Groups')}
                </Button>
                <Button
                  variant={selectionMode === 'candidates' ? 'default' : 'ghost'}
                  onClick={() => !addingCandidates && setSelectionMode('candidates')}
                  size="sm"
                  className="px-2 py-1 h-7 text-xs"
                  title={t('aiMatching.individuals', 'Candidates')}
                  disabled={addingCandidates}
                >
                  <User className="h-3 w-3 mr-1" />
                  {t('aiMatching.individuals', 'Candidates')}
                </Button>
              </div>
            </div>

            {/* Group Selection Mode */}
            {selectionMode === 'group' && (
              <>
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('offerDetail.searchGroups', 'Search groups...')}
                    value={teamSearch}
                    onChange={(e) => setTeamSearch(e.target.value)}
                    disabled={addingCandidates}
                    className="w-full pl-10 pr-4 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background border-input text-foreground placeholder-muted-foreground disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                {/* Teams List */}
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {teams.filter(team =>
                    team.name.toLowerCase().includes(teamSearch.toLowerCase())
                  ).length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">{t('offerDetail.noGroupsFound', 'No groups found')}</p>
                      <p className="text-sm text-muted-foreground">
                        {teamSearch
                          ? t('offerDetail.tryDifferentSearchTerm', 'Try a different search term')
                          : t('offerDetail.noGroupsAvailable', 'No groups available')}
                      </p>
                    </div>
                  ) : (
                    teams
                      .filter(team => team.name.toLowerCase().includes(teamSearch.toLowerCase()))
                      .map((team) => (
                        <div
                          key={team.id}
                          className={`p-3 border rounded-lg transition-colors ${addingCandidates
                            ? 'opacity-50 cursor-not-allowed'
                            : 'cursor-pointer'
                            } ${selectedTeamIds.includes(team.id)
                              ? 'border-primary bg-primary/5 dark:bg-primary/10'
                              : 'border-border hover:bg-muted'
                            }`}
                          onClick={() => {
                            if (addingCandidates) return
                            setSelectedTeamIds(prev => {
                              if (prev.includes(team.id)) {
                                return prev.filter(id => id !== team.id)
                              } else {
                                return [...prev, team.id]
                              }
                            })
                          }}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${selectedTeamIds.includes(team.id)
                              ? 'border-primary bg-primary'
                              : 'border-gray-300'
                              }`}>
                              {selectedTeamIds.includes(team.id) && (
                                <CheckCircle className="h-3 w-3 text-white" />
                              )}
                            </div>
                            <div
                              className="w-8 h-8 rounded-full flex-shrink-0"
                              style={{ backgroundColor: team.color }}
                            />
                            <div className="flex-1">
                              <h4 className="font-medium text-sm text-foreground">{team.name}</h4>
                              <p className="text-xs text-muted-foreground">
                                {team.candidates?.length || 0} {t('aiMatching.candidatesCount', { count: team.candidates?.length || 0 }).includes('candidate') ? 'candidates' : 'members'}
                              </p>
                              {team.description && (
                                <p className="text-xs text-muted-foreground mt-1">{team.description}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                  )}
                </div>

                {selectedTeamIds.length > 0 && (
                  <div className="text-sm text-muted-foreground">
                    {t('offerDetail.groupsSelected', { count: selectedTeamIds.length, defaultValue: `${selectedTeamIds.length} group${selectedTeamIds.length > 1 ? 's' : ''} selected` })}
                  </div>
                )}
              </>
            )}

            {/* Candidate Selection Mode */}
            {selectionMode === 'candidates' && (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('candidates.search', 'Search candidates...')}
                    value={candidateSearch}
                    onChange={(e) => handleSearchCandidates(e.target.value)}
                    disabled={addingCandidates}
                    className="w-full pl-10 pr-4 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background border-input text-foreground placeholder-muted-foreground disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                {/* Candidates List */}
                <div className="max-h-64 overflow-y-auto space-y-2 pr-1 custom-scrollbar relative min-h-[100px]">
                  {isSearchingCandidates && (
                    <div className="absolute inset-0 bg-background/50 backdrop-blur-[1px] z-20 flex flex-col items-center justify-center rounded-lg">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                      <p className="mt-2 text-xs text-muted-foreground">{t('common.searching', 'Searching...')}</p>
                    </div>
                  )}
                  {availableCandidates.length === 0 && !loadingAddCandidatesData ? (
                    <div className="text-center py-8">
                      <User className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">{t('offerDetail.noCandidatesAvailable', 'No candidates available')}</p>
                      <p className="text-sm text-muted-foreground">
                        {candidateSearch
                          ? t('offerDetail.tryDifferentSearchTerm', 'Try a different search term')
                          : t('offerDetail.allApplied', 'All candidates have already applied to this offer')}
                      </p>
                    </div>
                  ) : (
                    <>
                      {availableCandidates.map((candidate) => (
                        <div
                          key={candidate.id}
                          className={`p-3 border rounded-lg transition-colors ${addingCandidates
                            ? 'opacity-50 cursor-not-allowed'
                            : 'cursor-pointer'
                            } ${selectedCandidateIds.includes(candidate.id)
                              ? 'border-primary bg-primary/5 dark:bg-primary/10'
                              : 'border-border hover:bg-muted'
                            }`}
                          onClick={() => {
                            if (addingCandidates) return
                            setSelectedCandidateIds(prev => {
                              if (prev.includes(candidate.id)) {
                                return prev.filter(id => id !== candidate.id)
                              } else {
                                return [...prev, candidate.id]
                              }
                            })
                          }}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${selectedCandidateIds.includes(candidate.id)
                              ? 'border-primary bg-primary'
                              : 'border-gray-300'
                              }`}>
                              {selectedCandidateIds.includes(candidate.id) && (
                                <CheckCircle className="h-3 w-3 text-white" />
                              )}
                            </div>
                            <div className="w-8 h-8 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center text-white font-semibold text-xs">
                              {candidate.profile_picture ? (
                                <img
                                  src={getImageUrl(candidate.profile_picture) || candidate.profile_picture}
                                  alt={candidate.name}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                candidate.name
                                  .split(' ')
                                  .map((n) => n[0])
                                  .join('')
                                  .toUpperCase()
                              )}
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium text-sm text-foreground">{candidate.name}</h4>
                              <p className="text-xs text-muted-foreground">{candidate.email}</p>
                              {candidate.role && <p className="text-xs text-muted-foreground">{candidate.role}</p>}
                            </div>
                          </div>
                        </div>
                      ))}

                      {totalCandidates > 20 && (
                        <div className="pt-4 border-t border-border flex flex-col items-center space-y-3">
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                goToCandidatePage(1);
                              }}
                              disabled={candidatePage === 1 || loadingAddCandidatesData || loadingMoreCandidates || isSearchingCandidates}
                              className="h-8 w-8 p-0"
                              title={t('common.firstPage', 'First page')}
                            >
                              <ChevronsLeft className="h-4 w-4" />
                            </Button>

                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                goToCandidatePage(candidatePage - 1);
                              }}
                              disabled={candidatePage === 1 || loadingAddCandidatesData || loadingMoreCandidates || isSearchingCandidates}
                              className="h-8 w-8 p-0"
                              title={t('common.previousPage', 'Previous page')}
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>

                            <div className="flex items-center space-x-1">
                              {/* Simple version: Page X of Y */}
                              <span className="text-sm font-medium px-2">
                                {t('common.page', 'Page')} {candidatePage} / {Math.max(1, Math.ceil(totalCandidates / 20))}
                              </span>
                            </div>

                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                goToCandidatePage(candidatePage + 1);
                              }}
                              disabled={!hasMoreCandidates || loadingAddCandidatesData || loadingMoreCandidates || isSearchingCandidates}
                              className="h-8 w-8 p-0"
                              title={t('common.nextPage', 'Next page')}
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>

                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                goToCandidatePage(Math.max(1, Math.ceil(totalCandidates / 20)));
                              }}
                              disabled={candidatePage >= Math.ceil(totalCandidates / 20) || loadingAddCandidatesData || loadingMoreCandidates || isSearchingCandidates}
                              className="h-8 w-8 p-0"
                              title={t('common.lastPage', 'Last page')}
                            >
                              <ChevronsRight className="h-4 w-4" />
                            </Button>
                          </div>

                          <div className="text-xs text-muted-foreground">
                            {t('common.showingResults', {
                              count: totalCandidates,
                              from: (candidatePage - 1) * 20 + 1,
                              to: Math.min(candidatePage * 20, totalCandidates),
                              defaultValue: `Affichage de ${(candidatePage - 1) * 20 + 1} à ${Math.min(candidatePage * 20, totalCandidates)} sur ${totalCandidates} candidats`
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {selectedCandidateIds.length > 0 && (
                  <div className="text-sm text-muted-foreground">
                    {t('offerDetail.candidatesSelected', { count: selectedCandidateIds.length, defaultValue: `${selectedCandidateIds.length} candidate${selectedCandidateIds.length > 1 ? 's' : ''} selected` })}
                  </div>
                )}
              </>
            )}

            <div className="flex justify-between items-center pt-4 border-t border-border">
              <Button
                variant="ghost"
                onClick={() => {
                  if (selectionMode === 'group') {
                    const filteredTeams = teams.filter(team =>
                      team.name.toLowerCase().includes(teamSearch.toLowerCase())
                    )
                    if (selectedTeamIds.length === filteredTeams.length) {
                      setSelectedTeamIds([])
                    } else {
                      setSelectedTeamIds(filteredTeams.map(t => t.id))
                    }
                  } else {
                    if (selectedCandidateIds.length > 0) {
                      setSelectedCandidateIds([])
                    } else {
                      handleSelectAllGlobally()
                    }
                  }
                }}
                disabled={
                  addingCandidates ||
                  isSearchingCandidates ||
                  (selectionMode === 'group'
                    ? teams.filter(team => team.name.toLowerCase().includes(teamSearch.toLowerCase())).length === 0
                    : availableCandidates.filter(candidate =>
                      candidate.name.toLowerCase().includes(candidateSearch.toLowerCase()) ||
                      candidate.email.toLowerCase().includes(candidateSearch.toLowerCase()) ||
                      (candidate.role && candidate.role.toLowerCase().includes(candidateSearch.toLowerCase()))
                    ).length === 0)
                }
              >
                {selectionMode === 'group'
                  ? (selectedTeamIds.length === teams.filter(team => team.name.toLowerCase().includes(teamSearch.toLowerCase())).length
                    ? t('common.deselectAll', 'Deselect All')
                    : t('common.selectAll', 'Select All'))
                  : (selectedCandidateIds.length > 0
                    ? t('common.deselectAll', 'Deselect All')
                    : (isLocallyFetchingAllIds
                      ? t('common.loading', 'Loading...')
                      : `${t('common.selectAll', 'Select All')} (${totalCandidates})`))
                }
              </Button>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddCandidateModal(false)
                    setSelectedCandidateIds([])
                    setSelectedTeamIds([])
                    setTeamSearch('')
                    setCandidateSearch('')
                  }}
                  disabled={addingCandidates}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={handleAddCandidate}
                  disabled={
                    addingCandidates ||
                    (selectionMode === 'group'
                      ? selectedTeamIds.length === 0
                      : selectedCandidateIds.length === 0)
                  }
                  loading={addingCandidates}
                >
                  {addingCandidates
                    ? (selectionMode === 'group'
                      ? t('offerDetail.addingGroups', 'Adding Groups...')
                      : t('offerDetail.addingCandidates', 'Adding Candidates...'))
                    : (selectionMode === 'group'
                      ? selectedTeamIds.length > 0
                        ? t('offerDetail.addGroupsButton', { count: selectedTeamIds.length, defaultValue: `Add ${selectedTeamIds.length} Group${selectedTeamIds.length > 1 ? 's' : ''}` })
                        : t('offerDetail.addGroupsButton', 'Add Groups')
                      : selectedCandidateIds.length > 0
                        ? t('offerDetail.addCandidatesButton', { count: selectedCandidateIds.length, defaultValue: `Add ${selectedCandidateIds.length} Candidate${selectedCandidateIds.length > 1 ? 's' : ''}` })
                        : t('offerDetail.addCandidatesButton', 'Add Candidates'))
                  }
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Create Group Confirmation Dialog */}
      <Modal
        isOpen={showCreateGroupConfirm}
        onClose={() => setShowCreateGroupConfirm(false)}
        title={t('offerDetail.createGroupForCandidates', 'Create Group for Selected Candidates?')}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('offerDetail.createGroupConfirmMessage', {
              count: selectedCandidateIds.length,
              defaultValue: `You have selected ${selectedCandidateIds.length} candidates. Would you like to create a group for them before adding them to this offer?`
            })}
          </p>
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              onClick={handleSkipGroupCreation}
            >
              {t('offerDetail.skipGroupCreation', 'Skip')}
            </Button>
            <Button
              onClick={() => {
                setShowCreateGroupConfirm(false)
                setShowCreateGroupModal(true)
              }}
            >
              {t('offerDetail.createGroup', 'Create Group')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Create Group Modal */}
      <Modal
        isOpen={showCreateGroupModal}
        onClose={() => {
          if (!addingCandidates) {
            setShowCreateGroupModal(false)
          }
        }}
        title={t('offerDetail.createGroupTitle', 'Create Group from Selected Candidates')}
      >
        <CreateGroupFromCandidatesModal
          selectedCandidateIds={selectedCandidateIds}
          availableCandidates={availableCandidates}
          onCreateGroup={handleCreateGroupFromCandidates}
          onCancel={() => {
            if (!addingCandidates) {
              setShowCreateGroupModal(false)
            }
          }}
          loading={addingCandidates}
        />
      </Modal>

      {/* AI Matching Modal */}
      {
        showAIMatching && offer && (
          <AIMatchingModal
            isOpen={showAIMatching}
            onClose={() => setShowAIMatching(false)}
            offerId={offer.id}
            offerTitle={offer.job_title}
            offerStatus={offer.status}
            onScoresUpdated={async () => {
              await refreshApplications()
              // Sync offer.minimum_score after the modal persists it during apply
              try {
                const offerResponse = await offersApi.getById(offer.id)
                if (offerResponse?.data) {
                  setOffer(offerResponse.data)
                  if (offerResponse.data.minimum_score !== undefined) {
                    setMinimumScore(offerResponse.data.minimum_score ?? 70)
                  }
                }
              } catch (error) {
                console.error('Error refreshing offer after matching apply:', error)
              }
            }}
            minimumScore={minimumScore}
            onMinimumScoreChange={updateMinimumScore}
            onOfferPublished={async () => {
              // Refresh offer data after publishing
              if (id && !isNaN(parseInt(id))) {
                try {
                  const offerId = parseInt(id)
                  const offerResponse = await offersApi.getById(offerId)
                  if (offerResponse?.data) {
                    setOffer(offerResponse.data)
                  }
                } catch (error) {
                  console.error('Error refreshing offer after publish:', error)
                }
              }
            }}
          />
        )
      }

      {/* Manage Candidates Modal */}
      <ManageCandidatesModal
        isOpen={showManageCandidatesModal}
        onClose={() => setShowManageCandidatesModal(false)}
        offerId={offer.id}
        teams={teams}
        onApplicationsUpdated={refreshApplications}
      />

      {/* Add Step Modal (offer-specific hiring step) */}
      {
        showStepModal && (() => {
          const sorted = [...offerStepsForPosition].sort((a, b) => (a.order || 0) - (b.order || 0))
          const { positionOptions, defaultPosition } = buildStepPositionOptions(sorted, t, {
            useDirectOrder: true,
          })
          return (
            <HiringStepModal
              isOpen={showStepModal}
              onClose={() => setShowStepModal(false)}
              onSuccess={handleAddStepSuccess}
              step={null}
              positionOptions={positionOptions}
              defaultPosition={defaultPosition}
              stepTypes={getUserAddStepCategoryOptions(t)}
              stepColors={STEP_COLORS}
            />
          )
        })()
      }

      {
        canMassImport && (
          <AddCandidatesModal
            isOpen={showUploadCandidatesModal}
            onClose={() => setShowUploadCandidatesModal(false)}
            canMassImport={canMassImport}
            onImportComplete={async () => {
              await fetchAvailableCandidates()
              setShowUploadCandidatesModal(false)
              setSelectionMode('candidates')
            }}
          />
        )
      }

      {/* Schedule Interview Modal */}
      {
        selectedCandidateForInterview && (
          <ScheduleInterviewModal
            isOpen={showScheduleInterviewModal}
            onClose={() => {
              setShowScheduleInterviewModal(false)
              setSelectedCandidateForInterview(null)
              setSelectedStepOrderForInterview(undefined)
            }}
            candidate={selectedCandidateForInterview.candidate}
            application={selectedCandidateForInterview.application}
            offerId={offer?.id}
            steps={hiringSteps.map(step => ({
              id: step.id,
              name: step.name,
              order: step.order,
              step_type: step.step_type,
              color: step.color
            }))}
            defaultStepOrder={selectedStepOrderForInterview}
            existingInterview={selectedCandidateForInterview.existingInterview}
            onSuccess={handleInterviewScheduled}
          />
        )
      }
      {/* Video Modal */}
      <Modal
        isOpen={isVideoModalOpen}
        onClose={() => {
          setIsVideoModalOpen(false)
          setSelectedVideoUrl(null)
          setVideoModalTitle('')
          setSelectedInterviewForResponses(null)
          setActiveSegmentIndex(0)
        }}
        title={videoModalTitle}
        size="lg"
      >
        <div className="space-y-4">
          {/* Questions display */}
          {(() => {
            const currentInterview = selectedInterviewForResponses

            if (currentInterview && currentInterview.interview_responses && currentInterview.interview_responses.length > 0) {
              const hasSegments = currentInterview.interview_responses.some((r: any) => r.video_segment)

              if (hasSegments) {
                const activeResponse = currentInterview.interview_responses[activeSegmentIndex]

                return (
                  <div className="bg-muted p-4 rounded-lg flex flex-col items-center text-center space-y-2 min-h-[80px] justify-center transition-all duration-300">
                    <Badge variant="outline" className="mb-1 bg-primary/10 text-primary border-primary/20">
                      {t('offerDetail.activeQuestion', 'Active Question')}
                    </Badge>
                    <h4 className="font-medium text-lg text-foreground">
                      {activeResponse?.question_text}
                    </h4>
                  </div>
                )
              }

              // Fallback for single video with timestamps
              const activeResponse = currentInterview.interview_responses.find((response: any) => {
                const start = response.start_timestamp !== undefined && response.start_timestamp !== null ? response.start_timestamp : 0
                const end = response.end_timestamp !== undefined && response.end_timestamp !== null ? response.end_timestamp : Number.MAX_SAFE_INTEGER
                return currentVideoTime >= start && currentVideoTime <= end
              })

              return (
                <div className="bg-muted p-4 rounded-lg flex flex-col items-center text-center space-y-2 min-h-[100px] justify-center transition-all duration-300">
                  {activeResponse ? (
                    <>
                      <Badge variant="outline" className="mb-2 bg-primary/10 text-primary border-primary/20">
                        {t('offerDetail.activeQuestion', 'Active Question')}
                      </Badge>
                      <h4 className="font-medium text-lg text-foreground animate-in fade-in slide-in-from-bottom-2 duration-300">
                        {activeResponse.question_text}
                      </h4>
                      {activeResponse.end_timestamp !== null && activeResponse.end_timestamp !== undefined &&
                        activeResponse.start_timestamp !== null && activeResponse.start_timestamp !== undefined && (
                          <div className="flex items-center text-xs text-muted-foreground mt-2 font-mono">
                            <Clock className="h-3 w-3 mr-1" />
                            {/* Show time elapsed in question */}
                            {(() => {
                              const elapsed = Math.max(0, currentVideoTime - activeResponse.start_timestamp)
                              const total = activeResponse.end_timestamp - activeResponse.start_timestamp
                              const formatTime = (seconds: number) => {
                                const m = Math.floor(seconds / 60).toString().padStart(2, '0')
                                const s = Math.floor(seconds % 60).toString().padStart(2, '0')
                                return `${m}:${s}`
                              }
                              return `${formatTime(elapsed)} / ${formatTime(total)}`
                            })()}
                          </div>
                        )}
                    </>
                  ) : (
                    <div className="flex flex-col items-center text-muted-foreground">
                      <span className="text-sm italic">{t('offerDetail.noActiveQuestion', 'Waiting for next question...')}</span>
                      {(() => {
                        // Find next question
                        const nextResponse = currentInterview.interview_responses.find((r: any) => (r.start_timestamp || 0) > currentVideoTime)
                        if (nextResponse) {
                          const questionText = nextResponse.question_text
                          return <span className="text-xs mt-1">Next: {questionText.slice(0, 50)}{questionText.length > 50 ? '...' : ''}</span>
                        }
                        return null
                      })()}
                    </div>
                  )}
                </div>
              )
            } else if (currentInterview && currentInterview.questions) {
              // Fallback if interview_responses not available but questions are
              let allQuestions: { fr: string, en?: string }[] = []

              if (currentInterview.language === 'custom' && currentInterview.questions.fr && currentInterview.questions.en) {
                // Group FR and EN questions
                const sectionsFr = currentInterview.questions.fr || {};
                const sectionsEn = currentInterview.questions.en || {};

                Object.keys(sectionsFr).forEach(section => {
                  const qFrList = sectionsFr[section] || [];
                  const qEnList = sectionsEn[section] || [];

                  qFrList.forEach((qFrObj: any, idx: number) => {
                    const qEnObj = qEnList[idx];
                    allQuestions.push({
                      fr: typeof qFrObj === 'string' ? qFrObj : qFrObj.question,
                      en: qEnObj ? (typeof qEnObj === 'string' ? qEnObj : qEnObj.question) : undefined
                    });
                  });
                });
              } else {
                // Standard mode
                let flatQuestions: string[] = []
                if (Array.isArray(currentInterview.questions)) {
                  flatQuestions = currentInterview.questions.map((q: any) => typeof q === 'string' ? q : q.question)
                } else if (typeof currentInterview.questions === 'object') {
                  Object.values(currentInterview.questions).forEach((section: any) => {
                    if (Array.isArray(section)) {
                      section.forEach((q: any) => flatQuestions.push(typeof q === 'string' ? q : q.question))
                    }
                  })
                }
                allQuestions = flatQuestions.map(q => ({ fr: q }));
              }

              if (allQuestions.length > 0) {
                return (
                  <div className="bg-muted p-4 rounded-lg space-y-2 max-h-40 overflow-y-auto">
                    <h4 className="font-medium text-sm text-foreground mb-2">{t('offerDetail.interviewQuestions', 'Interview Questions')}</h4>
                    <ul className="space-y-3">
                      {allQuestions.map((q, i) => (
                        <li key={i} className="text-sm flex flex-col gap-1 border-b border-border/50 pb-2 last:border-0 last:pb-0">
                          <div className="flex gap-2">
                            <span className="font-medium text-muted-foreground shrink-0">Q{i + 1}:</span>
                            <div className="space-y-1">
                              <p className="flex items-center gap-2">
                                <span className="text-[9px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-1 rounded uppercase">FR</span>
                                {q.fr}
                              </p>
                              {q.en && (
                                <p className="flex items-center gap-2 text-muted-foreground italic">
                                  <span className="text-[9px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-1 rounded uppercase">EN</span>
                                  {q.en}
                                </p>
                              )}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              }
            }
            return null
          })()}

          <div className="aspect-video w-full bg-black rounded-lg overflow-hidden flex items-center justify-center">
            {selectedVideoUrl ? (
              <video
                src={getMediaUrl(selectedVideoUrl) || selectedVideoUrl}
                controls
                className="w-full h-full"
                autoPlay
                onTimeUpdate={(e) => setCurrentVideoTime(e.currentTarget.currentTime)}
              />
            ) : (
              <div className="text-white flex items-center gap-2">
                <Clock className="h-5 w-5 animate-spin" />
                <span>Chargement de la vidéo...</span>
              </div>
            )}
          </div>

          {/* Professional Navigation Bar for Segments */}
          {(() => {
            const currentInterview = selectedInterviewForResponses
            const hasSegments = currentInterview?.interview_responses?.some((r: any) => r.video_segment)

            if (hasSegments && currentInterview.interview_responses.length > 1) {
              return (
                <div className="flex items-center justify-between bg-accent/20 p-3 rounded-lg border border-border/50 shadow-sm animate-in fade-in slide-in-from-top-2 duration-500">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={activeSegmentIndex === 0}
                    onClick={() => {
                      const newIndex = activeSegmentIndex - 1
                      setActiveSegmentIndex(newIndex)
                      setSelectedVideoUrl(currentInterview.interview_responses[newIndex].video_segment)
                    }}
                    className="flex items-center gap-2 hover:bg-background transition-all duration-300 group"
                  >
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-background/80 group-hover:bg-primary transition-colors">
                      <ChevronLeft className="h-3 w-3 group-hover:text-white" />
                    </div>
                    <span className="font-medium text-sm">{t('common.previous', 'Précédent')}</span>
                  </Button>

                  <div className="flex flex-col items-center flex-1 max-w-[200px]">
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1.5 opacity-60">
                      {t('offerDetail.segmentNavigation', 'Réponses')}
                    </span>
                    <div className="flex items-center gap-1.5 w-full justify-center">
                      {currentInterview.interview_responses.map((_: any, idx: number) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setActiveSegmentIndex(idx)
                            setSelectedVideoUrl(currentInterview.interview_responses[idx].video_segment)
                          }}
                          className={`h-1.5 rounded-full transition-all duration-300 ${idx === activeSegmentIndex
                            ? 'bg-primary w-10 opacity-100'
                            : 'bg-muted-foreground/20 w-3 hover:bg-muted-foreground/40'
                            }`}
                          title={`Aller à la réponse ${idx + 1}`}
                        />
                      ))}
                    </div>
                    <div className="flex items-center gap-1.5 mt-2 bg-background/50 px-2 py-0.5 rounded-full border border-border/30">
                      <span className="text-[10px] font-bold text-primary">
                        {activeSegmentIndex + 1}
                      </span>
                      <span className="text-[10px] text-muted-foreground font-medium">
                        / {currentInterview.interview_responses.length}
                      </span>
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={activeSegmentIndex === currentInterview.interview_responses.length - 1}
                    onClick={() => {
                      const newIndex = activeSegmentIndex + 1
                      setActiveSegmentIndex(newIndex)
                      setSelectedVideoUrl(currentInterview.interview_responses[newIndex].video_segment)
                    }}
                    className="flex items-center gap-2 hover:bg-background transition-all duration-300 group text-right"
                  >
                    <span className="font-medium text-sm">{t('common.next', 'Suivant')}</span>
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-background/80 group-hover:bg-primary transition-colors">
                      <ChevronRight className="h-3 w-3 group-hover:text-white" />
                    </div>
                  </Button>
                </div>
              )
            }
            return null
          })()}
        </div>
      </Modal>
      {/* Interview Responses Modal */}
      <Modal
        isOpen={isResponseModalOpen}
        onClose={() => {
          setIsResponseModalOpen(false)
          setSelectedInterviewForResponses(null)
          setIsViewingOnlyQuestions(false)
          setIsEditingQuestions(false)
          setEditingQuestions(null)
          setEditingCommonQuestions(null)
          setShowAddInterviewQuestionSectionModal(false)
          setAddingInterviewQuestionDraft(null)
        }}
        title={isViewingOnlyQuestions ? t('offerDetail.interviewQuestions', 'Questions de l\'entretien') : t('offerDetail.interviewResponses', 'Réponses du candidat')}
        size="lg"
        footer={(
          <div className="flex justify-end gap-2">
            {isViewingOnlyQuestions && selectedInterviewForResponses?.status !== 'completed' && selectedInterviewForResponses?.status !== 'recording' && selectedInterviewForResponses?.status !== 'partial' && (
              <>
                {addingInterviewQuestionDraft ? (
                  <>
                    <Button variant="outline" onClick={handleCancelAddingInterviewQuestion} disabled={isSavingQuestions}>
                      {t('common.cancel', 'Annuler')}
                    </Button>
                    <Button onClick={handleSaveAddingInterviewQuestion} disabled={isSavingQuestions}>
                      {isSavingQuestions ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      {t('common.save', 'Enregistrer')}
                    </Button>
                  </>
                ) : isEditingQuestions ? (
                  <>
                    <Button variant="outline" onClick={handleOpenAddInterviewQuestionModal} disabled={isSavingQuestions}>
                      <Plus className="h-4 w-4 mr-2" />
                      {t('offerDetail.addInterviewQuestion', 'Ajouter une question')}
                    </Button>
                    <Button variant="outline" onClick={handleCancelEditingQuestions} disabled={isSavingQuestions}>
                      {t('common.cancel', 'Annuler')}
                    </Button>
                    <Button onClick={handleSaveEditedQuestions} disabled={isSavingQuestions}>
                      {isSavingQuestions ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      {t('common.save', 'Enregistrer')}
                    </Button>
                  </>
                ) : (
                  <div className="flex gap-2">
                    <Button onClick={handleOpenAddInterviewQuestionModal} variant="outline" className="border-primary/20 hover:bg-primary/5">
                      <Plus className="h-4 w-4 mr-2" />
                      {t('offerDetail.addInterviewQuestion', 'Ajouter une question')}
                    </Button>
                    <Button onClick={handleStartEditingQuestions} variant="outline" className="border-primary/20 hover:bg-primary/5">
                      <Edit className="h-4 w-4 mr-2" />
                      {t('common.edit', 'Modifier les questions')}
                    </Button>
                  </div>
                )}
              </>
            )}
            <Button
              variant={isEditingQuestions || addingInterviewQuestionDraft ? 'ghost' : 'default'}
              onClick={() => {
                setIsResponseModalOpen(false)
                setSelectedInterviewForResponses(null)
                setIsViewingOnlyQuestions(false)
                setIsEditingQuestions(false)
                setEditingQuestions(null)
                setEditingCommonQuestions(null)
                setShowAddInterviewQuestionSectionModal(false)
                setAddingInterviewQuestionDraft(null)
              }}
            >
              {t('common.close', 'Fermer')}
            </Button>
          </div>
        )}
      >
        <div className="space-y-6">
          {isViewingOnlyQuestions && selectedInterviewForResponses?.questions ? (
            <div className="grid gap-4">
              {addingInterviewQuestionDraft && !isEditingQuestions && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-primary border-b border-primary/10 pb-1">
                    {t(`offerDetail.sections.${addingInterviewQuestionDraft.section}`, getMatchingCategoryLabel(addingInterviewQuestionDraft.section, t))}
                  </h3>
                  <Card className="relative overflow-hidden rounded-2xl border border-primary/20 border-l-4 border-l-primary bg-background shadow-[0_8px_24px_rgba(15,118,110,0.08)]">
                    <CardHeader className="py-6 px-7">
                      <div className="flex items-start gap-4">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-sm font-bold text-primary mt-7">
                          <Plus className="h-5 w-5" />
                        </div>
                        <div className="flex-1 space-y-4">
                          <div className="space-y-2">
                            <label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                              {t('offerDetail.questionText', 'Texte de la question')}
                              {selectedInterviewForResponses.language === 'custom' ? ' (FR)' : ''}
                            </label>
                            <Input
                              value={addingInterviewQuestionDraft.question}
                              onChange={(e) => setAddingInterviewQuestionDraft(prev => prev ? { ...prev, question: e.target.value } : prev)}
                              placeholder={t('offerDetail.questionText', 'Texte de la question')}
                              className="h-12 w-full bg-background text-sm"
                              autoFocus
                            />
                          </div>
                          {selectedInterviewForResponses.language === 'custom' && (
                            <div className="space-y-2 pt-2 border-t border-primary/5">
                              <label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                                {t('offerDetail.questionText', 'Texte de la question')} (EN)
                              </label>
                              <Input
                                value={addingInterviewQuestionDraft.questionEn}
                                onChange={(e) => setAddingInterviewQuestionDraft(prev => prev ? { ...prev, questionEn: e.target.value } : prev)}
                                placeholder={t('offerDetail.questionText', 'Texte de la question')}
                                className="h-12 w-full bg-background text-sm italic text-foreground/80"
                              />
                            </div>
                          )}
                          <div className="space-y-3 pt-3 border-t border-primary/5">
                            <div className="space-y-2 max-w-[250px]">
                              <label className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                                <Clock className="h-3.5 w-3.5" />
                                {t('offerDetail.durationSeconds', 'Durée (secondes)')}
                              </label>
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  min={1}
                                  value={addingInterviewQuestionDraft.duration}
                                  onChange={(e) => setAddingInterviewQuestionDraft(prev => prev ? { ...prev, duration: parseInt(e.target.value) || 0 } : prev)}
                                  className="h-12 w-28 bg-background text-center font-semibold"
                                />
                                <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">secondes</span>
                              </div>
                            </div>
                            <div className="space-y-2 pt-2">
                              <label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                                {t('offerDetail.questionDescription', 'Description pour l’évaluation IA')}
                              </label>
                              <Textarea
                                value={addingInterviewQuestionDraft.description}
                                onChange={(e) => setAddingInterviewQuestionDraft(prev => prev ? { ...prev, description: e.target.value } : prev)}
                                placeholder={t('offerDetail.questionDescriptionPlaceholder', 'Description ou critères d’évaluation...')}
                                className="min-h-[76px] bg-background"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                </div>
              )}
              {isEditingQuestions && editingQuestions ? (
                // EDIT MODE
                selectedInterviewForResponses.language === 'custom' && editingQuestions?.fr && editingQuestions?.en ? (
                  Object.entries(editingQuestions.fr || {}).map(([section, questionsFr]: [string, any]) => {
                    const questionsEn = (editingQuestions.en || {})[section] || []
                    return (
                      <div key={section} className="space-y-4">
                        <h3 className="text-sm font-semibold text-primary border-b border-primary/10 pb-1">
                          {getMatchingCategoryLabel(section, t)}
                        </h3>
                        <div className="grid gap-4">
                          {questionsFr.map((qFrObj: any, idx: number) => {
                            const qEnObj = questionsEn[idx]
                            const qFr = typeof qFrObj === 'string' ? qFrObj : qFrObj.question
                            const qEn = qEnObj ? (typeof qEnObj === 'string' ? qEnObj : qEnObj.question) : null

                            return (
                              <Card key={idx} className="relative overflow-hidden rounded-2xl border border-primary/20 border-l-4 border-l-primary bg-background shadow-[0_8px_24px_rgba(15,118,110,0.08)]">
                                <CardHeader className="py-6 px-7">
                                  <div className="flex gap-4">
                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-sm font-bold text-primary mt-1">
                                      {idx + 1}
                                    </div>
                                    <div className="space-y-4 flex-1">
                                      <div className="space-y-2">
                                        <div className="flex items-center justify-between gap-2">
                                          <div className="flex items-center gap-2">
                                            <span className="text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-1.5 py-0.5 rounded uppercase">FR</span>
                                          </div>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleRemoveQuestion(section, idx, 'fr')}
                                            className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        </div>
                                        <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                          {t('offerDetail.questionText', 'Texte de la question')} (FR)
                                        </label>
                                        <Input
                                          value={qFr}
                                          onChange={(e) => handleUpdateQuestionText(section, idx, e.target.value, 'fr')}
                                          placeholder={t('offerDetail.questionText', 'Texte de la question')}
                                          className="h-12 text-sm bg-background"
                                        />
                                        {typeof qFrObj === 'object' && qFrObj?.duree_estimee_secondes && (
                                          <div className="flex items-center gap-1.5 mt-1 text-[10px] text-muted-foreground font-medium uppercase tracking-wider">
                                            <Clock className="h-3 w-3" />
                                            {qFrObj.duree_estimee_secondes}s
                                          </div>
                                        )}
                                      </div>
                                      {qEn !== null && (
                                        <div className="space-y-2 pt-2 border-t border-primary/5">
                                          <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                              <span className="text-[10px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 py-0.5 rounded uppercase">EN</span>
                                            </div>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => handleRemoveQuestion(section, idx, 'en')}
                                              className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                            >
                                              <Trash2 className="h-4 w-4" />
                                            </Button>
                                          </div>
                                          <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                            {t('offerDetail.questionText', 'Texte de la question')} (EN)
                                          </label>
                                          <Input
                                            value={qEn}
                                            onChange={(e) => handleUpdateQuestionText(section, idx, e.target.value, 'en')}
                                            placeholder={t('offerDetail.questionText', 'Texte de la question')}
                                            className="h-12 text-sm italic text-foreground/80 bg-background"
                                          />
                                          {typeof qEnObj === 'object' && qEnObj?.duree_estimee_secondes && (
                                            <div className="flex items-center gap-1.5 mt-1 text-[10px] text-muted-foreground font-medium uppercase tracking-wider">
                                              <Clock className="h-3 w-3" />
                                              {qEnObj.duree_estimee_secondes}s
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      <div className="space-y-3 pt-3 border-t border-primary/5">
                                        <div className="space-y-1.5 max-w-[220px]">
                                          <label className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                                            <Clock className="h-3.5 w-3.5" />
                                            {t('offerDetail.durationSeconds', 'Durée (secondes)')}
                                          </label>
                                          <div className="flex items-center gap-2">
                                            <Input
                                              type="number"
                                              min={1}
                                              value={typeof qFrObj === 'object' ? (qFrObj.duration ?? qFrObj.duree_estimee_secondes ?? 120) : 120}
                                              onChange={(e) => handleUpdateQuestionMetadata(section, idx, 'duration', e.target.value, 'fr')}
                                              className="h-12 w-28 bg-background text-center font-semibold"
                                            />
                                            <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">secondes</span>
                                          </div>
                                        </div>
                                        <div className="space-y-1.5">
                                          <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                            {t('offerDetail.questionDescription', 'Description pour l’évaluation IA')}
                                          </label>
                                          <Textarea
                                            value={typeof qFrObj === 'object' ? (qFrObj.description || '') : ''}
                                            onChange={(e) => handleUpdateQuestionMetadata(section, idx, 'description', e.target.value, 'fr')}
                                            placeholder={t('offerDetail.questionDescriptionPlaceholder', 'Description ou critères d’évaluation...')}
                                            className="min-h-[76px] bg-background"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </CardHeader>
                              </Card>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })
                ) : (
                  Object.entries(editingQuestions).filter(([, questions]: [string, any]) => Array.isArray(questions) && questions.length > 0).map(([section, questions]: [string, any]) => (
                    <div key={section} className="space-y-3">
                      <h3 className="text-sm font-semibold text-primary border-b border-primary/10 pb-1">
                        {getMatchingCategoryLabel(section, t)}
                      </h3>
                      <div className="grid gap-3">
                        {questions.map((questionObj: any, idx: number) => {
                          const qText = typeof questionObj === 'string' ? questionObj : questionObj.question
                          return (
                            <Card key={idx} className="relative overflow-hidden rounded-2xl border border-primary/20 border-l-4 border-l-primary bg-background shadow-[0_8px_24px_rgba(15,118,110,0.08)]">
                              <CardHeader className="py-6 px-7">
                                <div className="flex items-start gap-4">
                                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-sm font-bold text-primary mt-7">
                                    {idx + 1}
                                  </div>
                                  <div className="flex-1 space-y-2">
                                    <div className="absolute right-6 top-[52px]">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemoveQuestion(section, idx)}
                                        title={t('common.delete', 'Supprimer')}
                                        className="h-9 w-9 p-0 text-destructive/60 hover:text-destructive hover:bg-destructive/10"
                                      >
                                        <Trash2 className="h-5 w-5" />
                                      </Button>
                                    </div>
                                    <label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground pr-12">
                                      {t('offerDetail.questionText', 'Texte de la question')}
                                    </label>
                                    <Input
                                      value={qText}
                                      onChange={(e) => handleUpdateQuestionText(section, idx, e.target.value)}
                                      placeholder={t('offerDetail.questionText', 'Texte de la question')}
                                      className="h-12 w-full bg-background text-sm"
                                    />
                                    <div className="space-y-3 pt-3 border-t border-primary/5">
                                      <div className="space-y-2 max-w-[250px]">
                                        <label className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                                          <Clock className="h-3.5 w-3.5" />
                                          {t('offerDetail.durationSeconds', 'Durée (secondes)')}
                                        </label>
                                        <div className="flex items-center gap-2">
                                          <Input
                                            type="number"
                                            min={1}
                                            value={typeof questionObj === 'object' ? (questionObj.duration ?? questionObj.duree_estimee_secondes ?? 120) : 120}
                                            onChange={(e) => handleUpdateQuestionMetadata(section, idx, 'duration', e.target.value)}
                                            className="h-12 w-28 bg-background text-center font-semibold"
                                          />
                                          <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">secondes</span>
                                        </div>
                                      </div>
                                      <div className="space-y-2 pt-2">
                                        <label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                                          {t('offerDetail.questionDescription', 'Description pour l’évaluation IA')}
                                        </label>
                                        <Textarea
                                          value={typeof questionObj === 'object' ? (questionObj.description || '') : ''}
                                          onChange={(e) => handleUpdateQuestionMetadata(section, idx, 'description', e.target.value)}
                                          placeholder={t('offerDetail.questionDescriptionPlaceholder', 'Description ou critères d’évaluation...')}
                                          className="min-h-[76px] bg-background"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </CardHeader>
                            </Card>
                          )
                        })}
                      </div>
                    </div>
                  ))
                )
              ) : (
                // VIEW MODE
                selectedInterviewForResponses.language === 'custom' && selectedInterviewForResponses.questions?.fr && selectedInterviewForResponses.questions?.en ? (
                  // Custom language mode: show both FR and EN versions
                  Object.entries(selectedInterviewForResponses.questions.fr || {}).map(([section, questionsFr]: [string, any]) => {
                    const questionsEn = (selectedInterviewForResponses.questions.en || {})[section] || []
                    return (
                      <div key={section} className="space-y-4">
                        <h3 className="text-sm font-semibold text-primary border-b border-primary/10 pb-1">
                          {getMatchingCategoryLabel(section, t)}
                        </h3>
                        <div className="grid gap-4">
                          {questionsFr.map((qFrObj: any, idx: number) => {
                            const qEnObj = questionsEn[idx]
                            const qFr = typeof qFrObj === 'string' ? qFrObj : qFrObj.question
                            const qEn = qEnObj ? (typeof qEnObj === 'string' ? qEnObj : qEnObj.question) : null

                            return (
                              <Card key={idx} className="overflow-hidden border-primary/5 hover:border-primary/20 transition-colors shadow-sm">
                                <CardHeader className="bg-primary/5 py-4 px-5">
                                  <div className="flex gap-4">
                                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/20 text-xs font-bold text-primary mt-1">
                                      {idx + 1}
                                    </div>
                                    <div className="space-y-3 flex-1">
                                      {editingPath === `${section}:${idx}:fr` ? (
                                        <div className="space-y-2">
                                          <div className="flex items-center gap-2">
                                            <span className="text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-1.5 py-0.5 rounded uppercase shrink-0">FR</span>
                                          </div>
                                          <Textarea
                                            value={individualEditingValue}
                                            onChange={(e) => setIndividualEditingValue(e.target.value)}
                                            className="text-sm min-h-[80px]"
                                          />
                                          <div className="flex justify-end gap-2">
                                            <Button variant="ghost" size="sm" onClick={handleCancelIndividualEdit}>
                                              {t('common.cancel', 'Annuler')}
                                            </Button>
                                            <Button size="sm" onClick={handleSaveIndividualEdit} loading={isSavingQuestions}>
                                              {t('common.save', 'Enregistrer')}
                                            </Button>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="flex-1 space-y-1">
                                          <div className="flex items-start gap-2">
                                            <span className="text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-1.5 py-0.5 rounded uppercase shrink-0 mt-0.5">FR</span>
                                            <p className="text-sm font-medium text-foreground leading-relaxed">{qFr}</p>
                                          </div>
                                          {renderQuestionMetadata(qFrObj, 'ml-8')}
                                        </div>
                                      )}

                                      {qEn && (
                                        editingPath === `${section}:${idx}:en` ? (
                                          <div className="space-y-2 pt-2 border-t border-primary/5">
                                            <div className="flex items-center gap-2">
                                              <span className="text-[10px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 py-0.5 rounded uppercase shrink-0">EN</span>
                                            </div>
                                            <Textarea
                                              value={individualEditingValue}
                                              onChange={(e) => setIndividualEditingValue(e.target.value)}
                                              className="text-sm min-h-[80px] italic"
                                            />
                                            <div className="flex justify-end gap-2">
                                              <Button variant="ghost" size="sm" onClick={handleCancelIndividualEdit}>
                                                {t('common.cancel', 'Annuler')}
                                              </Button>
                                              <Button size="sm" onClick={handleSaveIndividualEdit} loading={isSavingQuestions}>
                                                {t('common.save', 'Enregistrer')}
                                              </Button>
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="flex-1 space-y-1">
                                            <div className="flex items-start gap-2 border-t border-border/40 pt-3 mt-1">
                                              <span className="text-[10px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 py-0.5 rounded uppercase shrink-0 mt-0.5">EN</span>
                                              <p className="text-sm text-foreground/80 leading-relaxed italic">{qEn}</p>
                                            </div>
                                            {renderQuestionMetadata(qEnObj, 'ml-8')}
                                          </div>
                                        )
                                      )}
                                    </div>
                                    {!editingPath && (
                                      <div className="flex flex-col items-center gap-1 shrink-0 pt-1">
                                        {/* Modifier FR */}
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleStartIndividualEdit('standard', idx, section, 'fr')}
                                          className="h-7 w-7 p-0 text-primary hover:text-primary hover:bg-primary/10"
                                          title="Modifier FR"
                                        >
                                          <Edit className="h-3.5 w-3.5" />
                                        </Button>
                                        {/* Modifier EN */}
                                        {qEn && (
                                          <>
                                            <div className="w-5 h-px bg-border/60 my-0.5" />
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => handleStartIndividualEdit('standard', idx, section, 'en')}
                                              className="h-7 w-7 p-0 text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                                              title="Modifier EN"
                                            >
                                              <Edit className="h-3.5 w-3.5" />
                                            </Button>
                                          </>
                                        )}
                                        {/* Séparateur — Supprimer (global FR+EN) */}
                                        <div className="w-5 h-px bg-border/60 my-0.5" />
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleRemoveQuestion(section, idx)}
                                          className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                          title="Supprimer (FR + EN)"
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                </CardHeader>
                              </Card>

                            )
                          })}
                        </div>
                      </div>
                    )
                  })
                ) : (
                  // Standard mode: show single language
                  Object.entries(selectedInterviewForResponses.questions).filter(([, questions]: [string, any]) => Array.isArray(questions) && questions.length > 0).map(([section, questions]: [string, any]) => (
                    <div key={section} className="space-y-3">
                      <h3 className="text-sm font-semibold text-primary border-b border-primary/10 pb-1">
                        {getMatchingCategoryLabel(section, t)}
                      </h3>
                      <div className="grid gap-3">
                        {questions.map((questionObj: any, idx: number) => {
                          const qText = typeof questionObj === 'string' ? questionObj : questionObj.question
                          const isEditing = editingPath === `${section}:${idx}`

                          return (
                            <Card key={idx} className="overflow-hidden border-primary/5 hover:border-primary/20 transition-colors shadow-sm">
                              <CardHeader className="bg-primary/5 py-3 px-4">
                                <div className="flex items-start gap-3">
                                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/20 text-[10px] font-bold text-primary mt-1">
                                    {idx + 1}
                                  </div>
                                  <div className="flex-1">
                                    {isEditing ? (
                                      <div className="space-y-2">
                                        <Textarea
                                          value={individualEditingValue}
                                          onChange={(e) => setIndividualEditingValue(e.target.value)}
                                          className="text-sm min-h-[80px]"
                                        />
                                        <div className="flex justify-end gap-2">
                                          <Button variant="ghost" size="sm" onClick={handleCancelIndividualEdit}>
                                            {t('common.cancel', 'Annuler')}
                                          </Button>
                                          <Button size="sm" onClick={handleSaveIndividualEdit} loading={isSavingQuestions}>
                                            {t('common.save', 'Enregistrer')}
                                          </Button>
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        <p className="text-sm text-foreground leading-relaxed">
                                          {qText}
                                        </p>
                                        {renderQuestionMetadata(questionObj)}
                                      </>
                                    )}
                                  </div>
                                  {!isEditing && (
                                    <div className="flex items-center gap-1 shrink-0">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleStartIndividualEdit('standard', idx, section)}
                                        className="h-7 w-7 p-0 text-primary hover:text-primary hover:bg-primary/10"
                                      >
                                        <Edit className="h-3.5 w-3.5" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemoveQuestion(section, idx)}
                                        className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </CardHeader>
                            </Card>
                          )
                        })}
                      </div>
                    </div>
                  ))
                )
              )}

              {/* Common Questions in Question Preview */}
              {(isEditingQuestions ? (editingCommonQuestions && editingCommonQuestions.length > 0) : (selectedInterviewForResponses.common_questions && selectedInterviewForResponses.common_questions.length > 0)) && (
                <div className="space-y-3 mt-6">
                  <h3 className="text-sm font-semibold capitalize text-primary border-b border-primary/10 pb-1 flex items-center gap-2">
                    <Sparkles className="h-4 w-4" />
                    {t('offerDetail.commonQuestions', 'Questions Communes')}
                  </h3>
                  <div className="grid gap-3">
                    {(isEditingQuestions ? editingCommonQuestions : selectedInterviewForResponses.common_questions).map((cq: any, idx: number) => {
                      const qText = typeof cq === 'string' ? cq : cq.question
                      const isIndividuallyEditing = editingPath === `common:${idx}`

                      return (
                        <Card key={`cq-${idx}`} className={`overflow-hidden border-primary/5 transition-colors shadow-sm ${(isEditingQuestions || isIndividuallyEditing) ? 'border-primary/10' : 'hover:border-primary/20 bg-primary/5'}`}>
                          <CardHeader className="py-3 px-4">
                            <div className="flex items-start gap-3">
                              <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/20 text-[10px] font-bold text-primary mt-1">
                                {idx + 1}
                              </div>
                              <div className="flex-1">
                                <div className="flex justify-between items-start gap-2">
                                  <div className="flex-1">
                                    {isEditingQuestions ? (
                                      <Textarea
                                        value={qText}
                                        onChange={(e) => handleUpdateCommonQuestionText(idx, e.target.value)}
                                        className="text-sm min-h-[80px]"
                                      />
                                    ) : isIndividuallyEditing ? (
                                      <div className="space-y-2">
                                        <Textarea
                                          value={individualEditingValue}
                                          onChange={(e) => setIndividualEditingValue(e.target.value)}
                                          className="text-sm min-h-[80px]"
                                        />
                                        <div className="flex justify-end gap-2">
                                          <Button variant="ghost" size="sm" onClick={handleCancelIndividualEdit}>
                                            {t('common.cancel', 'Annuler')}
                                          </Button>
                                          <Button size="sm" onClick={handleSaveIndividualEdit} loading={isSavingQuestions}>
                                            {t('common.save', 'Enregistrer')}
                                          </Button>
                                        </div>
                                      </div>
                                    ) : (
                                      <p className="text-sm text-foreground leading-relaxed">
                                        {qText}
                                      </p>
                                    )}
                                  </div>
                                  {!isEditingQuestions && !isIndividuallyEditing && (
                                    <div className="flex gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleStartIndividualEdit('common', idx)}
                                        className="h-7 w-7 p-0 text-primary hover:text-primary hover:bg-primary/10"
                                      >
                                        <Edit className="h-3.5 w-3.5" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemoveCommonQuestion(idx)}
                                        className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                                {renderQuestionMetadata(cq)}
                              </div>
                            </div>
                          </CardHeader>
                        </Card>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : selectedInterviewForResponses?.interview_responses && selectedInterviewForResponses.interview_responses.length > 0 ? (
            <div className="grid gap-4">
              {selectedInterviewForResponses.interview_responses.map((resp: any, idx: number) => (
                <Card key={resp.id || idx} className="overflow-hidden border-primary/10">
                  <CardHeader className="bg-primary/5 py-3 px-4">
                    <div className="flex items-center gap-2">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white">
                        {idx + 1}
                      </div>
                      <CardTitle className="text-sm font-semibold">{t('offerDetail.question', 'Question')}</CardTitle>
                    </div>
                    <p className="mt-2 text-sm text-foreground leading-relaxed">
                      {resp.question_text}
                    </p>
                  </CardHeader>
                  {!isViewingOnlyQuestions && (
                    <CardContent className="py-4 px-4">
                      <div className="flex items-start gap-2">
                        <Badge variant="outline" className="mt-0.5 shrink-0 bg-secondary/30 text-[10px]">
                          {t('offerDetail.response', 'Réponse')}
                        </Badge>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                          {resp.response || t('offerDetail.noResponse', 'Aucune transcription disponible pour cette question.')}
                        </p>
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          ) : (selectedInterviewForResponses?.questions || (selectedInterviewForResponses?.common_questions && selectedInterviewForResponses.common_questions.length > 0)) ? (
            <div className="grid gap-4">
              {selectedInterviewForResponses?.questions && Object.entries(selectedInterviewForResponses.questions).filter(([, questions]: [string, any]) => Array.isArray(questions) && questions.length > 0).map(([section, questions]: [string, any]) => (
                <div key={section} className="space-y-3">
                  <h3 className="text-sm font-semibold text-primary">
                    {getMatchingCategoryLabel(section, t)}
                  </h3>
                  {questions.map((questionObj: any, idx: number) => {
                    const qText = typeof questionObj === 'string' ? questionObj : questionObj.question
                    const isEditing = editingPath === `${section}:${idx}`

                    return (
                      <Card key={idx} className="overflow-hidden border-primary/10">
                        <CardHeader className="bg-primary/5 py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white shrink-0">
                              {idx + 1}
                            </div>
                            <CardTitle className="text-sm font-semibold flex-1">{t('offerDetail.question', 'Question')}</CardTitle>
                            {!isEditing && (
                              <div className="flex items-center gap-1 shrink-0">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleStartIndividualEdit('standard', idx, section)}
                                  className="h-7 w-7 p-0 text-primary hover:text-primary hover:bg-primary/10"
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveQuestion(section, idx)}
                                  className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            )}
                          </div>
                          {isEditing ? (
                            <div className="mt-3 space-y-2">
                              <Textarea
                                value={individualEditingValue}
                                onChange={(e) => setIndividualEditingValue(e.target.value)}
                                className="text-sm min-h-[80px]"
                              />
                              <div className="flex justify-end gap-2">
                                <Button variant="outline" size="sm" onClick={handleCancelIndividualEdit}>
                                  {t('common.cancel', 'Annuler')}
                                </Button>
                                <Button size="sm" onClick={handleSaveIndividualEdit} loading={isSavingQuestions}>
                                  {t('common.save', 'Enregistrer')}
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p className="mt-2 text-sm text-foreground leading-relaxed">
                                {qText}
                              </p>
                              {renderQuestionMetadata(questionObj)}
                            </>
                          )}
                        </CardHeader>
                      </Card>
                    )
                  })}
                </div>
              ))}

              {selectedInterviewForResponses?.common_questions && selectedInterviewForResponses.common_questions.length > 0 && (
                <div className="space-y-3 mt-4">
                  <h3 className="text-sm font-semibold capitalize text-primary flex items-center gap-2">
                    <Sparkles className="h-4 w-4" />
                    {t('offerDetail.commonQuestions', 'Questions Communes')}
                  </h3>
                  {selectedInterviewForResponses.common_questions.map((cq: any, idx: number) => {
                    const qText = typeof cq === 'string' ? cq : cq.question
                    const isEditing = editingPath === `common:${idx}`

                    return (
                      <Card key={`cq-${idx}`} className="overflow-hidden border-primary/10 bg-primary/5">
                        <CardHeader className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white shrink-0">
                              {idx + 1}
                            </div>
                            <CardTitle className="text-sm font-semibold flex-1">{t('offerDetail.commonQuestion', 'Question Commune')}</CardTitle>
                            {!isEditing && (
                              <div className="flex items-center gap-1 shrink-0">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleStartIndividualEdit('common', idx)}
                                  className="h-7 w-7 p-0 text-primary hover:text-primary hover:bg-primary/10"
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveCommonQuestion(idx)}
                                  className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            )}
                          </div>
                          {isEditing ? (
                            <div className="mt-3 space-y-2">
                              <Textarea
                                value={individualEditingValue}
                                onChange={(e) => setIndividualEditingValue(e.target.value)}
                                className="text-sm min-h-[80px]"
                              />
                              <div className="flex justify-end gap-2">
                                <Button variant="outline" size="sm" onClick={handleCancelIndividualEdit}>
                                  {t('common.cancel', 'Annuler')}
                                </Button>
                                <Button size="sm" onClick={handleSaveIndividualEdit} loading={isSavingQuestions}>
                                  {t('common.save', 'Enregistrer')}
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p className="mt-2 text-sm text-foreground leading-relaxed font-medium">
                                {qText}
                              </p>
                              <div className="flex items-center gap-1.5 mt-2 text-[10px] text-muted-foreground font-medium uppercase">
                                <Clock className="h-3 w-3" />
                                {cq.duration}s
                              </div>
                            </>
                          )}
                        </CardHeader>
                      </Card>
                    )
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <FileText className="h-8 w-8 text-muted-foreground opacity-20" />
              </div>
              <p className="text-muted-foreground">
                {t('offerDetail.noResponses', 'Aucune réponse disponible pour le moment.')}
              </p>
            </div>
          )}
        </div>
      </Modal >
      {/* Batch Generation Confirmation Modal */}
      < Modal
        isOpen={showBatchGenerateModal}
        onClose={() => setShowBatchGenerateModal(false)
        }
        title={t('offerDetail.generateInterviews', 'Générer les entretiens')}
        size="sm"
        footer={
          <div className="flex justify-between items-center w-full">
            {/* <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Switch
                  id="auto-generate-interviews-modal"
                  checked={autoGenerateInterviews}
                  onCheckedChange={handleAutoGenerateToggle}
                />
                <label htmlFor="auto-generate-interviews-modal" className="text-sm font-medium cursor-pointer">
                  {t('offerDetail.autoGenerate', 'Génération automatique')}
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="auto-send-emails-modal"
                  checked={autoSendInterviewEmails}
                  onCheckedChange={handleAutoSendEmailsToggle}
                />
                <label htmlFor="auto-send-emails-modal" className="text-sm font-medium cursor-pointer">
                  {t('offerDetail.autoSend', 'Envoi automatique d\'emails')}
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="auto-evaluate-interviews-modal"
                  checked={autoEvaluateInterviews}
                  onCheckedChange={handleAutoEvaluateToggle}
                />
                <label htmlFor="auto-evaluate-interviews-modal" className="text-sm font-medium cursor-pointer">
                  {t('offerDetail.autoEvaluate', 'Évaluation automatique')}
                </label>
              </div>
            </div> */}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowBatchGenerateModal(false)}
                disabled={isGeneratingBatch}
              >
                {t('common.cancel', 'Annuler')}
              </Button>
              <Button
                onClick={handleBatchGenerate}
                disabled={isGeneratingBatch || !offer?.interview_deadline || (Object.values(interviewQuestionsPerSection) as number[]).reduce((a: number, b: number) => a + b, 0) > 15 || (Object.values(interviewQuestionsPerSection) as number[]).reduce((a: number, b: number) => a + b, 0) === 0}
                loading={isGeneratingBatch}
              >
                {t('common.confirm', 'Confirmer')}
              </Button>
            </div>
          </div>
        }
      >
        <div className="py-6 space-y-6">
          <p className="text-sm text-muted-foreground">
            {t('offerDetail.confirmBatchGeneration', 'Veuillez configurer les entretiens.')}
          </p>

          <div className="space-y-3">
            <label className="text-sm font-medium">
              {t('common.language', 'Langue')}
            </label>
            <CustomSelect
              value={interviewLanguage}
              onChange={(value) => setInterviewLanguage(value as 'en' | 'fr' | 'custom')}
              options={[
                { value: 'fr', label: t('common.french', 'Français') },
                { value: 'en', label: t('common.english', 'Anglais') },
                { value: 'custom', label: t('common.custom', 'Personnalisé (Choix candidat)') }
              ]}
              placeholder={t('common.selectLanguage', 'Sélectionner une langue')}
              disabled={interviewAudios.length > 0}
            />
            {interviewAudios.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {t('offerDetail.languageAlreadySet', 'La langue a déjà été définie lors de la première génération et ne peut pas être modifiée.')}
              </p>
            )}
          </div>

          <div className="space-y-4 pt-2 border-t">
            <div className="flex justify-between items-center">
              <label className="text-sm font-semibold">
                {t('offerDetail.questionsPerSection', 'Questions par section')}
              </label>
              <Badge variant={(Object.values(interviewQuestionsPerSection) as number[]).reduce((a: number, b: number) => a + b, 0) > 15 ? 'destructive' : 'default'} className="flex gap-1">
                <span>Total: {(Object.values(interviewQuestionsPerSection) as number[]).reduce((a: number, b: number) => a + b, 0)}</span>
                <span>/ 15</span>
              </Badge>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {Object.entries(interviewQuestionsPerSection).map(([section, count]) => (
                <div key={section} className="space-y-2">
                  <label className="text-xs font-medium capitalize">
                    {t(`offerDetail.sections.${section}`, section.replace('_', ' '))}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      max="15"
                      disabled={interviewAudios.length > 0}
                      value={count}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0
                        setInterviewQuestionsPerSection(prev => ({
                          ...prev,
                          [section]: Math.min(15, Math.max(0, val))
                        }))
                      }}
                      className={`w-full px-3 py-1 text-sm border rounded-md bg-transparent focus:outline-none focus:ring-1 focus:ring-primary ${interviewAudios.length > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    />
                  </div>
                </div>
              ))}
            </div>

            {(Object.values(interviewQuestionsPerSection) as number[]).reduce((a: number, b: number) => a + b, 0) > 15 && (
              <p className="text-xs text-destructive font-medium">
                {t('offerDetail.totalQuestionsExceeded', 'Le nombre total de questions ne peut pas dépasser 15.')}
              </p>
            )}

            <p className="text-xs text-muted-foreground italic">
              {t('offerDetail.questionCountHint', 'Plus de questions permettent une évaluation plus approfondie mais augmentent la durée de l\'entretien.')}
            </p>
            {interviewAudios.length > 0 && (
              <p className="text-xs text-muted-foreground mt-2">
                {t('offerDetail.countsAlreadySet', 'La configuration des questions a déjà été définie lors de la première génération et ne peut pas être modifiée.')}
              </p>
            )}
          </div>
        </div>
      </Modal >

      {/* Choose section when adding a question to an interview */}
      <Modal
        isOpen={showAddInterviewQuestionSectionModal}
        onClose={() => setShowAddInterviewQuestionSectionModal(false)}
        title={t('offerDetail.chooseQuestionSection', 'Choisir une section')}
        size="sm"
        footer={(
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowAddInterviewQuestionSectionModal(false)}>
              {t('common.cancel', 'Annuler')}
            </Button>
          </div>
        )}
      >
        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            {t(
              'offerDetail.chooseQuestionSectionHint',
              'Cliquez sur + à côté d\'une section pour y ajouter une question.'
            )}
          </p>
          <div className="space-y-2">
            {MANUAL_QUESTION_SECTIONS.map((section) => (
              <div
                key={section}
                className="flex items-center justify-between gap-3 rounded-xl border border-primary/10 bg-muted/5 px-4 py-3 hover:border-primary/25 hover:bg-primary/5 transition-colors"
              >
                <span className="text-sm font-medium text-foreground">
                  {t(`offerDetail.sections.${section}`, getMatchingCategoryLabel(section, t))}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0 shrink-0 border-primary/20 text-primary hover:bg-primary hover:text-white"
                  onClick={() => handleAddQuestion(section)}
                  title={t('offerDetail.addQuestionToSection', 'Ajouter une question à cette section')}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {/* Common Questions Management Modal */}
      < Modal
        isOpen={showCommonQuestionsModal}
        onClose={() => {
          setShowCommonQuestionsModal(false)
          setIsCreatingManualQuestions(false)
        }}
        title={isCreatingManualQuestions
          ? t('offerDetail.createManualQuestions', 'Créer manuellement les questions')
          : t('offerDetail.manageCommonQuestions', 'Questions Communes')}
        size="lg"
      >
        <div className="py-2 space-y-6">
          {/* Professional Tip Banner */}
          <div className="relative overflow-hidden rounded-2xl border border-primary/10 bg-gradient-to-br from-primary/5 via-primary/[0.02] to-transparent p-5">
            <div className="relative z-10 flex gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Lightbulb className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <h4 className="text-sm font-bold text-primary flex items-center gap-2">
                  {t('common.tip', 'Conseil')}
                </h4>
                <p className="text-xs text-muted-foreground leading-relaxed max-w-[500px]">
                  {t('offerDetail.commonQuestionsDescription', 'Ces questions seront posées à tous les candidats lors de leur entretien. Utilisez-les pour évaluer les compétences transversales ou la culture de l\'entreprise.')}
                </p>
                {isCreatingManualQuestions ? (
                  <>
                    <p className="text-xs text-muted-foreground leading-relaxed max-w-[500px]">
                      {t('offerDetail.questionSectionHint', 'Chaque question est rattachée à une section d\'évaluation : la note finale applique le poids configuré pour cette section.')}
                    </p>
                    <p className="text-xs font-medium text-primary">
                      {t('offerDetail.manualQuestionsLimitHint', 'Vous pouvez créer jusqu’à 15 questions maximum pour un entretien de validation.')}
                    </p>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground leading-relaxed max-w-[500px]">
                    {t('offerDetail.commonQuestionsSectionHint', 'Ces questions sont automatiquement rattachées à la section Questions Communes.')}
                  </p>
                )}
              </div>
            </div>
            <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-primary/5 blur-2xl" />
          </div>

          {isCreatingManualQuestions && (
            <div className="space-y-3">
              <label className="text-sm font-medium">
                {t('common.language', 'Langue')}
                {!offer?.manual_interview_questions_created && commonQuestions.length === 0 && (
                  <span className="text-destructive ml-1">*</span>
                )}
              </label>
              <CustomSelect
                value={manualInterviewLanguage}
                onChange={(value) => setManualInterviewLanguage(value as 'en' | 'fr' | 'custom')}
                options={[
                  { value: 'fr', label: t('common.french', 'Français') },
                  { value: 'en', label: t('common.english', 'Anglais') },
                  { value: 'custom', label: t('common.custom', 'Personnalisé (Choix candidat)') }
                ]}
                placeholder={t('common.selectLanguage', 'Sélectionner une langue')}
                disabled={Boolean(
                  offer?.manual_interview_questions_created ||
                  commonQuestions.length > 0
                )}
              />
              {offer?.manual_interview_questions_created || commonQuestions.length > 0 ? (
                <p className="text-xs text-muted-foreground">
                  {offer?.manual_interview_questions_created
                    ? t('offerDetail.languageAlreadySet', 'La langue a déjà été définie lors de la première génération et ne peut pas être modifiée.')
                    : t('offerDetail.languageLockedAfterQuestions', 'La langue ne peut plus être modifiée dès qu\'au moins une question a été ajoutée.')}
                </p>
              ) : manualInterviewLanguage === 'custom' ? (
                <p className="text-xs text-muted-foreground">
                  {t('offerDetail.manualQuestionsCustomLanguageHint', 'Le candidat choisira la langue de l\'interface (français ou anglais) au début de l\'entretien.')}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {t('offerDetail.languageChooseBeforeQuestions', 'Choisissez la langue avant d\'ajouter des questions.')}
                </p>
              )}
            </div>
          )}

          <div className="space-y-5">
            {commonQuestions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed rounded-3xl border-primary/10 bg-muted/5 group hover:border-primary/20 transition-colors">
                <div className="h-20 w-20 rounded-3xl bg-primary/5 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                  <Sparkles className="h-10 w-10 text-primary/40" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">
                  {t('offerDetail.noCommonQuestions', 'Aucune question commune')}
                </h3>
                <p className="text-sm text-muted-foreground text-center max-w-[280px] mb-8 leading-relaxed">
                  {t('offerDetail.noCommonQuestionsDesc', 'Commencez par ajouter des questions qui seront posées à l\'ensemble des candidats.')}
                </p>
                <Button
                  disabled={!canAddManualQuestions}
                  onClick={handleAddManualInterviewQuestionClick}
                  className="rounded-full px-8 shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all font-semibold"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('offerDetail.addFirstQuestion', 'Ajouter une question')}
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between px-1">
                  <h3 className="text-sm font-bold text-muted-foreground/80 uppercase tracking-widest flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    {t('offerDetail.questionsList', 'Liste des questions')}
                    <Badge variant="secondary" className="ml-2 bg-primary/10 text-primary border-none font-bold">
                      {commonQuestions.length}
                    </Badge>
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!canAddManualQuestions}
                    onClick={handleAddManualInterviewQuestionClick}
                    className="h-9 rounded-xl text-primary hover:bg-primary/5 font-semibold group"
                  >
                    <Plus className="h-4 w-4 mr-2 group-hover:rotate-90 transition-transform duration-300" />
                    {t('offerDetail.addQuestion', 'Ajouter une question')}
                  </Button>
                </div>

                {isManualQuestionsLocked && isCreatingManualQuestions && (
                  <p className="text-xs text-muted-foreground px-1">
                    {t('offerDetail.manualQuestionsAddDisabled', 'L\'ajout de questions est désactivé après l\'enregistrement.')}
                  </p>
                )}

                {commonQuestionsLimitReached && (
                  <p className={`text-xs font-medium ${commonQuestionsLimitExceeded ? 'text-destructive' : 'text-muted-foreground'}`}>
                    {t('offerDetail.totalQuestionsExceeded', 'Le nombre total de questions ne peut pas dépasser 15.')}
                  </p>
                )}

                {isCreatingManualQuestions && commonQuestionsBySection.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-primary/5 bg-muted/5 px-4 py-3">
                    <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mr-1">
                      {t('offerDetail.questionsBySection', 'Répartition par section')}
                    </span>
                    {commonQuestionsBySection.map(({ section, count, weight }) => (
                      <Badge key={section} variant="outline" className="rounded-full border-primary/20 font-semibold">
                        {t(`offerDetail.sections.${section}`, section)} · {count} · {t('offerDetail.weightShort', 'Poids')} {weight}
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="grid gap-5">
                  {commonQuestions.map((q, index) => (
                    <div
                      key={index}
                      className="group relative overflow-hidden rounded-2xl border border-primary/5 bg-background shadow-sm hover:shadow-xl hover:border-primary/20 transition-all duration-300"
                    >
                      <div className="absolute top-0 left-0 w-1.5 h-full bg-primary/20 group-hover:bg-primary transition-colors" />

                      <div className="flex items-start gap-5 p-6 pl-8">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/5 text-xs font-bold text-primary mt-6">
                          {index + 1}
                        </div>

                        <div className="flex-1 space-y-5">
                          <div className="space-y-2">
                            <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                              {t('offerDetail.questionText', 'Énoncé de la question')}
                            </label>
                            <Input
                              placeholder={t('offerDetail.questionText')}
                              value={q.question}
                              disabled={Boolean(offer?.manual_interview_questions_created)}
                              onChange={(e) => {
                                const newQuestions = [...commonQuestions];
                                newQuestions[index].question = e.target.value;
                                setCommonQuestions(newQuestions);
                              }}
                              className="border-primary/5 bg-muted/5 focus-visible:ring-primary/20 min-h-[44px] rounded-xl text-sm font-medium"
                            />
                          </div>

                          <div className="flex flex-wrap items-start gap-8">
                            <div className="space-y-2">
                              <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                                <Clock className="h-3 w-3" />
                                {t('offerDetail.durationSeconds', 'Temps de réponse')}
                              </label>
                              <div className="flex items-center gap-3">
                                <Input
                                  type="number"
                                  className="w-24 border-primary/5 bg-muted/5 focus-visible:ring-primary/20 rounded-xl h-10 text-center font-bold"
                                  value={q.duration}
                                  disabled={Boolean(offer?.manual_interview_questions_created)}
                                  onChange={(e) => {
                                    const newQuestions = [...commonQuestions];
                                    newQuestions[index].duration = parseInt(e.target.value) || 0;
                                    setCommonQuestions(newQuestions);
                                  }}
                                />
                                <span className="text-xs font-semibold text-muted-foreground">{t('offerDetail.seconds', 'secondes')}</span>
                              </div>
                            </div>

                            {isCreatingManualQuestions ? (
                              <div className="space-y-2 min-w-[240px] flex-1">
                                <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                                  <Scale className="h-3 w-3" />
                                  {t('offerDetail.questionSection', 'Section d\'évaluation')}
                                </label>
                                <div className="flex items-center gap-3">
                                  <div className="flex-1">
                                    <CustomSelect
                                      options={manualQuestionSectionOptions}
                                      value={q.section || DEFAULT_MANUAL_QUESTION_SECTION}
                                      disabled={Boolean(offer?.manual_interview_questions_created)}
                                      onChange={(value) => {
                                        const newQuestions = [...commonQuestions];
                                        newQuestions[index].section = value as InterviewQuestionSection;
                                        setCommonQuestions(newQuestions);
                                      }}
                                    />
                                  </div>
                                  <Badge variant="secondary" className="shrink-0 bg-primary/10 text-primary border-none font-bold">
                                    {t('offerDetail.weightShort', 'Poids')} {effectiveSectionWeights[q.section || DEFAULT_MANUAL_QUESTION_SECTION] ?? 1}
                                  </Badge>
                                </div>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                                  <Scale className="h-3 w-3" />
                                  {t('offerDetail.questionSection', 'Section d\'évaluation')}
                                </label>
                                <Badge variant="secondary" className="bg-primary/10 text-primary border-none font-bold">
                                  {t('offerDetail.sections.questions_communes', 'Questions Communes')}
                                </Badge>
                              </div>
                            )}
                          </div>

                          <div className="space-y-2">
                            <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                              {t('offerDetail.questionDescription', 'Description pour l\'évaluation IA')}
                            </label>
                            <Textarea
                              placeholder={t('offerDetail.questionDescriptionPlaceholder', 'Description ou critères d\'évaluation...')}
                              value={q.description || ''}
                              disabled={Boolean(offer?.manual_interview_questions_created)}
                              onChange={(e) => {
                                const newQuestions = [...commonQuestions];
                                newQuestions[index].description = e.target.value;
                                setCommonQuestions(newQuestions);
                              }}
                              className="border-primary/5 bg-muted/5 focus-visible:ring-primary/20 min-h-[60px] rounded-xl text-sm font-medium"
                            />
                          </div>
                        </div>

                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={Boolean(offer?.manual_interview_questions_created)}
                          onClick={() => {
                            if (offer?.manual_interview_questions_created) return
                            const newQuestions = commonQuestions.filter((_, i) => i !== index);
                            setCommonQuestions(newQuestions);
                          }}
                          className="mt-6 h-10 w-10 rounded-xl text-destructive/40 hover:text-destructive hover:bg-destructive/10 transition-all disabled:opacity-40 disabled:pointer-events-none"
                        >
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 flex justify-end border-t border-primary/5 pt-6 sticky bottom-0 bg-gradient-to-t from-background via-background/95 to-transparent -mx-2 px-2 pb-2">
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowCommonQuestionsModal(false)
                        setIsCreatingManualQuestions(false)
                      }}
                      className="rounded-xl px-6 font-semibold"
                    >
                      {t('common.close', 'Fermer')}
                    </Button>
                    {!offer?.manual_interview_questions_created && <Button
                      onClick={isCreatingManualQuestions ? handleCreateManualQuestions : handleSaveCommonQuestions}
                      disabled={isSavingCommonQuestions || (isCreatingManualQuestions && (commonQuestionsLimitExceeded || !manualInterviewLanguage || !offer?.interview_deadline))}
                      className="rounded-xl px-8 shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all font-bold min-w-[200px]"
                    >
                      {isSavingCommonQuestions ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {t('common.saving', 'Enregistrement...')}
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          {isCreatingManualQuestions
                            ? t('common.save', 'Enregistrer')
                            : t('offerDetail.saveChanges', 'Enregistrer les modifications')}
                        </>
                      )}
                    </Button>}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </Modal >
      {/* Confirm language before adding the first manual question */}
      <Modal
        isOpen={showConfirmLanguageModal}
        onClose={() => setShowConfirmLanguageModal(false)}
        title={t('offerDetail.confirmInterviewLanguageTitle', 'Confirmer la langue')}
        size="sm"
        overlayClassName="z-[200]"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowConfirmLanguageModal(false)}
            >
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button onClick={handleConfirmLanguageAndAddQuestion}>
              {t('common.confirm', 'Confirmer')}
            </Button>
          </div>
        }
      >
        <div className="py-6 space-y-4">
          <p className="text-sm text-foreground">
            {t(
              'offerDetail.confirmInterviewLanguageMessage',
              'Confirmez-vous la langue de l\'entretien : {{language}} ?',
              { language: getInterviewLanguageLabel(manualInterviewLanguage) }
            )}
          </p>
          <p className="text-xs text-muted-foreground">
            {t(
              'offerDetail.confirmInterviewLanguageWarning',
              'Après confirmation, la langue ne pourra plus être modifiée dès qu\'au moins une question aura été ajoutée.'
            )}
          </p>
        </div>
      </Modal>
      {/* Reset Interview Confirmation Modal */}
      <Modal
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        title={t('offerDetail.resetInterview', 'Réinitialiser l\'entretien')}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowResetConfirm(false)}
              disabled={isResetting}
            >
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleResetInterview}
              disabled={isResetting}
              loading={isResetting}
            >
              {t('common.confirm', 'Confirmer')}
            </Button>
          </div>
        }
      >
        <div className="py-6 space-y-4">
          <p className="text-sm text-foreground">
            {t('offerDetail.confirmResetInterview', 'Êtes-vous sûr de vouloir réinitialiser cet entretien ?')}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('offerDetail.resetInterviewWarning', 'Toutes les réponses, vidéos et transcriptions actuelles seront définitivement supprimées. Cette action est irréversible.')}
          </p>
        </div>
      </Modal>

      {/* Status Change Confirmation Modal */}
      <Modal
        isOpen={showStatusChangeConfirm}
        onClose={() => {
          setShowStatusChangeConfirm(false)
          setPendingStatusChange(null)
        }}
        title={
          pendingStatusChange?.newStatus === 'published'
            ? t('offers.confirmPublish.title', 'Publish Offer')
            : pendingStatusChange?.newStatus === 'offer_closed'
              ? t('offers.confirmClose.title', 'Close Offer')
              : t('offers.confirmArchive.title', 'Archive Offer')
        }
      >
        <div className="space-y-4">
          <div>
            <p className="text-sm text-foreground mb-3">
              {pendingStatusChange?.newStatus === 'published'
                ? t('offers.confirmPublish.message', 'Are you sure you want to publish this offer?')
                : pendingStatusChange?.newStatus === 'offer_closed'
                  ? t('offers.confirmClose.message', 'Are you sure you want to close this offer?')
                  : t('offers.confirmArchive.message', 'Are you sure you want to archive this offer?')}
            </p>
            {pendingStatusChange?.newStatus === 'published' ? (
              <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-md p-3">
                <p className="text-sm font-medium text-amber-900 dark:text-amber-100 mb-2">
                  {t('offers.confirmPublish.warning', 'Important:')}
                </p>
                <ul className="text-xs text-amber-800 dark:text-amber-200 space-y-1">
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">•</span>
                    <span>{t('offers.confirmPublish.cannotDraft', 'You cannot return it to draft status')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">•</span>
                    <span>{t('offers.confirmPublish.onlyArchive', 'Afterwards you can close applications or archive it')}</span>
                  </li>
                </ul>
              </div>
            ) : pendingStatusChange?.newStatus === 'offer_closed' ? (
              <div className="bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 rounded-md p-3">
                <p className="text-sm font-medium text-orange-900 dark:text-orange-100 mb-2">
                  {t('offers.confirmClose.note', 'Note:')}
                </p>
                <ul className="text-xs text-orange-800 dark:text-orange-200 space-y-1">
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">•</span>
                    <span>{t('offers.confirmClose.noCandidates', 'No new candidates can be added while the offer is closed')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">•</span>
                    <span>{t('offers.confirmClose.canReopen', 'You will not be able to reactivate it, only archive it permanently')}</span>
                  </li>
                </ul>
              </div>
            ) : (
              <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                  {t('offers.confirmArchive.note', 'Note:')}
                </p>
                <ul className="text-xs text-blue-800 dark:text-blue-200 space-y-1">
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">•</span>
                    <span>{t('offers.confirmArchive.permanent', 'Archived offers cannot change status')}</span>
                  </li>
                </ul>
              </div>
            )}
          </div>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowStatusChangeConfirm(false)
                setPendingStatusChange(null)
              }}
              disabled={savingSection === pendingStatusChange?.section}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => {
                if (pendingStatusChange) {
                  performSaveSection(pendingStatusChange.section)
                  setShowStatusChangeConfirm(false)
                  setPendingStatusChange(null)
                }
              }}
              disabled={savingSection === pendingStatusChange?.section}
              className={
                pendingStatusChange?.newStatus === 'published'
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : pendingStatusChange?.newStatus === 'offer_closed'
                    ? 'bg-orange-600 hover:bg-orange-700 text-white'
                    : 'bg-red-600 hover:bg-red-700 text-white'
              }
            >
              {savingSection === pendingStatusChange?.section ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('common.saving', 'Saving')}
                </>
              ) : (
                pendingStatusChange?.newStatus === 'published'
                  ? t('offers.confirmPublish.confirm', 'Publish Offer')
                  : pendingStatusChange?.newStatus === 'offer_closed'
                    ? t('offers.confirmClose.confirm', 'Close Offer')
                    : t('offers.confirmArchive.confirm', 'Archive Offer')
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Zero Weights Warning Modal */}
      <Modal
        isOpen={showZeroWeightsWarning}
        onClose={() => setShowZeroWeightsWarning(false)}
        title={t('common.warning', 'Warning')}
      >
        <div className="space-y-4">
          <div className="flex items-start space-x-3 text-amber-600 dark:text-amber-500 bg-amber-50 dark:bg-amber-950/30 p-3 rounded-lg border border-amber-200 dark:border-amber-800">
            <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
            <p className="text-sm font-medium">
              {t('offers.messages.cannotPublishZeroWeights', "Il n’est pas possible d’activer l’offre si tous les poids des champs sont à 0, Au moins un poids doit être différent de 0. Veuillez d'abord modifier les poids avant d'activer l'offre.")}
            </p>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={() => setShowZeroWeightsWarning(false)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {t('common.ok', 'OK')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Interview Confirmation Modal */}
      <Modal
        isOpen={showDeleteInterviewConfirm}
        onClose={() => setShowDeleteInterviewConfirm(false)}
        title={t('offerDetail.deleteInterview', 'Supprimer l\'entretien')}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteInterviewConfirm(false)}
              disabled={isDeletingInterview}
            >
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteInterview}
              disabled={isDeletingInterview}
              loading={isDeletingInterview}
            >
              {t('common.confirm', 'Confirmer')}
            </Button>
          </div>
        }
      >
        <div className="py-6 space-y-4">
          <p className="text-sm text-foreground">
            {t(
              'offerDetail.confirmDeleteInterview',
              'Supprimer uniquement cet entretien ? Le candidat restera sur l\'offre en « En attente de génération ».'
            )}
          </p>
          <p className="text-xs text-muted-foreground">
            {t(
              'offerDetail.deleteInterviewWarning',
              'L\'entretien et ses réponses seront supprimés. Le candidat n\'est pas retiré de l\'offre.'
            )}
          </p>
        </div>
      </Modal>

      {/* Delete Question Confirmation Modal */}
      <Modal
        isOpen={showDeleteQuestionModal}
        onClose={() => {
          setShowDeleteQuestionModal(false)
          setDeleteQuestionTarget(null)
        }}
        title={t('offerDetail.deleteQuestion', 'Supprimer la question')}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteQuestionModal(false)
                setDeleteQuestionTarget(null)
              }}
              disabled={isSavingQuestions}
            >
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={isSavingQuestions}
              loading={isSavingQuestions}
            >
              {t('common.confirm', 'Confirmer')}
            </Button>
          </div>
        }
      >
        <div className="py-6 space-y-4">
          <p className="text-sm text-foreground">
            {t('offerDetail.confirmDeleteQuestion', 'Êtes-vous sûr de vouloir supprimer cette question ?')}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('offerDetail.deleteQuestionWarning', 'Cette action est irréversible.')}
          </p>
        </div>
      </Modal>
      {/* AI / Manual Evaluation Modal */}
      <Modal
        isOpen={evaluationModalOpen}
        onClose={() => {
          setEvaluationModalOpen(false)
          setIsManualEvaluationMode(false)
        }}
        title={
          isManualEvaluationMode
            ? t('offerDetail.manualEvaluationTitle', "Évaluation humaine de l'entretien")
            : t('offerDetail.aiEvaluationTitle', "Évaluation IA de l'entretien")
        }
        size="lg"
      >
        {selectedEvaluation && (
          <div className="space-y-6 p-1">
            <div className="flex items-center justify-between border-b pb-4">
              <div>
                <div className="text-sm text-muted-foreground">{t('offerDetail.globalScore', 'Score Global')}</div>
                <div className="text-3xl font-bold">
                  {isEditingEvaluation ? (
                    <span className="text-3xl font-bold text-primary">
                      {editedEvaluation?.global_score || 0}%
                    </span>
                  ) : (
                    `${selectedEvaluation.global_score}%`
                  )}
                </div>
                <div className="mt-1">
                  <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                    {isManualEvaluationMode
                      ? t('offerDetail.manualEvaluation', 'Évaluation humaine')
                      : t('offerDetail.evaluateWithAI', 'Évaluation IA')}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {!isEditingEvaluation && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                    onClick={() => {
                      setEditedEvaluation(JSON.parse(JSON.stringify(selectedEvaluation)));
                      setIsEditingEvaluation(true);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                    {t('common.edit', 'Modifier')}
                  </Button>
                )}
                <div className="text-right">
                  {isManualEvaluationMode ? (
                    <FileEdit className="h-10 w-10 text-primary/20" />
                  ) : (
                    <Brain className="h-10 w-10 text-primary/20" />
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h4 className="text-sm font-semibold flex items-center gap-2 text-emerald-600">
                  <CheckCircle2 className="h-4 w-4" />
                  {t('offerDetail.strengths', 'Points Forts')}
                </h4>
                {isEditingEvaluation ? (
                  <div className="space-y-2">
                    {editedEvaluation.strengths?.map((s: string, i: number) => (
                      <div key={i} className="flex gap-2">
                        <input
                          type="text"
                          value={s}
                          onChange={(e) => {
                            const newStrengths = [...editedEvaluation.strengths];
                            newStrengths[i] = e.target.value;
                            setEditedEvaluation({ ...editedEvaluation, strengths: newStrengths });
                          }}
                          className="w-full px-2 py-1 text-sm border rounded-md"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => {
                            const newStrengths = editedEvaluation.strengths.filter((_: any, idx: number) => idx !== i);
                            setEditedEvaluation({ ...editedEvaluation, strengths: newStrengths });
                          }}
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditedEvaluation({ ...editedEvaluation, strengths: [...(editedEvaluation.strengths || []), ""] })}
                    >
                      + {t('common.add', 'Ajouter')}
                    </Button>
                  </div>
                ) : (
                  <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground">
                    {selectedEvaluation.strengths?.map((s: string, i: number) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="space-y-2">
                <h4 className="text-sm font-semibold flex items-center gap-2 text-red-600">
                  <XCircle className="h-4 w-4" />
                  {t('offerDetail.weaknesses', 'Points Faibles')}
                </h4>
                {isEditingEvaluation ? (
                  <div className="space-y-2">
                    {editedEvaluation.weaknesses?.map((w: string, i: number) => (
                      <div key={i} className="flex gap-2">
                        <input
                          type="text"
                          value={w}
                          onChange={(e) => {
                            const newWeaknesses = [...editedEvaluation.weaknesses];
                            newWeaknesses[i] = e.target.value;
                            setEditedEvaluation({ ...editedEvaluation, weaknesses: newWeaknesses });
                          }}
                          className="w-full px-2 py-1 text-sm border rounded-md"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => {
                            const newWeaknesses = editedEvaluation.weaknesses.filter((_: any, idx: number) => idx !== i);
                            setEditedEvaluation({ ...editedEvaluation, weaknesses: newWeaknesses });
                          }}
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditedEvaluation({ ...editedEvaluation, weaknesses: [...(editedEvaluation.weaknesses || []), ""] })}
                    >
                      + {t('common.add', 'Ajouter')}
                    </Button>
                  </div>
                ) : (
                  <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground">
                    {selectedEvaluation.weaknesses?.map((w: string, i: number) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-semibold">{t('common.summary', 'Résumé')}</h4>
              {isEditingEvaluation ? (
                <textarea
                  value={editedEvaluation.summary || ""}
                  onChange={(e) => setEditedEvaluation({ ...editedEvaluation, summary: e.target.value })}
                  className="w-full px-3 py-2 text-sm border rounded-md min-h-[80px]"
                />
              ) : (
                <p className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-md italic">
                  "{selectedEvaluation.summary}"
                </p>
              )}
            </div>

            {((selectedEvaluation.overall_language_level && !isEditingEvaluation) || isEditingEvaluation) && (
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/20">
                <span className="text-sm font-semibold text-muted-foreground">
                  {t('offerDetail.overallLanguageLevel', 'Niveau linguistique global (CECR) :')}
                </span>
                {isEditingEvaluation ? (
                  <CustomSelect
                    value={editedEvaluation.overall_language_level || ""}
                    onChange={(val) => setEditedEvaluation({ ...editedEvaluation, overall_language_level: val })}
                    options={[
                      { value: 'A1', label: `A1 - ${t('offerDetail.cecr.A1', 'Débutant')}` },
                      { value: 'A2', label: `A2 - ${t('offerDetail.cecr.A2', 'Élémentaire')}` },
                      { value: 'B1', label: `B1 - ${t('offerDetail.cecr.B1', 'Intermédiaire')}` },
                      { value: 'B2', label: `B2 - ${t('offerDetail.cecr.B2', 'Intermédiaire avancé')}` },
                      { value: 'C1', label: `C1 - ${t('offerDetail.cecr.C1', 'Avancé')}` },
                      { value: 'C2', label: `C2 - ${t('offerDetail.cecr.C2', 'Maîtrise')}` }
                    ]}
                    placeholder={t('offerDetail.chooseLevel', 'Choisir un niveau')}
                  />
                ) : (
                  <>
                    <Badge
                      className={
                        selectedEvaluation.overall_language_level === 'C2' ? 'bg-violet-600 text-white border-violet-600' :
                          selectedEvaluation.overall_language_level === 'C1' ? 'bg-blue-600 text-white border-blue-600' :
                            selectedEvaluation.overall_language_level === 'B2' ? 'bg-emerald-600 text-white border-emerald-600' :
                              selectedEvaluation.overall_language_level === 'B1' ? 'bg-yellow-500 text-white border-yellow-500' :
                                selectedEvaluation.overall_language_level === 'A2' ? 'bg-orange-500 text-white border-orange-500' :
                                  'bg-red-500 text-white border-red-500'
                      }
                    >
                      {selectedEvaluation.overall_language_level}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {selectedEvaluation.overall_language_level === 'C2' ? t('offerDetail.cecr.C2', 'Maîtrise parfaite') :
                        selectedEvaluation.overall_language_level === 'C1' ? t('offerDetail.cecr.C1', 'Niveau avancé') :
                          selectedEvaluation.overall_language_level === 'B2' ? t('offerDetail.cecr.B2', 'Intermédiaire avancé') :
                            selectedEvaluation.overall_language_level === 'B1' ? t('offerDetail.cecr.B1', 'Intermédiaire') :
                              selectedEvaluation.overall_language_level === 'A2' ? t('offerDetail.cecr.A2', 'Élémentaire') :
                                t('offerDetail.cecr.A1', 'Débutant')}
                    </span>
                  </>
                )}
              </div>
            )}

            <div className="space-y-4">
              <h4 className="text-sm font-semibold border-b pb-2">{t('offerDetail.detailedAnalysis', 'Analyse détaillée par section')}</h4>
              <div className="space-y-6">
                {Object.entries((isEditingEvaluation ? editedEvaluation : selectedEvaluation).sections || {}).map(([sectionName, sectionData]: [string, any]) => (
                  sectionData.evaluations?.length > 0 && (
                    <div key={sectionName} className="space-y-3 p-3 border rounded-xl bg-card">
                      <div className="flex items-center justify-between">
                        <h5 className="text-sm font-medium uppercase tracking-wider text-primary flex items-center gap-2">
                          {t(`offerDetail.sections.${sectionName}`, sectionName.replace(/_/g, ' '))}
                        </h5>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">{t('offerDetail.sectionScore', 'Score section')}:</span>
                          <Badge variant="outline">{sectionData.section_score || 0}/10</Badge>
                        </div>
                      </div>
                      <div className="space-y-3">
                        {sectionData.evaluations?.map((evalItem: any, idx: number) => (
                          <div key={idx} className="p-3 border rounded-lg space-y-2 bg-muted/10">
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-xs font-medium text-foreground flex-1">
                                Q: {evalItem.question}
                              </div>
                            </div>
                            <div className="text-xs text-muted-foreground italic">
                              R: {evalItem.response || '-'}
                            </div>
                            <div className="flex flex-wrap gap-4 pt-2 border-t text-[10px]">
                              {sectionData.mode === 'A' ? (
                                <>
                                  <div className="flex items-center gap-1">
                                    <span className="text-muted-foreground">{t('offerDetail.expertise', 'Expertise')}:</span>
                                    {isEditingEvaluation ? (
                                      <input
                                        type="number"
                                        min="0"
                                        max="10"
                                        value={evalItem.expertise_score || 0}
                                        onChange={(e) => {
                                          const newSections = { ...editedEvaluation.sections };
                                          const newEvals = [...newSections[sectionName].evaluations];
                                          const clamped = Math.min(10, Math.max(0, parseInt(e.target.value) || 0));
                                          newEvals[idx] = { ...newEvals[idx], expertise_score: clamped };
                                          newSections[sectionName] = { ...newSections[sectionName], evaluations: newEvals };
                                          setEditedEvaluation({ ...editedEvaluation, sections: newSections });
                                        }}
                                        className="w-10 px-1 py-0.5 border rounded"
                                      />
                                    ) : (
                                      <span className="font-semibold">{evalItem.expertise_score}/10</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className="text-muted-foreground">{t('offerDetail.communication', 'Communication')}:</span>
                                    {isEditingEvaluation ? (
                                      <input
                                        type="number"
                                        min="0"
                                        max="10"
                                        value={evalItem.communication_score || 0}
                                        onChange={(e) => {
                                          const newSections = { ...editedEvaluation.sections };
                                          const newEvals = [...newSections[sectionName].evaluations];
                                          const clamped = Math.min(10, Math.max(0, parseInt(e.target.value) || 0));
                                          newEvals[idx] = { ...newEvals[idx], communication_score: clamped };
                                          newSections[sectionName] = { ...newSections[sectionName], evaluations: newEvals };
                                          setEditedEvaluation({ ...editedEvaluation, sections: newSections });
                                        }}
                                        className="w-10 px-1 py-0.5 border rounded"
                                      />
                                    ) : (
                                      <span className="font-semibold">{evalItem.communication_score}/10</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className="text-muted-foreground">{t('offerDetail.scoreCredibility', 'Score Crédibilité')}:</span>
                                    {isEditingEvaluation ? (
                                      <input
                                        type="number"
                                        min="0"
                                        max="10"
                                        value={evalItem.credibility_score || 0}
                                        onChange={(e) => {
                                          const newSections = { ...editedEvaluation.sections };
                                          const newEvals = [...newSections[sectionName].evaluations];
                                          const clamped = Math.min(10, Math.max(0, parseInt(e.target.value) || 0));
                                          newEvals[idx] = { ...newEvals[idx], credibility_score: clamped };
                                          newSections[sectionName] = { ...newSections[sectionName], evaluations: newEvals };
                                          setEditedEvaluation({ ...editedEvaluation, sections: newSections });
                                        }}
                                        className="w-10 px-1 py-0.5 border rounded"
                                      />
                                    ) : (
                                      <span className="font-semibold">{evalItem.credibility_score}/10</span>
                                    )}
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="flex items-center gap-1">
                                    <span className="text-muted-foreground">{t('offerDetail.clarity', 'Clarté')}:</span>
                                    {isEditingEvaluation ? (
                                      <input
                                        type="number"
                                        min="0"
                                        max="10"
                                        value={evalItem.clarity_score || 0}
                                        onChange={(e) => {
                                          const newSections = { ...editedEvaluation.sections };
                                          const newEvals = [...newSections[sectionName].evaluations];
                                          const clamped = Math.min(10, Math.max(0, parseInt(e.target.value) || 0));
                                          newEvals[idx] = { ...newEvals[idx], clarity_score: clamped };
                                          newSections[sectionName] = { ...newSections[sectionName], evaluations: newEvals };
                                          setEditedEvaluation({ ...editedEvaluation, sections: newSections });
                                        }}
                                        className="w-10 px-1 py-0.5 border rounded"
                                      />
                                    ) : (
                                      <span className="font-semibold">{evalItem.clarity_score}/10</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className="text-muted-foreground">{t('offerDetail.scoreConsistency', 'Score Cohérence')}:</span>
                                    {isEditingEvaluation ? (
                                      <input
                                        type="number"
                                        min="0"
                                        max="10"
                                        value={evalItem.consistency_score || 0}
                                        onChange={(e) => {
                                          const newSections = { ...editedEvaluation.sections };
                                          const newEvals = [...newSections[sectionName].evaluations];
                                          const clamped = Math.min(10, Math.max(0, parseInt(e.target.value) || 0));
                                          newEvals[idx] = { ...newEvals[idx], consistency_score: clamped };
                                          newSections[sectionName] = { ...newSections[sectionName], evaluations: newEvals };
                                          setEditedEvaluation({ ...editedEvaluation, sections: newSections });
                                        }}
                                        className="w-10 px-1 py-0.5 border rounded"
                                      />
                                    ) : (
                                      <span className="font-semibold">{evalItem.consistency_score}/10</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className="text-muted-foreground">{t('offerDetail.scoreDetail', 'Score Détail')}:</span>
                                    {isEditingEvaluation ? (
                                      <input
                                        type="number"
                                        min="0"
                                        max="10"
                                        value={evalItem.detail_score || 0}
                                        onChange={(e) => {
                                          const newSections = { ...editedEvaluation.sections };
                                          const newEvals = [...newSections[sectionName].evaluations];
                                          const clamped = Math.min(10, Math.max(0, parseInt(e.target.value) || 0));
                                          newEvals[idx] = { ...newEvals[idx], detail_score: clamped };
                                          newSections[sectionName] = { ...newSections[sectionName], evaluations: newEvals };
                                          setEditedEvaluation({ ...editedEvaluation, sections: newSections });
                                        }}
                                        className="w-10 px-1 py-0.5 border rounded"
                                      />
                                    ) : (
                                      <span className="font-semibold">{evalItem.detail_score}/10</span>
                                    )}
                                  </div>
                                </>
                              )}
                              {(evalItem.language_level || isEditingEvaluation) && (
                                <div className="flex items-center gap-1">
                                  <span className="text-muted-foreground">{t('offerDetail.languageLevel', 'Niveau linguistique')}:</span>
                                  {isEditingEvaluation ? (
                                    <select
                                      value={evalItem.language_level || "General"}
                                      onChange={(e) => {
                                        const newSections = { ...editedEvaluation.sections };
                                        const newEvals = [...newSections[sectionName].evaluations];
                                        newEvals[idx] = { ...newEvals[idx], language_level: e.target.value };
                                        newSections[sectionName] = { ...newSections[sectionName], evaluations: newEvals };
                                        setEditedEvaluation({ ...editedEvaluation, sections: newSections });
                                      }}
                                      className="px-1 py-0.5 border rounded bg-background"
                                    >
                                      <option value="General">{t('offerDetail.general', 'Général')}</option>
                                      <option value="Specialise">{t('offerDetail.specialized', 'Spécialisé')}</option>
                                    </select>
                                  ) : (
                                    <span className={`font-semibold ${evalItem.language_level === 'Specialise' || evalItem.language_level === 'Spécialisé' ? 'text-blue-600' : 'text-muted-foreground'}`}>
                                      {evalItem.language_level === 'Specialise' ? t('offerDetail.specialized', 'Spécialisé') :
                                        evalItem.language_level === 'General' ? t('offerDetail.general', 'Général') :
                                          evalItem.language_level}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            {isEditingEvaluation ? (
                              <textarea
                                value={evalItem.feedback || ""}
                                onChange={(e) => {
                                  const newSections = { ...editedEvaluation.sections };
                                  const newEvals = [...newSections[sectionName].evaluations];
                                  newEvals[idx] = { ...newEvals[idx], feedback: e.target.value };
                                  newSections[sectionName] = { ...newSections[sectionName], evaluations: newEvals };
                                  setEditedEvaluation({ ...editedEvaluation, sections: newSections });
                                }}
                                className="w-full px-2 py-1 text-[11px] border rounded"
                                placeholder="Feedback..."
                              />
                            ) : (
                              <p className="text-[11px] text-muted-foreground mt-1">
                                {evalItem.feedback}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                ))}
              </div>
            </div>

            <div className="pt-4 flex justify-end gap-3 border-t">
              {isEditingEvaluation ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsEditingEvaluation(false)
                      setIsManualEvaluationMode(false)
                      setEvaluationModalOpen(false)
                    }}
                  >
                    {t('common.cancel', 'Annuler')}
                  </Button>
                  <Button
                    onClick={handleSaveEvaluation}
                    loading={isSavingEvaluation}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {t('common.save', 'Enregistrer')}
                  </Button>
                </>
              ) : (
                <Button onClick={() => {
                  setEvaluationModalOpen(false)
                  setIsManualEvaluationMode(false)
                }}>
                  {t('common.close', 'Fermer')}
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Section Weights Modal */}
      <Modal
        isOpen={showSectionWeightsModal}
        onClose={() => setShowSectionWeightsModal(false)}
        title={t('offerDetail.modifyWeights', 'Modifier les poids des sections')}
        size="md"
      >
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground italic">
            {t('offerDetail.weightsInfo', 'Ajustez l\'importance de chaque section pour le calcul du score de l\'entretien AI. 1.0 est le poids standard.')}
          </p>
          <div className="space-y-3">
            {Object.entries({ ...DEFAULT_SECTION_WEIGHTS, ...(editingSectionWeights || {}) }).map(([key, anyValue], index) => {
              const value = anyValue as number;
              const max = 10;
              const percentage = (value / max) * 100;

              return (
                <div
                  key={key}
                  className="space-y-3 p-4 rounded-xl border bg-card/50 shadow-sm transition-all hover:bg-muted/10"
                >
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-semibold tracking-tight text-foreground/90">
                      {t(`offerDetail.sections.${key}`, key.replace(/_/g, ' '))}
                    </label>
                    <div className="flex items-center gap-1.5 bg-primary/10 px-2.5 py-1 rounded-full border border-primary/20">
                      <span className="text-sm font-bold text-primary">{Math.round(value * 10) / 10}/10</span>
                    </div>
                  </div>

                  <div className="relative group pt-2 px-1">
                    {/* Custom Progress Bar Track */}
                    <div className="h-2 w-full bg-muted rounded-full overflow-hidden relative border border-muted-foreground/10">
                      {/* Standard Marker (1.0 weight = 10% of 10) */}
                      <div
                        className="absolute left-[10%] top-0 h-full w-px bg-foreground/10 z-10"
                        title="Standard (1.0)"
                      />

                      {/* Filled Part with Gradient */}
                      <div
                        className="absolute top-0 left-0 h-full bg-gradient-to-r from-primary/80 to-primary transition-all duration-300 ease-out rounded-full"
                        style={{ width: `${percentage}%` }}
                      >
                        <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.2)_50%,transparent_100%)] animate-[shimmer_2s_infinite]" />
                      </div>
                    </div>

                    {/* Transparent Native Slider on Top */}
                    <input
                      type="range"
                      min="0"
                      max={max}
                      step="0.1"
                      value={value}
                      onChange={(e) => {
                        const newVal = parseFloat(e.target.value);
                        setEditingSectionWeights(prev => ({
                          ...(prev || DEFAULT_SECTION_WEIGHTS),
                          [key]: newVal
                        }));
                      }}
                      className="absolute -top-1 left-0 w-full h-8 opacity-0 cursor-pointer z-20"
                    />

                    {/* Tick Marks or Markers */}
                    <div className="flex justify-between text-[10px] text-muted-foreground/50 mt-2 px-0.5 font-medium">
                      {Array.from({ length: 11 }).map((_, i) => (
                        <div key={i} className="flex flex-col items-center">
                          <div className={`h-1 w-px mb-1 transition-colors ${value >= i ? 'bg-primary/50' : 'bg-muted-foreground/20'}`} />
                          <span className={value >= i ? 'text-primary/70' : ''}>{i}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setShowSectionWeightsModal(false)}
              disabled={isSavingSectionWeights}
            >
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button
              onClick={handleSaveSectionWeights}
              loading={isSavingSectionWeights}
              className="flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              {t('common.save', 'Enregistrer')}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showConfigureAlert}
        onClose={() => setShowConfigureAlert(false)}
        title={t('offerDetail.configureQuestionsFirst', 'Configuration requise')}
      >
        <div className="space-y-6">
          <div className="space-y-4 pt-2 border-t">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-foreground uppercase tracking-wider">
                {t('common.language', 'Langue des entretiens')}
              </label>
              <CustomSelect
                value={interviewLanguage}
                onChange={(value) => handleLanguageChange(value as 'en' | 'fr' | 'custom')}
                options={[
                  { value: 'fr', label: 'Français' },
                  { value: 'en', label: 'English' },
                  { value: 'custom', label: 'Custom' }
                ]}
              />
            </div>

            <div className="flex gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900/30 rounded-lg">
              <Info className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-800 dark:text-amber-200 leading-tight">
                {t('offerDetail.configureQuestionsFirst', "Veuillez configurer le nombre de questions par section en premier avant d'activer la génération automatique.")}
              </p>
            </div>

            <div className="space-y-4 pt-4 border-t">
              <div className="flex justify-between items-center">
                <label className="text-sm font-semibold">
                  {t('offerDetail.questionsPerSection', 'Questions par section')}
                </label>
                <Badge variant={(Object.values(interviewQuestionsPerSection) as number[]).reduce((a: number, b: number) => a + b, 0) > 15 || (Object.values(interviewQuestionsPerSection) as number[]).reduce((a: number, b: number) => a + b, 0) === 0 ? 'destructive' : 'default'} className="flex gap-1">
                  <span>Total: {(Object.values(interviewQuestionsPerSection) as number[]).reduce((a: number, b: number) => a + b, 0)}</span>
                  <span>/ 15</span>
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {Object.entries(interviewQuestionsPerSection).map(([section, count]) => (
                  <div key={section} className="space-y-2">
                    <label className="text-xs font-medium capitalize">
                      {t(`offerDetail.sections.${section}`, section.replace('_', ' '))}
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        max="15"
                        value={count}
                        disabled={interviewAudios.length > 0}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0
                          setInterviewQuestionsPerSection(prev => ({
                            ...prev,
                            [section]: Math.min(15, Math.max(0, val))
                          }))
                        }}
                        className={`w-full px-3 py-1 text-sm border rounded-md bg-transparent focus:outline-none focus:ring-1 focus:ring-primary ${interviewAudios.length > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {(Object.values(interviewQuestionsPerSection) as number[]).reduce((a: number, b: number) => a + b, 0) > 15 && (
              <p className="text-xs text-destructive font-medium">
                {t('offerDetail.totalQuestionsExceeded', 'Le nombre total de questions ne peut pas dépasser 15.')}
              </p>
            )}

            {(Object.values(interviewQuestionsPerSection) as number[]).reduce((a: number, b: number) => a + b, 0) === 0 && (
              <p className="text-xs text-destructive font-medium">
                {t('offerDetail.totalQuestionsZero', 'Le nombre total de questions doit être supérieur à 0.')}
              </p>
            )}

            {interviewAudios.length > 0 && (
              <p className="text-xs text-muted-foreground mt-2">
                {t('offerDetail.countsAlreadySet', 'La configuration des questions a déjà été définie lors de la première génération et ne peut pas être modifiée.')}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 border-t pt-4">
            <Button variant="outline" onClick={() => setShowConfigureAlert(false)} disabled={isSavingAutoConfig}>
              {t('common.cancel', 'Annuler')}
            </Button>
            {interviewAudios.length > 0 ? (
              <Button
                onClick={handleEnableAutoGenerateOnly}
                loading={isSavingAutoConfig}
                disabled={isSavingAutoConfig}
              >
                {t('offerDetail.enableAutoGenerate', 'Activer la génération automatique')}
              </Button>
            ) : (
              <Button
                onClick={handleSaveQuestionsAndEnableAuto}
                loading={isSavingAutoConfig}
                disabled={isSavingAutoConfig || (Object.values(interviewQuestionsPerSection) as number[]).reduce((a: number, b: number) => a + b, 0) > 15 || (Object.values(interviewQuestionsPerSection) as number[]).reduce((a: number, b: number) => a + b, 0) === 0}
              >
                {t('common.save', 'Enregistrer et activer')}
              </Button>
            )}
          </div>
        </div>
      </Modal>

      {/* Recommended Skills Modal – shown after offer activation when LLM returns skill data */}
      <RecommendedSkillsModal
        isOpen={showRecommendedSkillsModal}
        onClose={() => setShowRecommendedSkillsModal(false)}
        onAccept={handleAcceptRecommendedSkills}
        skills={recommendedSkillsData}
        isLoading={isSavingRecommendedSkills}
        initialWeights={{
          hard_skills: offer?.ai_matching_weights?.hard_skills ?? 2,
          soft_skills: offer?.ai_matching_weights?.soft_skills ?? 2,
          nice_to_have: offer?.ai_matching_weights?.nice_to_have ?? 0,
        }}
        initialLockedSkills={lockedItems}
        initialSkillWeights={offer?.skill_weights}
      />
    </div>
  )
}
